import { useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import api, { fmtError } from "../lib/api";
import FadeIn from "../components/FadeIn";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { Trash, Plus, PencilSimple, Question as QIcon, Pause, Play, Sparkle, Headphones, X, List, Bell, Warning, CheckCircle, Globe } from "@phosphor-icons/react";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "../components/ui/dropdown-menu";
import { DndContext, closestCenter } from "@dnd-kit/core";
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import ReactMarkdown from "react-markdown";
import MarkdownEditor from "../components/MarkdownEditor";
import IntelligenceCharts from "../components/IntelligenceCharts";
import IntelligenceInfographic from "../components/IntelligenceInfographic";
import { DownloadButtons } from "../components/admin/DownloadableChartCard";
import remarkGfm from "remark-gfm";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RcTooltip,
  ResponsiveContainer,
} from "recharts";

import Logo from "../components/Logo";
import PasswordInput, { PASSWORD_MIN_LENGTH, validatePassword } from "../components/PasswordInput";
import HelpHint from "../components/HelpHint";
import { VisibilityBadge } from "../components/VisibilityBadge";
import { PMAssignmentWidget } from "./AdminExtras";
import { Switch } from "../components/ui/switch";
import { useSiteContent, getCachedSiteContent } from "../lib/useSiteContent";
import ArticleBlockEditor from "../components/ArticleBlockEditor";
import ArticleBlocks from "../components/ArticleBlocks";
import LivePreviewModal from "../components/LivePreviewModal";
import PublishConfirmDialog from "../components/admin/PublishConfirmDialog";
import CohortSendDialog from "../components/admin/CohortSendDialog";
import InlineUrduEditor from "../components/admin/InlineUrduEditor";

// Small master-toggle card used inside the WaveBuilder. Shows the current
// approval breakdown + a big toggle to flip `show_urdu_version` for the
// wave. Reachable only in edit mode (wave must already exist).
function UrduWaveToggle({ waveId, initial, onChange }) {
  const [on, setOn] = useState(!!initial);
  const [busy, setBusy] = useState(false);
  const [totals, setTotals] = useState(null);
  const load = async () => {
    try {
      // Lightweight totals endpoint — no full items list, ~10× faster than /review.
      const r = await api.get(`/admin/i18n/waves/${waveId}/totals`);
      setTotals(r.data?.totals);
      setOn(!!r.data?.show_urdu_version);
    } catch {}
  };
  useEffect(() => { if (waveId) load(); }, [waveId]);
  const doToggle = async (next, force = false) => {
    setBusy(true);
    try {
      await api.post(`/admin/i18n/waves/${waveId}/toggle`, { show_urdu_version: next, force });
      setOn(next);
      onChange?.(next);
      toast.success(next ? "Urdu is now LIVE for patrons." : "Urdu disabled for this wave.");
      load();
    } catch (e) {
      const msg = e.response?.data?.detail || "Toggle failed";
      if (next && msg.startsWith("Cannot enable Urdu")) {
        if (window.confirm(`${msg}\n\nForce enable anyway (patrons will see un-approved AI drafts)?`)) {
          doToggle(true, true);
          return;
        }
      } else {
        toast.error(msg);
      }
    } finally { setBusy(false); }
  };
  const approvedPct = totals?.total ? Math.round(totals.approved / totals.total * 100) : 0;
  return (
    <div className="bg-white border-2 border-emerald-600 p-5" data-testid="wb-urdu-toggle-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="overline text-neutral-500 mb-1">اردو · URDU VERSION</div>
          <div className="text-lg font-black tracking-tight">
            Show Urdu to patrons: <span className={on ? "text-emerald-700" : "text-neutral-500"}>{on ? "ON" : "OFF"}</span>
          </div>
          <p className="text-xs text-neutral-600 mt-1 max-w-lg">
            Patrons who pick اردو will see this wave in Urdu <em>only</em> when this is ON.
            Individual field approvals below still gate: any un-approved field silently falls back to English.
          </p>
          {totals && (
            <div className="mt-3">
              <div className="text-xs text-neutral-500 mb-1 flex items-center justify-between">
                <span>
                  <span className="font-bold text-emerald-700">{totals.approved}</span> approved · <span className="font-bold text-amber-700">{totals.draft}</span> draft · <span className="font-bold text-red-700">{totals.stale}</span> stale · <span className="font-bold text-neutral-500">{totals.missing}</span> missing
                </span>
                <span className="font-mono">{approvedPct}%</span>
              </div>
              <div className="h-1.5 bg-neutral-200 overflow-hidden">
                <div className="h-full bg-emerald-500 transition-all" style={{ width: `${approvedPct}%` }} />
              </div>
              {(totals.draft + totals.stale + totals.missing) > 0 && (
                <div className="mt-3">
                  <a
                    href={`/admin/i18n/waves/${waveId}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 hover:text-emerald-900 underline underline-offset-4"
                    data-testid="wb-urdu-review-link"
                  >
                    Review {totals.draft} draft{totals.draft === 1 ? "" : "s"}
                    {totals.stale ? ` · ${totals.stale} stale` : ""}
                    {totals.missing ? ` · ${totals.missing} missing` : ""}
                    {" "}(questions & options) →
                  </a>
                </div>
              )}
            </div>
          )}
        </div>
        <Button
          type="button"
          onClick={() => doToggle(!on)}
          disabled={busy}
          data-testid="wb-urdu-toggle"
          className={`rounded-none border-2 border-black px-6 ${on ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-white text-black hover:bg-neutral-100"}`}
        >
          {on ? "Turn OFF" : "Turn ON →"}
        </Button>
      </div>
    </div>
  );
}
import ResultColumnsPicker from "../components/admin/ResultColumnsPicker";
import PreviewModal from "../components/admin/PreviewModal";
import { OrbitRail, useOrbitStore } from "../components/admin/OrbitRail";
// AdminIncompleteProfiles removed — Incomplete tab now uses the unified
// AdminUsers table (same row + dialog as other tabs). Bulk-reminder feature
// is available via /admin/email-templates → Custom Broadcast.
import * as HELP from "../lib/adminHelp";
import { formatDate, formatDateTime } from "../lib/date";

// Module-level so both QuestionForm and WaveBuilder can use it for audience targeting.
const FILTER_FIELDS = {
  gender: ["Male", "Female", "Other"],
  age_band: ["18-24", "25-34", "35-44", "45-54", "55+"],
  region: ["Punjab", "Sindh", "KPK", "Balochistan", "Islamabad Capital Territory", "GB", "AJK"],
  sec: ["A", "B", "C", "D", "E"],
  education: ["Primary", "Secondary", "Undergrad", "Postgrad", "PhD"],
  industry: ["Tech / Software", "Banking & Finance", "Telecom", "FMCG / Retail", "Healthcare", "Education", "Government", "Agriculture", "Manufacturing", "Energy", "Media / Advertising", "Hospitality", "Logistics", "Real Estate", "Other"],
  employment_type: ["Full-time", "Part-time", "Self-employed", "Business owner", "Student", "Homemaker", "Unemployed", "Retired"],
  household_income: ["<50k", "50k-100k", "100k-250k", "250k-500k", "500k+"],
  marital_status: ["Single", "Married", "Divorced", "Widowed"],
  vehicle: ["No", "Bike", "Car"],
  housing: ["Owner", "Tenant", "Live with Parents", "Hostel"],
  interests: ["Agencies", "Economy", "Entertainment", "Health", "Politics", "Retail", "Society", "Sports", "Tech", "Travel"],
  languages: ["Urdu", "English", "Punjabi", "Sindhi", "Pashto", "Saraiki", "Balochi"],
};

const ADMIN_NAV = [
  // [slug, label, roles] — roles ∈ {"admin","pm","researcher"}; empty = admin-only
  ["", "Dashboard", ["admin", "pm", "researcher"]],
  ["health", "Oodel Health", ["admin"]],
  ["quality-issues", "Quality Issues", ["admin"]],
  ["trackers", "Trackers", ["admin", "researcher"]],
  ["questions", "Question Bank", ["admin", "researcher"]],
  ["waves", "Waves", ["admin", "pm", "researcher"]],
  ["survey-results", "Survey Results", ["admin", "pm", "researcher"]],
  ["intelligence", (<span>oodel <sup className="text-[10px] -top-0.5 relative">x</sup></span>), ["admin", "pm", "researcher"]],
  ["profile-dna", "Patron Profile DNA", ["admin"]],
  ["sentiment", "Public Sentiment", ["admin", "researcher"]],
  ["customer-sentiment", "Businesses", ["admin"]],
  ["customer-sentiment-insights", "Review Queue", ["admin"]],
  ["customer-sentiment-templates", "Business Templates", ["admin"]],
  ["customer-sentiment-categories", "Categories", ["admin"]],
  ["customer-sentiment-performance", "Business Performance", ["admin"]],
  ["finance-revenue", "Revenue Overview", ["admin"]],
  ["finance-invoices", "oodel SCORE Invoices", ["admin"]],
  ["finance-payments", "Enterprise & Subscriber Payments", ["admin"]],
  ["audience", "Audience Explorer", ["admin", "pm"]],
  ["customers", "Business Customers", ["admin", "pm"]],
  ["brands", "Brand Sentiment", ["admin", "pm"]],
  // Incomplete Profiles is now a tab inside All Users (?tab=incomplete).
  ["inbox", "PM Inbox", ["admin", "pm"]],
  // Waitlist menu retired — un-converted signups are now stub users
  // surfaced via All Users → Incomplete profile tab.
  // ["waitlist", "Waitlist (Patrons)", ["admin", "pm"]],
  ["raffle", "Raffle", ["admin"]],
  ["rewards", "Rewards & Vouchers", ["admin"]],
  ["partners", "Partners", ["admin"]],
  ["referrals", "Referrals", ["admin"]],
  ["sectors", "Sectors CMS", ["admin"]],
  ["legal-pages", "Legal Pages", ["admin"]],
  ["site-content", "Site Content", ["admin"]],
  ["editorial", "Editorial", ["admin", "researcher"]],
  ["team", "Team", ["admin"]],
  ["users", "All Users", ["admin"]],
  ["ambassadors", "Campus Ambassadors", ["admin"]],
  ["demo-accounts", "Demo Accounts", ["admin"]],
  ["menu-permissions", "Menu Permissions", ["admin"]],
  ["i18n/labels", "Urdu Labels", ["admin"]],
  ["email-templates", "Email Templates", ["admin"]],
  ["email-outbox", "Email Outbox", ["admin"]],
  ["my-account", "My Account", ["admin", "pm", "researcher"]],
];

// Sidebar grouping — desktop only. Mobile drawer stays flat for simplicity.
// Order = Survey Content first, then by typical admin daily workflow.
// "uncategorised" items (Dashboard, Oodel Health, My Account) sit outside groups.
const ADMIN_GROUPS = [
  {
    id: "survey-content",
    label: "Survey Content",
    defaultOpen: true,   // only group expanded on first load
    slugs: ["trackers", "questions", "waves", "survey-results", "editorial", "intelligence", "profile-dna"],
  },
  {
    id: "public-sentiment",
    label: "Public Sentiment",
    defaultOpen: false,
    slugs: ["sentiment"],
  },
  {
    id: "customer-sentiment",
    label: "oodel SCORE",
    defaultOpen: false,
    slugs: ["customer-sentiment", "customer-sentiment-insights", "customer-sentiment-templates", "customer-sentiment-categories", "customer-sentiment-performance"],
  },
  {
    id: "finance",
    label: "Finance",
    defaultOpen: false,
    slugs: ["finance-revenue", "finance-invoices", "finance-payments"],
  },
  {
    id: "audience-customers",
    label: "Audience & Customers",
    defaultOpen: false,
    slugs: ["audience", "customers", "brands", "inbox"],
  },
  {
    id: "incentives-growth",
    label: "Incentives & Growth",
    defaultOpen: false,
    slugs: ["raffle", "rewards", "partners", "referrals"],
  },
  {
    id: "marketing",
    label: "Marketing & Public Site",
    defaultOpen: false,
    slugs: ["sectors", "legal-pages", "site-content"],
  },
  {
    id: "users-access",
    label: "Users & Access",
    defaultOpen: false,
    slugs: ["team", "users", "ambassadors", "demo-accounts", "menu-permissions"],
  },
  {
    id: "localization-comms",
    label: "Localization & Comms",
    defaultOpen: false,
    slugs: ["i18n/labels", "email-templates", "email-outbox"],
  },
];
const UNGROUPED_TOP = ["", "health", "quality-issues"];           // Dashboard, Oodel Health, Quality Issues
const UNGROUPED_BOTTOM = ["my-account"];        // My Account

function AdminNavItems({ onClick, alerts = 0, qiUnseen = 0, onQualityIssuesPage = false, inboxUnread = 0, onInboxPage = false, csInsightsPending = 0, onCsInsightsPage = false, growth = {}, role = "admin", menuOverrides = null, incentiveMode = "rewards", flat = false, onHealthPage = false }) {
  const visible = ADMIN_NAV.filter(([slug, , roles]) => {
    // Hide raffle entry unless admin has actually set incentive_mode = raffle.
    if (slug === "raffle" && incentiveMode !== "raffle") return false;
    // Hide Rewards & Vouchers unless admin has set incentive_mode = rewards.
    if (slug === "rewards" && incentiveMode !== "rewards") return false;
    // Urdu Labels stays visible for admins regardless of the master toggle —
    // admins prepare translations offline while the feature is invisible.
    if (role === "admin") return true;
    // PMs and researchers: if admin has set an override list, use it. Otherwise fall back to hardcoded role array.
    if (menuOverrides && (role === "pm" || role === "researcher")) {
      const allowed = menuOverrides[role] || [];
      return allowed.includes(slug);
    }
    return !roles || roles.includes(role);
  });
  const visibleBySlug = new Map(visible.map((row) => [row[0], row]));

  const renderLeaf = (slug, label) => {
    // Waitlist badge: new-24h signups + unconfirmed (whichever is more attention-worthy).
    const waitlistBadge = slug === "waitlist" ? (growth.waitlist_new_24h || 0) : 0;
    return (
      <NavLink
        key={slug}
        to={`/admin/${slug}`}
        end={slug === ""}
        onClick={onClick}
        className={({ isActive }) => `flex items-center justify-between rounded-xl px-3 py-2 text-sm transition-colors ${isActive ? 'bg-mango/10 text-mango font-semibold' : 'text-neutral-600 hover:bg-neutral-100 hover:text-black'}`}
        data-testid={`admin-nav-${slug || 'dashboard'}`}
      >
        <span>{label}</span>
        {slug === "health" && alerts > 0 && !onHealthPage && (
          <span className="bg-red-500 text-white text-[10px] font-mono font-bold px-1.5 py-0.5 leading-none rounded-full">{alerts}</span>
        )}
        {slug === "quality-issues" && qiUnseen > 0 && !onQualityIssuesPage && (
          <span className="bg-red-500 text-white text-[10px] font-mono font-bold px-1.5 py-0.5 leading-none rounded-full" data-testid="qi-sidebar-badge">{qiUnseen}</span>
        )}
        {slug === "inbox" && inboxUnread > 0 && !onInboxPage && (
          <span className="bg-red-500 text-white text-[10px] font-mono font-bold px-1.5 py-0.5 leading-none rounded-full" data-testid="inbox-sidebar-badge">{inboxUnread}</span>
        )}
        {slug === "customer-sentiment-insights" && csInsightsPending > 0 && !onCsInsightsPage && (
          <span className="bg-red-500 text-white text-[10px] font-mono font-bold px-1.5 py-0.5 leading-none rounded-full" data-testid="cs-insights-sidebar-badge">{csInsightsPending}</span>
        )}
        {waitlistBadge > 0 && (
          <span className="bg-mango text-white text-[10px] font-mono font-bold px-1.5 py-0.5 leading-none rounded-full" data-testid={`admin-nav-${slug}-badge`} title={`${waitlistBadge} new in last 24h`}>+{waitlistBadge}</span>
        )}
      </NavLink>
    );
  };

  // ============ FLAT MODE (mobile drawer) ============
  if (flat) {
    return <>{visible.map(([slug, label]) => renderLeaf(slug, label))}</>;
  }

  // ============ GROUPED MODE (desktop sidebar) ============
  // Top ungrouped (Dashboard, Oodel Health), then the collapsible groups,
  // then bottom ungrouped (My Account).
  return (
    <>
      {UNGROUPED_TOP.map((slug) => {
        const row = visibleBySlug.get(slug);
        return row ? renderLeaf(row[0], row[1]) : null;
      })}
      {ADMIN_GROUPS.map((g) => {
        const items = g.slugs.map((s) => visibleBySlug.get(s)).filter(Boolean);
        if (items.length === 0) return null;   // skip empty groups (e.g. all items hidden by mode/role)
        return (
          <AdminNavGroup key={g.id} group={g}>
            {items.map(([slug, label]) => renderLeaf(slug, label))}
          </AdminNavGroup>
        );
      })}
      <div className="mt-3 pt-3 border-t border-neutral-100">
        {UNGROUPED_BOTTOM.map((slug) => {
          const row = visibleBySlug.get(slug);
          return row ? renderLeaf(row[0], row[1]) : null;
        })}
      </div>
    </>
  );
}

// Collapsible group with localStorage-persisted open state.
// Auto-expands when the current route is inside the group (regardless of saved
// preference) so admins never lose context after a deep-link click.
function AdminNavGroup({ group, children }) {
  const loc = useLocation();
  const storageKey = `admin_nav_group_${group.id}_open`;
  const [open, setOpen] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved !== null) return saved === "true";
    } catch { /* ignore */ }
    return !!group.defaultOpen;
  });
  // Auto-expand when current path is inside this group.
  const currentSlug = loc.pathname.replace(/^\/admin\/?/, "").split("/")[0];
  const containsCurrent = group.slugs.includes(currentSlug);
  useEffect(() => {
    if (containsCurrent && !open) setOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containsCurrent]);
  const toggle = () => {
    setOpen((prev) => {
      const next = !prev;
      try { localStorage.setItem(storageKey, String(next)); } catch { /* ignore */ }
      return next;
    });
  };
  return (
    <div className="mt-1" data-testid={`admin-group-${group.id}`}>
      <button
        type="button"
        onClick={toggle}
        className="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-[13px] font-extrabold uppercase tracking-wide text-black transition-colors"
        data-testid={`admin-group-${group.id}-toggle`}
        aria-expanded={open}
      >
        <span className="truncate min-w-0">{group.label}</span>
        <span className="font-mono text-[12px] leading-none shrink-0">{open ? "−" : "+"}</span>
      </button>
      {open && <div className="space-y-0.5">{children}</div>}
    </div>
  );
}

export function AdminLayout() {
  const { user, logout, loading, loggingOut } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  // When admin is already viewing /admin/health, suppress the sidebar alert
  // badge — they're literally looking at the alert page, the red pill is
  // pure noise. The underlying `alerts` count still polls so the badge
  // reappears the moment they navigate away.
  const onHealthPage = loc.pathname.startsWith("/admin/health");
  const onQualityIssuesPage = loc.pathname.startsWith("/admin/quality-issues");
  const onInboxPage = loc.pathname.startsWith("/admin/inbox");
  const onCsInsightsPage = loc.pathname.startsWith("/admin/customer-sentiment-insights");
  const [drawer, setDrawer] = useState(false);
  const [alerts, setAlerts] = useState(() => {
    const c = api.getCached("/admin/health/alerts-summary");
    return c ? (c.warning || 0) + (c.critical || 0) : 0;
  });
  // PM Inbox badge: total unread messages across every customer/business
  // this user (admin or PM) can see. Same polling pattern as the Oodel
  // Health alerts badge above.
  const [inboxUnread, setInboxUnread] = useState(() => {
    const c = api.getCached("/admin/inbox/unread-summary");
    return c?.unread || 0;
  });
  // Quality-issues badge: unseen count since the admin's
  // `quality_issues_last_seen_at` (server-side). Polls every 60s.
  const [qiUnseen, setQiUnseen] = useState(() => {
    const c = api.getCached("/admin/quality-issues/summary");
    return c?.unseen || 0;
  });
  // Review Queue badge: total pending-review Oodel X insights (weekly
  // overviews + monthly/quarterly/yearly narratives) across every business
  // and branch, same polling pattern as the other sidebar badges.
  const [csInsightsPending, setCsInsightsPending] = useState(() => {
    const c = api.getCached("/admin/cs/ai-insights/pending-count");
    return c?.count || 0;
  });
  const [growth, setGrowth] = useState(() => api.getCached("/waitlist/admin/growth-summary") || {});
  // SWR cache for the per-role menu overrides — paints the sidebar instantly
  // on refresh, then silently revalidates from the server.
  const MENU_CACHE_KEY = "oodel_admin_menu_cache_v1";
  const menuCached = (() => {
    try { return JSON.parse(localStorage.getItem(MENU_CACHE_KEY) || "null"); } catch { return null; }
  })();
  const [menuOverrides, setMenuOverrides] = useState(menuCached);
  // Hardcoded "rewards" default caused the sidebar to briefly render Rewards
  // → swap to Raffle on every refresh. Seed from the cached CMS so the
  // sidebar paints with the admin's actual mode on first frame.
  const [incentiveMode, setIncentiveMode] = useState(() => {
    const c = getCachedSiteContent();
    return (c.incentive_mode || "rewards").toLowerCase();
  });
  useEffect(() => {
    if (loggingOut) return;
    if (!loading && (!user || !["admin", "pm", "researcher"].includes(user.role))) nav("/login");
  }, [user, loading, loggingOut, nav]);
  // Menu overrides — PMs/researchers respect what admin allowed.
  useEffect(() => {
    if (!user) return;
    api.get("/admin/menu-permissions/public").then((r) => {
      setMenuOverrides(r.data);
      try { localStorage.setItem(MENU_CACHE_KEY, JSON.stringify(r.data)); } catch { /* quota */ }
    }).catch(() => {});
    // Hide Raffle / legacy Rewards nav unless the corresponding incentive mode is on.
    api.get("/site-content").then((r) => setIncentiveMode((r.data?.incentive_mode || "rewards").toLowerCase())).catch(() => {});
  }, [user]);
  // Poll the health endpoint every 60s for alert count (shown in sidebar + bell). Admins only.
  useEffect(() => {
    if (!user || user.role !== "admin") return;
    const fetchAlerts = () => api.get("/admin/health/alerts-summary").then((r) => setAlerts(r.data.warning + r.data.critical)).catch(() => {});
    fetchAlerts();
    const t = setInterval(fetchAlerts, 60_000);
    return () => clearInterval(t);
  }, [user]);
  // Poll quality-issues summary every 60s for the sidebar badge. Admins only.
  // When the admin lands ON the page, the page itself POSTs /mark-seen so the
  // badge clears within the next poll. We force an immediate refetch on
  // navigation away so the badge re-syncs when they leave the page too.
  useEffect(() => {
    if (!user || user.role !== "admin") return;
    const fetchQI = () => api.get("/admin/quality-issues/summary").then((r) => setQiUnseen(r.data?.unseen || 0)).catch(() => {});
    fetchQI();
    const t = setInterval(fetchQI, 60_000);
    return () => clearInterval(t);
  }, [user, loc.pathname]);
  // Poll the PM Inbox unread-summary every 60s for the sidebar badge —
  // admins AND PMs both get this (a PM only sees their own assigned unread
  // count, per the endpoint's scoping — same as Oodel Health's alert badge).
  useEffect(() => {
    if (!user || !["admin", "pm"].includes(user.role)) return;
    const fetchInbox = () => api.get("/admin/inbox/unread-summary").then((r) => setInboxUnread(r.data?.unread || 0)).catch(() => {});
    fetchInbox();
    const t = setInterval(fetchInbox, 60_000);
    return () => clearInterval(t);
  }, [user]);

  // Poll the Review Queue pending count every 60s for the sidebar badge. Admins only.
  useEffect(() => {
    if (!user || user.role !== "admin") return;
    const fetchCsPending = () => api.get("/admin/cs/ai-insights/pending-count").then((r) => setCsInsightsPending(r.data?.count || 0)).catch(() => {});
    fetchCsPending();
    const t = setInterval(fetchCsPending, 60_000);
    return () => clearInterval(t);
  }, [user]);

  // Poll the waitlist growth-summary every 60s (PMs + admins). Shows badge on Waitlist nav.
  useEffect(() => {
    if (!user || !["admin", "pm"].includes(user.role)) return;
    const fetchGrowth = () => api.get("/waitlist/admin/growth-summary").then((r) => setGrowth(r.data || {})).catch(() => {});
    fetchGrowth();
    const t = setInterval(fetchGrowth, 60_000);
    return () => clearInterval(t);
  }, [user]);
  if (!user || menuOverrides === null) return <div className="p-12">Loading…</div>;
  const onLogout = () => { logout(); nav("/"); setDrawer(false); };
  const close = () => setDrawer(false);

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-[#F4F7F6]">
      {/* Mobile top bar */}
      <header className="md:hidden bg-white text-black px-4 h-14 flex items-center justify-between border-b border-neutral-200 sticky top-0 z-40">
        <Logo to="/admin" size="sm" suffix={<span className="overline text-neutral-400">CMS</span>} />
        <div className="flex items-center gap-2">
          {alerts > 0 && !onHealthPage && (
            <Link to="/admin/health" className="relative inline-flex items-center justify-center w-9 h-9" aria-label="View health alerts" data-testid="admin-alerts-bell">
              <Bell size={20} weight="fill" className="text-mango" />
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-mono font-bold px-1.5 py-0.5 leading-none rounded-full">{alerts}</span>
            </Link>
          )}
          <Sheet open={drawer} onOpenChange={setDrawer}>
            <SheetTrigger asChild>
              <button data-testid="admin-menu-trigger" aria-label="Open menu" className="w-9 h-9 inline-flex items-center justify-center rounded-lg border border-neutral-200 hover:bg-neutral-100">
                <List size={20} weight="bold" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[88vw] max-w-xs p-0 border-0 bg-white text-black flex flex-col" data-testid="admin-drawer">
              <SheetHeader className="p-5 border-b border-neutral-100 flex flex-row items-center justify-between space-y-0 flex-shrink-0">
                <SheetTitle asChild><Logo to="/admin" size="md" suffix={<span className="overline text-neutral-400">CMS</span>} /></SheetTitle>
              </SheetHeader>
              <div className="flex-1 overflow-y-auto">
                <div className="p-5 border-b border-neutral-100">
                  <div className="overline text-neutral-400 mb-1">SIGNED IN</div>
                  <div className="font-bold">{user.name}</div>
                  <div className="text-xs text-neutral-400 font-mono">{user.email}</div>
                </div>
                <nav className="py-2 px-3 space-y-0.5" data-testid="admin-drawer-nav">
                  <AdminNavItems onClick={close} alerts={alerts} qiUnseen={qiUnseen} onQualityIssuesPage={onQualityIssuesPage} inboxUnread={inboxUnread} onInboxPage={onInboxPage} csInsightsPending={csInsightsPending} onCsInsightsPage={onCsInsightsPage} growth={growth} role={user?.role || "admin"} menuOverrides={menuOverrides} incentiveMode={incentiveMode} onHealthPage={onHealthPage} flat />
                </nav>
              </div>
              <div className="p-5 border-t border-neutral-100 flex-shrink-0 bg-white">
                <Button onClick={onLogout} className="w-full rounded-xl bg-mango hover:bg-mango/90 text-white" data-testid="m-admin-logout">Sign out</Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </header>

      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-64 bg-white text-black p-5 border-r border-neutral-200 flex-col flex-shrink-0 md:sticky md:top-0 md:h-screen md:overflow-y-auto">
        <Logo to="/admin" size="md" suffix={<span className="overline text-neutral-400">CMS</span>} />
        <div className="overline text-neutral-400 mt-1 mb-6 flex items-center justify-between" style={{ fontSize: '0.55rem' }}>
          <span>CONTROL ROOM</span>
          {alerts > 0 && !onHealthPage && (
            <Link to="/admin/health" className="relative inline-flex items-center" data-testid="admin-alerts-bell-desktop">
              <Bell size={14} weight="fill" className="text-mango" />
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] font-mono font-bold px-1 leading-none rounded-full">{alerts}</span>
            </Link>
          )}
        </div>
        <nav className="space-y-0.5 flex-1 overflow-y-auto">
          <AdminNavItems alerts={alerts} qiUnseen={qiUnseen} onQualityIssuesPage={onQualityIssuesPage} inboxUnread={inboxUnread} onInboxPage={onInboxPage} csInsightsPending={csInsightsPending} onCsInsightsPage={onCsInsightsPage} growth={growth} role={user?.role || "admin"} menuOverrides={menuOverrides} incentiveMode={incentiveMode} onHealthPage={onHealthPage} />
        </nav>
        <div className="border-t border-neutral-100 pt-3 mt-3 flex items-center gap-2">
          <div className="w-9 h-9 rounded-full bg-mango/10 text-mango flex items-center justify-center font-bold text-sm shrink-0">{(user.name || "?")[0]}</div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold truncate">{user.name}</div>
            <button onClick={onLogout} className="text-xs text-neutral-400 hover:text-red-600" data-testid="admin-logout">Sign out</button>
          </div>
        </div>
      </aside>
      <main className="flex-1 p-4 md:p-8 overflow-auto"><Outlet /></main>
    </div>
  );
}

export function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(() => api.getCached("/admin/stats"));
  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => setStats({}));
  }, [user?.role]);
  if (!stats) return <div>Loading…</div>;
  const cards = [
    ["Panelists", stats.panelists], ["Questions", stats.questions],
    ["Trackers", stats.trackers], ["Active waves", stats.active_waves],
    ["Closed waves", stats.closed_waves], ["Total responses", stats.responses],
    ["Rewards", stats.rewards],
  ];
  return (
    <div>
      <div className="mb-8">
        <div className="text-[11px] font-bold tracking-widest text-mango uppercase">Overview</div>
        <h1 className="text-3xl font-black tracking-tight mt-0.5">Operations overview</h1>
        {user?.role === "admin" && (
          <p className="text-sm text-neutral-500 mt-1.5">
            Looking for revenue? It's moved to <Link to="/admin/finance-revenue" className="text-mango font-semibold hover:underline">Finance → Revenue Overview</Link>.
          </p>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="stats-grid">
        {cards.map(([l, v]) => (
          <div key={l} className="bg-white rounded-2xl border border-neutral-200 p-5">
            <div className="text-[10px] font-bold tracking-wider text-neutral-400 uppercase">{l}</div>
            <div className="text-2xl font-black mt-1.5">{v}</div>
          </div>
        ))}
      </div>

      {/* War-room charts — visible to admin + researcher (anyone passing require_staff). */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <AdminSignupsChart />
        <AdminLiveResponseStream />
      </div>
    </div>
  );
}

// ---------- Signups over time (line chart, split by acquisition path) ----------
const SIGNUPS_PERIODS = [
  { value: "today", label: "Today" },
  { value: "7d", label: "7D" },
  { value: "1m", label: "1M" },
  { value: "3m", label: "3M" },
  { value: "6m", label: "6M" },
  { value: "1y", label: "1Y" },
  { value: "all", label: "All" },
];

function AdminSignupsChart() {
  const [period, setPeriod] = useState("1m");
  const [data, setData] = useState(null);
  // Per-series visibility — click the legend to hide/show a line.
  const [hidden, setHidden] = useState({});
  const chartRef = useRef(null);
  const toggle = (k) => setHidden((h) => ({ ...h, [k]: !h[k] }));
  useEffect(() => {
    setData(null);
    api.get(`/admin/signups-timeseries?period=${period}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ series: [] }));
  }, [period]);
  const series = data?.series || [];
  const total = series.reduce((n, r) => n + (r.total || 0), 0);
  const bucketLabel = data?.bucket === "day" ? "day" : data?.bucket === "week" ? "week" : "month";
  // Aggregate per-series totals for legend pill copy.
  const totals = series.reduce(
    (acc, r) => ({
      waitlist: acc.waitlist + (r.waitlist || 0),
      direct: acc.direct + (r.direct || 0),
      referral: acc.referral + (r.referral || 0),
    }),
    { waitlist: 0, direct: 0, referral: 0 }
  );
  const seriesMeta = [
    { key: "waitlist", color: "#16A34A", label: "Waitlist" },
    { key: "direct",   color: "#0A0A0A", label: "Direct" },
    { key: "referral", color: "#F4C430", label: "Referral" },
  ];
  return (
    <div className="bg-white rounded-2xl border border-neutral-200 p-5" data-testid="admin-signups-chart">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <div className="overline text-mango">SIGNUPS OVER TIME</div>
          <h3 className="text-2xl font-black tracking-tighter mt-1">{total} signups · split by entry path</h3>
          <p className="text-xs text-neutral-500 mt-1">Bucket: 1 {bucketLabel} · counts only patrons with a complete profile. Waitlist (came in via the waitlist queue) · Direct (signed up directly, no referral) · Referral (used someone's referral link AND credit was awarded). Click a legend pill to hide/show that series.</p>
        </div>
        <div role="group" aria-label="Time range" className="inline-flex border border-neutral-300" data-testid="signups-time-range">
          {SIGNUPS_PERIODS.map((p) => (
            <button
              key={p.value}
              onClick={() => setPeriod(p.value)}
              data-testid={`signups-range-${p.value}`}
              className={`px-2.5 py-1 text-xs font-mono uppercase tracking-wider transition-colors ${
                period === p.value
                  ? "bg-black text-mango font-bold"
                  : "bg-white text-neutral-600 hover:bg-neutral-100"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <DownloadButtons targetRef={chartRef} filenameBase={`signups-${period}`} />
      </div>
      {/* Interactive legend — click a pill to toggle that series on/off. */}
      <div className="flex flex-wrap gap-2 mb-3" data-testid="signups-legend">
        {seriesMeta.map((m) => {
          const off = !!hidden[m.key];
          return (
            <button
              key={m.key}
              type="button"
              onClick={() => toggle(m.key)}
              data-testid={`signups-toggle-${m.key}`}
              className={`flex items-center gap-2 px-3 py-1 border-2 text-xs font-mono transition-all ${off ? "opacity-40 border-neutral-300 bg-neutral-50" : "border-black bg-white"}`}
              style={{ borderColor: off ? undefined : m.color }}
            >
              <span className="w-3 h-3 rounded-full" style={{ background: m.color, opacity: off ? 0.4 : 1 }} />
              <span className="font-bold">{m.label}</span>
              <span className="text-neutral-500">{totals[m.key]}</span>
            </button>
          );
        })}
      </div>
      <div ref={chartRef} style={{ width: "100%", height: 260 }}>
        {!data ? (
          <div className="h-full flex items-center justify-center text-neutral-400 text-sm">Loading…</div>
        ) : series.length === 0 ? (
          <div className="h-full flex items-center justify-center text-neutral-400 text-sm">No signups in this period.</div>
        ) : (
          <ResponsiveContainer>
            <LineChart data={series} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
              <CartesianGrid stroke="#e5e5e5" strokeDasharray="3 3" />
              <XAxis dataKey="bucket" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono, monospace" }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <RcTooltip contentStyle={{ fontSize: 12, borderRadius: 0 }} />
              {seriesMeta.map((m) => (
                <Line
                  key={m.key}
                  type="monotone"
                  dataKey={m.key}
                  stroke={m.color}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  name={m.label}
                  hide={!!hidden[m.key]}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

// ---------- Live respondent stream (manual refresh; admin pulls when ready) ----------
function AdminLiveResponseStream() {
  const [items, setItems] = useState(() => api.getCached("/admin/live-responses?limit=20")?.items || []);
  const [loading, setLoading] = useState(() => !api.getCached("/admin/live-responses?limit=20"));
  const [refreshing, setRefreshing] = useState(false);
  const [lastFetchedAt, setLastFetchedAt] = useState(null);
  const [liveWaveId, setLiveWaveId] = useState(null);
  // Gate the one-shot Repair Orphans button behind a CMS toggle. Default OFF
  // (admin flips it ON only when running a historical cleanup; deletes
  // auto-clean from Phase 8.21 onwards, so the button is rarely needed).
  const [showRepair, setShowRepair] = useState(false);
  useEffect(() => {
    api.get("/site-content").then((r) => {
      setShowRepair(((r.data?.admin_repair_orphans_button_enabled || "false") + "").toLowerCase() === "true");
    }).catch(() => {});
  }, []);
  const fetchNow = async () => {
    setRefreshing(true);
    try {
      const r = await api.get("/admin/live-responses?limit=20");
      setItems(r.data?.items || []);
      setLastFetchedAt(new Date());
    } catch { /* surfaced via lastFetchedAt staying old */ }
    finally { setLoading(false); setRefreshing(false); }
  };
  useEffect(() => { fetchNow(); }, []);  // initial load only — admin refreshes manually
  const fmtTime = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      const now = new Date();
      const diff = Math.round((now - d) / 1000);
      if (diff < 60) return `${diff}s ago`;
      if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
      if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
      return formatDate(d);
    } catch { return "—"; }
  };
  const fmtTtc = (s) => {
    if (s == null) return "—";
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    return `${m}m ${s % 60}s`;
  };
  return (
    <div className="bg-white rounded-2xl border border-neutral-200 p-5" data-testid="admin-live-stream">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <div className="overline text-mango flex items-center gap-2">
            <span className="inline-block w-1.5 h-1.5 bg-mango rounded-full" /> LIVE RESPONDENT STREAM
          </div>
          <h3 className="text-2xl font-black tracking-tighter mt-1">Last 20 responses</h3>
          <p className="text-xs text-neutral-500 mt-1">
            Names shown in full; demographic tag = gender · age · region · SEC. Click a wave name to see preliminary live fielding data (pre-ETL).
            {lastFetchedAt && (
              <> · Last refreshed <span className="font-mono">{fmtTime(lastFetchedAt.toISOString())}</span></>
            )}
          </p>
        </div>
        <button
          type="button"
          onClick={fetchNow}
          disabled={refreshing}
          data-testid="live-stream-refresh"
          className="inline-flex items-center gap-2 px-3 py-2 border-2 border-black bg-white text-sm font-bold hover:bg-mango hover:text-black disabled:opacity-50 transition-colors"
          title="Pull the latest 20 responses"
        >
          <span className={refreshing ? "inline-block animate-spin" : "inline-block"}>↻</span>
          {refreshing ? "Refreshing…" : "Refresh"}
        </button>
        <button
          type="button"
          data-testid="repair-orphans"
          title="Find responses whose patron has since been deleted, remove them, and rebuild wave counters. Idempotent — safe to run anytime."
          className={`inline-flex items-center gap-2 px-3 py-2 border-2 border-neutral-300 bg-white text-xs font-bold hover:bg-neutral-900 hover:text-white transition-colors ${showRepair ? "" : "hidden"}`}
          onClick={async () => {
            if (!window.confirm("Clean up responses from deleted users and rebuild wave counts?")) return;
            try {
              const { data } = await api.post("/admin/repair/orphaned-responses");
              const removed = data.orphan_responses_deleted || 0;
              const recounted = data.waves_recounted || 0;
              if (removed) toast.success(`Removed ${removed} orphan response${removed === 1 ? "" : "s"} · ${recounted} wave${recounted === 1 ? "" : "s"} recounted`);
              else toast.info("No orphan responses — everything's clean.");
              await fetchNow();
            } catch (err) { toast.error(err.response?.data?.detail || "Repair failed"); }
          }}
        >
          🧹 Repair orphans
        </button>
      </div>
      <div className="overflow-y-auto" style={{ maxHeight: 260 }}>
        {loading && items.length === 0 ? (
          <div className="text-sm text-neutral-400 py-6 text-center">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-sm text-neutral-400 py-6 text-center">No responses yet.</div>
        ) : (
          <ul className="divide-y divide-neutral-200" data-testid="live-stream-list">
            {items.map((r) => (
              <li key={r.id} className="py-2 grid grid-cols-12 gap-2 items-center text-sm" data-testid={`live-stream-row-${r.id}`}>
                <div className="col-span-3 md:col-span-2 font-mono text-xs text-neutral-500">{fmtTime(r.completed_at)}</div>
                <div className="col-span-5 md:col-span-4 truncate">
                  <span className="font-semibold">{r.redacted_name}</span>
                  {r.demographic_tag && <span className="text-xs text-neutral-500 ml-1">({r.demographic_tag})</span>}
                </div>
                <div className="col-span-3 md:col-span-4 truncate text-xs">
                  {r.wave_id ? (
                    <button
                      type="button"
                      onClick={() => setLiveWaveId(r.wave_id)}
                      data-testid={`live-stream-wave-${r.wave_id}`}
                      className="text-mango hover:underline font-medium"
                      title="Click to see preliminary live fielding (pre-ETL)"
                    >
                      {r.wave_name}
                    </button>
                  ) : (
                    <span className="text-neutral-600">{r.wave_name}</span>
                  )}
                  {r.is_void && (
                    <span
                      className="ml-1 text-[10px] bg-red-100 text-red-700 px-1"
                      title={r.void_reason
                        ? `Voided: ${r.void_reason.startsWith("fast_fill") ? "Fast-fill (completed too quickly)" : r.void_reason.startsWith("straight_line") ? "Straight-lining (same answer to every question)" : r.void_reason}${r.points_awarded ? ` · ${r.points_awarded > 0 ? "+" : ""}${r.points_awarded} pts` : ""}`
                        : "Voided — no points awarded"}
                    >
                      VOID
                    </span>
                  )}
                  {r.screened_out && (
                    <span className="ml-1 text-[10px] bg-neutral-200 text-neutral-700 px-1" title={`Demographic quota full · ${r.points_awarded || 0} pts compensation`}>SCREENED</span>
                  )}
                  {!r.is_void && !r.screened_out && r.points_awarded != null && (
                    <span className="ml-1 text-[10px] bg-emerald-50 text-emerald-700 px-1" title={`+${r.points_awarded} points awarded`}>+{r.points_awarded}pts</span>
                  )}
                </div>
                <div className="col-span-1 md:col-span-2 text-right font-mono text-xs text-neutral-500">{fmtTtc(r.time_to_complete_seconds)}</div>
              </li>
            ))}
          </ul>
        )}
      </div>
      {liveWaveId && <LivePreviewModal waveId={liveWaveId} onClose={() => setLiveWaveId(null)} />}
    </div>
  );
}

// Helper tooltip [?] icon used across admin
function Hint({ text }) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex w-4 h-4 items-center justify-center border border-neutral-400 rounded-full text-[10px] text-neutral-500 ml-1 cursor-help hover:bg-neutral-100">?</span>
        </TooltipTrigger>
        <TooltipContent className="max-w-[260px] text-xs bg-black text-white rounded-none">{text}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// =================== QUESTIONS ===================
export function AdminQuestions() {
  const [items, setItems] = useState(() => api.getCached("/questions") || []);
  const [trackers, setTrackers] = useState(() => api.getCached("/trackers") || []);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filters, setFilters] = useState({ q: "", sector: "", tracker_id: "", display_type: "", visibility: "" });
  const [previewQ, setPreviewQ] = useState(null);
  // Wave-usage index — key = question_id, value = [{id, name, status}, ...].
  // Powers the "Part of: <wave>" chips on each question card so admin can see
  // at-a-glance which surveys currently include each question.
  // SEEDED from the cached /waves response so the "USED IN" pills paint on
  // the very first frame — otherwise the questions list (which IS cached)
  // would render BEFORE waveUsage populated, and the pills would visibly
  // pop in a moment later. World-class rule: any data that decorates an
  // already-cached list must itself be cache-seeded so the decoration
  // doesn't visibly arrive late.
  const buildUsageIndex = (waves) => {
    const idx = {};
    for (const wv of (waves || [])) {
      for (const qid of (wv.question_ids || [])) {
        if (!idx[qid]) idx[qid] = [];
        idx[qid].push({ id: wv.id, name: wv.name, status: wv.status });
      }
    }
    return idx;
  };
  const [waveUsage, setWaveUsage] = useState(() => buildUsageIndex(api.getCached("/waves")));
  const [loading, setLoading] = useState(() => !api.getCached("/questions"));

  const load = async () => {
    try {
      const [q, t, w] = await Promise.all([
        api.get("/questions"),
        api.get("/trackers"),
        api.get("/waves"),  // we own this index client-side so the questions API stays cheap
      ]);
      setItems(q.data); setTrackers(t.data);
      setWaveUsage(buildUsageIndex(w.data));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this question? This cannot be undone.")) return;
    await api.delete(`/questions/${id}`);
    load();
  };

  const filtered = items.filter((q) => {
    if (filters.q && !q.text.toLowerCase().includes(filters.q.toLowerCase())) return false;
    if (filters.sector && q.sector !== filters.sector) return false;
    if (filters.tracker_id && q.tracker_id !== filters.tracker_id) return false;
    if (filters.display_type && q.display_type !== filters.display_type) return false;
    if (filters.visibility && (q.visibility || "public") !== filters.visibility) return false;
    return true;
  });

  return (
    <div>
      <div className="border-b-2 border-black pb-4 mb-6 flex justify-between items-end">
        <div>
          <div className="overline flex items-center gap-1">QUESTION BANK <HelpHint text={HELP.PAGE.questions} /></div>
          <h1 className="text-4xl font-black tracking-tighter">Questions</h1>
          <p className="text-sm text-neutral-600 mt-2 max-w-2xl">{HELP.PAGE.questions}</p>
        </div>
        <Button data-testid="new-question-btn" onClick={() => { setEditing(null); setOpen(true); }} className="bg-mango hover:bg-mango/90 text-white border-2 border-black rounded-none"><Plus size={16} className="mr-1" /> New question</Button>
      </div>

      {/* Filters */}
      <div className="bg-white border border-neutral-200 p-4 mb-4 grid grid-cols-1 md:grid-cols-5 gap-2" data-testid="q-filters">
        <Input placeholder="Search text..." value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} className="rounded-none" data-testid="q-filter-search" />
        <select value={filters.sector} onChange={(e) => setFilters({ ...filters, sector: e.target.value })} className="rounded-none border border-neutral-300 px-3 py-2 bg-white" data-testid="q-filter-sector">
          <option value="">All sectors</option>
          {SECTORS_LIST.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filters.tracker_id} onChange={(e) => setFilters({ ...filters, tracker_id: e.target.value })} className="rounded-none border border-neutral-300 px-3 py-2 bg-white" data-testid="q-filter-tracker">
          <option value="">All trackers</option>
          {trackers.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <select value={filters.display_type} onChange={(e) => setFilters({ ...filters, display_type: e.target.value })} className="rounded-none border border-neutral-300 px-3 py-2 bg-white" data-testid="q-filter-type">
          <option value="">Tracker + snapshot</option>
          <option value="tracker">Tracker only</option>
          <option value="snapshot">Snapshot only</option>
        </select>
        <select value={filters.visibility} onChange={(e) => setFilters({ ...filters, visibility: e.target.value })} className="rounded-none border border-neutral-300 px-3 py-2 bg-white" data-testid="q-filter-visibility">
          <option value="">Any visibility</option>
          <option value="public">Public</option>
          <option value="panelists">Panelists</option>
          <option value="subscribers">Subscribers</option>
          <option value="enterprise">Enterprise</option>
          <option value="private">Private</option>
        </select>
      </div>
      <div className="text-xs text-neutral-500 mb-3 font-mono">{filtered.length} of {items.length} question{items.length === 1 ? "" : "s"}</div>

      {open && <QuestionForm trackers={trackers} editing={editing} onClose={() => { setOpen(false); setEditing(null); load(); }} />}
      <div className="space-y-2 mt-2">
        {filtered.map((q) => {
          const tracker = trackers.find((t) => t.id === q.tracker_id);
          // Colour-code Tracker vs Snapshot so admin can sweep the list
          // visually — Trackers (recurring waves) are mango, Snapshots
          // (one-off polls) are teal. Bold weight + uppercase = scannable.
          const dt = (q.display_type || "").toLowerCase();
          const dtClass = dt === "tracker"
            ? "text-amber-600 font-bold uppercase"
            : dt === "snapshot"
              ? "text-emerald-600 font-bold uppercase"
              : "text-neutral-500";
          // Waves this question is part of (built once in `load()`).
          const usingWaves = waveUsage[q.id] || [];
          return (
            <div key={q.id} className="bg-white border border-neutral-200 p-5 flex items-center justify-between" data-testid={`q-${q.id}`}>
              <div className="min-w-0">
                <div className="overline truncate flex items-center gap-2">
                  <span>{q.format} · <span className={dtClass} data-testid={`q-displaytype-${q.id}`}>{q.display_type}</span>
                  {tracker ? ` · ${tracker.name}` : ""} · {q.sector || "no sector"} · {q.options.length} options</span>
                  {q.created_by_orbit && (
                    <span
                      className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.12em] font-mono font-bold text-mango border border-mango/50 bg-mango/5 px-1.5 py-0.5"
                      title={q.orbit_rationale || "Generated by ORBIT"}
                      data-testid={`q-${q.id}-orbit-badge`}
                    >
                      <img src="/brand/orbit.png" alt="" width={9} height={9} />
                      by ORBIT
                    </span>
                  )}
                </div>
                <div className="font-medium mt-1">{q.text}</div>
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <VisibilityBadge visibility={q.visibility || "public"} clientId={q.client_id} size="xs" />
                  {/* Wave-membership chips — clickable, status-coloured. Hidden
                      entirely when the question isn't part of any wave yet. */}
                  {usingWaves.length > 0 && (
                    <>
                      <span className="text-[10px] uppercase tracking-wider text-neutral-500">Used in:</span>
                      {usingWaves.map((wv) => {
                        const statusCls = wv.status === "active"
                          ? "border-emerald-500 text-emerald-700 bg-emerald-50"
                          : wv.status === "draft"
                            ? "border-amber-400 text-amber-700 bg-amber-50"
                            : wv.status === "paused"
                              ? "border-blue-400 text-blue-700 bg-blue-50"
                              : "border-neutral-300 text-neutral-500 bg-neutral-50";
                        return (
                          <Link
                            key={wv.id}
                            to={`/admin/waves/${wv.id}/edit`}
                            onClick={(e) => e.stopPropagation()}
                            className={`text-[10px] px-2 py-0.5 border ${statusCls} font-mono hover:bg-neutral-900 hover:text-white transition-colors`}
                            title={`${wv.status.toUpperCase()} wave · click to open`}
                            data-testid={`q-${q.id}-wave-${wv.id}`}
                          >
                            {wv.name} · {wv.status}
                          </Link>
                        );
                      })}
                    </>
                  )}
                </div>
              </div>
              <div className="flex gap-1 flex-shrink-0">
                <Button data-testid={`preview-q-${q.id}`} variant="ghost" size="sm" onClick={() => setPreviewQ(q)} title="Preview as patron">👁</Button>
                <Button data-testid={`edit-q-${q.id}`} variant="ghost" size="sm" onClick={() => { setEditing(q); setOpen(true); }}><PencilSimple size={18} /></Button>
                <Button data-testid={`del-q-${q.id}`} variant="ghost" size="sm" onClick={() => remove(q.id)}><Trash size={18} /></Button>
              </div>
            </div>
          );
        })}
        {!loading && filtered.length === 0 && <div className="text-center py-12 text-neutral-500">No questions match these filters.</div>}
      </div>
      {previewQ && (
        <PreviewModal open={true} mode="question" question={previewQ} onClose={() => setPreviewQ(null)} />
      )}
    </div>
  );
}

function QuestionForm({ trackers, editing, onClose }) {
  const [form, setForm] = useState(editing ? {
    text: editing.text, format: editing.format, max_selections: editing.max_selections,
    display_type: editing.display_type, tracker_id: editing.tracker_id,
    options: editing.options || [], visibility: editing.visibility || "public",
    client_id: editing.client_id || "",
    audience: editing.audience || "all",
    audience_filter: editing.audience_filter || {},
    chart_type: editing.chart_type || "bar",
    sector: editing.sector || "",
  } : {
    text: "", format: "single", max_selections: null,
    display_type: "snapshot", tracker_id: null, visibility: "public", client_id: "",
    audience: "all", audience_filter: {},
    chart_type: "bar", sector: "",
    options: [{ key: "opt_1", text: "", exclusive: false, exclude_from_trendline: false }],
  });
  const addOpt = () => setForm({ ...form, options: [...form.options, { key: `opt_${form.options.length + 1}`, text: "", exclusive: false, exclude_from_trendline: false }] });
  const updOpt = (i, patch) => setForm({ ...form, options: form.options.map((o, j) => j === i ? { ...o, ...patch } : o) });
  const delOpt = (i) => setForm({ ...form, options: form.options.filter((_, j) => j !== i) });

  const toggleFilter = (cat, val) => {
    const cur = form.audience_filter[cat] || [];
    const next = cur.includes(val) ? cur.filter((x) => x !== val) : [...cur, val];
    setForm({ ...form, audience_filter: { ...form.audience_filter, [cat]: next } });
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (payload.audience !== "niche") payload.audience_filter = null;
      if (editing) {
        await api.put(`/questions/${editing.id}`, payload);
        toast.success("Question updated");
      } else {
        await api.post("/questions", payload);
        toast.success("Question created");
      }
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  return (
    <form onSubmit={submit} className="bg-white border-2 border-black p-6 mb-6 space-y-5" data-testid="question-form">
      <div>
        <Label className="overline flex items-center gap-1">Question text * <HelpHint text={HELP.QUESTION.text} /></Label>
        <Textarea data-testid="qf-text" required value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} className="rounded-none" placeholder="e.g. Do you approve of the Prime Minister's performance?" />
        <InlineUrduEditor
          entity="question"
          entityId={editing?.id}
          field="text"
          enValue={form.text}
          initialUr={editing?.text_ur || ""}
          initialStatus={editing?.text_ur_status}
        />
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label className="overline flex items-center gap-1">Format <HelpHint text={`${HELP.QUESTION.format_single} · ${HELP.QUESTION.format_multiple} · ${HELP.QUESTION.format_nps} · ${HELP.QUESTION.format_likert}`} /></Label>
          <Select value={form.format} onValueChange={(v) => setForm({ ...form, format: v })}>
            <SelectTrigger className="rounded-none" data-testid="qf-format"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="single">Single Choice</SelectItem>
              <SelectItem value="multiple">Multiple Choice</SelectItem>
              <SelectItem value="nps">NPS (0-10)</SelectItem>
              <SelectItem value="likert">Likert (1-5)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="overline flex items-center gap-1">Display type <HelpHint text={`${HELP.QUESTION.display_tracker} · ${HELP.QUESTION.display_snapshot}`} /></Label>
          <Select value={form.display_type} onValueChange={(v) => setForm({ ...form, display_type: v, tracker_id: null })}>
            <SelectTrigger className="rounded-none" data-testid="qf-display"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="snapshot">Snapshot (Ad-hoc)</SelectItem>
              <SelectItem value="tracker">Tracker (Time-series)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="overline flex items-center gap-1">Visibility <HelpHint text="Public = everyone (incl. anonymous visitors). Panelists = any signed-in panelist (and above). Subscribers = paying customers (subscriber + enterprise). Enterprise = enterprise customers only. Private = scoped to ONE client_id only." /></Label>
          <Select value={form.visibility} onValueChange={(v) => setForm({ ...form, visibility: v })}>
            <SelectTrigger className="rounded-none" data-testid="qf-vis"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="public">Public — everyone (incl. anonymous)</SelectItem>
              <SelectItem value="panelists">Panelists — any signed-in panelist</SelectItem>
              <SelectItem value="subscribers">Subscribers — paying customers (incl. enterprise)</SelectItem>
              <SelectItem value="enterprise">Enterprise — enterprise tier only</SelectItem>
              <SelectItem value="private">Private — one client_id only</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {form.visibility === "private" && (
        <div>
          <Label className="overline flex items-center gap-1">Assigned Client ID <HelpHint text={HELP.QUESTION.client_id} /></Label>
          <Input data-testid="qf-client" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} className="rounded-none w-72 font-mono" placeholder="CUST_TELENOR_002" />
        </div>
      )}
      {form.format === "multiple" && (
        <div>
          <Label className="overline flex items-center gap-1">Max selections <HelpHint text={HELP.QUESTION.max_selections} /></Label>
          <Input data-testid="qf-max" type="number" value={form.max_selections || ""} onChange={(e) => setForm({ ...form, max_selections: parseInt(e.target.value) || null })} className="rounded-none w-32" />
        </div>
      )}
      {form.display_type === "tracker" && (
        <div>
          <Label className="overline flex items-center gap-1">Parent Tracker * <HelpHint text={HELP.QUESTION.tracker_id} /></Label>
          <Select value={form.tracker_id || ""} onValueChange={(v) => setForm({ ...form, tracker_id: v })}>
            <SelectTrigger className="rounded-none" data-testid="qf-tracker"><SelectValue placeholder="Select tracker" /></SelectTrigger>
            <SelectContent>{trackers.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="overline flex items-center gap-1">Sector <HelpHint text={HELP.QUESTION.sector} /></Label>
          <select value={form.sector || ""} onChange={(e) => setForm({ ...form, sector: e.target.value })} data-testid="qf-sector" className="rounded-none border border-neutral-300 w-full px-3 py-2 bg-white">
            <option value="">—</option>
            {SECTORS_LIST.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <Label className="overline flex items-center gap-1">Chart type <HelpHint text={HELP.QUESTION.chart_type} /></Label>
          <select value={form.chart_type || "bar"} onChange={(e) => setForm({ ...form, chart_type: e.target.value })} data-testid="qf-chart-type" className="rounded-none border border-neutral-300 w-full px-3 py-2 bg-white">
            <option value="bar">Bar — horizontal (default)</option>
            <option value="column">Column — vertical bars</option>
            <option value="line">Line</option>
            <option value="pie">Pie</option>
            <option value="donut">Donut</option>
            <option value="radial">Radial / Gauge (NPS)</option>
          </select>
        </div>
      </div>

      {/* AUDIENCE TARGETING — per-question screener (optional override of wave-level audience) */}
      <div className="border-2 border-dashed border-mango p-4 bg-mango/5">
        <Label className="overline mb-2 flex items-center gap-1">Audience targeting (screener) <HelpHint text="OPTIONAL per-question filter. The wave already has its own audience targeting; only add a filter here if THIS question should be asked of a narrower subset (e.g. a screener that only fires for tech-industry respondents). Leave as 'Global' to inherit the wave's audience." /></Label>
        <p className="text-xs text-neutral-500 mb-3">Wave-level audience targeting now applies to every question in the wave. Use this only when a specific question needs an extra screener filter on top.</p>
        <div className="flex gap-2 mb-3">
          <button type="button" data-testid="qf-aud-all" onClick={() => setForm({ ...form, audience: "all" })} className={`px-4 py-2 border-2 text-sm ${form.audience === "all" ? "bg-black text-white border-black" : "border-neutral-300"}`}>Inherit wave audience</button>
          <button type="button" data-testid="qf-aud-niche" onClick={() => setForm({ ...form, audience: "niche" })} className={`px-4 py-2 border-2 text-sm ${form.audience === "niche" ? "bg-mango text-white border-black" : "border-neutral-300"}`}>Add screener filter</button>
        </div>
        {form.audience === "niche" && (
          <div className="space-y-3" data-testid="qf-niche-builder">
            {Object.entries(FILTER_FIELDS).map(([cat, vals]) => (
              <div key={cat}>
                <div className="overline mb-1">{cat.replace("_", " ")}</div>
                <div className="flex flex-wrap gap-1">
                  {vals.map((v) => {
                    const sel = (form.audience_filter[cat] || []).includes(v);
                    return (
                      <button type="button" key={v} onClick={() => toggleFilter(cat, v)} className={`text-xs px-2 py-1 border ${sel ? "bg-black text-white border-black" : "border-neutral-300 hover:border-black"}`}>{v}</button>
                    );
                  })}
                </div>
              </div>
            ))}
            <div className="text-xs text-neutral-500 pt-2 border-t">AND between categories · OR within a category · empty category = no constraint</div>
          </div>
        )}
      </div>

      {(form.format === "single" || form.format === "multiple") && (
        <div>
          <Label className="overline mb-2 block">Answer options</Label>
          <div className="space-y-2">
            {form.options.map((o, i) => (
              <div key={i} className="border border-neutral-200 p-3">
                <div className="flex items-center gap-3">
                  <span
                    className="rounded-none w-24 shrink-0 font-mono text-[11px] bg-neutral-100 text-neutral-500 px-2 py-2 select-none overflow-hidden text-ellipsis whitespace-nowrap"
                    title={`Internal key (auto-generated): ${o.key}`}
                  >{o.key}</span>
                  <Input data-testid={`qf-opt-${i}`} placeholder={`Option ${i + 1} (e.g. Yes)`} value={o.text} onChange={(e) => updOpt(i, { text: e.target.value })} className="rounded-none flex-1 min-w-0" />
                  <label className="text-xs flex items-center gap-1">
                    <Checkbox checked={o.exclusive} onCheckedChange={(v) => updOpt(i, { exclusive: !!v })} /> Exclusive
                    <HelpHint text="Selecting this option (e.g. 'None of the above') automatically clears all other ticks." />
                  </label>
                  <label className="text-xs flex items-center gap-1">
                    <Checkbox checked={o.exclude_from_trendline} onCheckedChange={(v) => updOpt(i, { exclude_from_trendline: !!v })} /> Skip trend
                    <HelpHint text="Data is still collected, but this option is hidden from the public tracker line chart (useful for 'Don't know')." />
                  </label>
                  <Button type="button" variant="ghost" size="sm" onClick={() => delOpt(i)} title="Delete this option"><Trash size={14} /></Button>
                </div>
                {editing?.id && (
                  <div className="mt-2 pl-24 flex items-center gap-3">
                    <InlineUrduEditor
                      entity="question"
                      entityId={editing?.id}
                      field={{ optionIndex: i }}
                      enValue={o.text}
                      initialUr={o.text_ur || ""}
                      initialStatus={o.text_ur_status}
                      compact
                    />
                  </div>
                )}
              </div>
            ))}
            <Button type="button" data-testid="qf-add-opt" onClick={addOpt} variant="outline" className="rounded-none">+ Add option</Button>
          </div>
        </div>
      )}
      <div className="flex gap-2 pt-4 border-t">
        <Button type="submit" data-testid="qf-submit" className="bg-black hover:bg-neutral-800 rounded-none">{editing ? "Save changes" : "Create question"}</Button>
        <Button type="button" variant="ghost" onClick={onClose} className="rounded-none">Cancel</Button>
      </div>
    </form>
  );
}

// =================== TRACKERS ===================
export function AdminTrackers() {
  const [items, setItems] = useState(() => api.getCached("/trackers") || []);
  const [questions, setQuestions] = useState(() => api.getCached("/questions") || []);
  const [form, setForm] = useState({ name: "", category: "", description: "", sector: "", visibility: "public", client_id: "", chart_type: "line", featured_for_customers: false, show_respondent_count: false, created_at: new Date().toISOString().slice(0, 10) });
  const [editingId, setEditingId] = useState(null);
  const [drillId, setDrillId] = useState(null);

  const load = async () => {
    const [t, q] = await Promise.all([api.get("/trackers"), api.get("/questions")]);
    setItems(t.data);
    setQuestions(q.data);
  };
  useEffect(() => { load(); }, []);

  const reset = () => { setForm({ name: "", category: "", description: "", sector: "", visibility: "public", client_id: "", chart_type: "line", featured_for_customers: false, show_respondent_count: false, created_at: new Date().toISOString().slice(0, 10) }); setEditingId(null); };

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, client_id: form.visibility === "private" ? form.client_id : null };
    if (editingId) {
      await api.put(`/trackers/${editingId}`, payload);
      toast.success("Tracker updated");
    } else {
      await api.post("/trackers", payload);
      toast.success("Tracker created");
    }
    reset(); load();
  };

  const startEdit = (t) => {
    setForm({
      name: t.name, category: t.category || "", description: t.description || "",
      sector: t.sector || "", visibility: t.visibility || "public", client_id: t.client_id || "",
      chart_type: t.chart_type || "line", featured_for_customers: !!t.featured_for_customers,
      show_respondent_count: !!t.show_respondent_count,
      created_at: (t.created_at || "").slice(0, 10) || new Date().toISOString().slice(0, 10),
    });
    setEditingId(t.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const setActive = async (tid, qid) => {
    await api.put(`/trackers/${tid}/active-question`, { question_id: qid || null });
    toast.success("Canonical question updated");
    load();
  };

  return (
    <div>
      <div className="border-b-2 border-black pb-4 mb-6">
        <div className="overline flex items-center gap-1">TRACKERS <HelpHint text={HELP.PAGE.trackers} /></div>
        <h1 className="text-4xl font-black tracking-tighter">Tracker manager</h1>
        <p className="text-sm text-neutral-600 mt-2">{HELP.PAGE.trackers}</p>
      </div>
      <form onSubmit={submit} className="bg-white border-2 border-black p-5 space-y-4 mb-6" data-testid="tracker-form">
        <div className="overline text-mango">{editingId ? "EDITING TRACKER" : "ADD NEW TRACKER"}</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="overline flex items-center gap-1">Name * <HelpHint text={HELP.TRACKER.name} /></Label>
            <Input data-testid="tracker-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none" placeholder="e.g. PM Approval — Politics" />
          </div>
          <div>
            <Label className="overline flex items-center gap-1">Sector * <HelpHint text={HELP.TRACKER.sector} /></Label>
            <select required value={form.sector || ""} onChange={(e) => setForm({ ...form, sector: e.target.value })} className="rounded-none border border-neutral-300 w-full px-3 py-2 bg-white" data-testid="tracker-sector">
              <option value="">— pick a sector —</option>
              {SECTORS_LIST.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <Label className="overline flex items-center gap-1">Chart type <HelpHint text={HELP.TRACKER.chart_type} /></Label>
            <select data-testid="tracker-chart-type" value={form.chart_type || "line"} onChange={(e) => setForm({ ...form, chart_type: e.target.value })} className="rounded-none border border-neutral-300 w-full px-3 py-2 bg-white">
              <option value="line">Line (time-series, default)</option>
              <option value="area">Area (stacked time-series)</option>
              <option value="column">Column (vertical bars · latest)</option>
              <option value="bar">Bar (horizontal · latest)</option>
              <option value="pie">Pie (latest only)</option>
              <option value="donut">Donut (latest only)</option>
            </select>
          </div>
          <div>
            <Label className="overline flex items-center gap-1">Creation date <HelpHint text="Defaults to today. Override this if the tracker actually started earlier — it's the anchor used to compute the recurrence 'Next due' date whenever this tracker has no published wave results yet." /></Label>
            <Input
              type="date"
              data-testid="tracker-created-at"
              value={form.created_at || ""}
              onChange={(e) => setForm({ ...form, created_at: e.target.value })}
              className="rounded-none"
            />
          </div>
          <div>
            <Label className="overline flex items-center gap-1">Visibility <HelpHint text="Public = everyone (incl. anonymous). Panelists = any signed-in panelist. Subscribers = paying customers (incl. enterprise). Enterprise = enterprise tier only. Private = scoped to one client_id." /></Label>
            <select data-testid="tracker-visibility" value={form.visibility} onChange={(e) => setForm({ ...form, visibility: e.target.value })} className="rounded-none border border-neutral-300 w-full px-3 py-2 bg-white">
              <option value="public">Public — everyone (incl. anonymous)</option>
              <option value="panelists">Panelists — any signed-in panelist</option>
              <option value="subscribers">Subscribers — paying customers</option>
              <option value="enterprise">Enterprise — enterprise tier only</option>
              <option value="private">Private — one client_id only</option>
            </select>
          </div>
          {form.visibility === "private" && (
            <div className="md:col-span-2">
              <Label className="overline flex items-center gap-1">Client ID * <HelpHint text={HELP.TRACKER.client_id} /></Label>
              <Input data-testid="tracker-client" required value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} className="rounded-none" placeholder="CUST_JAZZ_001" />
            </div>
          )}
          <div className="md:col-span-2">
            <Label className="overline flex items-center gap-1">Description <HelpHint text={HELP.TRACKER.description} /></Label>
            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-none" placeholder="1-2 sentence brief shown on the tracker detail page" />
          </div>
          <label className="md:col-span-2 flex items-center gap-2 cursor-pointer">
            <Checkbox checked={form.featured_for_customers} onCheckedChange={(v) => setForm({ ...form, featured_for_customers: v })} />
            <span className="text-sm">Featured for customers <HelpHint text={HELP.TRACKER.featured_for_customers} /></span>
          </label>
          {/* PHASE 8.42 — Tracker-level mirror of the wave-level toggle. OFF
              by default so trackers built from sparse waves never inadvertently
              reveal per-point N to viewers. */}
          <label className="md:col-span-2 flex items-center gap-2 cursor-pointer" data-testid="tracker-show-respondent-count">
            <Checkbox checked={!!form.show_respondent_count} onCheckedChange={(v) => setForm({ ...form, show_respondent_count: v })} />
            <span className="text-sm">Show respondent count on chart points <span className="text-neutral-400 italic">— OFF by default. Turn ON only if every wave behind this tracker has enough N to publish.</span></span>
          </label>
        </div>
        <div className="flex gap-2">
          <Button data-testid="tracker-submit" type="submit" className="bg-black hover:bg-neutral-800 rounded-none">{editingId ? "Save changes" : "Add tracker"}</Button>
          {editingId && <Button type="button" variant="ghost" onClick={reset} className="rounded-none">Cancel</Button>}
        </div>
      </form>
      <div className="space-y-2">
        {items.map((t) => {
          const trackerQs = questions.filter((q) => q.tracker_id === t.id);
          return (
            <div key={t.id} className="border border-neutral-200 p-4 bg-white" data-testid={`tracker-row-${t.id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="overline">{t.sector || "no sector"} · {t.chart_type || "line"}</div>
                  <div className="font-medium">{t.name}</div>
                  {t.description && <div className="text-xs text-neutral-500 mt-1">{t.description}</div>}
                  <div className="mt-2"><VisibilityBadge visibility={t.visibility || "public"} clientId={t.client_id} size="xs" /></div>
                </div>
                <div className="flex gap-1">
                  <Button data-testid={`drill-tracker-${t.id}`} variant="outline" size="sm" onClick={() => setDrillId(t.id)} className="rounded-none">Demographics</Button>
                  <Button data-testid={`edit-tracker-${t.id}`} variant="ghost" size="sm" onClick={() => startEdit(t)}><PencilSimple size={18} /></Button>
                  <Button variant="ghost" size="sm" onClick={async () => { if (window.confirm(`Delete tracker '${t.name}'?`)) { await api.delete(`/trackers/${t.id}`); load(); } }}><Trash size={18} /></Button>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-neutral-100">
                <Label className="overline text-xs flex items-center gap-1">CANONICAL QUESTION FOR WAVES <HelpHint text={HELP.TRACKER.active_question_id} /></Label>
                {trackerQs.length === 0 ? (
                  <div className="text-xs text-neutral-500 mt-1">No questions yet for this tracker. <Link to="/admin/questions" className="text-mango underline">Create one →</Link></div>
                ) : (
                  <select
                    value={t.active_question_id || ""}
                    onChange={(e) => setActive(t.id, e.target.value)}
                    className="rounded-none border border-neutral-300 px-2 py-1.5 text-sm w-full md:w-1/2 mt-1"
                    data-testid={`tracker-active-q-${t.id}`}
                  >
                    <option value="">— none selected —</option>
                    {trackerQs.map((q) => <option key={q.id} value={q.id}>{q.text}</option>)}
                  </select>
                )}
              </div>
              <div className="mt-3 pt-3 border-t border-neutral-100 flex flex-wrap items-center gap-3">
                <Label className="overline text-xs">RECURRENCE</Label>
                <select
                  value={t.recurrence_cadence || "ad_hoc"}
                  disabled={t.recurrence_cadence === "retired"}
                  onChange={async (e) => {
                    try { await api.patch(`/trackers/${t.id}/cadence`, { cadence: e.target.value });
                      toast.success("Cadence updated"); load();
                    } catch { toast.error("Failed to update cadence"); }
                  }}
                  className="rounded-none border border-neutral-300 px-2 py-1.5 text-sm"
                  data-testid={`tracker-cadence-${t.id}`}
                >
                  <option value="ad_hoc">Ad-hoc (no schedule)</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                </select>
                {t.recurrence_cadence && t.recurrence_cadence !== "ad_hoc" && t.recurrence_cadence !== "retired" && (
                  <span className="text-xs text-neutral-500" data-testid={`tracker-next-due-${t.id}`}>
                    Next due: <span className="font-mono">{t.recurrence_next_due || "—"}</span>
                    {t.recurrence_next_due && t.recurrence_next_due < new Date().toISOString().slice(0,10) && (
                      <span className="ml-2 text-red-700 font-bold">OVERDUE</span>
                    )}
                    {t.recurrence_next_due_manual_override && (
                      <span className="ml-2 text-mango italic" title="Anchored to the Creation date field you set — stays until you change it again or a new wave closes for this tracker.">(manually set)</span>
                    )}
                  </span>
                )}
                {t.recurrence_cadence === "retired" ? (
                  <Button size="sm" variant="outline" onClick={async () => {
                    await api.post(`/trackers/${t.id}/revive`, { cadence: "monthly" });
                    toast.success("Tracker revived"); load();
                  }} className="rounded-none h-7 px-2.5 text-xs" data-testid={`tracker-revive-${t.id}`}>
                    Revive
                  </Button>
                ) : (
                  <Button size="sm" variant="outline" onClick={async () => {
                    if (!window.confirm(`Retire tracker '${t.name}'?\n\nHistorical data stays visible. The system will stop reminding you to include it in future waves. You can revive later.`)) return;
                    await api.post(`/trackers/${t.id}/retire`);
                    toast.success("Tracker retired"); load();
                  }} className="rounded-none h-7 px-2.5 text-xs" data-testid={`tracker-retire-${t.id}`}>
                    Retire
                  </Button>
                )}
                {t.recurrence_cadence === "retired" && t.retired_at && (
                  <span className="text-xs text-neutral-500 font-mono">Retired {t.retired_at.slice(0, 10)}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {drillId && <RespondentsDrillDownModal kind="trackers" id={drillId} title={items.find((x) => x.id === drillId)?.name} onClose={() => setDrillId(null)} />}
    </div>
  );
}

// =================== BRANDS ===================
// Colour-codes an admin sentiment_label pill: green for positive, neutral
// gray for Neutral, red for negative. Mirrors `SentimentLabelChip` in
// `Sentiment.jsx` (kept separate since Admin.jsx doesn't import patron-side
// components).
function sentimentLabelClass(label) {
  if (!label) return "bg-neutral-100 text-neutral-500";
  if (label.includes("Positive")) return "bg-mango/15 text-mango";
  if (label === "Neutral") return "bg-neutral-100 text-neutral-500";
  return "bg-red-50 text-red-600";
}

export function AdminSentiment() {
  const ADD_CATEGORY_SENTINEL = "__add_new_category__";
  const [items, setItems] = useState(() => api.getCached("/admin/sentiment/items") || []);
  const [categories, setCategories] = useState(() => api.getCached("/admin/sentiment/categories") || []);
  const [form, setForm] = useState({ name: "", photo_url: "", category_slug: "", description: "", show_public_trend: false });
  const [editingId, setEditingId] = useState(null);
  const [showAddCat, setShowAddCat] = useState(false);
  const [newCatName, setNewCatName] = useState("");
  const [flagState, setFlagState] = useState({ enabled: true, cooldown: 180 });
  const [deleteItemTarget, setDeleteItemTarget] = useState(null); // item being confirmed for deletion
  const [deleteCatTarget, setDeleteCatTarget] = useState(null);   // category being confirmed for deletion
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [loading, setLoading] = useState(() => !api.getCached("/admin/sentiment/items"));

  const load = async () => {
    try {
      const [it, cs, fs] = await Promise.all([
        api.get("/admin/sentiment/items"),
        api.get("/admin/sentiment/categories"),
        api.get("/sentiment/feature").catch(() => ({ data: { public_sentiment_enabled: true, cooldown_days: 180 } })),
      ]);
      setItems(it.data); setCategories(cs.data);
      setFlagState({ enabled: fs.data.public_sentiment_enabled, cooldown: fs.data.cooldown_days });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const reset = () => { setForm({ name: "", photo_url: "", category_slug: "", description: "", show_public_trend: false }); setEditingId(null); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.category_slug) { toast.error("Pick a category"); return; }
    try {
      if (editingId) {
        await api.put(`/admin/sentiment/items/${editingId}`, form);
        toast.success("Item updated");
      } else {
        await api.post("/admin/sentiment/items", form);
        toast.success("Item added");
      }
      reset(); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const startEdit = (it) => {
    setEditingId(it.id);
    setForm({ name: it.name, photo_url: it.photo_url || "", category_slug: it.category_slug, description: it.description || "", show_public_trend: !!it.show_public_trend });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const doDeleteItem = async () => {
    if (!deleteItemTarget) return;
    setDeleteBusy(true);
    try {
      await api.delete(`/admin/sentiment/items/${deleteItemTarget.id}`);
      toast.success(`${deleteItemTarget.name} deleted`);
      setDeleteItemTarget(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    } finally {
      setDeleteBusy(false);
    }
  };

  const doDeleteCategory = async () => {
    if (!deleteCatTarget) return;
    setDeleteBusy(true);
    try {
      await api.delete(`/admin/sentiment/categories/${deleteCatTarget.id}`);
      toast.success(`"${deleteCatTarget.name}" category deleted`);
      setDeleteCatTarget(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Category delete failed");
    } finally {
      setDeleteBusy(false);
    }
  };

  const addCategory = async () => {
    if (!newCatName.trim()) return;
    try {
      const res = await api.post("/admin/sentiment/categories", { name: newCatName.trim(), sort_order: categories.length + 1 });
      const created = res.data;
      setCategories([...categories, created]);
      setForm({ ...form, category_slug: created.slug });
      setNewCatName(""); setShowAddCat(false);
      toast.success(`Added category "${created.name}"`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add category");
    }
  };

  const onCategoryChange = (val) => {
    if (val === ADD_CATEGORY_SENTINEL) { setShowAddCat(true); return; }
    setForm({ ...form, category_slug: val });
  };

  const itemCountFor = (slug) => items.filter((it) => it.category_slug === slug).length;

  return (
    <div data-testid="admin-sentiment">
      <div className="border-b-2 border-black pb-4 mb-6">
        <div className="overline">PUBLIC SENTIMENT — RATINGS</div>
        <h1 className="text-4xl font-black tracking-tighter">Public Sentiment manager</h1>
        <p className="text-sm text-neutral-600 mt-2">People, brands and personalities anyone can rate 1-5 ★. Cooldown is currently <b>{flagState.cooldown} days</b>.</p>
      </div>
      {!flagState.enabled && (
        <div className="border-l-4 border-amber-500 bg-amber-50 p-4 mb-6 text-sm">
          <b>Feature is OFF.</b> Public Sentiment is hidden from the website and panelist dashboards. Turn it on in <Link to="/admin/site-content" className="underline">Site Content → Feature toggles</Link>.
        </div>
      )}

      {/* Category directory — add/delete. Deleting a category is blocked
          server-side while it still has items attached (400 error surfaced
          via toast below), so admins reassign/remove those items first. */}
      <div className="bg-white border-2 border-black p-5 mb-6" data-testid="sentiment-categories-panel">
        <div className="overline mb-3">CATEGORIES ({categories.length})</div>
        <div className="flex flex-wrap gap-2">
          {categories.map((c) => (
            <div key={c.slug} className="flex items-center gap-1.5 border border-neutral-300 rounded-full pl-3 pr-1.5 py-1 text-sm" data-testid={`sentiment-category-chip-${c.slug}`}>
              <span className="font-medium">{c.name}</span>
              <span className="text-xs text-neutral-400">({itemCountFor(c.slug)})</span>
              <button
                type="button"
                onClick={() => setDeleteCatTarget(c)}
                className="w-5 h-5 flex items-center justify-center rounded-full text-neutral-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                title="Delete category"
                data-testid={`sentiment-category-delete-${c.slug}`}
              >
                <Trash size={11} />
              </button>
            </div>
          ))}
          {!loading && categories.length === 0 && <div className="text-sm text-neutral-400 italic">No categories yet — add one from the item form below.</div>}
        </div>
      </div>

      <form onSubmit={submit} className="bg-white border-2 border-black p-5 space-y-4 mb-6" data-testid="sentiment-form">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><Label className="overline">Display name</Label><Input data-testid="sentiment-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none" placeholder="e.g. Imran Khan" /></div>
          <div><Label className="overline flex items-center gap-1">Photo URL <HelpHint text="Recommended: square photo, min 280×280px (any background works — square-cropped, not circular). Displays as a 64×64px thumbnail on the /sentiment list grid, and up to 140×140px on that person/brand's detail page hero." /></Label><Input data-testid="sentiment-photo" value={form.photo_url} onChange={(e) => setForm({ ...form, photo_url: e.target.value })} className="rounded-none" placeholder="https://..." /></div>
        </div>
        <div>
          <Label className="overline">Category</Label>
          <Select value={form.category_slug} onValueChange={onCategoryChange}>
            <SelectTrigger className="rounded-none" data-testid="sentiment-category"><SelectValue placeholder="Pick a category…" /></SelectTrigger>
            <SelectContent>
              {categories.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
              <SelectItem value={ADD_CATEGORY_SENTINEL} data-testid="sentiment-add-category-trigger">+ Add Category</SelectItem>
            </SelectContent>
          </Select>
          {showAddCat && (
            <div className="mt-3 flex gap-2 items-end" data-testid="sentiment-add-category-row">
              <div className="flex-1"><Label className="overline text-xs">New category name</Label><Input data-testid="new-category-name" value={newCatName} onChange={(e) => setNewCatName(e.target.value)} className="rounded-none" placeholder="e.g. Musicians" /></div>
              <Button type="button" onClick={addCategory} data-testid="new-category-save" className="rounded-none bg-black hover:bg-neutral-800">Create</Button>
              <Button type="button" variant="ghost" onClick={() => { setShowAddCat(false); setNewCatName(""); }} className="rounded-none">Cancel</Button>
            </div>
          )}
        </div>
        <div><Label className="overline">Description (optional)</Label><Textarea data-testid="sentiment-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-none" rows={2} /></div>
        <div className="border-t border-neutral-200 pt-3">
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={!!form.show_public_trend}
              onChange={(e) => setForm({ ...form, show_public_trend: e.target.checked })}
              data-testid="sentiment-show-public-trend"
              className="mt-1"
            />
            <div>
              <div className="text-sm font-bold">Show 12-month trend chart on the public page</div>
              <div className="text-xs text-neutral-500 mt-0.5">When off, the trend chart is hidden from patrons even if data exists. Recommended ON only when this brand has ≥3 months of consistent rating activity.</div>
            </div>
          </label>
        </div>
        <div className="flex gap-2">
          <Button type="submit" data-testid="sentiment-submit" className="rounded-none bg-black hover:bg-neutral-800">{editingId ? "Update item" : "Add item"}</Button>
          {editingId && <Button type="button" variant="ghost" onClick={reset} className="rounded-none">Cancel edit</Button>}
        </div>
      </form>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="sentiment-items-grid">
        {items.map((it) => (
          <div key={it.id} className="border border-neutral-200 bg-white p-4 flex items-start gap-3" data-testid={`sentiment-item-${it.id}`}>
            <div className="w-12 h-12 bg-neutral-100 border border-neutral-300 overflow-hidden flex-shrink-0">
              {it.photo_url ? <img src={it.photo_url} alt={it.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-xl font-black text-neutral-300">{it.name[0]}</div>}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold truncate">{it.name}</div>
              <div className="overline text-neutral-500">{it.category_slug}</div>
              <div className="text-sm font-mono mt-1 flex items-center gap-2 flex-wrap">
                <span>{it.avg_rating ?? "—"}★ · {it.total_votes || 0} votes</span>
                {it.sentiment_label && (
                  <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded not-italic font-sans ${sentimentLabelClass(it.sentiment_label)}`} data-testid={`sentiment-item-label-${it.id}`}>{it.sentiment_label}</span>
                )}
              </div>
              {it.show_public_trend && <div className="inline-block mt-1 px-1.5 py-0.5 bg-mango/15 text-mango text-[10px] font-bold tracking-wide">PUBLIC TREND ON</div>}
              <Link to={`/admin/sentiment/${it.id}`} className="block mt-2 text-xs font-bold text-mango hover:underline" data-testid={`sentiment-insights-${it.id}`}>View respondent insights →</Link>
            </div>
            <div className="flex flex-col gap-1">
              <Button size="sm" variant="ghost" onClick={() => startEdit(it)} data-testid={`sentiment-edit-${it.id}`}><PencilSimple size={14} /></Button>
              <Button size="sm" variant="ghost" onClick={() => setDeleteItemTarget(it)} data-testid={`sentiment-delete-${it.id}`}><Trash size={14} /></Button>
            </div>
          </div>
        ))}
        {!loading && items.length === 0 && <div className="col-span-full text-center py-12 text-neutral-500">No items yet — add the first one above.</div>}
      </div>

      {/* In-app confirm dialogs (NOT window.confirm — silently blocked inside
          the preview panel's iframe without allow="modals"). */}
      <Dialog open={!!deleteItemTarget} onOpenChange={(o) => { if (!o) setDeleteItemTarget(null); }}>
        <DialogContent className="rounded-none border-2 border-black max-w-md" data-testid="sentiment-item-delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black tracking-tighter">Delete {deleteItemTarget?.name}?</DialogTitle>
            <DialogDescription className="text-neutral-700 pt-1">
              This permanently deletes the item and every vote/history record on it ({deleteItemTarget?.total_votes || 0} vote{deleteItemTarget?.total_votes === 1 ? "" : "s"}). This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="pt-2">
            <Button variant="outline" onClick={() => setDeleteItemTarget(null)} disabled={deleteBusy} data-testid="sentiment-item-delete-cancel">Cancel</Button>
            <Button onClick={doDeleteItem} disabled={deleteBusy} className="bg-red-600 hover:bg-red-700 text-white rounded-none" data-testid="sentiment-item-delete-confirm">{deleteBusy ? "Deleting…" : "Delete permanently"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!deleteCatTarget} onOpenChange={(o) => { if (!o) setDeleteCatTarget(null); }}>
        <DialogContent className="rounded-none border-2 border-black max-w-md" data-testid="sentiment-category-delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black tracking-tighter">Delete "{deleteCatTarget?.name}"?</DialogTitle>
            <DialogDescription className="text-neutral-700 pt-1">
              {itemCountFor(deleteCatTarget?.slug) > 0
                ? `This category still has ${itemCountFor(deleteCatTarget?.slug)} item(s) — reassign or delete them first, then retry.`
                : "This cannot be undone."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="pt-2">
            <Button variant="outline" onClick={() => setDeleteCatTarget(null)} disabled={deleteBusy} data-testid="sentiment-category-delete-cancel">Cancel</Button>
            <Button onClick={doDeleteCategory} disabled={deleteBusy} className="bg-red-600 hover:bg-red-700 text-white rounded-none" data-testid="sentiment-category-delete-confirm">{deleteBusy ? "Deleting…" : "Delete permanently"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Demographic dimension labels for the breakdown panels below.
const SENTIMENT_DIMS = [
  { key: "gender", label: "Gender" },
  { key: "age_band", label: "Age band" },
  { key: "region", label: "Region" },
  { key: "sec", label: "SEC" },
  { key: "industry", label: "Industry" },
  { key: "employment_type", label: "Employment type" },
  { key: "education", label: "Education" },
  { key: "marital_status", label: "Marital status" },
];

// Admin — Public Sentiment respondent insights (who rated it + full
// demographics). Built to be safely shareable with the rated brand/
// institution: the on-screen table shows the respondent's name (useful for
// internal QA/fraud checks), but the "Export CSV" button calls the backend's
// anonymised export (sequential "R-00001" refs, no name/email/phone) so
// admins have a one-click, privacy-safe file to hand to a brand contact.
export function AdminSentimentInsights() {
  const { iid } = useParams();
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({ gender: "", age_band: "", region: "", sec: "" });
  const [page, setPage] = useState(1);
  const [period, setPeriod] = useState("1y"); // 3m | 6m | 1y | all — drives the trend chart + PDF export
  const pageSize = 25;
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  const load = () => {
    const params = { page, page_size: pageSize, period };
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    api.get(`/admin/sentiment/items/${iid}/insights`, { params }).then((r) => setData(r.data)).catch(() => setData(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [iid, page, filters, period]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/admin/sentiment/items/${iid}/insights/export.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${data?.item?.slug || "sentiment"}-insights.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Anonymised CSV downloaded — safe to share with the brand/institution.");
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const exportPdf = async () => {
    setExportingPdf(true);
    try {
      const res = await api.get(`/admin/sentiment/items/${iid}/insights/pdf`, { params: { period }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${data?.item?.slug || "sentiment"}-oodel-sentiment-report.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("One-page PDF report downloaded — ready to send to the brand contact.");
    } catch {
      toast.error("PDF export failed");
    } finally {
      setExportingPdf(false);
    }
  };

  if (data === null) return <div className="p-12 text-neutral-500">Loading…</div>;
  if (data === false) return <div className="p-12 text-neutral-500">Item not found. <Link to="/admin/sentiment" className="underline">Back to Public Sentiment</Link></div>;

  const { item, histogram, trend, trend_delta, breakdowns, respondents, total_respondents } = data;
  const histTotal = Object.values(histogram || {}).reduce((a, b) => a + (b || 0), 0);
  const totalPages = Math.max(1, Math.ceil(total_respondents / pageSize));
  const maxBucketAvg = 5;
  const PERIODS = [{ v: "3m", l: "3M" }, { v: "6m", l: "6M" }, { v: "1y", l: "1Y" }, { v: "all", l: "All" }];

  return (
    <div data-testid="admin-sentiment-insights">
      <div className="border-b-2 border-black pb-4 mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <Link to="/admin/sentiment" className="overline text-neutral-400 hover:text-mango">← PUBLIC SENTIMENT</Link>
          <h1 className="text-4xl font-black tracking-tighter mt-1">{item.name}</h1>
          <p className="text-sm text-neutral-600 mt-1 flex items-center gap-2 flex-wrap">
            <span>{item.category_slug} · {item.avg_rating ?? "—"}★ average · {item.total_votes} rater{item.total_votes === 1 ? "" : "s"}</span>
            {item.sentiment_label && <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${sentimentLabelClass(item.sentiment_label)}`} data-testid="sentiment-insights-label">{item.sentiment_label}</span>}
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={exportPdf} disabled={exportingPdf} variant="outline" className="rounded-none border-2 border-black" data-testid="sentiment-insights-export-pdf">
            {exportingPdf ? "Exporting…" : "⬇ One-page PDF report"}
          </Button>
          <Button onClick={exportCsv} disabled={exporting || !total_respondents} className="rounded-none bg-black hover:bg-neutral-800" data-testid="sentiment-insights-export-csv">
            {exporting ? "Exporting…" : "⬇ Export anonymised CSV"}
          </Button>
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="border-2 border-black bg-white p-4">
          <div className="overline text-neutral-400">AVG RATING</div>
          <div className="text-3xl font-black mt-1">{item.avg_rating ?? "—"}<span className="text-lg">★</span></div>
        </div>
        <div className="border-2 border-black bg-white p-4">
          <div className="overline text-neutral-400">TOTAL RATERS</div>
          <div className="text-3xl font-black mt-1">{item.total_votes}</div>
        </div>
        <div className="border-2 border-black bg-white p-4">
          <div className="overline text-neutral-400">12-MO TREND</div>
          <div className={`text-3xl font-black mt-1 ${trend_delta > 0 ? "text-mango" : trend_delta < 0 ? "text-red-600" : ""}`}>
            {trend_delta == null ? "—" : `${trend_delta > 0 ? "+" : ""}${trend_delta}`}
          </div>
        </div>
        <div className="border-2 border-black bg-white p-4">
          <div className="overline text-neutral-400">DEMOGRAPHIC DIMENSIONS</div>
          <div className="text-3xl font-black mt-1">{SENTIMENT_DIMS.filter((d) => (breakdowns[d.key] || []).length > 0).length}/8</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {/* Histogram */}
        <div className="border-2 border-black bg-white p-5">
          <div className="overline mb-3">STAR DISTRIBUTION</div>
          {[5, 4, 3, 2, 1].map((s) => {
            const n = histogram?.[String(s)] || 0;
            const pct = histTotal ? Math.round((n / histTotal) * 1000) / 10 : 0;
            return (
              <div key={s} className="flex items-center gap-3 mb-2" data-testid={`insights-hist-${s}`}>
                <div className="text-sm w-6">{s}★</div>
                <div className="flex-1 bg-neutral-100 h-4 relative overflow-hidden">
                  <div className="bg-mango h-4" style={{ width: `${pct}%` }} />
                </div>
                <div className="text-sm w-16 text-right text-neutral-500">{n} ({pct}%)</div>
              </div>
            );
          })}
        </div>

        {/* Rating trend */}
        <div className="border-2 border-black bg-white p-5">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="overline">RATING TREND</div>
            <div className="flex gap-1">
              {PERIODS.map((p) => (
                <button
                  key={p.v}
                  onClick={() => { setPage(1); setPeriod(p.v); }}
                  className={`px-2.5 py-1 text-xs font-bold border ${period === p.v ? "bg-black text-white border-black" : "border-neutral-300 text-neutral-500 hover:border-black"}`}
                  data-testid={`sentiment-insights-period-${p.v}`}
                >{p.l}</button>
              ))}
            </div>
          </div>
          <div style={{ height: 160 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trend || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} tickFormatter={(m) => m?.slice(2)} />
                <YAxis domain={[1, 5]} tick={{ fontSize: 10 }} />
                <RcTooltip />
                <Line type="monotone" dataKey="avg" stroke="#16A34A" strokeWidth={2.5} dot={{ r: 3 }} connectNulls={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Demographic breakdowns — one panel per dimension */}
      <div className="border-2 border-black bg-white p-5 mb-6" data-testid="sentiment-insights-breakdowns">
        <div className="overline mb-4">RESPONDENT DEMOGRAPHICS</div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
          {SENTIMENT_DIMS.map((dim) => {
            const rows = breakdowns[dim.key] || [];
            return (
              <div key={dim.key}>
                <div className="text-xs font-bold uppercase tracking-wider text-neutral-500 mb-2">{dim.label}</div>
                {rows.length === 0 && <div className="text-xs text-neutral-400 italic">No data yet</div>}
                {rows.map((r) => (
                  <div key={r.bucket} className="mb-1.5" title={`${r.avg}★ · n=${r.n}`}>
                    <div className="flex justify-between text-xs mb-0.5">
                      <span className="truncate">{r.bucket}</span>
                      <span className="font-mono text-neutral-500">{r.avg}★ (n={r.n})</span>
                    </div>
                    <div className="bg-neutral-100 h-1.5">
                      <div className="bg-mango h-1.5" style={{ width: `${(r.avg / maxBucketAvg) * 100}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>

      {/* Respondent table */}
      <div className="border-2 border-black bg-white p-5" data-testid="sentiment-insights-respondents">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="overline">RESPONDENTS ({total_respondents})</div>
          <div className="flex gap-2 flex-wrap">
            {["gender", "age_band", "region", "sec"].map((k) => (
              <Select key={k} value={filters[k] || "__all__"} onValueChange={(v) => { setPage(1); setFilters({ ...filters, [k]: v === "__all__" ? "" : v }); }}>
                <SelectTrigger className="rounded-none h-8 w-32 text-xs" data-testid={`sentiment-insights-filter-${k}`}><SelectValue placeholder={k} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All {SENTIMENT_DIMS.find((d) => d.key === k)?.label}</SelectItem>
                  {(breakdowns[k] || []).map((r) => <SelectItem key={r.bucket} value={r.bucket}>{r.bucket}</SelectItem>)}
                </SelectContent>
              </Select>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b-2 border-black text-left">
                <th className="py-2 pr-3">Respondent</th>
                <th className="py-2 pr-3">Rating</th>
                <th className="py-2 pr-3">Date</th>
                <th className="py-2 pr-3">Gender</th>
                <th className="py-2 pr-3">Age</th>
                <th className="py-2 pr-3">Region</th>
                <th className="py-2 pr-3">City</th>
                <th className="py-2 pr-3">SEC</th>
                <th className="py-2 pr-3">Industry</th>
                <th className="py-2 pr-3">Employment</th>
                <th className="py-2 pr-3">Education</th>
                <th className="py-2 pr-3">Marital</th>
              </tr>
            </thead>
            <tbody>
              {respondents.map((r, i) => (
                <tr key={r.user_id || i} className="border-b border-neutral-100" data-testid={`sentiment-insights-row-${i}`}>
                  <td className="py-2 pr-3 font-medium">{r.name}</td>
                  <td className="py-2 pr-3 font-mono">{r.stars}★</td>
                  <td className="py-2 pr-3 text-neutral-500">{r.voted_at ? formatDate(r.voted_at) : "—"}</td>
                  <td className="py-2 pr-3">{r.gender || "—"}</td>
                  <td className="py-2 pr-3">{r.age_band || "—"}</td>
                  <td className="py-2 pr-3">{r.region || "—"}</td>
                  <td className="py-2 pr-3">{r.city || "—"}</td>
                  <td className="py-2 pr-3">{r.sec || "—"}</td>
                  <td className="py-2 pr-3">{r.industry || "—"}</td>
                  <td className="py-2 pr-3">{r.employment_type || "—"}</td>
                  <td className="py-2 pr-3">{r.education || "—"}</td>
                  <td className="py-2 pr-3">{r.marital_status || "—"}</td>
                </tr>
              ))}
              {respondents.length === 0 && (
                <tr><td colSpan={12} className="py-8 text-center text-neutral-400 italic">No respondents match this filter.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4 text-xs">
            <Button variant="outline" size="sm" className="rounded-none" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="sentiment-insights-prev-page">← Prev</Button>
            <span>Page {page} of {totalPages}</span>
            <Button variant="outline" size="sm" className="rounded-none" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="sentiment-insights-next-page">Next →</Button>
          </div>
        )}
      </div>
    </div>
  );
}

// =================== WAVES ===================
const QUOTA_CATS = {
  gender: ["Male", "Female", "Other"],
  age: ["18-24", "25-34", "35-44", "45-54", "55+"],
  region: ["Punjab", "Sindh", "KPK", "Balochistan", "Islamabad Capital Territory", "GB", "AJK"],
  sec: ["A", "B", "C", "D", "E"],
};

export function AdminWaves() {
  const [waves, setWaves] = useState(() => api.getCached("/waves") || []);
  const [drillId, setDrillId] = useState(null);
  const [introEditing, setIntroEditing] = useState(null);  // wave object being edited
  const [publishing, setPublishing] = useState(null);  // wave being published (drives confirm dialog)
  const [previewing, setPreviewing] = useState(null);   // wave being previewed
  const [cohortDialog, setCohortDialog] = useState(null);  // { wave, cohort: 'incomplete'|'waitlist' }
  const siteContent = useSiteContent();
  const extraCohortsEnabled = String(siteContent.wave_extra_cohort_emails_enabled ?? "true").toLowerCase() === "true";
  const urduEnabled = String(siteContent.urdu_feature_enabled ?? "true").toLowerCase() !== "false";
  const load = () => api.get("/waves").then((r) => setWaves(r.data));
  useEffect(() => { load(); }, []);
  // Filter — Active / Closed / All (default). "Active" also includes paused
  // and draft waves since from a project-management perspective they're all
  // "not yet closed". This is a client-side filter over the same fetched list.
  const [statusFilter, setStatusFilter] = useState("all");
  const shownWaves = waves.filter((w) => {
    if (statusFilter === "all") return true;
    if (statusFilter === "active") return w.status !== "closed";
    if (statusFilter === "closed") return w.status === "closed";
    return true;
  });

  return (
    <div>
      <div className="border-b-2 border-black pb-4 mb-6 flex justify-between items-end">
        <div>
          <div className="overline flex items-center gap-1">WAVES <HelpHint text={HELP.PAGE.waves} /></div>
          <h1 className="text-4xl font-black tracking-tighter">Survey waves</h1>
          <p className="text-sm text-neutral-600 mt-2 max-w-3xl">{HELP.PAGE.waves}</p>
        </div>
        <Link to="/admin/waves/new"><Button data-testid="new-wave-btn" className="bg-mango hover:bg-oodel-dark text-white border-2 border-black rounded-none">+ New wave</Button></Link>
      </div>
      {/* Status filter — All (default) / Active / Closed. Client-side; matches
          the "not-yet-closed" mental model rather than the exact enum. */}
      <div className="mb-4 flex items-center gap-2">
        <div className="text-xs font-mono text-neutral-500 mr-2">SHOW:</div>
        {[["all", "All"], ["active", "Active"], ["closed", "Closed"]].map(([key, label]) => (
          <button
            key={key}
            type="button"
            data-testid={`wave-filter-${key}`}
            onClick={() => setStatusFilter(key)}
            className={`px-3 py-1 text-xs font-bold uppercase tracking-wider border-2 transition-colors ${
              statusFilter === key ? "bg-black text-white border-black" : "bg-white text-neutral-700 border-neutral-300 hover:border-black"
            }`}
          >
            {label} <span className={`ml-1 font-mono ${statusFilter === key ? "opacity-80" : "text-neutral-500"}`}>
              ({key === "all" ? waves.length : key === "active" ? waves.filter((w) => w.status !== "closed").length : waves.filter((w) => w.status === "closed").length})
            </span>
          </button>
        ))}
      </div>
      <div className="space-y-2">
        {shownWaves.map((w) => (
          <div key={w.id} className="bg-white border border-neutral-200 p-5 flex items-center justify-between" data-testid={`admin-wave-${w.id}`}>
            <div className="cursor-pointer flex-1" onClick={() => setDrillId(w.id)} data-testid={`drill-wave-${w.id}`}>
              <div className="overline flex items-center gap-2">
                <span className={`inline-block px-2 py-0.5 ${w.status === 'active' ? 'bg-green-600 text-white' : w.status === 'paused' ? 'bg-yellow-500 text-white' : w.status === 'closed' ? 'bg-neutral-700 text-white' : 'bg-neutral-200'}`}>{w.status}</span>
                · {w.question_ids.length} questions · target {w.target_sample}
                {(w.points_award != null || w.points_screened_out != null) && (
                  <span className="text-mango font-mono">· {w.points_award ?? "—"}pts / {w.points_screened_out ?? "—"}pts screen</span>
                )}
              </div>
              <div className="font-bold text-lg flex items-center gap-2">
                {w.name}
                {w.status === "draft" && (
                  <button
                    type="button"
                    onClick={async (e) => {
                      e.stopPropagation();
                      const newName = window.prompt("Rename wave:", w.name);
                      if (!newName || newName === w.name) return;
                      try {
                        await api.patch(`/waves/${w.id}`, { name: newName });
                        toast.success("Renamed");
                        load();
                      } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
                    }}
                    title="Rename (drafts only)"
                    data-testid={`rename-wave-${w.id}`}
                    className="text-neutral-400 hover:text-mango"
                  >
                    <PencilSimple size={14} />
                  </button>
                )}
                {/* Edit intro — available on ANY wave status (presentation-only fields). */}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setIntroEditing(w); }}
                  title="Edit pre-survey intro screen"
                  data-testid={`edit-intro-${w.id}`}
                  className="text-neutral-400 hover:text-mango text-xs font-bold border border-neutral-300 hover:border-mango px-2 py-0.5 ml-1"
                >
                  Intro
                </button>
              </div>
              <div className="font-mono text-xs text-neutral-500 mt-1">
                Responses: {w.current_counts?.total || 0} · Click for demographic breakdown ↗
                {w.activated_at ? (
                  <span data-testid={`wave-started-at-${w.id}`}> · Started {formatDate(w.activated_at)}</span>
                ) : w.created_at && (
                  <span data-testid={`wave-created-at-${w.id}`}> · Created {formatDate(w.created_at)}</span>
                )}
                {w.published_at && (
                  <span data-testid={`wave-published-at-${w.id}`}> · Published {formatDate(w.published_at)}</span>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <Link to={`/admin/waves/${w.id}/report`}>
                <Button data-testid={`report-${w.id}`} variant="outline" size="sm" className="rounded-none" title="Per-question results + publishing matrix" onClick={(e) => e.stopPropagation()}>
                  Report
                </Button>
              </Link>
              {(w.status === "draft" || w.status === "active" || w.status === "paused") && (
                <Link to={`/admin/waves/${w.id}/edit`} onClick={(e) => e.stopPropagation()}>
                  <Button
                    data-testid={`edit-${w.id}`}
                    variant="outline"
                    size="sm"
                    className="rounded-none"
                    title={w.status === "draft"
                      ? "Edit this draft wave (questions, sector, audience, dates, points, quotas)"
                      : "Edit live wave — change points, quotas, end date, audience filter, name, intro (questions list / sector / start date stay locked)"}
                  >
                    Edit
                  </Button>
                </Link>
              )}
              {w.status === "draft" && <Button data-testid={`preview-${w.id}`} onClick={(e) => { e.stopPropagation(); setPreviewing(w.id); }} variant="outline" className="rounded-none">👁 Preview</Button>}
              {w.status === "draft" && <Button data-testid={`publish-${w.id}`} onClick={(e) => { e.stopPropagation(); setPublishing(w); }} className="bg-black hover:bg-neutral-800 rounded-none">Publish</Button>}
              {(w.status === "active" || w.status === "paused") && extraCohortsEnabled && (
                <>
                  <Button
                    data-testid={`send-incomplete-${w.id}`}
                    variant="outline"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); setCohortDialog({ wave: w, cohort: "incomplete" }); }}
                    className="rounded-none border-amber-500 text-amber-700 hover:bg-amber-50"
                    title="Email patrons who have a password but haven't finished their profile yet"
                  >
                    📨 Incomplete
                  </Button>
                  <Button
                    data-testid={`send-waitlist-${w.id}`}
                    variant="outline"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); setCohortDialog({ wave: w, cohort: "waitlist" }); }}
                    className="rounded-none border-violet-500 text-violet-700 hover:bg-violet-50"
                    title="Email legacy waitlist users (passwordless) — they get a signed set-password link"
                  >
                    📨 Waitlist
                  </Button>
                </>
              )}
              {w.status === "active" && <Button data-testid={`pause-${w.id}`} variant="outline" onClick={async (e) => { e.stopPropagation(); await api.post(`/waves/${w.id}/pause`); toast.success("Wave paused"); load(); }} className="rounded-none"><Pause size={16} className="mr-1" /> Pause</Button>}
              {w.status === "paused" && <Button data-testid={`resume-${w.id}`} onClick={async (e) => { e.stopPropagation(); await api.post(`/waves/${w.id}/resume`); toast.success("Wave resumed"); load(); }} className="bg-black hover:bg-neutral-800 rounded-none"><Play size={16} className="mr-1" /> Resume</Button>}
              {(w.status === "active" || w.status === "paused") && <Button data-testid={`close-${w.id}`} onClick={async (e) => { e.stopPropagation(); await api.post(`/waves/${w.id}/close`); toast.success("Wave closed. Results are PENDING — go to Survey Results to publish.", { action: { label: "Open", onClick: () => window.location.assign(`/admin/survey-results/${w.id}`) } }); load(); }} className="bg-mango hover:bg-oodel-dark rounded-none text-white">Close &amp; process</Button>}
              {w.status === "closed" && <Link to={`/admin/survey-results/${w.id}`} className="inline-flex items-center px-3 h-9 text-sm border-2 border-emerald-600 text-emerald-700 hover:bg-emerald-50 rounded-none font-bold" data-testid={`view-results-${w.id}`} onClick={(e) => e.stopPropagation()}>📊 Results</Link>}
              {w.status === "closed" && <Button data-testid={`reprocess-${w.id}`} variant="outline" onClick={async (e) => { e.stopPropagation(); await api.post(`/waves/${w.id}/close`); toast.success("Re-processed (idempotent)"); load(); }} className="rounded-none">Re-run ETL</Button>}
              <Button variant="ghost" size="sm" onClick={async (e) => { e.stopPropagation(); if (window.confirm(`Delete wave '${w.name}'?`)) { await api.delete(`/waves/${w.id}`); load(); } }}><Trash size={18} /></Button>
            </div>
          </div>
        ))}
      </div>
      {drillId && <RespondentsDrillDownModal kind="waves" id={drillId} title={waves.find((x) => x.id === drillId)?.name} onClose={() => setDrillId(null)} />}
      {introEditing && (
        <WaveIntroEditorModal
          wave={introEditing}
          onClose={() => setIntroEditing(null)}
          onSaved={() => { setIntroEditing(null); load(); }}
        />
      )}
      {publishing && (
        <PublishConfirmDialog
          open={true}
          wave={publishing}
          onClose={() => setPublishing(null)}
          onPublished={() => { setPublishing(null); load(); }}
        />
      )}
      {cohortDialog && (
        <CohortSendDialog
          open={true}
          wave={cohortDialog.wave}
          cohort={cohortDialog.cohort}
          onClose={() => setCohortDialog(null)}
          onSent={() => { setCohortDialog(null); load(); }}
        />
      )}
      {previewing && (
        <PreviewModal open={true} mode="wave" waveId={previewing} onClose={() => setPreviewing(null)} />
      )}
    </div>
  );
}

// =================== WAVE INTRO EDITOR MODAL ===================
// Admin-only mini-editor for the per-wave "Before you start" intro screen.
// Works on any wave status (presentation-only fields). Empty values → patrons
// see the global default (or skip the intro entirely if that's also blank).
function WaveIntroEditorModal({ wave, onClose, onSaved }) {
  const [topic, setTopic] = useState(wave.intro_topic || "");
  const [text, setText] = useState(wave.intro_text || "");
  const [disclaimer, setDisclaimer] = useState(wave.wave_disclaimer || "");
  const [featuredQid, setFeaturedQid] = useState(wave.featured_question_id || "");
  const [publicQuestions, setPublicQuestions] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/waves/${wave.id}/public-questions`).then((r) => setPublicQuestions(r.data)).catch(() => setPublicQuestions([]));
  }, [wave.id]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/waves/${wave.id}`, {
        intro_topic: topic.trim() ? topic.trim() : null,
        intro_text: text.trim() ? text : null,
        wave_disclaimer: disclaimer.trim() ? disclaimer : null,
        featured_question_id: featuredQid || null,
      });
      toast.success("Saved");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const clearBoth = async () => {
    if (!window.confirm("Clear intro topic and text? (Disclaimer is kept — clear separately if you need to.)")) return;
    setSaving(true);
    try {
      await api.patch(`/waves/${wave.id}`, { intro_topic: null, intro_text: null });
      toast.success("Cleared — patrons will fall back to the global default");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="wave-intro-modal">
      <div className="bg-white border-2 border-black w-full max-w-2xl max-h-[90vh] flex flex-col hard-shadow" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b-2 border-black">
          <div>
            <div className="overline">WAVE INTRO</div>
            <div className="font-bold">{wave.name}</div>
          </div>
          <Button variant="ghost" onClick={onClose} className="rounded-none"><X size={18} /></Button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <div className="bg-neutral-50 border border-neutral-200 p-3 text-xs text-neutral-600">
            Patrons see this screen <strong>before question 1</strong>. Both fields are optional — leave blank to use the <strong>global default</strong> from Site Content → "Survey Intro Defaults". If the global default is also blank, the survey starts on Q1 with no intro.
            <br />Supports placeholders: <code>{`{{topic}}`}</code> and <code>{`{{points}}`}</code> (points are taken from this wave's `points_award`).
          </div>
          <div>
            <Label className="overline text-xs">Topic label</Label>
            <Input
              data-testid="wave-intro-topic"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              className="rounded-none"
              placeholder="e.g. consumer mobile habits"
            />
          </div>
          <div>
            <Label className="overline text-xs">Intro text</Label>
            <Textarea
              data-testid="wave-intro-text"
              rows={8}
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="rounded-none font-mono text-xs"
              placeholder="Leave blank to use the global default."
            />
          </div>
          {/* ITER 62 — Public snapshot-archive disclaimer. Shown under the
              question count on /snapshots/wave/{wid} — e.g. methodology note,
              limitations, or a client-facing caveat. Multi-line supported. */}
          <div className="border-t border-neutral-200 pt-4 mt-2">
            <Label className="overline text-xs">Snapshot archive disclaimer (public)</Label>
            <Textarea
              data-testid="wave-disclaimer"
              rows={4}
              value={disclaimer}
              onChange={(e) => setDisclaimer(e.target.value)}
              className="rounded-none text-sm"
              placeholder="e.g. Results are indicative and reflect self-reported attitudes at a moment in time. Sample composition may skew urban."
            />
            <div className="text-[10px] text-neutral-500 mt-1">
              Rendered under the wave title on the public Snapshots archive page. Leave blank to hide entirely.
            </div>
          </div>
          {/* ITER 74 — which chart's bars get rendered into the og:image
              shown when this wave's Share link is posted on WhatsApp/
              Facebook/etc. Options are limited to questions that are
              actually public+published — same rule the share endpoint
              itself falls back to when nothing is picked here. */}
          <div className="border-t border-neutral-200 pt-4 mt-2">
            <Label className="overline text-xs">Featured chart for sharing</Label>
            <select
              data-testid="wave-featured-question"
              value={featuredQid}
              onChange={(e) => setFeaturedQid(e.target.value)}
              className="w-full border-2 border-black rounded-none px-3 py-2 text-sm mt-1 bg-white"
            >
              <option value="">Auto — first public chart ({publicQuestions[0]?.question_text?.slice(0, 40) || "none published yet"}{publicQuestions[0]?.question_text?.length > 40 ? "…" : ""})</option>
              {publicQuestions.map((q) => (
                <option key={q.question_id} value={q.question_id}>{q.question_text}</option>
              ))}
            </select>
            <div className="text-[10px] text-neutral-500 mt-1">
              This chart's bars are rendered into the preview image (og:image) shown when the Share button's link is posted on WhatsApp, Facebook, X, etc. Only public, published questions are selectable.
            </div>
          </div>
        </div>
        <div className="border-t border-grid p-4 flex justify-between items-center">
          <Button variant="outline" onClick={clearBoth} disabled={saving} className="rounded-none text-xs" data-testid="wave-intro-clear">Clear both → fall back to default</Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={saving} className="rounded-none">Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="wave-intro-save" className="bg-black hover:bg-neutral-800 rounded-none text-white">{saving ? "Saving…" : "Save intro"}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// =================== RESPONDENTS DRILL-DOWN MODAL ===================
function RespondentsDrillDownModal({ kind, id, title, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/admin/${kind}/${id}/respondents`).then((r) => setData(r.data)).catch(() => setData({ error: true })); }, [kind, id]);
  const Bar = ({ rows }) => {
    if (!rows || rows.length === 0) return <div className="text-xs text-neutral-500">No data</div>;
    const max = Math.max(...rows.map((r) => r.n));
    return (
      <div className="space-y-1">
        {rows.map((r) => (
          <div key={r.label} className="flex items-center gap-2 text-xs">
            <div className="w-24 text-neutral-700 truncate">{r.label}</div>
            <div className="flex-1 bg-neutral-100"><div className="bg-mango h-4" style={{ width: `${(r.n / max) * 100}%` }} /></div>
            <div className="w-10 text-right font-mono">{r.n}</div>
          </div>
        ))}
      </div>
    );
  };
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="respondents-modal">
      <div className="bg-white border-2 border-black w-full max-w-5xl max-h-[90vh] flex flex-col hard-shadow" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center p-4 border-b-2 border-black">
          <div>
            <div className="overline">{kind === "waves" ? "WAVE" : "TRACKER"} · DEMOGRAPHIC BREAKDOWN</div>
            <div className="font-bold text-lg">{title}</div>
          </div>
          <Button variant="ghost" onClick={onClose} className="rounded-none"><X size={18} /></Button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          {!data && <div className="text-neutral-500">Loading…</div>}
          {data?.error && <div className="text-red-600">Failed to load.</div>}
          {data && !data.error && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-neutral-200 mb-6">
                <div className="bg-white p-4"><div className="overline">TOTAL</div><div className="font-mono text-2xl font-black">{data.total ?? data.wave_count ?? 0}</div></div>
                {kind === "waves" && <>
                  <div className="bg-white p-4"><div className="overline">COMPLETED</div><div className="font-mono text-2xl font-black text-green-700">{data.completed}</div></div>
                  <div className="bg-white p-4"><div className="overline">SCREENED OUT</div><div className="font-mono text-2xl font-black text-yellow-600">{data.screened_out}</div></div>
                  <div className="bg-white p-4"><div className="overline">VOIDED</div><div className="font-mono text-2xl font-black text-red-600">{data.void}</div></div>
                </>}
                {kind === "trackers" && <div className="bg-white p-4"><div className="overline">WAVES FEEDING</div><div className="font-mono text-2xl font-black">{data.wave_count}</div><div className="text-[10px] text-neutral-500 mt-1">{data.closed_wave_count ?? 0} closed (ETL run)</div></div>}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div><div className="overline mb-2">BY GENDER</div><Bar rows={data.by_gender} /></div>
                <div><div className="overline mb-2">BY AGE</div><Bar rows={data.by_age} /></div>
                <div><div className="overline mb-2">BY REGION</div><Bar rows={data.by_region} /></div>
                <div><div className="overline mb-2">BY SEC</div><Bar rows={data.by_sec} /></div>
                <div className="md:col-span-2"><div className="overline mb-2">TOP CITIES</div><Bar rows={data.by_city} /></div>
                {data.by_industry?.length > 0 && <div className="md:col-span-2"><div className="overline mb-2">BY INDUSTRY</div><Bar rows={data.by_industry} /></div>}
              </div>
              {data.completion_timeline?.length > 0 && (
                <div className="mt-6">
                  <div className="overline mb-2">COMPLETION TIMELINE</div>
                  <div className="space-y-1">
                    {data.completion_timeline.map((d) => (
                      <div key={d.date} className="flex items-center gap-2 text-xs">
                        <div className="w-24 font-mono">{d.date}</div>
                        <div className="flex-1 bg-neutral-100"><div className="bg-mango h-3" style={{ width: `${(d.n / Math.max(...data.completion_timeline.map((x) => x.n))) * 100}%` }} /></div>
                        <div className="w-10 text-right font-mono">{d.n}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function SortableQ({ q, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, setActivatorNodeRef } = useSortable({ id: q.id });
  const vis = q.visibility || "public";
  return (
    <div ref={setNodeRef} {...attributes} style={{ transform: CSS.Transform.toString(transform), transition }} className="bg-white border border-neutral-200 p-3 flex justify-between items-center gap-3" data-testid={`wave-q-${q.id}`}>
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <span ref={setActivatorNodeRef} {...listeners} className="cursor-grab text-neutral-400 hover:text-black select-none" title="Drag to reorder">⋮⋮</span>
        <span className="overline shrink-0">{q.format}</span>
        <span className="text-sm truncate flex-1">{q.text}</span>
        <VisibilityBadge visibility={vis} clientId={q.client_id} size="xs" className="shrink-0" />
        {q.audience === "niche" && (
          <span className="overline text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-900 ring-1 ring-amber-300 shrink-0" title="This question has its own screener / niche filter on top of the wave-level audience.">SCREENER</span>
        )}
      </div>
      <button
        type="button"
        data-testid={`wave-q-remove-${q.id}`}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); onRemove(q.id); }}
        className="ml-2 w-7 h-7 inline-flex items-center justify-center text-lg text-neutral-500 hover:bg-red-50 hover:text-red-600"
        title="Remove from wave"
      >×</button>
    </div>
  );
}

// ── ORBIT mobile launcher (opens a Sheet on <2xl) ──
function OrbitRailMobile({ selectedQuestions, setSelected, audience, audienceFilter, waveKey }) {
  const [open, setOpen] = useState(false);
  const [store, patch] = useOrbitStore(waveKey);
  const defaults = {
    audience: audience === "niche"
      ? Object.entries(audienceFilter || {}).filter(([,v]) => (v || []).length).map(([k,v]) => `${k}: ${v.join(", ")}`).join(" · ")
      : "",
  };
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="2xl:hidden fixed right-4 bottom-4 z-40 flex items-center gap-2 bg-white text-black border-2 border-black hover:bg-mango hover:text-white hover:border-mango px-3 py-2 hard-shadow transition-colors"
        data-testid="orbit-launcher-mobile"
      >
        <img src="/brand/orbit.png" alt="" width={18} height={18} className="orbit-mark-spin" />
        <span className="font-black text-[11px] tracking-[0.18em] uppercase">Open</span>
      </button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="right"
          className="2xl:hidden w-[360px] max-w-[92vw] p-0 bg-neutral-50 border-l-2 border-black overflow-y-auto"
          data-testid="orbit-sheet"
        >
          <SheetHeader className="sr-only"><SheetTitle>ORBIT</SheetTitle></SheetHeader>
          <div className="p-3">
            <OrbitRail
              selectedQuestions={selectedQuestions}
              defaults={defaults}
              onClose={() => setOpen(false)}
              store={store}
              patch={patch}
              onApplyOrder={(orderedIds) => {
                const map = new Map(selectedQuestions.map((q) => [q.id, q]));
                const reordered = orderedIds.map((id) => map.get(id)).filter(Boolean);
                if (reordered.length === selectedQuestions.length) {
                  setSelected(reordered);
                  toast.success("ORBIT applied the suggested order");
                }
              }}
              onAddOneToWave={(question) => {
                setSelected((prev) => prev.some((q) => q.id === question.id) ? prev : [...prev, question]);
              }}
            />
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}


export function AdminWaveBuilder() {
  const nav = useNavigate();
  const siteContent = useSiteContent();
  const orbitEnabled = String(siteContent.orbit_enabled ?? "true").toLowerCase() !== "false";
  // Wave-key for ORBIT persistence (stable across tab switches, wipes on new-wave load).
  const { id: routeWaveId } = useParams();
  const orbitWaveKey = routeWaveId || "new";
  const [orbitStore, orbitPatch] = useOrbitStore(orbitWaveKey);
  // Edit-mode: when the route is /admin/waves/:id/edit, useParams gives us
  // the wave id and we hydrate the form from /waves/:id, switching the
  // submit handler to PATCH instead of POST.
  const { id: editingId } = useParams();
  const [editLoaded, setEditLoaded] = useState(!editingId);
  const [questions, setQuestions] = useState([]);
  const [trackers, setTrackers] = useState([]);
  const [selected, setSelected] = useState([]);
  // ITER 107 — full wave list (admin scope) fetched purely so the
  // "Featured Wave" toggle below can look up which OTHER wave (if any) is
  // currently featured, to name it in the swap-confirmation prompt.
  // iter31 — hydration buffer: when editing a draft wave, /api/waves/{id}
  // returns question_ids as an ARRAY OF STRINGS, but our `selected` state
  // holds full question OBJECTS (used by drag-sort, filter, map, etc.). We
  // stash IDs here then a separate effect resolves them once /questions
  // arrives. Without this, the edit screen showed blank + uneditable
  // because every downstream component expected objects.
  const [pendingSelectedIds, setPendingSelectedIds] = useState(null);
  const [allWaves, setAllWaves] = useState([]);
  // ITER 109 — swap-confirmation state. Was previously `window.confirm()`,
  // which the platform's preview iframe silently blocks (no `allow="modals"`
  // permission) — it returns falsy with zero visible popup, which looked
  // exactly like "the toggle doesn't respond to clicks at all". A real
  // in-app Dialog has no such restriction.
  const [featuredSwapPrompt, setFeaturedSwapPrompt] = useState(null);
  const _siteContent = useSiteContent();
  const urduEnabled = String(_siteContent.urdu_feature_enabled ?? "true").toLowerCase() !== "false";
  const [form, setForm] = useState({ name: "", target_sample: 1000, points_award: "", points_screened_out: "", expected_minutes: "", fast_fill_penalty_points: "", start_date: "", end_date: "", cover_image: "", intro_topic: "", intro_text: "", geo_scope: "both", sector_key: "", secondary_sectors: [], show_overseas_in_public: true, show_overseas_in_panelist: true, show_overseas_in_subscriber: true, show_overseas_in_enterprise: true, show_overseas_in_private: true, show_respondent_count: false, result_columns: [], name_ur: "", name_ur_status: "missing", intro_topic_ur: "", intro_topic_ur_status: "missing", intro_text_ur: "", intro_text_ur_status: "missing", show_urdu_version: false, is_featured: false });
  const [audience, setAudience] = useState("all");          // "all" | "niche" — applies to the WHOLE wave (Phase A)
  const [audienceFilter, setAudienceFilter] = useState({}); // {gender:[…], region:[…], …}
  const [defaults, setDefaults] = useState({ award: 50, screened: 5 });
  const [overseasMaster, setOverseasMaster] = useState(false);
  const [sectors, setSectors] = useState([]); // [{key,label,...}] for the sector picker
  const [quotas, setQuotas] = useState({
    gender: { Male: 50, Female: 50 },
    age: { "18-24": 20, "25-34": 25, "35-44": 25, "45-54": 20, "55+": 10 },
    region: { Punjab: 53, Sindh: 23, KPK: 14, Balochistan: 5, "Islamabad Capital Territory": 3, GB: 1, AJK: 1 },
    sec: { A: 5, B: 15, C: 30, D: 30, E: 20 },
  });

  const toggleAudCat = (cat, val) => {
    const cur = audienceFilter[cat] || [];
    const next = cur.includes(val) ? cur.filter((x) => x !== val) : [...cur, val];
    setAudienceFilter({ ...audienceFilter, [cat]: next });
  };

  // ITER 107/109 — "Featured Wave" toggle. Only one wave may be featured at
  // a time (server-side enforced too, see PATCH/PUT /waves). Swap conflicts
  // are confirmed via an in-app Dialog (see `featuredSwapPrompt` below), not
  // `window.confirm()` — see the state declaration above for why.
  const toggleFeatured = (checked) => {
    if (!checked) { setForm({ ...form, is_featured: false }); return; }
    const other = allWaves.find((w) => w.is_featured && w.id !== editingId);
    if (other) { setFeaturedSwapPrompt(other); return; }
    setForm({ ...form, is_featured: true });
  };

  useEffect(() => {
    Promise.all([api.get("/questions"), api.get("/trackers"), api.get("/site-content").catch(() => ({ data: {} })), api.get("/sectors").catch(() => ({ data: [] })), api.get("/waves").catch(() => ({ data: [] }))])
      .then(([q, t, sc, sec, w]) => {
        setQuestions(q.data);
        setTrackers(t.data);
        setDefaults({
          award: Number(sc.data.default_points_award) || 50,
          screened: Number(sc.data.default_points_screened) || 5,
        });
        setOverseasMaster(String((sc.data || {}).overseas_program_enabled || "").toLowerCase() === "true");
        setSectors(sec.data || []);
        setAllWaves(w.data || []);
      });
  }, []);

  // EDIT MODE — hydrate from server when editingId is present in the URL.
  // Live waves (active / paused) are loadable but a subset of fields are
  // locked (questions list, sector, start_date, geo_scope) — toggled via
  // `isLiveEdit` below. Closed waves bounce back since they're read-only.
  const [isLiveEdit, setIsLiveEdit] = useState(false);
  useEffect(() => {
    if (!editingId) return;
    api.get(`/waves/${editingId}`).then(({ data: w }) => {
      if (w.status === "closed") {
        toast.error("Closed waves are read-only. Open Survey Results to view aggregated data, or clone the wave for a new run.");
        nav("/admin/waves");
        return;
      }
      setIsLiveEdit(w.status !== "draft");
      setForm({
        name: w.name || "",
        target_sample: w.target_sample ?? 1000,
        points_award: w.points_award ?? "",
        points_screened_out: w.points_screened_out ?? "",
        expected_minutes: w.expected_minutes ?? "",
        fast_fill_penalty_points: w.fast_fill_penalty_points ?? "",
        start_date: w.start_date || "",
        end_date: w.end_date || "",
        cover_image: w.cover_image || "",
        intro_topic: w.intro_topic || "",
        intro_text: w.intro_text || "",
        name_ur: w.name_ur || "",
        name_ur_status: w.name_ur_status || "missing",
        intro_topic_ur: w.intro_topic_ur || "",
        intro_topic_ur_status: w.intro_topic_ur_status || "missing",
        intro_text_ur: w.intro_text_ur || "",
        intro_text_ur_status: w.intro_text_ur_status || "missing",
        show_urdu_version: !!w.show_urdu_version,
        geo_scope: w.geo_scope || "both",
        sector_key: w.sector_key || "",
        secondary_sectors: w.secondary_sectors || [],
        show_overseas_in_public: w.show_overseas_in_public !== false,
        show_overseas_in_panelist: w.show_overseas_in_panelist !== false,
        show_overseas_in_subscriber: w.show_overseas_in_subscriber !== false,
        show_overseas_in_enterprise: w.show_overseas_in_enterprise !== false,
        show_overseas_in_private: w.show_overseas_in_private !== false,
        show_respondent_count: w.show_respondent_count === true,
        result_columns: Array.isArray(w.result_columns) ? w.result_columns : [],
        is_featured: !!w.is_featured,
      });
      setSelected([]);  // will be hydrated by the IDs-resolver effect below
      setPendingSelectedIds(Array.isArray(w.question_ids) ? w.question_ids : []);
      setAudience(w.audience || "all");
      setAudienceFilter(w.audience_filter || {});
      if (w.quotas) {
        // ── Migrate legacy region quota keys into the canonical set ──
        // Old waves saved with combined `ICT/GB/AJK` or short `ICT` keys
        // need their values folded into the new "Islamabad Capital Territory"
        // bucket. Any orphan keys are dropped here so the Σ pill matches
        // what the user actually sees on screen (the root cause of the
        // "weird calculation" bug). The same defence runs on save below.
        const clean = JSON.parse(JSON.stringify(w.quotas));
        if (clean.region) {
          const r = clean.region;
          const ictTarget = "Islamabad Capital Territory";
          // Fold any legacy keys into the canonical one (additively, so
          // multiple legacy keys collapse correctly).
          for (const legacy of ["ICT", "ICT/GB/AJK", "Islamabad"]) {
            if (r[legacy] !== undefined) {
              r[ictTarget] = (Number(r[ictTarget]) || 0) + (Number(r[legacy]) || 0);
              delete r[legacy];
            }
          }
          // Drop any region key that's NOT in the canonical list so it can't
          // silently inflate the sum.
          const allowed = new Set(QUOTA_CATS.region);
          for (const k of Object.keys(r)) {
            if (!allowed.has(k)) delete r[k];
          }
          // Ensure every canonical key exists (default 0).
          for (const k of QUOTA_CATS.region) if (r[k] === undefined) r[k] = 0;
        }
        setQuotas(clean);
      }
      setEditLoaded(true);
    }).catch((err) => {
      toast.error(err.response?.data?.detail || "Could not load wave");
      nav("/admin/waves");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingId]);

  // iter31 — resolve question_ids → full question objects once both the
  // wave payload and the /questions list have arrived. Preserves the
  // wave's original question order (which matters — patrons see them in
  // this order during the survey).
  useEffect(() => {
    if (!pendingSelectedIds || !questions.length) return;
    const map = new Map(questions.map((q) => [q.id, q]));
    const resolved = pendingSelectedIds.map((id) => map.get(id)).filter(Boolean);
    setSelected(resolved);
    setPendingSelectedIds(null);
  }, [pendingSelectedIds, questions]);

  // Sum ONLY the keys that are rendered as inputs for that category. This
  // prevents orphan / legacy keys (e.g. an old "ICT/GB/AJK" combined region
  // value sitting in a migrated wave's quotas blob) from silently inflating
  // the Σ pill and making the math look wrong to the admin.
  const sums = Object.fromEntries(
    Object.entries(QUOTA_CATS).map(([cat, allowed]) => [
      cat,
      allowed.reduce((acc, key) => acc + Number((quotas[cat] || {})[key] || 0), 0),
    ])
  );
  const allValid = Object.values(sums).every((s) => Math.abs(s - 100) < 0.01);

  // ---- duplicate-tracker guardrail ----
  const trackerCounts = {};
  selected.forEach((q) => { if (q.tracker_id) trackerCounts[q.tracker_id] = (trackerCounts[q.tracker_id] || 0) + 1; });
  const dupTrackerIds = Object.entries(trackerCounts).filter(([, n]) => n > 1).map(([tid]) => tid);
  const dupTrackerNames = dupTrackerIds.map((tid) => trackers.find((t) => t.id === tid)?.name).filter(Boolean);

  const addQuestion = (q) => {
    if (q.tracker_id && trackerCounts[q.tracker_id]) {
      const tname = trackers.find((t) => t.id === q.tracker_id)?.name || "this tracker";
      if (!window.confirm(`⚠️ This wave already has a question for the "${tname}" tracker. Adding a second creates an ambiguous data point for the time-series. Continue anyway?`)) return;
    }
    setSelected([...selected, q]);
  };

  // ---- Pattern A: toggle canonical question of a tracker in/out of the wave ----
  const toggleTracker = (tid) => {
    const tr = trackers.find((t) => t.id === tid);
    if (!tr) return;
    const qid = tr.active_question_id;
    if (!qid) { toast.error("This tracker has no canonical question — set one in the Trackers tab."); return; }
    if (selected.some((s) => s.id === qid)) {
      // Remove
      setSelected(selected.filter((s) => s.id !== qid));
      toast.info(`Removed canonical question for ${tr.name}`);
    } else {
      const q = questions.find((x) => x.id === qid);
      if (q) {
        setSelected([...selected, q]);
        toast.success(`Added canonical question for ${tr.name}`);
      }
    }
  };

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldI = selected.findIndex((q) => q.id === active.id);
    const newI = selected.findIndex((q) => q.id === over.id);
    setSelected(arrayMove(selected, oldI, newI));
  };

  const submit = async () => {
    if (!form.name || selected.length === 0) { toast.error("Add a name and at least one question"); return; }
    if (!allValid) { toast.error("Quotas must each sum to 100%"); return; }
    // Make the 4 reward/timing fields mandatory — admin must consciously pick
    // values rather than letting the form back-fill silent defaults that may
    // diverge from what the patron actually sees on the survey card.
    const missing = [];
    if (form.points_award === "" || form.points_award === null) missing.push("Completion points");
    if (form.points_screened_out === "" || form.points_screened_out === null) missing.push("Screen-out points");
    if (form.expected_minutes === "" || form.expected_minutes === null) missing.push("Expected fill time");
    if (form.fast_fill_penalty_points === "" || form.fast_fill_penalty_points === null) missing.push("Fast-fill penalty");
    if (missing.length) { toast.error("Required: " + missing.join(", ")); return; }
    if (dupTrackerNames.length && !window.confirm(`Warning: this wave has multiple questions for: ${dupTrackerNames.join(", ")}. Save anyway?`)) return;
    try {
      // ── Sanitise quotas before sending: drop any orphan keys that aren't
      // in the canonical QUOTA_CATS lists. This kills the legacy
      // "ICT/GB/AJK" drift at the source — once a live wave is saved
      // through this form, its quotas blob is permanently clean.
      const cleanQuotas = {};
      for (const [cat, allowed] of Object.entries(QUOTA_CATS)) {
        const src = quotas[cat] || {};
        cleanQuotas[cat] = {};
        for (const k of allowed) cleanQuotas[cat][k] = Number(src[k] || 0);
      }
      const payload = {
        name: form.name, target_sample: Number(form.target_sample),
        quotas: cleanQuotas, question_ids: selected.map((q) => q.id),
        points_award: Number(form.points_award),
        points_screened_out: Number(form.points_screened_out),
        expected_minutes: Number(form.expected_minutes),
        fast_fill_penalty_points: Number(form.fast_fill_penalty_points),
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        cover_image: form.cover_image ? form.cover_image.trim() : null,
        intro_topic: form.intro_topic ? form.intro_topic.trim() : null,
        intro_text: form.intro_text ? form.intro_text : null,
        sector_key: form.sector_key || null,
        secondary_sectors: (form.secondary_sectors || []).filter((s) => s && s !== form.sector_key).slice(0, 3),
        audience,
        audience_filter: audience === "niche" ? audienceFilter : null,
        geo_scope: form.geo_scope,
        show_overseas_in_public: form.show_overseas_in_public,
        show_overseas_in_panelist: form.show_overseas_in_panelist,
        show_overseas_in_subscriber: form.show_overseas_in_subscriber,
        show_overseas_in_enterprise: form.show_overseas_in_enterprise,
        show_overseas_in_private: form.show_overseas_in_private,
        show_respondent_count: !!form.show_respondent_count,
        is_featured: !!form.is_featured,
      };
      if (editingId) {
        if (isLiveEdit) {
          // Running/paused wave: backend will reject draft-only fields, so
          // strip the un-editable ones client-side. Sending PATCH instead of
          // PUT signals partial-update intent.
          // NOTE: geo_scope + show_overseas_in_* are now live-editable (admin
          // can re-target a running wave to overseas patrons). Only the truly
          // structural fields stay locked.
          const liveSafe = { ...payload };
          ["question_ids", "start_date", "sector_key", "secondary_sectors"].forEach((k) => delete liveSafe[k]);
          await api.patch(`/waves/${editingId}`, liveSafe);
          toast.success("Live wave updated · changes apply to new responses");
        } else {
          await api.put(`/waves/${editingId}`, payload);
          toast.success("Wave updated");
        }
      } else {
        await api.post("/waves", payload);
        toast.success("Wave created as draft");
      }
      nav("/admin/waves");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      {!editLoaded && editingId ? (
        <div className="p-12 text-center text-neutral-400">Loading wave…</div>
      ) : (
      <>
      <div className="border-b-2 border-black pb-4 mb-6">
        <div className="overline flex items-center gap-1">WAVE BUILDER <HelpHint text={HELP.PAGE.waves} /></div>
        <h1 className="text-4xl font-black tracking-tighter">{editingId ? (isLiveEdit ? "Edit LIVE wave" : "Edit draft wave") : "New wave"}</h1>
        <p className="text-sm text-neutral-600 mt-2 max-w-3xl">{HELP.PAGE.waves}</p>
        {isLiveEdit && (
          <div className="mt-4 bg-amber-50 border-2 border-amber-400 p-3 text-xs text-amber-900 max-w-3xl" data-testid="live-edit-banner">
            <b>Editing a live wave.</b> You can change rewards, quotas, timing, end date, audience filter, name, and intro copy — these only affect <i>future</i> responses; already-submitted answers are untouched. <span className="font-bold">Locked</span> for audit integrity: questions list, start date, sector, geo scope. If you need to change those, <b>clone</b> this wave to a new draft instead.
          </div>
        )}
      </div>

      <div className={`grid grid-cols-1 md:grid-cols-2 ${orbitEnabled ? "2xl:grid-cols-[1fr_1fr_320px]" : ""} gap-6`}>
        <div className="space-y-4">
          <div className="bg-white border-2 border-black p-5">
            <Label className="overline flex items-center gap-1">Wave name * <HelpHint text={HELP.WAVE.name} /></Label>
            <Input data-testid="wb-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none mb-2" placeholder="e.g. Pulse · Oct 2026" />
            <InlineUrduEditor
              entity="wave"
              entityId={editingId}
              field="name"
              enValue={form.name}
              initialUr={form.name_ur || ""}
              initialStatus={form.name_ur_status}
            />
            <Label className="overline flex items-center gap-1 mt-4">Target sample size <HelpHint text={HELP.WAVE.target_sample} /></Label>
            <Input data-testid="wb-target" type="number" value={form.target_sample} onChange={(e) => setForm({ ...form, target_sample: e.target.value })} className="rounded-none" />

            <div className="mt-5 pt-4 border-t border-neutral-200">
              <div className="overline mb-1 text-mango flex items-center gap-1">REWARDS <HelpHint text={`${HELP.WAVE.points_award} · ${HELP.WAVE.points_screened}`} /></div>
              <p className="text-xs text-neutral-500 mb-3">Required. Set a per-wave value — no silent fallback to site defaults. Site defaults today: <b>{defaults.award} pts</b> completion · <b>{defaults.screened} pts</b> screen-out.</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="overline text-xs">Completion points <span className="text-red-500">*</span></Label>
                  <Input data-testid="wb-points-award" type="number" min="0" required value={form.points_award} onChange={(e) => setForm({ ...form, points_award: e.target.value })} placeholder="e.g. 50" className="rounded-none" />
                </div>
                <div>
                  <Label className="overline text-xs">Screened-out compensation <span className="text-red-500">*</span></Label>
                  <Input data-testid="wb-points-screened" type="number" min="0" required value={form.points_screened_out} onChange={(e) => setForm({ ...form, points_screened_out: e.target.value })} placeholder="e.g. 5" className="rounded-none" />
                </div>
              </div>
            </div>

            <div className="mt-5 pt-4 border-t border-neutral-200">
              <div className="overline mb-1 flex items-center gap-1">FIELDING WINDOW <HelpHint text="Start date is when the wave goes live. Leave end date blank to keep the wave open until you manually 'Close & process'." /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="overline text-xs">Start date</Label>
                  <Input data-testid="wb-start-date" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} disabled={isLiveEdit} className="rounded-none" />
                </div>
                <div>
                  <Label className="overline text-xs">End date (optional)</Label>
                  <Input data-testid="wb-end-date" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="rounded-none" placeholder="open-ended" />
                </div>
              </div>
              <p className="text-xs text-neutral-500 mt-2">End date blank means the wave stays open until an admin clicks <b>Close &amp; process</b>. {isLiveEdit && <span className="text-amber-700">Start date is locked on live waves.</span>}</p>
              <div className="mt-3">
                <Label className="overline text-xs flex items-center gap-1">Cover image URL (optional) <HelpHint text="Shown as the poster-style cover photo on the Waves grid card (portrait 4:5 crop) and as a small 48×48px thumbnail next to this wave in the Completed Surveys list. Recommended size: 1000×1250px (4:5 portrait ratio, min 800×1000px) — a square image (e.g. 500×500) will get cropped on the left and right edges to fit the portrait card, so avoid important text/logos near the side edges. Leave blank to show a plain text-only card (matches the reference design)." /></Label>
                <Input data-testid="wb-cover-image" value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} className="rounded-none" placeholder="https://...jpg" />
                {form.cover_image && (
                  <img src={form.cover_image} alt="" className="mt-2 h-20 w-36 object-cover border border-neutral-300" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                )}
              </div>
              <div className="mt-4 flex items-center justify-between gap-3 bg-mango/5 border border-mango/20 p-3">
                <div>
                  <Label className="overline text-xs flex items-center gap-1">Featured Wave <HelpHint text="Only ONE wave can be Featured at a time. The Featured wave replaces whatever the patron dashboard's homepage 'Featured Wave' spotlight card currently shows. Turning this ON for a different wave automatically turns it OFF for whichever wave had it before." /></Label>
                  <p className="text-xs text-neutral-500 mt-0.5">Show this wave on the patron dashboard homepage.</p>
                </div>
                <Switch data-testid="wb-is-featured" checked={!!form.is_featured} onCheckedChange={toggleFeatured} />
              </div>
            </div>

            {/* ITER 109 — Featured Wave swap-confirmation dialog (in-app,
                not window.confirm — see state declaration above). */}
            <Dialog open={!!featuredSwapPrompt} onOpenChange={(o) => !o && setFeaturedSwapPrompt(null)}>
              <DialogContent className="max-w-md" data-testid="featured-swap-dialog">
                <DialogHeader>
                  <DialogTitle>Swap the Featured Wave?</DialogTitle>
                </DialogHeader>
                <p className="text-sm text-neutral-600">
                  "<b>{featuredSwapPrompt?.name}</b>" is currently the Featured Wave on the patron dashboard homepage. Set "<b>{form.name || "this wave"}</b>" as the new Featured Wave instead? This will un-feature "{featuredSwapPrompt?.name}".
                </p>
                <div className="flex justify-end gap-2 mt-4">
                  <Button variant="outline" data-testid="featured-swap-cancel" onClick={() => setFeaturedSwapPrompt(null)}>Cancel</Button>
                  <Button
                    data-testid="featured-swap-confirm"
                    className="bg-mango hover:bg-mango/90"
                    onClick={() => { setForm({ ...form, is_featured: true }); setFeaturedSwapPrompt(null); }}
                  >
                    Confirm swap
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            <div className="mt-5 pt-4 border-t border-neutral-200">
              <div className="overline mb-1 flex items-center gap-1">TIME-TO-FILL &amp; FAST-FILL TRAP <HelpHint text="Expected minutes is shown to patrons. Fast-fill penalty is deducted only on the patron's SECOND fast-fill offense in the same wave; the wave is then closed for that patron. 0 = no points penalty (patron just earns nothing on this wave)." /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="overline text-xs">Expected fill time (minutes) <span className="text-red-500">*</span></Label>
                  <Input data-testid="wb-expected-minutes" type="number" min="1" required value={form.expected_minutes} onChange={(e) => setForm({ ...form, expected_minutes: e.target.value })} placeholder="e.g. 3" className="rounded-none" />
                </div>
                <div>
                  <Label className="overline text-xs">Fast-fill penalty (pts) <span className="text-red-500">*</span></Label>
                  <Input data-testid="wb-fast-fill-penalty" type="number" min="0" required value={form.fast_fill_penalty_points} onChange={(e) => setForm({ ...form, fast_fill_penalty_points: e.target.value })} placeholder="e.g. 100 (0 = no points penalty)" className="rounded-none" />
                </div>
              </div>
              <p className="text-xs text-neutral-500 mt-2">If 0, fast-fillers just won&apos;t earn points on this wave (no balance deduction).</p>
            </div>
          </div>

          {/* URDU MASTER SWITCH — per-wave. Always visible to admins so they
              can prepare translations even while the master feature toggle
              is OFF; patrons still won't see Urdu until the master flips ON. */}
          {editingId && (
            <UrduWaveToggle waveId={editingId} initial={form.show_urdu_version} onChange={(v) => setForm({ ...form, show_urdu_version: v })} />
          )}

          {/* PRE-SURVEY INTRO — shown to patron BEFORE question 1 */}
          <div className="bg-white border-2 border-black p-5" data-testid="wb-intro">
            <Label className="overline mb-1 flex items-center gap-1">Pre-survey intro screen <HelpHint text="Patrons see this screen right after they tap 'Start' on the survey, before question 1. Use {{points}} to inject this wave's points and {{topic}} for the topic label. Leave blank to use the global default from Site Content → Survey Intro Defaults." /></Label>
            <p className="text-xs text-neutral-500 mb-3">Both fields are optional — leave blank to use the global default.</p>
            <div className="space-y-3">
              <div>
                <Label className="overline text-xs">Topic label</Label>
                <Input data-testid="wb-intro-topic" value={form.intro_topic} onChange={(e) => setForm({ ...form, intro_topic: e.target.value })} className="rounded-none" placeholder="e.g. consumer banking habits" />
                <InlineUrduEditor
                  entity="wave"
                  entityId={editingId}
                  field="intro_topic"
                  enValue={form.intro_topic}
                  initialUr={form.intro_topic_ur || ""}
                  initialStatus={form.intro_topic_ur_status}
                />
              </div>
              <div>
                <Label className="overline text-xs">Intro text (supports {`{{points}}`} and {`{{topic}}`})</Label>
                <Textarea data-testid="wb-intro-text" rows={6} value={form.intro_text} onChange={(e) => setForm({ ...form, intro_text: e.target.value })} className="rounded-none font-mono text-xs" placeholder="Leave blank to use the global default." />
                <InlineUrduEditor
                  entity="wave"
                  entityId={editingId}
                  field="intro_text"
                  enValue={form.intro_text}
                  initialUr={form.intro_text_ur || ""}
                  initialStatus={form.intro_text_ur_status}
                />
              </div>
            </div>
          </div>

          {/* AUDIENCE TARGETING — wave-level (Phase A) */}
          <div className="bg-white border-2 border-black p-5" data-testid="wb-audience">
            <Label className="overline mb-1 flex items-center gap-1">Audience targeting <HelpHint text="Who gets ASKED (the respondents). 'All Panelists' = anyone matching the quota grid below. 'Niche Demographic' = additionally filter to a subset (e.g. Female × Punjab × Age 25-34). AND between categories, OR within a category. This is separate from result visibility (set per question)." /></Label>
            <p className="text-xs text-neutral-500 mb-3">Wave-level audience applies to <b>every question</b> in the wave. Individual questions can still add a screener filter on top.</p>
            <div className="flex gap-2 mb-3">
              <button type="button" data-testid="wb-aud-all" onClick={() => setAudience("all")} className={`px-4 py-2 border-2 text-sm ${audience === "all" ? "bg-black text-white border-black" : "border-neutral-300"}`}>Global (All Panelists)</button>
              <button type="button" data-testid="wb-aud-niche" onClick={() => setAudience("niche")} className={`px-4 py-2 border-2 text-sm ${audience === "niche" ? "bg-mango text-white border-black" : "border-neutral-300"}`}>Niche Demographic</button>
            </div>
            {audience === "niche" && (
              <div className="space-y-3 max-h-72 overflow-auto pr-2" data-testid="wb-niche-builder">
                {Object.entries(FILTER_FIELDS).map(([cat, vals]) => (
                  <div key={cat}>
                    <div className="overline mb-1">{cat.replace("_", " ")}</div>
                    <div className="flex flex-wrap gap-1">
                      {vals.map((v) => {
                        const sel = (audienceFilter[cat] || []).includes(v);
                        return (
                          <button type="button" key={v} onClick={() => toggleAudCat(cat, v)} className={`text-xs px-2 py-1 border ${sel ? "bg-black text-white border-black" : "border-neutral-300 hover:border-black"}`}>{v}</button>
                        );
                      })}
                    </div>
                  </div>
                ))}
                <div className="text-xs text-neutral-500 pt-2 border-t">AND between categories · OR within a category · empty category = no constraint</div>
              </div>
            )}
          </div>

          {/* SECTOR CLASSIFICATION — drives the "match patron's sectors_of_interest"
              filter on the panelist dashboard. Optional: leave blank for global waves. */}
          <div className="bg-white border-2 border-black p-5" data-testid="wb-sector">
            <Label className="overline mb-1 block">Sector — which interest area does this wave belong to?</Label>
            <p className="text-xs text-neutral-500 mb-3">
              The <b>PRIMARY sector</b> is where this wave is listed in editorial (Trackers index, /sectors page, articles).
              <br />
              <b>SECONDARY sectors</b> (up to 3) widen the audience — patrons who picked <i>any</i> of these as an interest will see the wave. But the wave still lists in <b>one</b> editorial place. Use it when a wave straddles fields (e.g. an "economic confidence under new policy" wave is primary=politics, secondary=economy).
              <br />
              Leave both blank for cross-cutting national waves (politics-of-the-day, demographics, census) that should reach <i>every</i> panelist.
            </p>
            <div className="mb-3">
              <Label className="overline mb-2 block text-[10px]">PRIMARY SECTOR</Label>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, sector_key: "", secondary_sectors: [] })}
                  data-testid="wb-sector-general"
                  className={`px-3 py-1.5 border-2 text-xs ${!form.sector_key ? "bg-black text-white border-black" : "bg-white border-neutral-300 hover:border-black"}`}
                >
                  General / All patrons
                </button>
                {sectors.map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => setForm({ ...form, sector_key: s.key, secondary_sectors: (form.secondary_sectors || []).filter((x) => x !== s.key) })}
                    data-testid={`wb-sector-${s.key}`}
                    className={`px-3 py-1.5 border-2 text-xs ${form.sector_key === s.key ? "bg-mango text-black border-black" : "bg-white border-neutral-300 hover:border-black"}`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
            {/* Secondary sector multi-select — hidden when no primary is set,
                because a "general" wave is already shown to everyone. */}
            {form.sector_key && (
              <div>
                <Label className="overline mb-2 block text-[10px]">
                  SECONDARY SECTORS — widen audience (up to 3, optional)
                  <span className="ml-2 font-mono text-neutral-500">
                    {(form.secondary_sectors || []).length}/3 picked
                  </span>
                </Label>
                <div className="flex flex-wrap gap-2">
                  {sectors.filter((s) => s.key !== form.sector_key).map((s) => {
                    const picked = (form.secondary_sectors || []).includes(s.key);
                    const cap = (form.secondary_sectors || []).length >= 3 && !picked;
                    return (
                      <button
                        key={s.key}
                        type="button"
                        disabled={cap}
                        onClick={() => {
                          const cur = new Set(form.secondary_sectors || []);
                          if (cur.has(s.key)) cur.delete(s.key);
                          else if (cur.size < 3) cur.add(s.key);
                          setForm({ ...form, secondary_sectors: [...cur] });
                        }}
                        data-testid={`wb-secondary-${s.key}`}
                        className={`px-3 py-1.5 border-2 text-xs ${picked ? "bg-[#2E8B57] text-white border-[#2E8B57]" : cap ? "bg-neutral-100 border-neutral-200 text-neutral-400 cursor-not-allowed" : "bg-white border-neutral-300 hover:border-black"}`}
                        title={cap ? "Max 3 secondary sectors" : (picked ? "Click to remove" : "Click to add")}
                      >
                        {picked && "+ "}{s.label}
                      </button>
                    );
                  })}
                </div>
                {(form.secondary_sectors || []).length > 0 && (
                  <p className="mt-2 text-xs text-neutral-600">
                    <b>Visibility preview:</b> Patrons who picked <b>{[form.sector_key, ...form.secondary_sectors].map((k) => sectors.find((s) => s.key === k)?.label || k).join(" OR ")}</b> in their profile will see this wave.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* GEO SCOPE — always visible. The overseas_program_enabled master
              toggle no longer hides this UI (admins were missing it). Instead
              we show a small inline notice when the master is OFF so the
              admin knows overseas-only waves won't actually reach anyone. */}
          <div className="bg-white border-2 border-black p-5" data-testid="wb-geo-scope">
            <Label className="overline mb-1 flex items-center gap-1">Geo scope — who can take this wave?</Label>
            <p className="text-xs text-neutral-500 mb-3">Decide which cohort sees this wave on their dashboard. <b>Pakistan only</b> hides it from overseas panelists; <b>Overseas only</b> targets the diaspora exclusively.</p>
            {!overseasMaster && (
              <div className="bg-amber-50 border-l-4 border-amber-500 px-3 py-2 mb-3 text-xs text-amber-900">
                <b>Note:</b> The overseas program master switch is currently <b>OFF</b> (Site Content → Feature toggles → <code className="font-mono">overseas_program_enabled</code>). You can still pick any geo scope below, but waves set to <b>Overseas only</b> will be hidden from every viewer until the master switch is flipped ON.
              </div>
            )}
            <div className="flex flex-wrap gap-2 mb-4">
              {[
                ["pk_only", "🇵🇰 Pakistan only", "Excludes overseas panelists"],
                ["both", "🌐 Pakistan + Overseas", "Both cohorts can take this wave"],
                ["overseas_only", "✈️ Overseas only", "Only diaspora panelists; PK panelists won't see it"],
              ].map(([val, lab, sub]) => (
                <button
                  key={val}
                  type="button"
                  onClick={() => setForm({ ...form, geo_scope: val })}
                  data-testid={`wb-geo-${val}`}
                  className={`text-left px-4 py-2 border-2 text-sm flex-1 min-w-[180px] ${form.geo_scope === val ? "bg-mango text-black border-black" : "bg-white border-neutral-300 hover:border-black"}`}
                >
                  <div className="font-bold">{lab}</div>
                  <div className="text-[11px] text-neutral-600 mt-0.5">{sub}</div>
                </button>
              ))}
            </div>

            {overseasMaster && (
              <>
                <Label className="overline mb-1 mt-3 block">Show overseas respondents in results</Label>
                <p className="text-xs text-neutral-500 mb-2">Granular control: hide overseas data from some views while still keeping it in others. Default = visible in every view.</p>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  {[
                    ["show_overseas_in_public", "Public site"],
                    ["show_overseas_in_panelist", "Panelist app"],
                    ["show_overseas_in_subscriber", "Subscriber hub"],
                    ["show_overseas_in_enterprise", "Enterprise"],
                    ["show_overseas_in_private", "Private vault"],
                  ].map(([k, lab]) => (
                    <label key={k} className="flex items-center gap-2 text-xs p-2 border border-neutral-200 cursor-pointer hover:border-black" data-testid={`wb-${k}`}>
                      <input
                        type="checkbox"
                        checked={!!form[k]}
                        onChange={(e) => setForm({ ...form, [k]: e.target.checked })}
                        className="w-4 h-4"
                      />
                      <span>{lab}</span>
                    </label>
                  ))}
                </div>
              </>
            )}
          </div>
          {/* PHASE 8.41 — Show respondent count (display-only). Default OFF so
              waves with low N never inadvertently reveal their sample size to
              viewers; admin opts in per-wave when N is large enough to share. */}
          <div className="bg-white border-2 border-black p-5" data-testid="wb-respondent-count-section">
            <Label className="overline mb-1 block">Respondent count visibility</Label>
            <p className="text-xs text-neutral-500 mb-3">When OFF (default), the published results page hides &ldquo;N = X respondents&rdquo; from every viewer audience (public, panelist, subscriber, enterprise, private). Turn ON only if you&apos;re confident the sample is large enough to publish. Changeable at any time.</p>
            <label className="flex items-center gap-3 text-sm p-3 border border-neutral-200 cursor-pointer hover:border-black" data-testid="wb-show-respondent-count">
              <input
                type="checkbox"
                checked={!!form.show_respondent_count}
                onChange={(e) => setForm({ ...form, show_respondent_count: e.target.checked })}
                className="w-4 h-4"
              />
              <span><b>Show respondent count to viewers</b> &mdash; when ON, results display &ldquo;N = X respondents&rdquo;.</span>
            </label>
          </div>
          <ResultColumnsPicker
            value={form.result_columns || []}
            onChange={(arr) => setForm({ ...form, result_columns: arr })}
          />

          <div className="bg-white border-2 border-black p-5" data-testid="wb-auto-include">
            <Label className="overline mb-1 flex items-center gap-1">Quick add · pick trackers <HelpHint text={HELP.WAVE.auto_include_trackers} /></Label>
            <p className="text-xs text-neutral-500 mb-3">Selecting a tracker imports its canonical question (set in the Trackers tab). Saves you re-picking the same questions every wave.</p>
            {(() => {
              const todayStr = new Date().toISOString().slice(0, 10);
              const overdueTrackers = trackers.filter((t) => t.recurrence_cadence && t.recurrence_cadence !== "ad_hoc" && t.recurrence_cadence !== "retired" && t.recurrence_next_due && t.recurrence_next_due < todayStr);
              const dueSoon = trackers.filter((t) => t.recurrence_cadence && t.recurrence_cadence !== "ad_hoc" && t.recurrence_cadence !== "retired" && t.recurrence_next_due && t.recurrence_next_due >= todayStr && t.recurrence_next_due <= new Date(Date.now() + 7 * 86400000).toISOString().slice(0,10));
              const dueList = [...overdueTrackers, ...dueSoon];
              if (dueList.length === 0) return null;
              return (
                <div className="mb-4 p-3 border-2 border-amber-400 bg-amber-50" data-testid="wb-due-trackers">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div>
                      <div className="font-bold text-amber-900 text-sm">⏰ {dueList.length} tracker{dueList.length === 1 ? "" : "s"} due to be included in this wave</div>
                      <div className="text-[11px] text-amber-700 mt-0.5">Based on each tracker&apos;s recurrence cadence. Click &quot;Select all&quot; to add their canonical questions in one click.</div>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => {
                        let added = 0;
                        const newSelected = [...selected];
                        for (const t of dueList) {
                          if (!t.active_question_id) continue;
                          if (newSelected.some((s) => s.id === t.active_question_id)) continue;
                          const q = questions.find((x) => x.id === t.active_question_id);
                          if (q) { newSelected.push(q); added++; }
                        }
                        setSelected(newSelected);
                        toast.success(`Added ${added} due-tracker question${added === 1 ? "" : "s"} to this wave`);
                      }}
                      className="bg-amber-600 hover:bg-amber-700 text-white rounded-none h-8 text-xs px-3"
                      data-testid="wb-add-all-due"
                    >
                      Select all due
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5 text-xs">
                    {dueList.map((t) => {
                      const overdue = t.recurrence_next_due < todayStr;
                      const inWave = selected.some((q) => q.tracker_id === t.id);
                      return (
                        <div key={t.id} className="flex items-center justify-between gap-2 p-1.5 border border-amber-300 bg-white">
                          <div className="min-w-0 flex-1">
                            <div className="font-bold truncate">{inWave && <span className="text-emerald-700 mr-1">✓</span>}{t.name}</div>
                            <div className="text-[10px] text-neutral-500 truncate">{t.recurrence_cadence} · due {t.recurrence_next_due}{overdue ? " · OVERDUE" : ""}</div>
                          </div>
                          {!inWave && t.active_question_id && (
                            <button type="button" onClick={() => toggleTracker(t.id)} className="text-[10px] uppercase tracking-wider font-bold underline">Add</button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-56 overflow-auto">
              {trackers.map((t) => {
                const alreadyIn = selected.some((q) => q.tracker_id === t.id);
                const canonical = t.active_question_id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    disabled={!canonical}
                    onClick={() => toggleTracker(t.id)}
                    data-testid={`wb-tracker-${t.id}`}
                    className={`text-left p-2 border text-xs transition disabled:cursor-not-allowed disabled:opacity-50 ${alreadyIn ? "border-mango bg-mango/10" : "border-neutral-200 hover:border-black"}`}
                  >
                    <div className="font-bold flex items-center gap-1">
                      {alreadyIn && <span className="text-mango">✓</span>}
                      {t.name}
                    </div>
                    <div className="text-neutral-500 mt-0.5">
                      {alreadyIn ? "click to remove" : (canonical ? "click to add canonical question" : "⚠ no canonical question set")}
                    </div>
                  </button>
                );
              })}
              {trackers.length === 0 && <div className="text-xs text-neutral-500 col-span-full">No trackers yet — create one first.</div>}
            </div>
          </div>

          <div className="bg-white border-2 border-black p-5">
            <Label className="overline mb-3 flex items-center gap-1">Question bank — pick individually <HelpHint text={HELP.WAVE.question_picker} /></Label>
            <div className="space-y-1 max-h-72 overflow-auto">
              {questions.filter((q) => !selected.find((s) => s.id === q.id)).map((q) => (
                <button key={q.id} data-testid={`wb-add-${q.id}`} type="button" onClick={() => addQuestion(q)} className="w-full text-left p-2 text-sm hover:bg-neutral-50 border border-neutral-100 flex items-center gap-2">
                  <span className="flex-1 truncate">+ {q.text}</span>
                  <VisibilityBadge visibility={q.visibility || "public"} clientId={q.client_id} size="xs" className="shrink-0" />
                </button>
              ))}
            </div>
          </div>

          <div className="bg-white border-2 border-black p-5">
            <Label className="overline mb-3 block">Selected questions (drag to reorder)</Label>
            {dupTrackerNames.length > 0 && (
              <div className="border-l-4 border-amber-500 bg-amber-50 p-3 mb-3 text-xs" data-testid="dup-tracker-warning">
                ⚠️ Multiple questions for: <b>{dupTrackerNames.join(", ")}</b>. Time-series will be ambiguous — consider removing duplicates.
              </div>
            )}
            <DndContext collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <SortableContext items={selected.map((q) => q.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-1">
                  {selected.map((q) => (
                    <SortableQ key={q.id} q={q} onRemove={(id) => setSelected(selected.filter((x) => x.id !== id))} />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
            {selected.length === 0 && <div className="text-neutral-400 text-sm">No questions selected.</div>}
          </div>
        </div>

        <div className="space-y-4">
          {Object.entries(QUOTA_CATS).map(([cat, vals]) => (
            <div key={cat} className="bg-white border-2 border-black p-5">
              <div className="flex justify-between items-center mb-3">
                <Label className="overline flex items-center gap-1">{cat} quota <HelpHint text={HELP.WAVE[`quota_${cat}`] || HELP.WAVE.quotas} /></Label>
                <div className={`font-mono text-sm font-bold px-2 py-1 ${Math.abs(sums[cat] - 100) < 0.01 ? 'bg-black text-white' : 'bg-red-500 text-white'}`} data-testid={`quota-sum-${cat}`}>
                  Σ {sums[cat]}%
                </div>
              </div>
              <div className="space-y-2">
                {vals.map((v) => (
                  <div key={v} className="flex items-center gap-3">
                    <span className="text-sm w-32">{v}</span>
                    <Input data-testid={`quota-${cat}-${v}`} type="number" value={quotas[cat][v] ?? 0} onChange={(e) => setQuotas({ ...quotas, [cat]: { ...quotas[cat], [v]: Number(e.target.value) } })} className="rounded-none w-24 font-mono" />
                    <span className="text-neutral-400">%</span>
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div className="bg-white border-2 border-black p-5" data-testid="wb-publish-summary">
            <div className="overline mb-2 flex items-center gap-1">Publishing matrix <HelpHint text="Where each question's result will land once the wave is closed. Result visibility is set PER QUESTION (independent of audience targeting). A single wave can publish to many destinations at once." /></div>
            {selected.length === 0 ? (
              <div className="text-xs text-neutral-500">Add questions to preview where results will land.</div>
            ) : (
              <ul className="space-y-1 text-xs">
                {["public", "panelists", "subscribers", "enterprise", "private"].map((tier) => {
                  const matched = selected.filter((q) => (q.visibility || "public") === tier);
                  if (matched.length === 0) return null;
                  return (
                    <li key={tier} className="flex items-center justify-between gap-2 py-1 border-b border-neutral-100" data-testid={`wb-publish-row-${tier}`}>
                      <VisibilityBadge visibility={tier} size="xs" />
                      <span className="font-mono text-neutral-700 shrink-0">{matched.length} q{matched.length === 1 ? "" : "s"}</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <Button data-testid="wb-publish" disabled={!allValid} onClick={submit} className="w-full bg-mango hover:bg-mango/90 text-white border-2 border-black rounded-none disabled:opacity-50 hard-shadow">
            {allValid ? "Create wave (as draft)" : "Fix quota errors to continue"}
          </Button>
        </div>

        {/* ORBIT — research co-pilot rail (3rd column on 2xl+, slide-in Sheet below) */}
        {orbitEnabled && (
          <div className="hidden 2xl:block">
            <div className="sticky top-4">
              <OrbitRail
                selectedQuestions={selected}
                defaults={{ audience: audience === "niche" ? Object.entries(audienceFilter).filter(([,v]) => v.length).map(([k,v]) => `${k}: ${v.join(", ")}`).join(" · ") : "" }}
                store={orbitStore}
                patch={orbitPatch}
                onApplyOrder={(orderedIds) => {
                  const map = new Map(selected.map((q) => [q.id, q]));
                  const reordered = orderedIds.map((id) => map.get(id)).filter(Boolean);
                  if (reordered.length === selected.length) {
                    setSelected(reordered);
                    toast.success("ORBIT applied the suggested order");
                  }
                }}
                onAddOneToWave={(question) => {
                  setSelected((prev) => prev.some((q) => q.id === question.id) ? prev : [...prev, question]);
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Floating launcher for screens smaller than 2xl */}
      {orbitEnabled && <OrbitRailMobile selectedQuestions={selected} setSelected={setSelected} audience={audience} audienceFilter={audienceFilter} waveKey={orbitWaveKey} />}
      </>
      )}
    </div>
  );
}

// =================== REWARDS ===================
const REWARD_BLANK = {
  name: "", points_cost: 1000, image: "", description: "", voucher_codes_text: "",
  delivery_type: "unique_code", global_code: "", max_redemptions: "", qr_image: "",
  charity_name: "", charity_url: "", min_points_to_redeem: 0, residents_only: false,
  overseas_only: false, partner_id: "", expires_at: "", active: true,
};

export function AdminRewards() {
  const [tab, setTab] = useState("rewards"); // "rewards" | "redemptions"
  const [items, setItems] = useState(() => api.getCached("/admin/rewards") || []);
  const [partners, setPartners] = useState([]);
  const [form, setForm] = useState(REWARD_BLANK);
  const [codeQty, setCodeQty] = useState(""); // sanity-check only, not sent to backend
  const [editingId, setEditingId] = useState(null);
  const [redemptions, setRedemptions] = useState([]);
  const [redemptionFilter, setRedemptionFilter] = useState(""); // reward_id, "" = all
  const [redemptionPartnerFilter, setRedemptionPartnerFilter] = useState(""); // partner_id, "" = all
  const [redemptionsLoading, setRedemptionsLoading] = useState(true);
  const load = () => api.get("/admin/rewards").then((r) => setItems(r.data));
  const loadRedemptions = (rewardId, partnerId) => api.get("/admin/redemptions", { params: { ...(rewardId ? { reward_id: rewardId } : {}), ...(partnerId ? { partner_id: partnerId } : {}) } }).then((r) => setRedemptions(r.data)).finally(() => setRedemptionsLoading(false));
  useEffect(() => { load(); api.get("/admin/partners").then((r) => setPartners(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (tab === "redemptions") loadRedemptions(redemptionFilter || undefined, redemptionPartnerFilter || undefined); }, [tab, redemptionFilter, redemptionPartnerFilter]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.delivery_type === "unique_code" && codeQty !== "" && form.voucher_codes_text) {
      const pastedCount = form.voucher_codes_text.split("\n").map((l) => l.trim()).filter(Boolean).length;
      if (pastedCount !== Number(codeQty)) {
        toast.error(`You said ${codeQty} codes but pasted ${pastedCount} — fix before saving.`);
        return;
      }
    }
    const payload = { ...form, max_redemptions: form.max_redemptions === "" ? null : Number(form.max_redemptions), partner_id: form.partner_id || null, expires_at: form.expires_at || null };
    if (editingId) {
      await api.put(`/rewards/${editingId}`, payload);
      toast.success("Reward updated");
    } else {
      await api.post("/rewards", payload);
      toast.success("Reward created");
    }
    setForm(REWARD_BLANK); setCodeQty(""); setEditingId(null);
    load();
  };
  const startEdit = (r) => {
    setForm({
      name: r.name, points_cost: r.points_cost, image: r.image || "", description: r.description || "",
      voucher_codes_text: "", delivery_type: r.delivery_type || "unique_code", global_code: r.global_code || "",
      max_redemptions: r.max_redemptions ?? "", qr_image: r.qr_image || "",
      charity_name: r.charity_name || "", charity_url: r.charity_url || "",
      min_points_to_redeem: r.min_points_to_redeem || 0, residents_only: !!r.residents_only, overseas_only: !!r.overseas_only,
      partner_id: r.partner_id || "", expires_at: r.expires_at ? r.expires_at.slice(0, 10) : "", active: r.active !== false,
    });
    setCodeQty(""); setEditingId(r.id); window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const stockLabel = (r) => {
    const s = r.stock_info || {};
    if (s.unlimited) return "Unlimited";
    if (s.total === null || s.total === undefined) return "—";
    return `${s.remaining} of ${s.total} left`;
  };

  const exportCsv = () => {
    const rows = [["Redemption ref", "Date", "Patron name", "Patron email", "Reward", "Partner", "Delivery type", "Code", "Points spent", "Status"]];
    redemptions.forEach((r) => rows.push([r.redemption_ref || "", r.redeemed_at || "", r.user_name || "", r.user_email || "", r.reward_name || "", r.partner_name || "", r.delivery_type || "", r.code || "", r.points_spent, r.fulfillment_status || ""]));
    const csv = rows.map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `redemptions-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const fulfill = async (rid) => {
    await api.put(`/admin/redemptions/${rid}/fulfill`);
    toast.success("Marked fulfilled");
    loadRedemptions(redemptionFilter || undefined, redemptionPartnerFilter || undefined);
  };

  return (
    <div>
      <div className="border-b-2 border-black pb-4 mb-6 flex items-end justify-between">
        <div><div className="overline">REWARDS STORE</div><h1 className="text-4xl font-black tracking-tighter">Rewards & Vouchers</h1></div>
        <div className="flex gap-2">
          <button data-testid="rw-tab-rewards" onClick={() => setTab("rewards")} className={`px-4 py-2 text-xs font-bold uppercase tracking-wide border-2 border-black ${tab === "rewards" ? "bg-black text-white" : "bg-white hover:bg-neutral-100"}`}>Rewards</button>
          <button data-testid="rw-tab-redemptions" onClick={() => setTab("redemptions")} className={`px-4 py-2 text-xs font-bold uppercase tracking-wide border-2 border-black ${tab === "redemptions" ? "bg-black text-white" : "bg-white hover:bg-neutral-100"}`}>Redemptions</button>
        </div>
      </div>

      {tab === "rewards" && (
      <>
      <form onSubmit={submit} className="bg-white border-2 border-black p-5 space-y-3 mb-6">
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="overline">Name *</Label><Input data-testid="rw-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none" /></div>
          <div><Label className="overline">Points cost * <span className="text-neutral-400 font-normal">(deducted from patron's balance on redemption)</span></Label><Input data-testid="rw-cost" type="number" required value={form.points_cost} onChange={(e) => setForm({ ...form, points_cost: Number(e.target.value) })} className="rounded-none" /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="overline flex items-center gap-1">Image URL <HelpHint text="Exact ratio: 2:1 (e.g. 1200×600px, or 1600×800px for retina). The card now crops to this fixed ratio at every screen size, so any image at 2:1 will display edge-to-edge with zero cropping on the sides." /></Label><Input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className="rounded-none" /></div>
          <div>
            <Label className="overline">Partner (optional — for logo/name display) <Link to="/admin/partners" className="text-neutral-400 font-normal underline" data-testid="rw-manage-partners-link">Manage partners →</Link></Label>
            <select data-testid="rw-partner" value={form.partner_id} onChange={(e) => setForm({ ...form, partner_id: e.target.value })} className="w-full border-2 border-neutral-300 rounded-none h-10 px-2 text-sm">
              <option value="">— No partner —</option>
              {partners.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
        </div>
        <div><Label className="overline">Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-none" /></div>
        <div>
          <Label className="overline mb-2 block">Delivery mechanism</Label>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {[["unique_code", "Unique Code Pool"], ["global_code", "Global Code"], ["qr_image", "QR Image"], ["manual_cashout", "Manual Cash-out"], ["charity", "Charity Donation"]].map(([k, l]) => (
              <button key={k} type="button" onClick={() => setForm({ ...form, delivery_type: k })} data-testid={`del-${k}`} className={`px-3 py-2 border-2 text-xs ${form.delivery_type === k ? 'bg-black text-white border-black' : 'border-neutral-300 hover:border-black'}`}>{l}</button>
            ))}
          </div>
        </div>
        {form.delivery_type === "unique_code" && (
          <div className="space-y-2 bg-neutral-50 border border-neutral-200 p-3">
            <div className="grid grid-cols-3 gap-3 items-end">
              <div className="col-span-1">
                <Label className="overline">Quantity <span className="text-neutral-400 font-normal">(sanity check)</span></Label>
                <Input data-testid="rw-code-qty" type="number" min="0" value={codeQty} onChange={(e) => setCodeQty(e.target.value)} className="rounded-none" placeholder="e.g. 10" />
              </div>
              <div className="col-span-2 text-xs text-neutral-500 pb-2">Enter how many codes you're about to paste — we'll warn you if the count doesn't match what you paste below. Stock is always the actual number of codes in the pool.</div>
            </div>
            <div>
              <Label className="overline">Voucher codes (one per line) — appended to existing pool when editing</Label>
              <Textarea data-testid="rw-codes" value={form.voucher_codes_text} onChange={(e) => setForm({ ...form, voucher_codes_text: e.target.value })} className="rounded-none font-mono text-xs h-32" placeholder={"VOUCHER-1234\nVOUCHER-5678"} />
            </div>
          </div>
        )}
        {form.delivery_type === "global_code" && (
          <div className="grid grid-cols-3 gap-3 bg-neutral-50 border border-neutral-200 p-3">
            <div className="col-span-2"><Label className="overline">Global code (everyone who redeems gets this exact code)</Label><Input data-testid="rw-global-code" value={form.global_code} onChange={(e) => setForm({ ...form, global_code: e.target.value })} className="rounded-none font-mono" placeholder="oodel20" /></div>
            <div><Label className="overline">Number of vouchers <span className="text-neutral-400 font-normal">(blank = unlimited)</span></Label><Input data-testid="rw-max-redemptions" type="number" min="0" value={form.max_redemptions} onChange={(e) => setForm({ ...form, max_redemptions: e.target.value })} className="rounded-none" placeholder="e.g. 10" /></div>
          </div>
        )}
        {form.delivery_type === "qr_image" && (
          <div className="grid grid-cols-3 gap-3 bg-neutral-50 border border-neutral-200 p-3">
            <div className="col-span-2"><Label className="overline flex items-center gap-1">QR image URL <HelpHint text="Note: this currently displays as a plain text/link (not an embedded inline image) inside the patron's redemption receipt and history — inline QR rendering is planned but not built yet. Use a direct, publicly-reachable image URL so patrons can open/save it." /></Label><Input value={form.qr_image} onChange={(e) => setForm({ ...form, qr_image: e.target.value })} className="rounded-none" placeholder="https://..." /></div>
            <div><Label className="overline">Number of vouchers <span className="text-neutral-400 font-normal">(blank = unlimited)</span></Label><Input type="number" min="0" value={form.max_redemptions} onChange={(e) => setForm({ ...form, max_redemptions: e.target.value })} className="rounded-none" placeholder="e.g. 10" /></div>
          </div>
        )}
        {form.delivery_type === "manual_cashout" && (
          <div className="bg-yellow-50 border border-yellow-300 p-3 text-xs text-yellow-800">Each redemption queues a manual EasyPaisa/JazzCash transfer. Process them under the <strong>Redemptions</strong> tab above.</div>
        )}
        {form.delivery_type === "charity" && (
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="overline">Charity name</Label><Input data-testid="rw-charity-name" value={form.charity_name} onChange={(e) => setForm({ ...form, charity_name: e.target.value })} className="rounded-none" placeholder="Edhi Foundation" /></div>
            <div><Label className="overline">Charity website</Label><Input value={form.charity_url} onChange={(e) => setForm({ ...form, charity_url: e.target.value })} className="rounded-none" placeholder="https://edhi.org" /></div>
          </div>
        )}
        <div className="grid grid-cols-4 gap-3 pt-3 border-t">
          <div>
            <Label className="overline">Min points to redeem</Label>
            <Input type="number" min="0" data-testid="rw-min-points" value={form.min_points_to_redeem} onChange={(e) => setForm({ ...form, min_points_to_redeem: Number(e.target.value) })} className="rounded-none" />
          </div>
          <div>
            <Label className="overline">Expires on <span className="text-neutral-400 font-normal">(optional)</span></Label>
            <Input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className="rounded-none" />
          </div>
          <label className="flex items-center gap-2 mt-7"><Checkbox checked={form.residents_only} onCheckedChange={(v) => setForm({ ...form, residents_only: !!v, overseas_only: false })} /> <span className="text-sm">PK residents only</span></label>
          <label className="flex items-center gap-2 mt-7"><Checkbox checked={form.overseas_only} onCheckedChange={(v) => setForm({ ...form, overseas_only: !!v, residents_only: false })} /> <span className="text-sm">Overseas only</span></label>
        </div>
        <label className="flex items-center gap-2"><Switch data-testid="rw-active" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: !!v })} /> <span className="text-sm">Active (visible to patrons)</span></label>
        <div className="flex gap-2">
          <Button data-testid="rw-submit" type="submit" className="bg-black hover:bg-neutral-800 rounded-none">{editingId ? "Save changes" : "Create reward"}</Button>
          {editingId && <Button type="button" variant="ghost" onClick={() => { setForm(REWARD_BLANK); setCodeQty(""); setEditingId(null); }}>Cancel edit</Button>}
        </div>
      </form>
      <div className="space-y-2">
        {items.map((r) => (
          <div key={r.id} className="border border-neutral-200 p-4 bg-white flex justify-between items-center" data-testid={`reward-row-${r.id}`}>
            <div>
              <div className="font-bold flex items-center gap-2">{r.name} {r.active === false && <span className="text-[10px] uppercase bg-neutral-200 text-neutral-600 px-1.5 py-0.5">Inactive</span>} {r.expired && <span className="text-[10px] uppercase bg-red-100 text-red-700 px-1.5 py-0.5">Expired</span>}</div>
              <div className="overline">{r.points_cost} pts · {r.delivery_type || "unique_code"} · {stockLabel(r)} {r.partner_name && `· ${r.partner_name}`}</div>
            </div>
            <div className="flex gap-1 items-center">
              <Button data-testid={`view-redemptions-${r.id}`} variant="ghost" size="sm" className="text-xs" onClick={() => { setRedemptionFilter(r.id); setRedemptionPartnerFilter(""); setTab("redemptions"); }}>View redemptions</Button>
              <Button data-testid={`edit-rw-${r.id}`} variant="ghost" size="sm" onClick={() => startEdit(r)}><PencilSimple size={16} /></Button>
              <Button variant="ghost" size="sm" onClick={async () => { if (window.confirm("Delete?")) { await api.delete(`/rewards/${r.id}`); load(); } }}><Trash size={18} /></Button>
            </div>
          </div>
        ))}
      </div>
      </>
      )}

      {tab === "redemptions" && (
      <div data-testid="rw-redemptions-panel">
        <div className="flex items-center justify-between mb-3 gap-3">
          <div className="flex items-center gap-2">
            <select data-testid="rw-redemption-filter" value={redemptionFilter} onChange={(e) => setRedemptionFilter(e.target.value)} className="border-2 border-neutral-300 rounded-none h-10 px-2 text-sm">
              <option value="">All rewards</option>
              {items.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
            <select data-testid="rw-redemption-partner-filter" value={redemptionPartnerFilter} onChange={(e) => setRedemptionPartnerFilter(e.target.value)} className="border-2 border-neutral-300 rounded-none h-10 px-2 text-sm">
              <option value="">All partners</option>
              {partners.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            {(redemptionFilter || redemptionPartnerFilter) && (
              <button type="button" onClick={() => { setRedemptionFilter(""); setRedemptionPartnerFilter(""); }} className="text-xs text-neutral-400 underline" data-testid="rw-redemption-filter-clear">Clear filters</button>
            )}
          </div>
          <Button data-testid="rw-export-csv" variant="outline" onClick={exportCsv} className="rounded-none">Export CSV</Button>
        </div>
        <div className="border border-neutral-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b border-neutral-200 text-left text-xs uppercase tracking-wide text-neutral-500">
              <tr><th className="p-3">Date</th><th className="p-3">Patron</th><th className="p-3">Reward</th><th className="p-3">Partner</th><th className="p-3">Type</th><th className="p-3">Code</th><th className="p-3">Points</th><th className="p-3">Status</th><th className="p-3">Ref</th><th className="p-3"></th></tr>
            </thead>
            <tbody>
              {redemptions.map((r) => (
                <tr key={r.id} className="border-b border-neutral-100" data-testid={`redemption-row-${r.id}`}>
                  <td className="p-3 whitespace-nowrap">{r.redeemed_at ? formatDateTime(r.redeemed_at) : "—"}</td>
                  <td className="p-3">{r.user_name || "—"}<div className="text-xs text-neutral-400">{r.user_email}</div></td>
                  <td className="p-3">{r.reward_name}</td>
                  <td className="p-3">{r.partner_name || "—"}</td>
                  <td className="p-3">{r.delivery_type}</td>
                  <td className="p-3 font-mono text-xs">{r.code}</td>
                  <td className="p-3">{r.points_spent}</td>
                  <td className="p-3">
                    {r.fulfillment_status === "pending"
                      ? <Button size="sm" variant="outline" className="text-xs rounded-none" onClick={() => fulfill(r.id)}>Mark fulfilled</Button>
                      : <span className="text-xs text-neutral-500 capitalize">{r.fulfillment_status}</span>}
                  </td>
                  <td className="p-3 font-mono text-xs text-neutral-400">{r.redemption_ref || "—"}</td>
                </tr>
              ))}
              {!redemptionsLoading && redemptions.length === 0 && <tr><td colSpan={10} className="p-8 text-center text-neutral-400">No redemptions yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      )}
    </div>
  );
}



// =================== EDITORIAL · AI ===================
const SECTORS_LIST = ["agencies","economy","entertainment","health","politics","retail","society","sports","tech","travel"];

// PHASE 8.45 — Multi-tier editorial helpers
const TIER_ORDER = ["public", "panelists", "subscribers", "enterprise"];
const TIER_META = {
  public:      { label: "Public",      color: "bg-emerald-600",   hint: "Anyone can read" },
  panelists:   { label: "Panelists",   color: "bg-sky-600",       hint: "Logged-in patrons only" },
  subscribers: { label: "Subscribers", color: "bg-violet-600",    hint: "Paid subscribers +" },
  enterprise:  { label: "Enterprise",  color: "bg-black",         hint: "Enterprise tier only" },
};
function TierBadge({ tier, size = "sm" }) {
  const m = TIER_META[tier] || TIER_META.public;
  const cls = size === "xs" ? "px-1.5 py-0.5 text-[9px]" : "px-2 py-0.5 text-[10px]";
  return <span className={`${m.color} text-white ${cls} font-bold uppercase tracking-wider`} title={m.hint}>{m.label}</span>;
}

export function AdminEditorial() {
  const [articles, setArticles] = useState(() => api.getCached("/admin/articles") || []);
  const [waves, setWaves] = useState(() => api.getCached("/waves") || []);
  const [busy, setBusy] = useState(false);
  const [audioBusy, setAudioBusy] = useState(null);
  const [audioPreviewOpen, setAudioPreviewOpen] = useState(() => new Set());
  const [loading, setLoading] = useState(() => !api.getCached("/admin/articles"));
  const load = async () => {
    try {
      const [a, w] = await Promise.all([api.get("/admin/articles"), api.get("/waves")]);
      setArticles(a.data); setWaves(w.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const generate = async (waveId, sector, tier, demographicFactors) => {
    setBusy(true);
    try {
      await api.post("/admin/articles/generate", { wave_id: waveId, sector, access_tier: tier || "public", demographic_factors: demographicFactors || [] });
      toast.success(`Article drafted (${TIER_META[tier || "public"].label} tier) by Gemini 3 Pro`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI generation failed");
    } finally { setBusy(false); }
  };
  const generateAudio = async (aid) => {
    setAudioBusy(aid);
    try {
      await api.post(`/admin/articles/${aid}/audio`, { voice: "onyx" });
      toast.success("Audio rendered (TTS-1-HD, voice: onyx)");
      // ITER 92 — auto-open the preview panel right after rendering, so the
      // admin can listen + read the script BEFORE publishing (previously
      // the only way to hear it was to publish the article first).
      setAudioPreviewOpen((prev) => new Set(prev).add(aid));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "TTS failed");
    } finally { setAudioBusy(null); }
  };
  const toggleAudioPreview = (aid) => {
    setAudioPreviewOpen((prev) => {
      const next = new Set(prev);
      next.has(aid) ? next.delete(aid) : next.add(aid);
      return next;
    });
  };
  const togglePublish = async (a) => {
    const next = a.status === "published" ? "draft" : "published";
    await api.put(`/admin/articles/${a.id}`, { ...a, status: next });
    toast.success(next === "published" ? "Published" : "Unpublished");
    load();
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this article? This cannot be undone.")) return;
    await api.delete(`/admin/articles/${id}`);
    load();
  };
  const spinCompanion = async (a, tier) => {
    try {
      await api.post(`/admin/articles/${a.id}/companion`, { access_tier: tier });
      toast.success(`${TIER_META[tier].label} companion drafted`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Companion creation failed");
    }
  };
  const [reviewing, setReviewing] = useState(null);
  const saveReview = async (updated) => {
    try {
      await api.put(`/admin/articles/${updated.id}`, updated);
      setReviewing(null);
      load();
      toast.success("Article saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save article");
    }
  };

  const closedWaves = waves.filter((w) => w.status === "closed");
  // Group articles by wave so admin can see tier-coverage across companions.
  const grouped = useMemo(() => {
    const bucket = new Map();
    articles.forEach((a) => {
      const key = a.wave_id || "__unassigned__";
      if (!bucket.has(key)) bucket.set(key, []);
      bucket.get(key).push(a);
    });
    return bucket;
  }, [articles]);
  return (
    <div>
      <div className="border-b-2 border-black pb-4 mb-6"><div className="overline">EDITORIAL</div><h1 className="text-4xl font-black tracking-tighter">Editorial actions</h1></div>

      <div className="bg-white border-2 border-black p-5 mb-8">
        <div className="overline mb-3 flex items-center gap-2"><Sparkle size={14} /> GENERATE FROM CLOSED WAVE (GEMINI 3 PRO)</div>
        {!loading && closedWaves.length === 0 && <div className="text-neutral-500 text-sm">Close a wave first to enable AI article generation.</div>}
        <div className="space-y-2">
          {closedWaves.map((w) => (
            <WaveAIRow key={w.id} w={w} onGenerate={generate} busy={busy} coverage={(grouped.get(w.id) || []).map((a) => a.access_tier || "public")} />
          ))}
        </div>
      </div>

      <div className="space-y-3">
        {articles.map((a) => {
          const siblings = (grouped.get(a.wave_id) || []).filter((s) => s.id !== a.id);
          const coveredTiers = new Set([a.access_tier || "public", ...siblings.map((s) => s.access_tier || "public")]);
          const missingTiers = TIER_ORDER.filter((t) => !coveredTiers.has(t));
          return (
          <div key={a.id} className="bg-white border border-neutral-200 p-5" data-testid={`article-${a.id}`}>
            <div className="flex items-start gap-4">
              <div className="flex-1">
                <div className="overline mb-1 flex gap-2 items-center flex-wrap">
                  <span className={`px-2 py-0.5 ${a.status === 'published' ? 'bg-green-700 text-white' : 'bg-neutral-300'}`}>{a.status}</span>
                  <TierBadge tier={a.access_tier || "public"} />
                  <span>{a.sector?.toUpperCase()}</span>
                  {a.ai_generated && <span className="inline-flex items-center gap-1 text-mango"><Sparkle size={11} /> AI</span>}
                  {a.audio_url && <span className="inline-flex items-center gap-1"><Headphones size={11} /> AUDIO</span>}
                  {a.tier_label && <span className="text-neutral-500">· {a.tier_label}</span>}
                </div>
                <div className="font-bold text-lg leading-snug">{a.title}</div>
                <div className="text-xs text-neutral-500 mt-1 font-mono flex items-center gap-2 flex-wrap">
                  <span>{formatDateTime(a.created_at)}</span>
                  {a.slug && (
                    <a href={`/articles/${a.slug}`} target="_blank" rel="noreferrer" className="text-mango hover:underline" data-testid={`article-view-live-${a.id}`}>
                      /articles/{a.slug} →
                    </a>
                  )}
                </div>
                {siblings.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1 items-center text-[11px] text-neutral-500">
                    <span className="uppercase tracking-wider">Companions:</span>
                    {siblings.map((s) => (
                      <button
                        key={s.id}
                        onClick={() => setReviewing(s)}
                        className="inline-flex items-center gap-1 border border-neutral-300 px-1.5 py-0.5 hover:border-black"
                        data-testid={`companion-jump-${s.id}`}
                      >
                        <TierBadge tier={s.access_tier || "public"} size="xs" />
                        <span className="truncate max-w-[160px]">{s.title}</span>
                      </button>
                    ))}
                  </div>
                )}
                {a.wave_id && missingTiers.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1 items-center text-[11px] text-neutral-500">
                    <span className="uppercase tracking-wider">+ add tier version:</span>
                    {missingTiers.map((t) => (
                      <button
                        key={t}
                        onClick={() => spinCompanion(a, t)}
                        className="inline-flex items-center gap-1 border border-dashed border-neutral-400 px-1.5 py-0.5 hover:border-black hover:bg-neutral-50"
                        data-testid={`add-companion-${a.id}-${t}`}
                      >
                        + <TierBadge tier={t} size="xs" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex gap-1 flex-wrap">
                <Button data-testid={`review-${a.id}`} onClick={() => setReviewing(a)} variant="outline" size="sm" className="rounded-none"><PencilSimple size={14} className="mr-1" />Review</Button>
                <Button data-testid={`audio-${a.id}`} disabled={audioBusy === a.id} onClick={() => generateAudio(a.id)} variant="outline" size="sm" className="rounded-none"><Headphones size={14} className="mr-1" />{audioBusy === a.id ? "Rendering..." : a.audio_url ? "Re-render" : "Audio"}</Button>
                {a.audio_url && (
                  <Button
                    data-testid={`audio-preview-toggle-${a.id}`}
                    variant="outline"
                    size="sm"
                    className="rounded-none"
                    onClick={() => toggleAudioPreview(a.id)}
                  >
                    {audioPreviewOpen.has(a.id) ? "Hide preview" : "Preview"}
                  </Button>
                )}
                {a.audio_url && (
                  <Button
                    data-testid={`audio-toggle-${a.id}`}
                    variant="outline"
                    size="sm"
                    className={`rounded-none ${a.audio_enabled === false ? "bg-neutral-200 text-neutral-500" : "bg-mango/10 text-black border-mango"}`}
                    onClick={async () => {
                      const next = !(a.audio_enabled !== false);
                      await api.put(`/admin/articles/${a.id}`, { audio_enabled: next });
                      toast.success(next ? "Audio shown to readers" : "Audio hidden from readers");
                      load();
                    }}
                  >
                    {a.audio_enabled === false ? "Audio hidden" : "Audio shown"}
                  </Button>
                )}
                <Button data-testid={`pub-${a.id}`} onClick={() => togglePublish(a)} size="sm" className="bg-black hover:bg-neutral-800 rounded-none">{a.status === 'published' ? 'Unpublish' : 'Publish'}</Button>
                <Button variant="ghost" size="sm" onClick={() => remove(a.id)}><Trash size={14} /></Button>
              </div>
            </div>
            {/* ITER 92 — listen + read the full script BEFORE publishing, so
                a bad take can be caught and re-rendered without going live
                first. Works regardless of `status`/`audio_enabled`. */}
            {a.audio_url && audioPreviewOpen.has(a.id) && (
              <div className="mt-3 pt-3 border-t border-neutral-200 space-y-2" data-testid={`audio-preview-panel-${a.id}`}>
                <audio
                  data-testid={`audio-preview-player-${a.id}`}
                  src={`${process.env.REACT_APP_BACKEND_URL}${a.audio_url}`}
                  controls
                  className="w-full h-10"
                />
                {a.audio_script && (
                  <div>
                    <div className="overline text-[10px] text-neutral-500 mb-1">Rendered script</div>
                    <div
                      className="text-sm text-neutral-700 whitespace-pre-wrap max-h-48 overflow-y-auto bg-neutral-50 border border-neutral-200 p-3 font-mono leading-relaxed"
                      data-testid={`audio-preview-script-${a.id}`}
                    >
                      {a.audio_script}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );})}
        {!loading && articles.length === 0 && <div className="text-neutral-500">No articles yet.</div>}
      </div>
      {reviewing && <ArticleReviewModal article={reviewing} allArticles={articles} onClose={() => setReviewing(null)} onSave={saveReview} />}
    </div>
  );
}

// PRD Task 2 — hybrid demographic factor picker. Age/Gender/SEC/Region
// always analyzed (shown as static "always on" badges); Education and
// Marital status are optional add-ons for a specific researcher angle.
const EXTRA_DEMO_FACTORS = [
  { key: "education", label: "Education" },
  { key: "marital_status", label: "Marital status" },
];

function WaveAIRow({ w, onGenerate, busy, coverage = [] }) {
  const [sector, setSector] = useState("society");
  const [tier, setTier] = useState("public");
  const [demoFactors, setDemoFactors] = useState([]);
  const toggleFactor = (key) => setDemoFactors((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
  return (
    <div className="flex items-center gap-3 border border-neutral-200 p-3 flex-wrap" data-testid={`gen-row-${w.id}`}>
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate">{w.name}</div>
        <div className="overline text-neutral-500 flex items-center gap-2 flex-wrap">
          <span>CLOSED · {w.question_ids.length} questions</span>
          {coverage.length > 0 && (
            <span className="flex gap-1">
              {[...new Set(coverage)].map((t) => <TierBadge key={t} tier={t} size="xs" />)}
            </span>
          )}
        </div>
      </div>
      <select value={sector} onChange={(e) => setSector(e.target.value)} className="border border-neutral-300 px-2 py-1 text-sm">
        {SECTORS_LIST.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      <select value={tier} onChange={(e) => setTier(e.target.value)} className="border border-neutral-300 px-2 py-1 text-sm" data-testid={`gen-tier-${w.id}`}>
        {TIER_ORDER.map((t) => <option key={t} value={t}>{TIER_META[t].label}</option>)}
      </select>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button type="button" variant="outline" size="sm" className="rounded-none text-xs" data-testid={`gen-demo-picker-${w.id}`}>
            Demographics{demoFactors.length > 0 ? ` (+${demoFactors.length})` : ""}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <div className="px-2 py-1.5 text-[10px] uppercase tracking-wide text-neutral-400">Always analyzed</div>
          <div className="px-2 pb-2 text-xs text-neutral-500">Age · Gender · SEC · Region</div>
          <DropdownMenuSeparator />
          <div className="px-2 py-1.5 text-[10px] uppercase tracking-wide text-neutral-400">Add an extra angle (optional)</div>
          {EXTRA_DEMO_FACTORS.map((f) => (
            <DropdownMenuItem key={f.key} onSelect={(e) => { e.preventDefault(); toggleFactor(f.key); }} data-testid={`gen-demo-${f.key}-${w.id}`} className="flex items-center gap-2">
              <Checkbox checked={demoFactors.includes(f.key)} className="pointer-events-none" />
              <span>{f.label}</span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <Button data-testid={`gen-${w.id}`} disabled={busy} onClick={() => onGenerate(w.id, sector, tier, demoFactors)} className="bg-mango hover:bg-oodel-dark text-white border-2 border-black rounded-none"><Sparkle size={14} className="mr-1" />{busy ? "Drafting..." : "Generate"}</Button>
    </div>
  );
}

// =================== ARTICLE REVIEW MODAL ===================
function ArticleReviewModal({ article, allArticles, onClose, onSave }) {
  // ITER 66 — Auto-migrate legacy markdown → blocks when the modal first
  // opens on a body-only article. Ensures the editor is 1:1 with what
  // logged-in patrons will see and enables per-block Urdu translation.
  const initialBlocks = (() => {
    const existing = article.blocks;
    if (Array.isArray(existing) && existing.length > 0) return existing;
    const body = (article.body || "").trim();
    if (!body) return [];
    const out = [];
    body.split(/\n{2,}/).forEach((para) => {
      const t = para.trim();
      if (!t) return;
      if (/^---+$/.test(t)) { out.push({ type: "divider" }); return; }
      const hMatch = t.match(/^(#{1,3})\s+(.+)$/);
      if (hMatch) { out.push({ type: "headline", level: hMatch[1].length, text: hMatch[2].trim() }); return; }
      if (t.startsWith(">")) { out.push({ type: "pullquote", text: t.replace(/^>\s?/gm, "").trim() }); return; }
      out.push({ type: "text", text: t });
    });
    return out;
  })();
  const [draft, setDraft] = useState({ ...article, blocks: initialBlocks });
  // ITER 66 — Visual editor is now the ONLY editor. Markdown is retired.
  const [tab, setTab] = useState("blocks"); // 'blocks' | 'preview'
  // ITER 109 — swap-confirmation state (in-app Dialog, not window.confirm —
  // see the matching WaveBuilder comment for why: the platform's preview
  // iframe silently blocks window.confirm(), which looked exactly like the
  // toggle not responding to clicks at all).
  const [featuredSwapPrompt, setFeaturedSwapPrompt] = useState(null);

  // ITER 107/109 — "Featured Article" toggle. Only one article may be
  // featured at a time (server-side enforced too, see PUT /admin/articles/{aid}).
  const toggleFeatured = (checked) => {
    if (!checked) { setDraft({ ...draft, is_featured: false }); return; }
    const other = (allArticles || []).find((a) => a.is_featured && a.id !== article.id);
    if (other) { setFeaturedSwapPrompt(other); return; }
    setDraft({ ...draft, is_featured: true });
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="article-review-modal">
      <div className="bg-white border-2 border-black w-full max-w-4xl max-h-[90vh] flex flex-col hard-shadow" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b-2 border-black">
          <div>
            <div className="overline">EDITORIAL REVIEW · {article.ai_generated ? "AI DRAFT" : "MANUAL"}</div>
            <div className="font-bold">{draft.title}</div>
          </div>
          <Button variant="ghost" onClick={onClose} className="rounded-none"><X size={18} /></Button>
        </div>
        <div className="flex border-b">
          <button onClick={() => setTab("blocks")} data-testid="review-tab-blocks" className={`px-5 py-3 text-sm font-bold border-b-2 ${tab === "blocks" ? "border-mango" : "border-transparent text-neutral-500"}`}>Visual editor</button>
          <button onClick={() => setTab("preview")} data-testid="review-tab-preview" className={`px-5 py-3 text-sm font-bold border-b-2 ${tab === "preview" ? "border-mango" : "border-transparent text-neutral-500"}`}>Preview</button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-4">
            <div>
              <Label className="overline">Title · English</Label>
              <Input data-testid="review-title" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} className="rounded-none text-lg font-bold" />
            </div>
            {/* ITER 66 — Urdu title is a first-class field, editable + translatable inline. */}
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 items-end">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Label className="overline">Title · اردو</Label>
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 font-bold ${
                    draft.title_ur_status === "approved" ? "bg-emerald-600 text-white"
                    : draft.title_ur_status === "stale" ? "bg-yellow-500 text-white"
                    : (draft.title_ur || "") ? "bg-neutral-500 text-white"
                    : "bg-neutral-300 text-neutral-600"
                  }`}>
                    {draft.title_ur_status === "approved" ? "APPROVED"
                     : draft.title_ur_status === "stale" ? "STALE"
                     : (draft.title_ur || "") ? "DRAFT" : "MISSING"}
                  </span>
                  {draft.title_ur && draft.title_ur_status !== "approved" && (
                    <button
                      type="button"
                      onClick={() => setDraft({ ...draft, title_ur_status: "approved" })}
                      className="text-[10px] uppercase tracking-wider px-2 py-0.5 border border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                      data-testid="review-approve-title-ur"
                    >Approve</button>
                  )}
                  {draft.title_ur_status === "approved" && (
                    <button
                      type="button"
                      onClick={() => setDraft({ ...draft, title_ur_status: "draft" })}
                      className="text-[10px] uppercase tracking-wider px-2 py-0.5 border border-neutral-400 text-neutral-600 hover:bg-neutral-50"
                      data-testid="review-unapprove-title-ur"
                    >Back to draft</button>
                  )}
                </div>
                <Input
                  dir="rtl"
                  lang="ur"
                  data-testid="review-title-ur"
                  value={draft.title_ur || ""}
                  onChange={(e) => setDraft({ ...draft, title_ur: e.target.value, title_ur_status: "draft" })}
                  placeholder="اردو عنوان — یا ترجمہ کا بٹن دبائیں"
                  className="rounded-none text-right text-lg font-bold"
                />
              </div>
              <Button
                type="button"
                variant="outline"
                className="rounded-none h-10"
                data-testid="translate-title-ur"
                onClick={async () => {
                  if (!article.id) { alert("Save the article as a draft first."); return; }
                  try {
                    const r = await api.post(`/admin/i18n/articles/${article.id}/blocks/translate`, {
                      block_index: -1,
                      text: draft.title,
                      field: "title",
                    }).catch(() => null);
                    // Fallback path — the -1 index isn't supported by blocks/translate.
                    // Use the article-level draft endpoint which handles title.
                    if (!r) {
                      const r2 = await api.post(`/admin/i18n/articles/${article.id}/draft`);
                      if (r2.data?.title_ur) setDraft({ ...draft, title_ur: r2.data.title_ur, title_ur_status: r2.data.title_ur_status || "draft" });
                    } else if (r.data?.block) {
                      setDraft({ ...draft, ...r.data.block });
                    }
                  } catch (e) {
                    alert(e.response?.data?.detail || "Translation failed");
                  }
                }}
              ><Globe size={14} className="mr-1" /> Translate title</Button>
            </div>
            {/* ITER 68 — Admin-editable SEO slug. Auto-generated at creation
                time from the title; admins can hand-tune it for SEO. Backend
                rejects the save with a clear error if another article already
                owns the slug. */}
            <div>
              <Label className="overline">URL slug (SEO-friendly link)</Label>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs text-neutral-500 font-mono whitespace-nowrap">{window.location.origin}/articles/</span>
                <Input
                  data-testid="review-slug"
                  value={draft.slug || ""}
                  onChange={(e) => setDraft({ ...draft, slug: e.target.value })}
                  placeholder="shehbaz-sharif-popularity-ranking"
                  className="rounded-none font-mono text-xs"
                />
              </div>
              {draft.id && (
                <a href={`/articles/${draft.slug || draft.id}`} target="_blank" rel="noreferrer" className="text-[11px] text-mango hover:underline mt-1 inline-block" data-testid="review-slug-preview-link">
                  View live →
                </a>
              )}
            </div>
            <div><Label className="overline">Cover image URL (1600 × 900 recommended — also shown as a 128×128px square-cropped thumbnail on the patron dashboard's "Featured Article" card and discovery rail)</Label>
              <Input
                data-testid="review-cover-image"
                value={draft.cover_image || ""}
                onChange={(e) => setDraft({ ...draft, cover_image: e.target.value })}
                placeholder="https://images.unsplash.com/photo-…?w=1600&h=900&fit=crop"
                className="rounded-none font-mono text-xs"
              />
              {draft.cover_image && (
                <div className="mt-2 relative aspect-[16/9] max-w-md border border-neutral-300 overflow-hidden bg-neutral-100">
                  <img src={draft.cover_image} alt="cover preview" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
                </div>
              )}
            </div>
            <div className="flex items-center justify-between gap-3 bg-mango/5 border border-mango/20 p-3">
              <div>
                <Label className="overline text-xs flex items-center gap-1">Featured Article <HelpHint text="Only ONE article can be Featured at a time. The Featured article replaces whatever the patron dashboard's homepage 'Featured Article' spotlight card currently shows. Turning this ON for a different article automatically turns it OFF for whichever article had it before." /></Label>
                <p className="text-xs text-neutral-500 mt-0.5">Show this article on the patron dashboard homepage.</p>
              </div>
              <Switch data-testid="review-is-featured" checked={!!draft.is_featured} onCheckedChange={toggleFeatured} />
            </div>

            {/* ITER 109 — Featured Article swap-confirmation dialog (in-app,
                not window.confirm). */}
            <Dialog open={!!featuredSwapPrompt} onOpenChange={(o) => !o && setFeaturedSwapPrompt(null)}>
              <DialogContent className="max-w-md" data-testid="featured-swap-dialog">
                <DialogHeader>
                  <DialogTitle>Swap the Featured Article?</DialogTitle>
                </DialogHeader>
                <p className="text-sm text-neutral-600">
                  "<b>{featuredSwapPrompt?.title}</b>" is currently the Featured Article on the patron dashboard homepage. Set "<b>{draft.title || "this article"}</b>" as the new Featured Article instead? This will un-feature "{featuredSwapPrompt?.title}".
                </p>
                <div className="flex justify-end gap-2 mt-4">
                  <Button variant="outline" data-testid="featured-swap-cancel" onClick={() => setFeaturedSwapPrompt(null)}>Cancel</Button>
                  <Button
                    data-testid="featured-swap-confirm"
                    className="bg-mango hover:bg-mango/90"
                    onClick={() => { setDraft({ ...draft, is_featured: true }); setFeaturedSwapPrompt(null); }}
                  >
                    Confirm swap
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            <div><Label className="overline">Sector</Label>
              <select value={draft.sector || ""} onChange={(e) => setDraft({ ...draft, sector: e.target.value })} className="border border-neutral-300 px-2 py-1 text-sm rounded-none">
                {SECTORS_LIST.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            {/* PHASE 8.45 — Multi-tier editorial: tier selector + optional friendly label */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="overline">Access tier</Label>
                <div className="flex items-center gap-2 mt-1">
                  <select
                    value={draft.access_tier || "public"}
                    onChange={(e) => setDraft({ ...draft, access_tier: e.target.value })}
                    className="border border-neutral-300 px-2 py-1 text-sm rounded-none"
                    data-testid="review-access-tier"
                  >
                    {TIER_ORDER.map((t) => <option key={t} value={t}>{TIER_META[t].label}</option>)}
                  </select>
                  <TierBadge tier={draft.access_tier || "public"} />
                </div>
                <div className="text-[10px] text-neutral-500 mt-1">{TIER_META[draft.access_tier || "public"].hint}. Higher tiers cascade — subscribers also see panelists + public.</div>
              </div>
              <div>
                <Label className="overline">Tier label (optional badge)</Label>
                <Input
                  data-testid="review-tier-label"
                  value={draft.tier_label || ""}
                  onChange={(e) => setDraft({ ...draft, tier_label: e.target.value })}
                  placeholder="e.g. Enterprise Deep-Dive"
                  className="rounded-none"
                />
              </div>
            </div>
          </div>
          {tab === "blocks" ? (
            <div className="mt-6">
              <ArticleBlockEditor
                blocks={draft.blocks}
                legacyBody={draft.body}
                articleId={article.id}
                onChange={(next) => setDraft({ ...draft, blocks: next })}
                onTranslateAll={(fresh) => {
                  // Hydrate title Urdu from the article-level draft response too.
                  if (fresh?.title_ur) {
                    setDraft((d) => ({ ...d, title_ur: fresh.title_ur, title_ur_status: fresh.title_ur_status || d.title_ur_status }));
                  }
                }}
              />
            </div>
          ) : (
            <article className="prose prose-neutral max-w-none mt-6">
              <h1>{draft.title}</h1>
              <div className="overline">{draft.sector?.toUpperCase()}</div>
              <ArticleBlocks blocks={draft.blocks} body={draft.body} />
            </article>
          )}
        </div>
        <div className="border-t border-grid p-4 flex gap-2 justify-end">
          <Button data-testid="review-save-draft" variant="outline" onClick={() => onSave({ ...draft, status: "draft" })} className="rounded-none">Save draft</Button>
          <Button data-testid="review-publish" onClick={() => onSave({ ...draft, status: "published" })} className="bg-mango hover:bg-oodel-dark text-white border-2 border-black rounded-none">Publish</Button>
        </div>
      </div>
    </div>
  );
}

// =================== USERS & SUBSCRIPTIONS ===================
// Tiny presentational block used in the All Users KPI strip.
function KpiCard({ label, value, testId, colour = "text-black" }) {
  return (
    <div className="bg-white border-2 border-black p-3" data-testid={testId}>
      <div className="overline text-[10px] text-neutral-500">{label}</div>
      <div className={`text-3xl font-black mt-1 ${colour}`}>{value}</div>
    </div>
  );
}

export function AdminUsers() {
  const siteContent = useSiteContent();
  // Admin can toggle tab visibility from Site Content CMS. Defaults: all ON.
  const tabsCfg = {
    all: String(siteContent.users_tab_all_enabled ?? "true").toLowerCase() !== "false",
    complete: String(siteContent.users_tab_complete_enabled ?? "true").toLowerCase() !== "false",
    incomplete: String(siteContent.users_tab_incomplete_enabled ?? "true").toLowerCase() !== "false",
    password_pending: String(siteContent.users_tab_password_pending_enabled ?? "true").toLowerCase() !== "false",
    qr_pending: String(siteContent.users_tab_qr_pending_enabled ?? "true").toLowerCase() !== "false",
  };
  const [users, setUsers] = useState(() => api.getCached("/admin/users") || []);
  const [resetInfo, setResetInfo] = useState(null);
  const [ledgerUser, setLedgerUser] = useState(null);  // {id, email, name}
  const [detailUser, setDetailUser] = useState(null);  // full user doc for modal
  const [messageUser, setMessageUser] = useState(null);  // user being messaged via the email modal
  const [search, setSearch] = useState("");
  const [filterRole, setFilterRole] = useState("");
  const [filterTier, setFilterTier] = useState("");
  const [filterRegion, setFilterRegion] = useState("");  // "" / "Punjab" / "Sindh" / ... / "Overseas" / "—"
  const [filterStatus, setFilterStatus] = useState("");  // "" / "active" / "suspended"
  const [filterProfile, setFilterProfile] = useState("");  // "" / "complete" / "incomplete" / "no_password"
  const [filterSource, setFilterSource] = useState("");    // "" / "legacy_waitlist" / "direct"
  // PHASE 8.45 — Points range filter + sort dropdown (admin operational view)
  const [filterPoints, setFilterPoints] = useState("");  // "" / "0" / "1-1000" / ... / "5000+"
  const [sortBy, setSortBy] = useState("latest_signup");  // default = previous behaviour
  // Pagination — rendering all 2000 rows into the DOM at once was the main
  // cause of this page feeling slow (the /admin/users fetch itself is a
  // single request, not the bottleneck). Filters/search/stat cards still
  // run over the full in-memory list (cheap JS array ops); only the actual
  // <table> body is paginated.
  const PAGE_SIZE = 50;
  const [page, setPage] = useState(1);
  // Tabs: "all" = every row, "complete" = profile completed, "incomplete" = the
  // bulk-email reminder module (was a standalone /admin/incomplete-profiles
  // page before — consolidated here so all user-management lives in one place),
  // "password_pending" = legacy-waitlist stubs that need to set a password.
  // URL ?tab=incomplete keeps the old standalone route deep-linkable.
  const initialTab = (typeof window !== "undefined" && new URLSearchParams(window.location.search).get("tab")) || "all";
  const [tab, setTab] = useState(["all", "complete", "incomplete", "password_pending", "qr_pending"].includes(initialTab) ? initialTab : "all");
  // ready flips on first /admin/users response — gates the stat cards + table
  // in a FadeIn so the user never sees 0/0/0 or a "Showing 0 of 0" flash.
  const [ready, setReady] = useState(false);
  const load = () => api.get("/admin/users").then((r) => { setUsers(r.data); setReady(true); }).catch(() => setReady(true));
  const [pendingMigration, setPendingMigration] = useState(() => api.getCached("/admin/migrate/waitlist-pending-count"));
  const [dupeBonuses, setDupeBonuses] = useState(() => api.getCached("/admin/duplicate-welcome-bonuses"));
  const [ghostPwCount, setGhostPwCount] = useState(() => api.getCached("/admin/cleanup/ghost-passwords/count")?.ghost_password_users || 0);
  const loadPending = () => api.get("/admin/migrate/waitlist-pending-count").then((r) => setPendingMigration(r.data)).catch(() => {});
  const loadDupes = () => api.get("/admin/duplicate-welcome-bonuses").then((r) => setDupeBonuses(r.data)).catch(() => {});
  const loadGhostPw = () => api.get("/admin/cleanup/ghost-passwords/count").then((r) => setGhostPwCount(r.data?.ghost_password_users || 0)).catch(() => {});
  useEffect(() => { load(); loadPending(); loadDupes(); loadGhostPw(); }, []);
  // Reset to page 1 whenever the visible row-set could change shape —
  // otherwise switching tabs/filters while on page 3 could land on an
  // out-of-range empty page.
  useEffect(() => { setPage(1); }, [tab, search, filterRole, filterTier, filterRegion, filterStatus, filterProfile, filterSource, filterPoints, sortBy]);
  const [migrating, setMigrating] = useState(false);
  const impersonationEnabled = String(siteContent.admin_impersonation_enabled ?? "false").toLowerCase() === "true";
  const impersonate = async (u) => {
    if (u.role === "admin") { toast.error("Cannot impersonate another admin."); return; }
    if (!window.confirm(`Start a 30-minute impersonation session as ${u.name || u.email}?\n\nA red banner will show on every page until you exit. All actions are logged in the audit trail.`)) return;
    try {
      const { data } = await api.post(`/admin/impersonate/${u.id}`);
      const adminTok = localStorage.getItem("pollpak_token");
      if (adminTok) sessionStorage.setItem("pollpak_admin_token", adminTok);
      // Preserve the admin's own profile snapshot for restore-on-exit so we
      // don't re-fetch and so the admin layout has its user object ready.
      const adminUserRaw = localStorage.getItem("pollpak_user_cache");
      if (adminUserRaw) sessionStorage.setItem("pollpak_admin_user_cache", adminUserRaw);
      sessionStorage.setItem("pollpak_impersonation_target", JSON.stringify(data.target));
      localStorage.setItem("pollpak_token", data.token);
      // CRITICAL: wipe the cached user object so AuthProvider doesn't hydrate
      // the impersonated session with the admin's identity (which would cause
      // /panelist to redirect to /login because admin.role !== 'patron').
      localStorage.removeItem("pollpak_user_cache");
      // Also wipe per-user API + CMS caches so the impersonated patron sees
      // their own data, not whatever the admin previously loaded.
      try {
        Object.keys(localStorage).forEach((k) => {
          if (k.startsWith("oodel_api_v1::") || k === "oodel-site-content-v1") localStorage.removeItem(k);
        });
      } catch (_) {}
      window.location.href = "/panelist";
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start impersonation");
    }
  };

  const cleanupGhostPasswords = async () => {
    if (!window.confirm(`Clean up ${ghostPwCount} ghost passwords?\n\nThese are legacy-waitlist users whose password_hash was silently written by an old startup script — but they NEVER actually claimed their account (no password_set_at, no last_login_at).\n\nNullifying these hashes will move them back into the "No password set" cohort where they belong, so they correctly appear in the Password Pending tab and KPI.\n\nIdempotent + safe — only touches unclaimed legacy_waitlist rows.`)) return;
    try {
      const { data } = await api.post("/admin/cleanup/ghost-passwords");
      toast.success(`Fixed ${data.users_fixed} ghost-password rows. They'll now appear in Password Pending.`);
      await load();
      await loadGhostPw();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Cleanup failed");
    }
  };
  const importWaitlist = async () => {
    if (!window.confirm(`Import waitlist signups into Users?\n\nThis will convert every un-migrated row in waitlist_signups into a passwordless patron stub (source: "legacy_waitlist"). Safe to re-run — already-migrated rows are skipped.`)) return;
    setMigrating(true);
    try {
      const { data } = await api.post("/admin/migrate/waitlist-to-users");
      toast.success(`Imported ${data.users_created} new patrons. ${data.already_users_now_marked_converted} already existed.`);
      await load();
      await loadPending();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setMigrating(false);
    }
  };
  const reconcileDupes = async () => {
    if (!window.confirm(`Reconcile duplicate welcome bonuses?\n\n${dupeBonuses?.count || 0} users received the welcome bonus more than once (pre-fix race condition). This will KEEP the first credit per user and void the rest, debiting the over-credited points. Safe + idempotent.`)) return;
    try {
      const { data } = await api.post("/admin/duplicate-welcome-bonuses/reconcile");
      toast.success(`Fixed ${data.users_fixed} users · debited ${data.total_points_debited} pts.`);
      await load();
      await loadDupes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reconcile failed");
    }
  };

  const setTier = async (u, tier) => {
    const currentlyIsCA = !!u.is_campus_ambassador;
    // ── Campus Ambassador promotion ─────────────────────────────────
    if (tier === "campus_ambassador") {
      if (currentlyIsCA) { toast("Already a Campus Ambassador."); return; }
      const campus = prompt(`Campus / university for ${u.name || u.email}? (e.g., "IBA Karachi")`);
      if (!campus || !campus.trim()) return;
      const rateRaw = prompt("Per-conversion payout (₨)?", "20");
      if (rateRaw == null) return;
      const rate = Number(rateRaw) || 20;
      try {
        await api.post("/admin/ambassadors", { user_id: u.id, campus: campus.trim(), rate_pkr: rate });
        toast.success(`${u.email} promoted to Campus Ambassador`);
        load();
      } catch (err) {
        toast.error(err.response?.data?.detail || "Promotion failed");
      }
      return;
    }
    // ── If user is currently a CA and admin picks any other tier, demote first ──
    if (currentlyIsCA) {
      if (!window.confirm(`Demote ${u.email} from Campus Ambassador back to ${tier}? Their ambassador link will redirect to their peer referral link.`)) return;
      try {
        const caId = u.campus_ambassador_record_id;
        if (caId) {
          await api.patch(`/admin/ambassadors/${caId}`, { status: "terminated" });
        }
      } catch (err) {
        toast.error(err.response?.data?.detail || "Demote failed");
        return;
      }
    }
    const cid = tier === "enterprise" ? (u.client_id || prompt("Client ID for this enterprise account?", "ACME-001")) : null;
    if (tier === "enterprise" && !cid) return;
    try {
      await api.put(`/admin/users/${u.id}/subscription`, { tier, client_id: cid });
      toast.success(`${u.email} → ${tier === "free" ? "patron" : tier}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || (currentlyIsCA ? "Demoted from CA but failed to set new tier — please retry." : "Tier change failed"));
    }
    load();
  };
  const toggleSuspend = async (u) => {
    const reason = u.suspended ? "" : (prompt("Reason for suspension?", "Suspicious activity") || "");
    await api.put(`/admin/users/${u.id}/suspend`, { suspended: !u.suspended, reason });
    toast.success(u.suspended ? "Unsuspended" : "Suspended");
    load();
  };
  const deleteUser = async (u) => {
    if (!window.confirm(`Delete ${u.email}? This cannot be undone.`)) return;
    await api.delete(`/admin/users/${u.id}`);
    load();
  };
  const resetPassword = async (u) => {
    if (!window.confirm(`Generate a NEW temporary password for ${u.email}?\nThe user will be forced to change it on next login.`)) return;
    try {
      const { data } = await api.post(`/admin/users/${u.id}/reset-password`);
      setResetInfo({ email: data.email, temp_password: data.temp_password });
      toast.success(`Temporary password generated for ${u.email}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    }
  };

  // oodel SCORE (QR feedback) opt-in signups are hidden from every other tab
  // until they complete their profile — they get their own tab instead, so
  // "All Users" isn't flooded with feedback-only respondents who may never
  // actually become active patrons.
  // NOTE: declared here (not further below) because `filtered` below invokes
  // it synchronously during this render pass — a `const` declared later in
  // the function body would throw a "Cannot access before initialization" TDZ error.
  const isQrPending = (u) => u.source === "cs_qr_respondent" && u.profile_status !== "complete";

  // Client-side filter / search across the loaded list.
  const q = search.trim().toLowerCase();
  const filtered = users.filter((u) => {
    if (tab === "qr_pending") return isQrPending(u);
    if (isQrPending(u)) return false;  // hidden everywhere else until profile complete
    // Tab-level filter first — all 4 tabs share the same table now.
    if (tab === "complete" && u.profile_status !== "complete") return false;
    if (tab === "incomplete" && u.profile_status !== "incomplete") return false;
    if (tab === "password_pending" && u.profile_status !== "no_password") return false;
    if (q) {
      const hay = [u.name, u.email, u.client_id, u.profile?.city, u.country_of_residence, u.referral_code].filter(Boolean).join(" ").toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (filterRole && u.role !== filterRole) return false;
    if (filterTier === "campus_ambassador") {
      if (!u.is_campus_ambassador) return false;
    } else if (filterTier) {
      if (u.is_campus_ambassador) return false;  // CAs only show under their own filter
      if ((u.subscription_tier || "free") !== filterTier) return false;
    }
    if (filterRegion) {
      const regionForUser = u.is_overseas ? "Overseas" : (u.profile?.region || "—");
      if (regionForUser !== filterRegion) return false;
    }
    if (filterStatus === "active" && u.suspended) return false;
    if (filterStatus === "suspended" && !u.suspended) return false;
    if (filterProfile && u.profile_status !== filterProfile) return false;
    if (filterSource === "legacy_waitlist" && u.source !== "legacy_waitlist") return false;
    if (filterSource === "cs_qr_respondent" && u.source !== "cs_qr_respondent") return false;
    // "Direct signup" — opposite of every other source: no referrer, no CA, no waitlist origin.
    if (filterSource === "direct") {
      if (u.source === "legacy_waitlist") return false;
      if (u.source === "cs_qr_respondent") return false;
      if (u.invited_by) return false;
      if (u.channel === "campus_ambassador" || u.campus_ambassador_id) return false;
    }
    // "Referral" — has invited_by but is NOT routed through the CA pipeline.
    if (filterSource === "referral") {
      if (!u.invited_by) return false;
      if (u.channel === "campus_ambassador" || u.campus_ambassador_id) return false;
    }
    // "CA invite" — explicitly routed through Campus Ambassador attribution.
    if (filterSource === "campus_ambassador") {
      if (!(u.channel === "campus_ambassador" || u.campus_ambassador_id)) return false;
    }
    // PHASE 8.45 — points-range filter
    if (filterPoints) {
      const p = Number(u.points) || 0;
      if (filterPoints === "0" && p !== 0) return false;
      else if (filterPoints === "1-1000" && (p < 1 || p > 1000)) return false;
      else if (filterPoints === "1001-2000" && (p < 1001 || p > 2000)) return false;
      else if (filterPoints === "2001-3000" && (p < 2001 || p > 3000)) return false;
      else if (filterPoints === "3001-4000" && (p < 3001 || p > 4000)) return false;
      else if (filterPoints === "4001-5000" && (p < 4001 || p > 5000)) return false;
      else if (filterPoints === "5000+" && p <= 5000) return false;
    }
    return true;
  }).sort((a, b) => {
    // PHASE 8.45 — sortBy dropdown. Default "latest_signup" matches the
    // previous behaviour so this is non-breaking.
    if (sortBy === "highest_points") {
      return (Number(b.points) || 0) - (Number(a.points) || 0);
    }
    if (sortBy === "most_recent_activity") {
      const ra = a.last_login_at || a.last_active_at || a.created_at || "";
      const rb = b.last_login_at || b.last_active_at || b.created_at || "";
      return rb.localeCompare(ra);
    }
    if (sortBy === "most_surveys") {
      return (Number(b.surveys_completed) || 0) - (Number(a.surveys_completed) || 0);
    }
    if (sortBy === "most_referrals") {
      return (Number(b.referrals_count) || 0) - (Number(a.referrals_count) || 0);
    }
    // Default: latest signup (existing behaviour, untouched)
    const ta = a.created_at || a.signup_at || a.last_login_at || "";
    const tb = b.created_at || b.signup_at || b.last_login_at || "";
    if (ta === tb) return 0;
    if (!ta) return 1;
    if (!tb) return -1;
    return tb.localeCompare(ta);
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageSafe = Math.min(page, totalPages);
  const paginated = filtered.slice((pageSafe - 1) * PAGE_SIZE, pageSafe * PAGE_SIZE);
  const roles = Array.from(new Set(users.map((u) => u.role).filter(Boolean))).sort();
  const overseasTotal = users.filter((u) => u.is_overseas).length;
  // Tab counts — patrons only; admin/PM/researcher rows never have a meaningful profile_complete flag.
  const patronUsers = users.filter((u) => ["patron", "panelist"].includes(u.role) && !isQrPending(u));
  const completeCount = patronUsers.filter((u) => u.profile_status === "complete").length;
  // Incomplete = has a REAL working password but profile_complete is still false.
  // (Previously this was `total - complete` which incorrectly bundled the
  // passwordless / waitlist users into the Incomplete count — making the
  // dashboard KPI disagree with the /admin/waves cohort send buttons.)
  const incompleteCount = patronUsers.filter((u) => u.profile_status === "incomplete").length;
  const fpCount = patronUsers.filter((u) => u.is_founding_patron).length;
  // KPI: any patron without a password set (typically legacy_waitlist stubs
  // but covers anyone — admin reset, manual db edit etc. The Source column
  // makes the origin visible per-row.)
  const pwPendingCount = patronUsers.filter((u) => u.profile_status === "no_password").length;
  const qrPendingCount = users.filter(isQrPending).length;

  return (
    <div>
      <div className="border-b-2 border-black pb-4 mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="overline">USERS</div>
          <h1 className="text-4xl font-black tracking-tighter">Subscriptions & access</h1>
          <p className="text-xs text-neutral-500 mt-2" style={{ visibility: ready ? "visible" : "hidden" }}>Total <b>{users.length}</b> · In Pakistan <b>{users.length - overseasTotal}</b> · Overseas <b className="text-mango">{overseasTotal}</b>.</p>
        </div>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name, email, city, client_id, invite code…"
          className="px-3 py-2 border-2 border-black bg-white text-sm focus:outline-none focus:ring-2 focus:ring-mango w-full md:w-96"
          data-testid="users-search"
        />
      </div>
      {/* Ghost-password banner — runs ABOVE the dupe-bonus banner because
          fixing this immediately corrects the Password Pending KPI + tab counts. */}
      {ghostPwCount > 0 && (
        <div className="bg-amber-50 border-2 border-amber-500 p-4 mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3" data-testid="ghost-pw-banner">
          <div className="text-sm">
            <div className="font-bold text-amber-900">{ghostPwCount} legacy-waitlist {ghostPwCount === 1 ? "user has" : "users have"} a ghost password</div>
            <div className="text-amber-800 mt-1">An old startup script silently wrote a <code>password_hash</code> to these users — but they never actually claimed their account. That's why they're showing up in <i>Incomplete (has password)</i> instead of <i>No password set</i>. Cleaning up moves them back to the right cohort.</div>
          </div>
          <button
            onClick={cleanupGhostPasswords}
            data-testid="cleanup-ghost-pw-btn"
            className="px-5 py-3 bg-amber-600 text-white font-bold text-sm whitespace-nowrap hover:bg-amber-700 border-2 border-amber-800"
          >
            Clean up {ghostPwCount} {ghostPwCount === 1 ? "row" : "rows"} →
          </button>
        </div>
      )}
      {/* Duplicate welcome-bonus reconciliation banner — only shows if legacy
          duplicate credits exist (pre-fix race condition). Idempotent once cleared. */}
      {dupeBonuses && dupeBonuses.count > 0 && (
        <div className="bg-red-50 border-2 border-red-500 p-4 mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3" data-testid="dupe-bonus-banner">
          <div className="text-sm">
            <div className="font-bold text-red-900">{dupeBonuses.count} {dupeBonuses.count === 1 ? "user has" : "users have"} duplicate welcome-bonus credits</div>
            <div className="text-red-700 mt-1">Pre-fix race condition double-credited the +200 welcome bonus on rapid double-clicks. Reconcile keeps the FIRST credit and debits the rest.</div>
          </div>
          <button
            onClick={reconcileDupes}
            data-testid="reconcile-dupes-btn"
            className="px-5 py-3 bg-red-600 text-white font-bold text-sm whitespace-nowrap hover:bg-red-700 border-2 border-red-900"
          >
            Reconcile {dupeBonuses.count} {dupeBonuses.count === 1 ? "user" : "users"} →
          </button>
        </div>
      )}
      {/* Waitlist-import banner — only shows when there are still un-migrated
          waitlist_signups rows. One click runs the migration; on a fresh deploy
          this is how the live admin pulls every dormant signup into Users so
          they appear in the Password Pending tab. */}
      {pendingMigration && pendingMigration.pending_migration > 0 && (
        <div className="bg-mango/10 border-2 border-mango p-4 mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3" data-testid="waitlist-import-banner">
          <div className="text-sm">
            <div className="font-bold">{pendingMigration.pending_migration} legacy waitlist signups not yet imported</div>
            <div className="text-neutral-700 mt-1">These people joined via the old waitlist (name + email only) but were never converted into patrons. Import them as passwordless stubs so they appear in the Password Pending tab and receive new wave invitations.</div>
          </div>
          <button
            onClick={importWaitlist}
            disabled={migrating}
            data-testid="import-waitlist-btn"
            className="px-5 py-3 bg-black text-white font-bold text-sm whitespace-nowrap hover:bg-mango hover:text-black border-2 border-black disabled:opacity-50"
          >
            {migrating ? "Importing…" : `Import ${pendingMigration.pending_migration} signups →`}
          </button>
        </div>
      )}
      {/* KPI cards — quick at-a-glance counts for patron base health. */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6" data-testid="users-kpis">
        <KpiCard label="Total patrons" value={patronUsers.length} testId="kpi-total" />
        <KpiCard label="Complete profile" value={completeCount} testId="kpi-complete" colour="text-emerald-600" />
        <KpiCard label="Incomplete profile" value={incompleteCount} testId="kpi-incomplete" colour="text-amber-600" />
        <KpiCard label="Founding Patrons" value={fpCount} testId="kpi-fp" colour="text-mango" />
        <KpiCard label="Waitlist — needs password" value={pwPendingCount} testId="kpi-pw-pending" colour="text-neutral-500" />
        <KpiCard label="oodel SCORE signups (pending)" value={qrPendingCount} testId="kpi-qr-pending" colour="text-amber-700" />
      </div>
      {/* Tab strip — admin-toggleable via Site Content CMS (users_tab_*_enabled).
          "Incomplete profile" hands off to a dedicated bulk-email reminder module. */}
      <div className="flex items-center gap-1 border-b-2 border-black mb-5">
        {[
          tabsCfg.all && { key: "all", label: `All users (${users.length - qrPendingCount})` },
          tabsCfg.complete && { key: "complete", label: `Complete profile (${completeCount})` },
          tabsCfg.incomplete && { key: "incomplete", label: `Incomplete profile (${incompleteCount})` },
          tabsCfg.password_pending && { key: "password_pending", label: `Password pending (${pwPendingCount})` },
          tabsCfg.qr_pending && { key: "qr_pending", label: `oodel SCORE signups (${qrPendingCount})` },
        ].filter(Boolean).map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`users-tab-${t.key}`}
            className={`px-5 py-3 text-sm font-bold border-b-2 -mb-0.5 ${tab === t.key ? "border-mango text-black" : "border-transparent text-neutral-500 hover:text-black"}`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {/* All 4 tabs share the same table + detail dialog so clicking a user
          name behaves identically everywhere. Filter logic below + filterProfile
          state handle the tab-specific narrowing. */}
      <>
      {/* Incomplete-tab handoff banner — points operator at the broadcast tool
          where the bulk-reminder UX now lives. */}
      {tab === "incomplete" && (
        <div className="bg-neutral-50 border-l-4 border-mango p-3 mb-4 text-xs text-neutral-700">
          Want to email these users? Go to <a href="/admin/email-templates" className="underline font-bold">Email Templates → Custom Broadcast</a> · Audience: <b>Patrons with incomplete profile</b> · click <b>"Use suggested template"</b> for a starter body.
        </div>
      )}
      {tab === "qr_pending" && (
        <div className="bg-amber-50 border-l-4 border-amber-500 p-3 mb-4 text-xs text-neutral-700" data-testid="qr-pending-banner">
          Patrons who opted in to create an oodel account from a business's oodel SCORE QR feedback form, but haven't claimed/completed their profile yet. They're hidden from every other tab until they do — once complete, they show up normally under "All users" / "Complete profile".
        </div>
      )}
      {/* (Filter bar + table render below — unchanged from before tabs) */}
      {/* Filter bar */}
      <div className="bg-white border-2 border-black p-4 mb-5 flex flex-wrap gap-4 items-center text-xs" data-testid="users-filters">
        <div className="flex items-center gap-2">
          <span className="overline">role</span>
          <select value={filterRole} onChange={(e) => setFilterRole(e.target.value)} data-testid="filter-role" className="border-2 border-neutral-300 px-2 py-1 text-xs">
            <option value="">Any</option>
            {roles.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <span className="overline">tier</span>
          <select value={filterTier} onChange={(e) => setFilterTier(e.target.value)} data-testid="filter-tier" className="border-2 border-neutral-300 px-2 py-1 text-xs">
            <option value="">Any</option>
            <option value="free">patron</option>
            <option value="subscriber">subscriber</option>
            <option value="enterprise">enterprise</option>
            <option value="campus_ambassador">campus ambassador</option>
          </select>
        </div>
        {/* PHASE 8.45 — Points range filter + sort dropdown */}
        <div className="flex items-center gap-2">
          <span className="overline">points</span>
          <select value={filterPoints} onChange={(e) => setFilterPoints(e.target.value)} data-testid="filter-points" className="border-2 border-neutral-300 px-2 py-1 text-xs">
            <option value="">Any</option>
            <option value="0">0</option>
            <option value="1-1000">1 – 1,000</option>
            <option value="1001-2000">1,001 – 2,000</option>
            <option value="2001-3000">2,001 – 3,000</option>
            <option value="3001-4000">3,001 – 4,000</option>
            <option value="4001-5000">4,001 – 5,000</option>
            <option value="5000+">5,000+</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <span className="overline">sort</span>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} data-testid="sort-by" className="border-2 border-neutral-300 px-2 py-1 text-xs">
            <option value="highest_points">Highest → lowest points</option>
            <option value="latest_signup">Latest signup</option>
            <option value="most_recent_activity">Most recent activity</option>
            <option value="most_surveys">Most surveys completed</option>
            <option value="most_referrals">Most referrals brought in</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <span className="overline">region</span>
          <select
            value={filterRegion}
            onChange={(e) => setFilterRegion(e.target.value)}
            data-testid="filter-region"
            className="border-2 border-neutral-300 px-2 py-1 text-xs"
          >
            <option value="">Any</option>
            <option value="Punjab">Punjab</option>
            <option value="Sindh">Sindh</option>
            <option value="KPK">KPK</option>
            <option value="Balochistan">Balochistan</option>
            <option value="Islamabad Capital Territory">Islamabad Capital Territory</option>
            <option value="GB">Gilgit-Baltistan</option>
            <option value="AJK">Azad Jammu & Kashmir</option>
            <option value="Overseas">Overseas</option>
            <option value="—">— (not set)</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <span className="overline">status</span>
          {[{ v: "", l: "Any" }, { v: "active", l: "Active" }, { v: "suspended", l: "Suspended" }].map((r) => (
            <button key={r.v} onClick={() => setFilterStatus(r.v)} data-testid={`filter-status-${r.v || "any"}`} className={`px-3 py-1 text-xs border ${filterStatus === r.v ? "bg-black text-white border-black" : "border-neutral-300 hover:border-black"}`}>{r.l}</button>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="overline">profile</span>
          {[
            { v: "", l: "Any" },
            { v: "complete", l: "Complete" },
            { v: "incomplete", l: "Incomplete (has password)" },
            { v: "no_password", l: "No password set" },
            { v: "not_applicable", l: "N/A · Business" },
          ].map((r) => (
            <button key={r.v} onClick={() => setFilterProfile(r.v)} data-testid={`filter-profile-${r.v || "any"}`} className={`px-3 py-1 text-xs border ${filterProfile === r.v ? "bg-black text-white border-black" : "border-neutral-300 hover:border-black"}`}>{r.l}</button>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="overline">source</span>
          {[
            { v: "", l: "Any" },
            { v: "legacy_waitlist", l: "Waitlist" },
            { v: "cs_qr_respondent", l: "QR feedback" },
            { v: "direct", l: "Direct signup" },
            { v: "referral", l: "Referral" },
            { v: "campus_ambassador", l: "CA invite" },
          ].map((r) => (
            <button key={r.v} onClick={() => setFilterSource(r.v)} data-testid={`filter-source-${r.v || "any"}`} className={`px-3 py-1 text-xs border ${filterSource === r.v ? "bg-black text-white border-black" : "border-neutral-300 hover:border-black"}`}>{r.l}</button>
          ))}
        </div>
        <div className="ml-auto text-neutral-500 font-mono" style={{ visibility: ready ? "visible" : "hidden" }}>Showing {filtered.length} of {users.length}</div>
      </div>
      <FadeIn ready={ready}>
      {resetInfo && (
        <div className="bg-mango/10 border-2 border-mango p-5 mb-6 hard-shadow" data-testid="reset-pw-card">
          <div className="overline text-mango mb-2">TEMPORARY PASSWORD GENERATED — SHARE WITH USER</div>
          <div className="text-sm">User: <strong>{resetInfo.email}</strong></div>
          <div className="font-mono text-2xl bg-black text-white inline-block px-4 py-2 mt-3 select-all">{resetInfo.temp_password}</div>
          <div className="text-xs text-neutral-600 mt-3">Send this password to the user. They will be forced to change it on first login. <strong>This password is shown only once.</strong></div>
          <Button onClick={() => { navigator.clipboard.writeText(resetInfo.temp_password); toast.success("Copied"); }} className="rounded-none bg-black hover:bg-neutral-800 text-white mt-3 mr-2" data-testid="copy-temp-pw">Copy password</Button>
          <Button variant="outline" onClick={() => setResetInfo(null)} className="rounded-none mt-3" data-testid="dismiss-temp-pw">Dismiss</Button>
        </div>
      )}
      {/* Client ID column hides entirely when nobody has one — empty columns
          add visual noise. The moment any user gets a client_id (enterprise
          mapping), the column re-appears for everyone. */}
      {(() => null)()}
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-y-2 border-black">
            <th className="text-left py-3 px-2 overline">User</th>
            <th className="text-left py-3 px-2 overline">Role</th>
            <th className="text-left py-3 px-2 overline">Tier</th>
            <th className="text-left py-3 px-2 overline">Region</th>
            <th className="text-right py-3 px-2 overline" data-testid="th-points">Points</th>
            <th className="text-left py-3 px-2 overline">Profile</th>
            <th className="text-left py-3 px-2 overline">Source</th>
            <th className="text-left py-3 px-2 overline">Status</th>
            {users.some((u) => u.client_id) && <th className="text-left py-3 px-2 overline">Client ID</th>}
            <th className="text-right py-3 px-2 overline">Actions</th>
          </tr>
        </thead>
        <tbody>
          {paginated.map((u) => {
            const PROFILE_STYLE = {
              complete: { label: "Complete", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
              incomplete: { label: "Incomplete", cls: "bg-amber-100 text-amber-800 border-amber-300" },
              no_password: { label: "No password", cls: "bg-neutral-200 text-neutral-700 border-neutral-400" },
              not_applicable: { label: "N/A · Business", cls: "bg-sky-100 text-sky-800 border-sky-300" },
            };
            const ps = PROFILE_STYLE[u.profile_status] || PROFILE_STYLE.incomplete;
            return (
            <tr key={u.id} className={`border-b border-neutral-200 ${u.suspended ? 'bg-red-50' : ''}`} data-testid={`user-${u.id}`}>
              <td className="py-3 px-2">
                <button
                  onClick={() => setDetailUser(u)}
                  className="text-left hover:text-mango"
                  data-testid={`user-name-${u.id}`}
                  title="Click for full details"
                >
                  <div className="font-medium underline-offset-2 hover:underline">{u.name}</div>
                  <div className="text-xs text-neutral-500">{u.email}</div>
                </button>
              </td>
              <td className="py-3 px-2 font-mono text-xs">{u.role}</td>
              <td className="py-3 px-2">
                {(() => {
                  const isGL = !!u.is_growth_lead;
                  const isCA = u.is_campus_ambassador && !isGL;
                  const label = isGL ? "growth lead" : isCA ? "campus ambassador" : ((u.subscription_tier === "free" || !u.subscription_tier) ? "patron" : u.subscription_tier);
                  const cls = isGL
                    ? "bg-black text-mango border border-mango"
                    : isCA
                      ? "bg-mango text-black border border-black"
                      : u.subscription_tier === "enterprise"
                        ? "bg-mango text-white"
                        : u.subscription_tier === "subscriber"
                          ? "bg-black text-white"
                          : "bg-neutral-200";
                  return <span className={`font-mono text-xs px-2 py-0.5 ${cls}`}>{label}</span>;
                })()}
              </td>
              <td className="py-3 px-2 text-xs">
                {(() => {
                  if (u.is_overseas) return <span className="bg-mango/10 text-mango px-2 py-0.5 border border-mango">Overseas</span>;
                  const r = u.profile?.region;
                  if (!r) return <span className="text-neutral-400 italic">—</span>;
                  return <span className="text-neutral-700">{r}</span>;
                })()}
              </td>
              <td className="py-3 px-2 text-right font-mono text-xs tabular-nums" data-testid={`points-${u.id}`}>
                {(Number(u.points) || 0).toLocaleString()}
              </td>
              <td className="py-3 px-2">
                <span className={`text-xs px-2 py-0.5 border font-medium ${ps.cls}`} data-testid={`profile-status-${u.id}`}>{ps.label}</span>
              </td>
              <td className="py-3 px-2">
                {(() => {
                  // Priority: campus-ambassador channel > peer referral > legacy waitlist > direct.
                  // CA wins over referral because attribution is one-channel-per-human (the
                  // signup endpoint stamps `channel="campus_ambassador"` when both cookies
                  // are present at signup time).
                  if (u.channel === "campus_ambassador" || u.campus_ambassador_id) {
                    return <span className="text-xs px-2 py-0.5 border font-medium bg-mango/15 text-mango border-mango" data-testid={`source-${u.id}`}>CA invite</span>;
                  }
                  if (u.invited_by) {
                    return <span className="text-xs px-2 py-0.5 border font-medium bg-emerald-50 text-emerald-800 border-emerald-300" data-testid={`source-${u.id}`}>Referral</span>;
                  }
                  if (u.source === "cs_qr_respondent") {
                    return <span className="text-xs px-2 py-0.5 border font-medium bg-amber-50 text-amber-800 border-amber-300" data-testid={`source-${u.id}`}>QR — {u.source_business_name || "oodel SCORE"}</span>;
                  }
                  if (u.source === "legacy_waitlist") {
                    return <span className="text-xs px-2 py-0.5 border font-medium bg-violet-50 text-violet-800 border-violet-300" data-testid={`source-${u.id}`}>Waitlist</span>;
                  }
                  return <span className="text-xs px-2 py-0.5 border font-medium bg-blue-50 text-blue-800 border-blue-300" data-testid={`source-${u.id}`}>Direct signup</span>;
                })()}
              </td>
              <td className="py-3 px-2">{u.suspended ? <span className="text-xs text-red-700 font-bold">SUSPENDED</span> : <span className="text-xs text-green-700">active</span>}</td>
              {users.some((x) => x.client_id) && <td className="py-3 px-2 font-mono text-xs">{u.client_id || "—"}</td>}
              <td className="py-3 px-2 text-right whitespace-nowrap">
                <select value={u.is_campus_ambassador ? "campus_ambassador" : (u.subscription_tier || "free")} onChange={(e) => setTier(u, e.target.value)} className="border border-neutral-300 text-sm px-2 py-1 mr-2" data-testid={`tier-${u.id}`}>
                  <option value="free">patron</option>
                  <option value="subscriber">subscriber</option>
                  <option value="enterprise">enterprise</option>
                  <option value="campus_ambassador">campus ambassador</option>
                </select>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" data-testid={`user-actions-${u.id}`} className="text-xs">
                      Actions ▾
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-52">
                    <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setTimeout(() => setMessageUser({ id: u.id, name: u.name, email: u.email }), 0); }} data-testid={`action-email-${u.id}`}>
                      Send email
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setTimeout(() => resetPassword(u), 0); }} data-testid={`action-reset-${u.id}`}>
                      Reset password
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setTimeout(() => setLedgerUser({ id: u.id, name: u.name, email: u.email }), 0); }} data-testid={`action-ledger-${u.id}`}>
                      View points ledger
                    </DropdownMenuItem>
                    {impersonationEnabled && u.role !== "admin" && (
                      <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setTimeout(() => impersonate(u), 0); }} data-testid={`action-impersonate-${u.id}`}>
                        Impersonate (30-min)
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setTimeout(() => toggleSuspend(u), 0); }} data-testid={`action-suspend-${u.id}`}>
                      {u.suspended ? "Unsuspend account" : "Suspend account"}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setTimeout(() => deleteUser(u), 0); }} className="text-red-600 focus:text-red-700 focus:bg-red-50" data-testid={`action-delete-${u.id}`}>
                      Delete user
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </td>
            </tr>
            );
          })}
          {filtered.length === 0 && (
            <tr><td colSpan={users.some((x) => x.client_id) ? 9 : 8} className="py-10 text-center text-neutral-500 italic">No users match your filters.</td></tr>
          )}
        </tbody>
      </table>
      {filtered.length > 0 && (
        <div className="flex items-center justify-between gap-3 mt-4 flex-wrap" data-testid="users-pagination">
          <div className="text-xs text-neutral-500 font-mono">
            Page {pageSafe} of {totalPages} · rows {(pageSafe - 1) * PAGE_SIZE + 1}–{Math.min(pageSafe * PAGE_SIZE, filtered.length)} of {filtered.length}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={pageSafe <= 1}
              className="px-3 py-1.5 text-xs font-bold border-2 border-black disabled:opacity-30 disabled:cursor-not-allowed hover:bg-black hover:text-white transition-colors"
              data-testid="users-page-prev"
            >← Prev</button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={pageSafe >= totalPages}
              className="px-3 py-1.5 text-xs font-bold border-2 border-black disabled:opacity-30 disabled:cursor-not-allowed hover:bg-black hover:text-white transition-colors"
              data-testid="users-page-next"
            >Next →</button>
          </div>
        </div>
      )}
      {ledgerUser && <AdminUserLedgerDialog user={ledgerUser} onClose={() => setLedgerUser(null)} />}
      {messageUser && <AdminSendEmailDialog user={messageUser} onClose={() => setMessageUser(null)} />}
      {detailUser && <AdminUserDetailDialog user={detailUser} onClose={() => setDetailUser(null)} onChanged={(updated) => {
        // Keep the parent's users list AND the currently-open dialog in sync
        // when the dialog mutates a row (FP grant/revoke, suspend, etc.).
        // Without this, closing + reopening a user shows the pre-mutation
        // values because the dialog reads from the stale `users` array.
        setUsers((prev) => prev.map((u) => u.id === updated.id ? { ...u, ...updated } : u));
        setDetailUser((prev) => prev ? { ...prev, ...updated } : prev);
      }} onLedger={() => { setLedgerUser({ id: detailUser.id, name: detailUser.name, email: detailUser.email }); setDetailUser(null); }} />}
      </FadeIn>
        </>
    </div>
  );
}

// Detail modal — shows everything we know about a user in a single readable card.
function AdminSendEmailDialog({ user, onClose }) {
  // Quick-pick templates — admin can edit freely after picking. The template
  // also drives the `warn_reason` field on the user record when "mark as
  // warned" is ON, so downstream trust-score logic can categorise the warning.
  const TEMPLATES = [
    { key: "name_verification", label: "Name verification",
      subject: "Please update your name on oodel.",
      body: `We noticed your profile name doesn't look like a real name. To remain eligible for rewards and raffles, oodel reserves the right to verify identity at the time of payout. Accounts that can't be verified will be suspended.\n\nPlease log in and update your full real name within 14 days.\n\nIf this is your real name, please reply to this email so we can update our records.` },
    { key: "phone_missing", label: "Phone number missing",
      subject: "One step away from full eligibility",
      body: `To qualify for rewards and raffles, please add and verify a working phone number on your profile.\n\nThis takes less than a minute — just open the profile page and click "Add phone".` },
    { key: "profile_incomplete", label: "Profile incomplete",
      subject: "Complete your profile to unlock surveys",
      body: `You're almost there! Please finish your profile so we can match you with the right surveys and reward you accurately.\n\nLog in and head to the profile page — it'll take 3 minutes.` },
    { key: "thanks_active", label: "Thanks (active member)",
      subject: "Thank you for being an active panelist",
      body: `Just a quick note to say thank you for participating regularly. Your responses make a real difference to the research published on oodel.\n\nKeep an eye on your dashboard for upcoming surveys.` },
    { key: "custom", label: "Custom", subject: "", body: "" },
  ];
  const [tplKey, setTplKey] = useState("name_verification");
  const [subject, setSubject] = useState(TEMPLATES[0].subject);
  const [body, setBody] = useState(TEMPLATES[0].body);
  const [createNotification, setCreateNotification] = useState(true);
  const [markWarned, setMarkWarned] = useState(true);
  const [sending, setSending] = useState(false);
  const pickTemplate = (k) => {
    const t = TEMPLATES.find((x) => x.key === k);
    if (!t) return;
    setTplKey(k);
    setSubject(t.subject);
    setBody(t.body);
    // The "thanks" template should NOT mark the user as warned by default.
    setMarkWarned(k !== "thanks_active" && k !== "custom");
  };
  // Build the preview HTML inline — must mirror the backend template exactly
  // (so what you see is what arrives in the inbox).
  const previewHtml = (() => {
    const safe = body.split("\n\n").filter((p) => p.trim()).map((para) =>
      `<p style='margin:0 0 14px 0;line-height:1.55;'>${para.replace(/\n/g, "<br/>")}</p>`
    ).join("");
    return `<!doctype html><html><body style='font-family:system-ui,-apple-system,Segoe UI,Helvetica,sans-serif;color:#111;background:#f5f5f5;margin:0;padding:24px;'>
<div style='max-width:560px;margin:0 auto;background:#ffffff;border:2px solid #000;'>
  <div style='background:#000;color:#fff;padding:16px 24px;font-weight:900;letter-spacing:-0.02em;font-size:22px;'>oodel<span style='color:#16A34A;'>.</span></div>
  <div style='padding:24px;'>
    <p style='margin:0 0 6px 0;font-size:12px;color:#666;text-transform:uppercase;letter-spacing:0.08em;'>Hi ${user.name || "there"},</p>
    <h2 style='margin:0 0 18px 0;font-size:22px;font-weight:900;letter-spacing:-0.01em;'>${subject || "(no subject)"}</h2>
    ${safe}
    <p style='margin:28px 0 0 0;font-size:13px;color:#666;'>— The oodel. team</p>
  </div>
</div></body></html>`;
  })();
  const send = async () => {
    if (!subject.trim() || !body.trim()) {
      toast.error("Subject and body are required.");
      return;
    }
    if (!window.confirm(`Send this email to ${user.name || user.email}?`)) return;
    setSending(true);
    try {
      await api.post(`/admin/users/${user.id}/message`, {
        subject, body_markdown: body,
        create_notification: createNotification,
        mark_warned: markWarned,
        template_key: tplKey,
      });
      toast.success("Email queued for delivery");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send");
    } finally {
      setSending(false);
    }
  };
  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[95vh] overflow-hidden flex flex-col" data-testid="send-email-dialog">
        <DialogHeader>
          <DialogTitle className="font-black text-2xl tracking-tighter">Send email · {user.name || user.email}</DialogTitle>
        </DialogHeader>
        <div className="grid md:grid-cols-2 gap-4 overflow-y-auto pr-1">
          {/* LEFT — compose */}
          <div className="space-y-3">
            <div>
              <div className="overline text-[10px] text-neutral-500 mb-1">Template</div>
              <select value={tplKey} onChange={(e) => pickTemplate(e.target.value)} className="w-full border border-neutral-300 px-2 py-1.5 text-sm" data-testid="email-template-picker">
                {TEMPLATES.map((t) => (<option key={t.key} value={t.key}>{t.label}</option>))}
              </select>
            </div>
            <div>
              <div className="overline text-[10px] text-neutral-500 mb-1">Subject</div>
              <input value={subject} onChange={(e) => setSubject(e.target.value)} className="w-full border border-neutral-300 px-2 py-1.5 text-sm" data-testid="email-subject" />
            </div>
            <div>
              <div className="overline text-[10px] text-neutral-500 mb-1">Body (plain text · use blank lines to start a new paragraph)</div>
              <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={12} className="w-full border border-neutral-300 px-2 py-1.5 text-sm font-mono leading-relaxed" data-testid="email-body" />
            </div>
            <div className="space-y-2 pt-2 border-t border-neutral-200">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={createNotification} onChange={(e) => setCreateNotification(e.target.checked)} data-testid="email-also-notification" />
                <span>Also create an in-app notification (bell icon)</span>
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={markWarned} onChange={(e) => setMarkWarned(e.target.checked)} data-testid="email-mark-warned" />
                <span>Mark account as <b>warned</b> (used by trust-score system)</span>
              </label>
            </div>
            <div className="text-[10px] text-neutral-400 italic">Will be delivered via Resend (no immediate send if Resend isn't configured — the row sits in <code>email_outbox</code> until the worker picks it up).</div>
          </div>
          {/* RIGHT — preview */}
          <div className="border-l border-neutral-200 md:pl-4">
            <div className="overline text-[10px] text-neutral-500 mb-2">Preview · arrives as ↓</div>
            <iframe
              title="Email preview"
              srcDoc={previewHtml}
              className="w-full h-[480px] border border-neutral-300 bg-neutral-100"
              data-testid="email-preview"
            />
            <div className="mt-2 text-[11px] text-neutral-500">
              <div>To: <b>{user.email}</b></div>
              <div>From: <b>oodel. (admin)</b></div>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 pt-3 border-t border-neutral-200">
          <Button onClick={onClose} variant="ghost" disabled={sending}>Cancel</Button>
          <Button onClick={send} disabled={sending} className="bg-mango hover:bg-mango/90 text-black border-2 border-black" data-testid="email-send-btn">
            {sending ? "Sending…" : "Send →"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Detail modal — shows everything we know about a user in a single readable card.
function AdminUserDetailDialog({ user, onClose, onLedger, onChanged }) {
  const profile = user.profile || {};
  const ls = profile.lifestyle || {};
  const [isFP, setIsFP] = useState(!!user.is_founding_patron);
  const [fpNumber, setFpNumber] = useState(user.founding_patron_number || null);
  const [fpBusy, setFpBusy] = useState(false);
  // DNA-driven profile render: pull the live field list so every lifestyle
  // key the patron filled is shown, grouped by section (matches what the
  // patron sees on their /panelist/profile page). Future-proof: when admin
  // adds a new DNA field via CMS, it auto-appears here without code change.
  const [dnaFields, setDnaFields] = useState(() => api.getCached("/admin/profile-dna/public") || []);
  useEffect(() => {
    api.get("/admin/profile-dna/public").then((r) => setDnaFields(r.data || [])).catch(() => {});
  }, []);
  // Group DNA fields by section, preserving the order admin set. Hidden fields
  // (admin-disabled in CMS) are excluded — they don't have data either.
  // is_core: true rows are also skipped here — they're already rendered
  // under the hardcoded RESIDENCY + CORE DEMOGRAPHICS sections above, so
  // including them again would duplicate (e.g. show Region twice).
  const dnaSections = (() => {
    const order = [];
    const map = new Map();
    for (const f of dnaFields) {
      if (f.hidden) continue;
      if (f.is_core) continue;
      const sec = f.section || "Custom";
      if (!map.has(sec)) { map.set(sec, []); order.push(sec); }
      map.get(sec).push(f);
    }
    return order.map((s) => [s, map.get(s)]);
  })();
  // Format a single lifestyle answer for display. Arrays → comma-list.
  // Numbers → string. Empty/null → em-dash via Row component.
  const formatVal = (v) => {
    if (Array.isArray(v)) return v.length ? v.join(", ") : "";
    if (v === false) return "No";
    if (v === true) return "Yes";
    return v;
  };
  const toggleFP = async () => {
    const next = !isFP;
    if (!window.confirm(next
      ? `Grant Founding Patron status to ${user.name}?\n\nThey will get the FP badge, the survey-points multiplier, and any anniversary bonus you've enabled. A new FP slot # is assigned if they don't already have one.`
      : `Revoke Founding Patron status from ${user.name}?\n\nTheir FP slot # is preserved so re-granting later restores the original number.`)) return;
    setFpBusy(true);
    try {
      const { data } = await api.put(`/admin/users/${user.id}/founding-patron`, { is_founding_patron: next });
      setIsFP(!!data.is_founding_patron);
      setFpNumber(data.founding_patron_number || null);
      // Sync the parent's users list + the dialog's source-of-truth user prop
      // so re-opening the same user immediately shows the new badge state
      // (instead of flashing the stale value from the parent's cached list).
      if (onChanged) onChanged(data);
      toast.success(next ? `${user.name} is now a Founding Patron` : `Founding Patron status revoked`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setFpBusy(false); }
  };
  const Row = ({ label, value }) => (
    <div className="grid grid-cols-3 gap-3 py-1.5 border-b border-neutral-100 text-sm">
      <div className="overline text-neutral-500">{label}</div>
      <div className="col-span-2 font-mono text-xs break-all">{value === undefined || value === null || value === "" ? <span className="text-neutral-400">—</span> : String(value)}</div>
    </div>
  );
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="user-detail-modal">
      <div className="bg-white max-w-3xl w-full p-6 border-2 border-black hard-shadow max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="border-b-2 border-black pb-4 mb-4 flex items-start justify-between gap-3">
          <div>
            <div className="overline mb-1">USER DETAIL</div>
            <h2 className="text-3xl font-black tracking-tighter">{user.name}</h2>
            <p className="text-sm text-neutral-600 mt-1">{user.email} · <span className="font-mono">{user.role}</span></p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button onClick={onLedger} className="rounded-none bg-mango hover:bg-mango/90 text-white" data-testid="open-ledger-from-detail">Points ledger →</Button>
            <Button variant="outline" onClick={onClose} className="rounded-none" data-testid="close-detail">Close</Button>
          </div>
        </div>

        <section className="mb-5">
          <div className="overline mb-2 text-mango">ACCOUNT</div>
          <Row label="User ID" value={user.id} />
          <Row label="Created" value={formatDate(user.created_at)} />
          <Row label="Tier" value={user.subscription_tier || "free"} />
          <Row label="Client ID" value={user.client_id} />
          <Row label="Status" value={user.suspended ? `SUSPENDED — ${user.suspend_reason || "no reason"}` : "active"} />
          <div className="grid grid-cols-3 gap-3 py-1.5 border-b border-neutral-100 text-sm items-center" data-testid="fp-row">
            <div className="overline text-neutral-500">Founding Patron</div>
            <div className="col-span-2 flex items-center gap-3 flex-wrap">
              <span className={`text-xs font-mono px-2 py-0.5 border ${isFP ? "bg-mango/10 border-mango text-mango" : "border-neutral-300 text-neutral-500"}`}>
                {isFP ? `✦ FOUNDING PATRON${fpNumber ? ` · #${fpNumber}` : ""}` : "Not a Founding Patron"}
              </span>
              <Button
                onClick={toggleFP}
                disabled={fpBusy}
                variant={isFP ? "outline" : "default"}
                size="sm"
                className={`rounded-none text-xs ${isFP ? "" : "bg-mango hover:bg-mango/90 text-white"}`}
                data-testid="toggle-fp"
              >
                {fpBusy ? "Saving…" : isFP ? "Revoke" : "Grant FP status"}
              </Button>
            </div>
          </div>
          <Row label="Points balance" value={user.points} />
          <Row label="Email verified" value={user.email_verified ? "Yes" : "No"} />
          <Row label="Phone" value={user.phone_number} />
          <Row label="Phone verified" value={user.phone_verified ? "Yes" : "No"} />
        </section>

        <section className="mb-5">
          <div className="overline mb-2 text-mango">RESIDENCY</div>
          <Row label="Country" value={user.country_of_residence || profile.country || "Pakistan"} />
          <Row label="Is overseas" value={user.is_overseas ? "Yes" : "No"} />
          <Row label="Detected country (IP)" value={user.detected_country} />
          <Row label="Region" value={profile.region} />
          <Row label="City" value={profile.city} />
        </section>

        {profile.profile_complete && (
          <section className="mb-5">
            <div className="overline mb-2 text-mango">CORE DEMOGRAPHICS</div>
            <Row label="DOB" value={profile.dob} />
            <Row label="Age band" value={profile.age_band} />
            <Row label="Gender" value={profile.gender} />
            <Row label="SEC" value={profile.sec} />
            <Row label="Sectors of interest" value={(profile.sectors_of_interest || []).join(", ")} />
          </section>
        )}

        {/* Full DNA-driven profile render — every lifestyle field admin defined
            in Site Content → Profile DNA shows here, section-grouped. Empty
            sections (where every field is hidden or unanswered) collapse out. */}
        {profile.profile_complete && dnaSections.map(([section, fields]) => {
          const visibleFields = fields.filter((f) => {
            const v = ls[f.key];
            // Show even if blank so admin can see "this field wasn't answered" — but skip kind=cnic when hidden.
            return v !== undefined || !f.hidden;
          });
          if (!visibleFields.length) return null;
          return (
            <section key={section} className="mb-5" data-testid={`detail-dna-section-${section.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="overline mb-2 text-mango">{section.toUpperCase()}</div>
              {visibleFields.map((f) => (
                <Row key={f.key} label={f.label || f.key} value={formatVal(ls[f.key])} />
              ))}
            </section>
          );
        })}

        {!profile.profile_complete && (
          <div className="bg-amber-50 border-l-4 border-amber-400 p-3 text-xs text-amber-900 mb-5">
            This user has not yet completed their profile.
          </div>
        )}
      </div>
    </div>
  );
}

// Admin per-user points ledger viewer. Mirrors the patron-facing /panelist/points
// view but with full identity + 2000-row history. Used for support / audit.
function AdminUserLedgerDialog({ user, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/admin/users/${user.id}/points-history`)
      .then((r) => setData(r.data))
      .catch(() => setData({ rows: [], totals: {} }));
  }, [user.id]);
  const fmtDate = (iso) => formatDateTime(iso);
  const exportCsv = () => {
    if (!data?.rows?.length) return;
    const header = ["date", "delta", "reason", "balance_after", "ref_collection", "ref_id", "note"];
    const lines = [header.join(",")].concat(
      data.rows.map((r) => [
        r.created_at, r.delta, r.reason_label, r.balance_after,
        r.ref_collection || "", r.ref_id || "",
        '"' + (r.note || "").replace(/"/g, '""') + '"',
      ].join(","))
    );
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `points-ledger-${user.email}.csv`; a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none border-2 border-black p-0 max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="admin-user-ledger">
        <DialogHeader className="bg-black text-white p-5">
          <DialogTitle className="text-2xl font-black tracking-tighter">{user.name}'s points ledger</DialogTitle>
          <div className="text-xs text-neutral-400 font-mono">{user.email} · {user.id}</div>
        </DialogHeader>
        {!data ? (
          <div className="p-8 text-center text-neutral-500">Loading ledger…</div>
        ) : (
          <>
            <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-3 border-b-2 border-black">
              <Tile2 label="CURRENT" value={(data.user?.points ?? 0).toLocaleString()} highlight />
              <Tile2 label="LIFETIME EARNED" value={(data.totals?.lifetime_earned ?? 0).toLocaleString()} />
              <Tile2 label="LIFETIME SPENT" value={(data.totals?.lifetime_spent ?? 0).toLocaleString()} />
              <Tile2 label="SURVEYS · WINS" value={`${data.totals?.n_surveys_completed ?? 0} · ${data.totals?.n_raffle_wins ?? 0}`} />
            </div>
            <div className="p-3 flex justify-between items-center bg-neutral-50 border-b border-neutral-300">
              <div className="text-xs text-neutral-600">{data.rows.length} ledger rows · newest first</div>
              <Button onClick={exportCsv} variant="outline" size="sm" className="rounded-none text-xs" data-testid="export-ledger-csv">⬇ Export CSV</Button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-white sticky top-0 border-b-2 border-black">
                  <tr>
                    <th className="text-left px-3 py-2 overline text-[10px]">WHEN</th>
                    <th className="text-left px-3 py-2 overline text-[10px]">EVENT</th>
                    <th className="text-left px-3 py-2 overline text-[10px]">DETAILS</th>
                    <th className="text-right px-3 py-2 overline text-[10px]">Δ</th>
                    <th className="text-right px-3 py-2 overline text-[10px]">BAL</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.length === 0 ? (
                    <tr><td colSpan="5" className="p-6 text-center text-neutral-500 text-xs">No ledger rows yet.</td></tr>
                  ) : data.rows.map((r) => (
                    <tr key={r.id} className={`border-b border-neutral-100 hover:bg-neutral-50 ${r.voided ? "bg-neutral-100 text-neutral-400" : ""}`}>
                      <td className="px-3 py-2 font-mono text-[11px] text-neutral-500 whitespace-nowrap">{fmtDate(r.created_at)}</td>
                      <td className={`px-3 py-2 font-medium text-xs ${r.voided ? "line-through" : ""}`}>
                        {r.reason_label}
                        {r.voided && <span className="ml-2 not-italic no-underline inline-block bg-red-600 text-white text-[9px] font-bold tracking-widest px-1.5 py-0.5">VOIDED</span>}
                      </td>
                      <td className="px-3 py-2 text-xs text-neutral-600">
                        <span className={r.voided ? "line-through" : ""}>{r.note || "—"}</span>
                        {r.voided && r.void_reason && <span className="ml-1 not-italic no-underline text-[10px] text-red-700 font-bold">· {r.void_reason}</span>}
                        {r.ref_collection && <span className="ml-1 font-mono text-[10px] text-neutral-400">({r.ref_collection}:{(r.ref_id || "").slice(0, 8)})</span>}
                      </td>
                      <td className={`px-3 py-2 text-right font-mono font-bold ${r.voided ? "line-through text-neutral-400" : r.delta > 0 ? "text-emerald-600" : "text-red-600"}`}>{r.delta > 0 ? "+" : ""}{r.delta.toLocaleString()}</td>
                      <td className={`px-3 py-2 text-right font-mono text-neutral-700 ${r.voided ? "line-through text-neutral-400" : ""}`}>{(r.balance_after ?? 0).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Tile2({ label, value, highlight }) {
  return (
    <div className={`border-2 border-black p-3 ${highlight ? "bg-mango text-black" : "bg-white"}`}>
      <div className="overline text-[10px]">{label}</div>
      <div className="font-mono text-xl md:text-2xl font-black mt-1">{value}</div>
    </div>
  );
}

// =================== BUSINESS CUSTOMERS ===================
const INTEREST_OPTIONS = [
  ["national_metrics", "National metrics"],
  ["niche_surveys", "Niche surveys"],
  ["brand_tracking", "Brand tracking"],
  ["political_polling", "Political polling"],
  ["custom_research", "Custom research"],
];

export function AdminCustomers() {
  const [items, setItems] = useState(() => api.getCached("/admin/customers") || []);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [pwInfo, setPwInfo] = useState(null);
  const [pmCustomer, setPmCustomer] = useState(null);  // open PM-assignment modal for this customer
  const [loading, setLoading] = useState(() => !api.getCached("/admin/customers"));

  const load = () => api.get("/admin/customers").then((r) => setItems(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const remove = async (id) => { if (window.confirm("Delete this customer account?")) { await api.delete(`/admin/customers/${id}`); load(); } };
  const resetPassword = async (c) => {
    if (!window.confirm(`Generate a NEW temporary password for ${c.email}?\nThe customer will be forced to change it on next login.`)) return;
    try {
      const { data } = await api.post(`/admin/customers/${c.id}/reset-password`);
      setPwInfo({ customer: c, password: data.temp_password, kind: "reset" });
      toast.success("New temporary password generated.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    }
  };

  return (
    <div>
      <div className="border-b-2 border-black pb-4 mb-6 flex justify-between items-end">
        <div><div className="overline">BUSINESS CUSTOMERS</div><h1 className="text-4xl font-black tracking-tighter">Clients & subscriptions</h1></div>
        <Button data-testid="new-customer-btn" onClick={() => { setEditing(null); setOpen(true); }} className="bg-mango hover:bg-mango/90 text-white border-2 border-black rounded-none"><Plus size={16} className="mr-1" /> Add customer</Button>
      </div>

      {open && <CustomerForm editing={editing} onClose={(created) => { setOpen(false); setEditing(null); load(); if (created?.temp_password) setPwInfo({ customer: created.customer, password: created.temp_password, kind: "new" }); }} />}

      {pwInfo && (
        <div className="bg-mango/10 border-2 border-mango p-5 mb-6 hard-shadow" data-testid="customer-temp-pw">
          <div className="overline text-mango mb-2">{pwInfo.kind === "new" ? "ACCOUNT CREATED — TEMPORARY PASSWORD" : "PASSWORD RESET — NEW TEMPORARY PASSWORD"}</div>
          <div className="text-sm">Customer: <strong>{pwInfo.customer.name || pwInfo.customer.contact_name}</strong> ({pwInfo.customer.email || pwInfo.customer.contact_email})</div>
          <div className="font-mono text-2xl bg-black text-white inline-block px-4 py-2 mt-3 select-all">{pwInfo.password}</div>
          <div className="text-xs text-neutral-600 mt-3">Send this password to the customer. They will be forced to change it on first login. <strong>Shown only once.</strong></div>
          <Button onClick={() => { navigator.clipboard.writeText(pwInfo.password); toast.success("Copied"); }} className="rounded-none bg-black hover:bg-neutral-800 text-white mt-3 mr-2">Copy password</Button>
          <Button variant="outline" onClick={() => setPwInfo(null)} className="rounded-none mt-3">Dismiss</Button>
        </div>
      )}

      <table className="w-full border-collapse">
        <thead><tr className="border-y-2 border-black">
          <th className="text-left py-3 px-2 overline">Customer</th>
          <th className="text-left py-3 px-2 overline">Tier</th>
          <th className="text-left py-3 px-2 overline">Sector</th>
          <th className="text-left py-3 px-2 overline">Interests</th>
          <th className="text-left py-3 px-2 overline">Status</th>
          <th className="text-right py-3 px-2 overline">Actions</th>
        </tr></thead>
        <tbody>
          {items.map((c) => (
            <tr key={c.id} className="border-b border-neutral-200" data-testid={`customer-${c.id}`}>
              <td className="py-3 px-2">
                <div className="font-bold">{c.name}</div>
                <div className="text-xs text-neutral-500">{c.email} · {c.company || "—"}</div>
              </td>
              <td className="py-3 px-2"><span className={`font-mono text-xs px-2 py-0.5 ${c.subscription_tier === 'enterprise' ? 'bg-mango text-white' : 'bg-black text-white'}`}>{c.subscription_tier}</span>{c.client_id && <div className="font-mono text-xs text-neutral-500 mt-1">{c.client_id}</div>}</td>
              <td className="py-3 px-2 text-xs"><span className="font-mono uppercase tracking-wider text-neutral-700">{c.sector_key || "other"}</span></td>
              <td className="py-3 px-2 text-xs">{(c.interests || []).map((i) => i.replace(/_/g, ' ')).join(', ') || "—"}</td>
              <td className="py-3 px-2"><span className={`text-xs ${c.must_change_password ? 'text-yellow-700' : 'text-green-700'}`}>{c.must_change_password ? "must change pw" : "active"}</span></td>
              <td className="py-3 px-2 text-right whitespace-nowrap">
                <Button variant="ghost" size="sm" onClick={() => setPmCustomer(c)} title="Assign PMs" data-testid={`assign-pms-${c.id}`}>👤 PMs ({(c.account_manager_ids || []).length})</Button>
                <Button data-testid={`reset-${c.id}`} variant="ghost" size="sm" onClick={() => resetPassword(c)} title="Reset password">🔑 Reset PW</Button>
                <Button variant="ghost" size="sm" onClick={() => { setEditing(c); setOpen(true); }}><PencilSimple size={16} /></Button>
                <Button variant="ghost" size="sm" onClick={() => remove(c.id)}><Trash size={16} /></Button>
              </td>
            </tr>
          ))}
          {!loading && items.length === 0 && <tr><td colSpan={5} className="py-12 text-center text-neutral-400">No business customers yet. Click "Add customer" to onboard one.</td></tr>}
        </tbody>
      </table>

      {pmCustomer && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setPmCustomer(null)} data-testid="pm-assign-modal">
          <div className="bg-white max-w-md w-full p-6 border-2 border-black hard-shadow" onClick={(e) => e.stopPropagation()}>
            <div className="overline mb-2">PM ASSIGNMENT</div>
            <h3 className="text-xl font-bold mb-1">{pmCustomer.company}</h3>
            <div className="text-xs text-neutral-500 mb-5">{pmCustomer.email}</div>
            <PMAssignmentWidget customerId={pmCustomer.id} currentIds={pmCustomer.account_manager_ids || []} onChange={() => { setPmCustomer(null); load(); }} />
          </div>
        </div>
      )}
    </div>
  );
}

function CustomerForm({ editing, onClose }) {
  const [form, setForm] = useState(editing ? {
    company: editing.company || "", contact_name: editing.name || "",
    contact_email: editing.email || "", phone: editing.phone || "",
    interests: editing.interests || [], tier: editing.subscription_tier || "subscriber",
    client_id: editing.client_id || "", notes: editing.notes || "",
    annual_fee: editing.annual_fee || 0, per_survey_fee: editing.per_survey_fee || 0,
    payment_status: editing.payment_status || "pending",
    sector_key: editing.sector_key || "other",
  } : { company: "", contact_name: "", contact_email: "", phone: "", interests: [], tier: "subscriber", client_id: "", notes: "", annual_fee: 0, per_survey_fee: 0, payment_status: "pending", sector_key: "other" });
  const [sectorOptions, setSectorOptions] = useState([]);
  useEffect(() => {
    api.get("/sectors").then((r) => setSectorOptions(r.data || [])).catch(() => {});
  }, []);

  const toggleI = (k) => setForm({ ...form, interests: form.interests.includes(k) ? form.interests.filter((x) => x !== k) : [...form.interests, k] });

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/admin/customers/${editing.id}`, form);
        toast.success("Customer updated");
        onClose();
      } else {
        const { data } = await api.post("/admin/customers", form);
        toast.success("Customer created. Temporary password generated.");
        onClose({ customer: data.customer, temp_password: data.temp_password });
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  return (
    <form onSubmit={submit} className="bg-white border-2 border-black p-6 mb-6 space-y-4" data-testid="customer-form">
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="overline">Company *</Label><Input data-testid="cf-company" required value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="rounded-none" /></div>
        <div><Label className="overline">Contact name *</Label><Input data-testid="cf-contact" required value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="rounded-none" /></div>
        <div><Label className="overline">Login email *</Label><Input data-testid="cf-email" type="email" required value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="rounded-none" disabled={!!editing} /></div>
        <div><Label className="overline">Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-none" /></div>
        <div><Label className="overline">Subscription tier *</Label>
          <select value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value })} className="border border-neutral-300 px-3 py-2 w-full rounded-none">
            <option value="subscriber">Subscriber</option><option value="enterprise">Enterprise</option>
          </select>
        </div>
        {form.tier === "enterprise" && (
          <div><Label className="overline">Client ID *</Label><Input required value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} placeholder="e.g., ACME-001" className="rounded-none" /></div>
        )}
        <div><Label className="overline">Annual fee (USD)</Label><Input type="number" min="0" value={form.annual_fee} onChange={(e) => setForm({ ...form, annual_fee: Number(e.target.value) })} className="rounded-none" /></div>
        <div><Label className="overline">Per-survey fee (USD)</Label><Input type="number" min="0" value={form.per_survey_fee} onChange={(e) => setForm({ ...form, per_survey_fee: Number(e.target.value) })} className="rounded-none" /></div>
        <div><Label className="overline">Payment status</Label>
          <select value={form.payment_status} onChange={(e) => setForm({ ...form, payment_status: e.target.value })} className="border border-neutral-300 px-3 py-2 w-full rounded-none">
            <option value="paid">Paid</option><option value="pending">Pending</option><option value="overdue">Overdue</option>
          </select>
        </div>
        <div data-testid="cf-sector-wrap"><Label className="overline">Sector</Label>
          <select data-testid="cf-sector" value={form.sector_key} onChange={(e) => setForm({ ...form, sector_key: e.target.value })} className="border border-neutral-300 px-3 py-2 w-full rounded-none">
            {sectorOptions.filter((s) => s.active).map((s) => (
              <option key={s.key} value={s.key}>{s.label}</option>
            ))}
            <option value="other">Other</option>
          </select>
        </div>
      </div>
      <div>
        <Label className="overline mb-2 block">Interests (drives dashboard scoping)</Label>
        <div className="flex flex-wrap gap-2">
          {INTEREST_OPTIONS.map(([k, l]) => (
            <button key={k} type="button" data-testid={`int-${k}`} onClick={() => toggleI(k)} className={`px-3 py-1.5 border-2 text-xs ${form.interests.includes(k) ? 'bg-black text-white border-black' : 'border-neutral-300 hover:border-black'}`}>{l}</button>
          ))}
        </div>
      </div>
      <div><Label className="overline">Internal notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-none" rows={2} /></div>
      <div className="flex gap-2 pt-3 border-t">
        <Button data-testid="cf-submit" type="submit" className="bg-black hover:bg-neutral-800 rounded-none">{editing ? "Save changes" : "Create + send invitation"}</Button>
        <Button type="button" variant="ghost" onClick={() => onClose()} className="rounded-none">Cancel</Button>
      </div>
    </form>
  );
}


// =================== SITE CONTENT (CMS for public copy) ===================
// Top-level tab classification — mirrors the admin sidebar's own feature
// groups 1:1 (Homepage/Marketing, Public Sentiment, Customer Sentiment,
// Survey Content, Patron Experience, Incentives, Navigation, Global) so a
// feature's page-copy always lives under the SAME name as its sidebar tools.
// Maps every group name → one of these tabs so the large field set is
// browsable without an infinite scroll. To add a new group:
//   1. Add the `{ group, keys }` entry to SITE_CONTENT_KEYS below.
//   2. Add the group name to one of the arrays here.
// A group whose name is NOT in any array falls through to "Other" so nothing
// gets silently dropped.
const SITE_CONTENT_TABS = ["Homepage & Marketing", "Public Sentiment", "Customer Sentiment / SCORE", "Survey Content & Waves", "Patron Experience", "Incentives & Growth", "Navigation & Branding", "Global Settings"];
const SITE_CONTENT_TAB_GROUPS = {
  "Homepage & Marketing": [
    "Homepage — hero & stat tiles (leave any number blank to hide that tile)",
    "Homepage logic",
    "Homepage — Founding Patron CTA (waitlist signup band)",
    "Home — Methodology strip",
    "SEO — global defaults",
    "SEO — per-page title + description",
    "Why oodel — For Patrons page (/for-patrons)",
    "Why oodel — For Businesses page (/for-businesses)",
    "Why oodel — Methodology page (/methodology)",
    "Subscriber / Enterprise dashboard copy",
    "Sign-in page (left sidebar)",
    "Join-the-panel page (left sidebar)",
    "Footer — legal links (toggle each on/off)",
  ],
  "Public Sentiment": [
    "Public Sentiment — page text",
    "Public Sentiment — toggles",
  ],
  "Customer Sentiment / SCORE": [
    "Customer Sentiment — page text",
    "Customer Sentiment — Invoice text",
    "Document Signing — signer page copy",
    "Business Dashboard — page text (English only — this dashboard is business/admin-facing, not translated to Urdu)",
  ],
  "Survey Content & Waves": [
    "Survey Content & Waves — toggles",
    "Chart rendering",
    "Survey quality & fraud",
    "Survey Intro Defaults",
  ],
  "Patron Experience": [
    "Patron dashboard (/panelist)",
    "Patron profile page (/panelist/profile)",
    "Patron Points History page (/panelist/points)",
    "Panelist settings",
    "Mobile App Onboarding (3-slide welcome — Capacitor app only, web is unaffected)",
  ],
  "Incentives & Growth": [
    "Incentive engine (Rewards · Raffle · Off)",
    "Waitlist & founding patron",
    "Patron signup page (/register)",
    "Patron onboarding rewards",
    "Founding Patron lifetime perks",
    "Referral program",
    "Personal invite landing page (/invite/:code)",
    "Campus Ambassador landing page (/cap/:slug)",
    "Raffle settings",
    "Raffle fairness",
    "Reward defaults",
  ],
  "Navigation & Branding": [
    "Navigation",
    "Public site navigation (on/off)",
    "Public site navigation — rename menu labels",
    "Patron App — Menu visibility",
    "Footer",
    "App Store Buttons (footer)",
  ],
  "Global Settings": [
    "Urdu localization — master toggle",
    "Google Analytics (GA4)",
    "Phone OTP & verification",
    "Admin tools (rarely needed)",
  ],
};

const SITE_CONTENT_KEYS = [
  { group: "Incentive engine (Rewards · Raffle · Off)", keys: [
    { key: "incentive_mode", label: "Active mode — only one can be on at a time", type: "radio", options: [
      { value: "rewards", label: "Rewards (points + vouchers)" },
      { value: "raffle",  label: "Raffle (points + monthly draw)" },
      { value: "off",     label: "Off (no incentives)" },
    ]},
  ]},
  { group: "Waitlist & founding patron", keys: [
    { key: "waitlist_open", label: "Accept new waitlist signups on the homepage (toggle OFF to switch to direct signup with email OTP)", type: "toggle" },
    { key: "founding_patron_cap", label: "Number of patrons who get the ✦ Founding Patron badge. Bump anytime to enlarge the cohort.", type: "text", placeholder: "500" },
    { key: "waitlist_display_override", label: "Marketing baseline for the public \"X / cap joined\" counter on the homepage. Set to 0 to show the real confirmed signup count. Set any positive number (e.g. 100) and any new signups that happen AFTER are added on top — e.g. set 100 → next signup shows 101. Set 200 later → next signup shows 201. Never affects internal analytics.", type: "text", placeholder: "0" },
    { key: "wave_extra_cohort_emails_enabled", label: "Show the '📨 Incomplete' + '📨 Waitlist' follow-up buttons on each active wave row. Turn OFF once every legacy waitlist user has converted — the buttons (and their server endpoints) silently disable.", type: "toggle" },
  ]},
  { group: "Patron signup page (/register)", keys: [
    { key: "signup_phone_disclaimer_enabled", label: "Show the small-print sentence on the signup form explaining when we'll ask for phone + OTP.", type: "toggle" },
    { key: "signup_phone_disclaimer_text", label: "The exact disclaimer copy shown under the password fields. Edit freely.", type: "longtext", placeholder: "We'll ask for your phone (verified via OTP) when you complete your profile after sign-up — keeps this first step lighter." },
  ]},
  { group: "Patron onboarding rewards", keys: [
    { key: "welcome_bonus_points", label: "Welcome bonus — points credited the first time a patron completes their profile. Awarded once per patron, idempotent.", type: "text", placeholder: "200" },
  ]},
  { group: "Founding Patron lifetime perks", keys: [
    { key: "founding_patron_point_multiplier", label: "Survey-points multiplier applied to every wave a Founding Patron completes. e.g. 1.2 = +20% bonus. Set to 1.0 to disable.", type: "text", placeholder: "1.2" },
    { key: "founding_patron_anniversary_bonus_enabled", label: "Auto-credit a loyalty bonus every year on each Founding Patron's join anniversary (checked lazily on login)", type: "toggle" },
    { key: "founding_patron_anniversary_bonus_points", label: "Annual anniversary bonus amount (in points). Sent as a silent credit + email + in-app notification.", type: "text", placeholder: "500" },
  ]},
  { group: "Referral program", keys: [
    { key: "referral_program_enabled", label: "Enable the share-a-friend referral program. When OFF: share UI is hidden from patron dashboards, /invite/:code links silently redirect to homepage, and no points are awarded on conversions. Recommended to keep OFF during the waitlist phase and turn ON once you start accepting direct signups.", type: "toggle" },
  ]},
  { group: "Patron dashboard (/panelist)", keys: [
    { key: "panelist_hero_headline", label: "Hero headline (English — top of patron dashboard). Press Enter for a line break — e.g. 'Your voice helps' on line 1, 'shape Pakistan.' on line 2, matching the reference mockup.", type: "textarea", placeholder: "Your voice helps\nshape Pakistan." },
    { key: "panelist_hero_headline_ur", label: "Hero headline — Urdu translation (shown to patrons who switch to اردو). Leave blank to fall back to the English version above.", type: "textarea", placeholder: "آپ کی رائے\nپاکستان کی تشکیل میں مدد دیتی ہے۔" },
    { key: "panelist_hero_subtext", label: "Hero sub-text (English), shown under the headline.", type: "textarea", placeholder: "Take part in surveys, read the latest findings, and earn rewards for your participation." },
    { key: "panelist_hero_subtext_ur", label: "Hero sub-text — Urdu translation. Leave blank to fall back to English.", type: "textarea", placeholder: "سروے میں حصہ لیں، تازہ ترین نتائج پڑھیں، اور اپنی شرکت پر انعامات حاصل کریں۔" },
    { key: "panelist_hero_image_url", label: "Hero illustration on the patron dashboard, shown to the right of the headline. Recommended: 900×700px or larger (square or 4:3), light/transparent background works best — it's displayed contained at up to ~380px wide.", type: "text", placeholder: "https://...png" },
    { key: "panelist_referral_image_url", label: "Illustration for the 'Invite a friend' card (replaces the default icon when set). Recommended: 400×400px square, transparent PNG works best — displayed at ~96×96px.", type: "text", placeholder: "https://...png" },
    { key: "panelist_current_wave_image_url", label: "Thumbnail shown inside the 'Current Wave' spotlight card on the patron dashboard. This is a GLOBAL image (same for every wave) — since it rarely changes, it's set once here rather than per-wave. Recommended: 400×400px square. Leave blank to hide the thumbnail.", type: "text", placeholder: "https://...png" },
    { key: "panelist_latest_article_image_url", label: "Thumbnail shown inside the 'Latest Article' spotlight card on the patron dashboard. This is a GLOBAL image (same for every article) — since it rarely changes, it's set once here rather than per-article. Recommended: 400×400px square. Leave blank to hide the thumbnail.", type: "text", placeholder: "https://...png" },
    { key: "panelist_footer_p1", label: "Confidentiality footer (English) — paragraph 1. Rendered in small font below the Available/Completed Surveys lists.", type: "textarea", placeholder: "Your survey responses are confidential…" },
    { key: "panelist_footer_p1_ur", label: "Confidentiality footer — paragraph 1, Urdu translation. Leave blank to fall back to English.", type: "textarea", placeholder: "آپ کے سروے کے جوابات خفیہ ہیں…" },
    { key: "panelist_footer_p2", label: "Confidentiality footer (English) — paragraph 2.", type: "textarea", placeholder: "We do not share your personal identity…" },
    { key: "panelist_footer_p2_ur", label: "Confidentiality footer — paragraph 2, Urdu translation. Leave blank to fall back to English.", type: "textarea", placeholder: "ہم آپ کے سروے کے جوابات کے ساتھ…" },
  ]},
  { group: "Patron profile page (/panelist/profile)", keys: [
    { key: "panelist_profile_heading", label: "Main heading shown below 'Your Profile'. Explains why we ask demographic questions.", type: "textarea", placeholder: "Answer accurately. We use this to match you with relevant surveys…" },
    { key: "panelist_profile_privacy_p1", label: "🔒 Privacy First disclaimer — paragraph 1 (small font box at the top of the profile).", type: "textarea", placeholder: "To make sure survey results reflect Pakistan accurately…" },
    { key: "panelist_profile_privacy_p2", label: "🔒 Privacy First disclaimer — paragraph 2.", type: "textarea", placeholder: "All responses are confidential and analysed only in aggregate…" },
    { key: "panelist_profile_privacy_p3", label: "🔒 Privacy First disclaimer — paragraph 3 (optional). Leave blank to hide. Use this to explain WHY a real name matters — e.g. that real names are required to claim raffle / voucher rewards.", type: "textarea", placeholder: "Why we ask for your real name: …" },
  ]},
  { group: "Admin tools (rarely needed)", keys: [
    { key: "admin_repair_orphans_button_enabled", label: "Show the 🧹 Repair orphans button on the admin Dashboard. Only useful as a one-time cleanup to remove responses from users who were deleted BEFORE the auto-cleanup fix. Going forward, every user-delete auto-cleans + recounts — so you can keep this OFF unless you're doing a historical sweep.", type: "toggle" },
    { key: "admin_impersonation_enabled", label: "🕵️ Enable user impersonation for admins. When ON, the All Users table shows a 'View as user' button on every non-admin row. Clicking grants a 30-minute scoped session as that user (red banner shows across all pages while active). Every start/end is logged in the audit trail. Keep OFF in production unless you need to investigate a specific support issue, then turn it back off.", type: "toggle" },
    { key: "users_tab_all_enabled", label: "Show the 'All users' tab on /admin/users (toggle OFF to hide).", type: "toggle" },
    { key: "users_tab_complete_enabled", label: "Show the 'Complete profile' tab on /admin/users.", type: "toggle" },
    { key: "users_tab_incomplete_enabled", label: "Show the 'Incomplete profile' tab on /admin/users (bulk-email reminder module).", type: "toggle" },
    { key: "users_tab_password_pending_enabled", label: "Show the 'Password pending' tab on /admin/users — lists legacy-waitlist patrons who haven't set a password yet. Useful while migrating the dormant base; can be hidden once everyone has set their password.", type: "toggle" },
  ]},
  { group: "Personal invite landing page (/invite/:code)", keys: [
    { key: "invite_overline", label: "Small overline above the headline", type: "text", placeholder: "✦ PERSONAL INVITE" },
    { key: "invite_headline_template", label: "Headline template — use {name} as the placeholder for the inviter's first name (which is rendered in mango colour). e.g. '{name} invited you to oodel.'", type: "text", placeholder: "{name} invited you to oodel." },
    { key: "invite_subline_waitlist_open", label: "Subline shown when waitlist is OPEN (visitor will join the waitlist).", type: "textarea", placeholder: "You'll claim a spot on the founding patron waitlist…" },
    { key: "invite_subline_waitlist_closed", label: "Subline shown when waitlist is CLOSED (visitor will create an account directly).", type: "textarea", placeholder: "Create your account, complete your profile…" },
    { key: "invite_cta_waitlist_open", label: "CTA button label — waitlist OPEN", type: "text", placeholder: "Join the waitlist →" },
    { key: "invite_cta_waitlist_closed", label: "CTA button label — waitlist CLOSED", type: "text", placeholder: "Create your account →" },
    { key: "invite_pillar_1_title", label: "Pillar 1 — title", type: "text", placeholder: "Earn points" },
    { key: "invite_pillar_1_body", label: "Pillar 1 — body", type: "textarea", placeholder: "Every survey you complete pays…" },
    { key: "invite_pillar_2_title", label: "Pillar 2 — title", type: "text", placeholder: "Stay anonymous" },
    { key: "invite_pillar_2_body", label: "Pillar 2 — body", type: "textarea", placeholder: "Your answers are aggregated…" },
    { key: "invite_pillar_3_title", label: "Pillar 3 — title", type: "text", placeholder: "Shape the data" },
    { key: "invite_pillar_3_body", label: "Pillar 3 — body", type: "textarea", placeholder: "Help build the most representative panel…" },
    { key: "invite_already_member_text", label: "Footer — 'Already a member?' prefix", type: "text", placeholder: "Already a member?" },
    { key: "invite_signin_link_text", label: "Footer — sign-in link label", type: "text", placeholder: "Sign in instead" },
    { key: "invite_code_label", label: "Footer — invite-code label", type: "text", placeholder: "Invite code:" },
    { key: "invite_invalid_title", label: "Invalid-code state — title", type: "text", placeholder: "Hmm — that invite link's expired" },
    { key: "invite_invalid_body", label: "Invalid-code state — body", type: "textarea", placeholder: "No problem. You can still join the panel directly below." },
    { key: "invite_invalid_cta", label: "Invalid-code state — CTA label", type: "text", placeholder: "Visit oodel. →" },
  ]},
  { group: "Campus Ambassador landing page (/cap/:slug)", keys: [
    { key: "cap_overline", label: "Small overline above the headline", type: "text", placeholder: "CAMPUS AMBASSADOR INVITE" },
    { key: "cap_headline_template", label: "Headline template — use {name} for the CA's first name and {campus} for their campus. e.g. '{name} brought you to oodel.'", type: "text", placeholder: "{name} brought you to oodel." },
    { key: "cap_intro_template", label: "Intro paragraph template — supports {name} and {campus} placeholders.", type: "textarea", placeholder: "{name} is a Campus Ambassador at {campus} — they're inviting you to join…" },
    { key: "cap_pillar_1_title", label: "Pillar 1 — title", type: "text", placeholder: "Earn points" },
    { key: "cap_pillar_1_body", label: "Pillar 1 — body", type: "textarea", placeholder: "Every survey you complete pays…" },
    { key: "cap_pillar_2_title", label: "Pillar 2 — title", type: "text", placeholder: "Stay anonymous" },
    { key: "cap_pillar_2_body", label: "Pillar 2 — body", type: "textarea", placeholder: "Your answers are aggregated…" },
    { key: "cap_pillar_3_title", label: "Pillar 3 — title", type: "text", placeholder: "Shape the data" },
    { key: "cap_pillar_3_body", label: "Pillar 3 — body", type: "textarea", placeholder: "Help build the most representative panel…" },
    { key: "cap_cta_label", label: "CTA button label", type: "text", placeholder: "Create your account →" },
    { key: "cap_already_member_text", label: "Footer — 'Already a member?' prefix", type: "text", placeholder: "Already a member?" },
    { key: "cap_signin_link_text", label: "Footer — sign-in link label", type: "text", placeholder: "Sign in instead" },
    { key: "cap_link_label", label: "Footer — invite-link label (e.g. 'Invite link:')", type: "text", placeholder: "Invite link:" },
  ]},
  { group: "Footer — legal links (toggle each on/off)", keys: [
    { key: "legal_privacy_enabled", label: "Show the 'Privacy notice' link in the public site footer", type: "toggle" },
    { key: "legal_terms_enabled", label: "Show the 'Terms' link in the public site footer", type: "toggle" },
  ]},
  { group: "Raffle settings", keys: [
    { key: "raffle_base_threshold", label: "Default points required to be eligible in a raffle draw", type: "text", placeholder: "5000" },
    { key: "raffle_max_entries", label: "Max entries a patron can hold (caps the 1 + draws_missed_consecutive loyalty boost)", type: "text", placeholder: "12" },
  ]},
  { group: "Homepage logic", keys: [
    { key: "homepage_charts_min_waves", label: "Minimum number of closed public waves/snapshots before the homepage 'Latest Insights' grid appears. Under this threshold, the Founding Patron CTA dominates.", type: "text", placeholder: "10" },
  ]},
  { group: "Homepage — Founding Patron CTA (waitlist signup band)", keys: [
    { key: "cta_overline_default", label: "Overline above heading (when seats are still available)", type: "text", placeholder: "✦ FOUNDING PATRON ✦" },
    { key: "cta_overline_full", label: "Overline above heading (when all founding seats are taken)", type: "text", placeholder: "✦ JOIN THE WAITLIST ✦" },
    { key: "cta_heading_lead", label: "Heading — text before the cap number (seats available)", type: "text", placeholder: "Be one of the first" },
    { key: "cta_heading_trail", label: "Heading — text after the cap number (seats available)", type: "text", placeholder: "voices defining how Pakistan thinks." },
    { key: "cta_heading_full_lead", label: "Heading — text before the cap number (seats full)", type: "text", placeholder: "The first" },
    { key: "cta_heading_full_trail", label: "Heading — text after the cap number (seats full)", type: "text", placeholder: "seats are taken. Join the next wave." },
    { key: "cta_subtitle_default", label: "Subtitle paragraph (seats available)", type: "textarea", placeholder: "Join the waitlist today — when our first wave launches, you'll be the first to take it, earn rewards, and get a Founding Patron badge for life." },
    { key: "cta_subtitle_full", label: "Subtitle paragraph (seats full)", type: "textarea", placeholder: "All Founding Patron spots are claimed. Join the regular waitlist — you'll still get early access and earn rewards." },
    { key: "cta_name_placeholder", label: "Name field placeholder", type: "text", placeholder: "Your name" },
    { key: "cta_email_placeholder", label: "Email field placeholder", type: "text", placeholder: "email@example.com" },
    { key: "cta_submit_label", label: "Submit button label", type: "text", placeholder: "Join →" },
    { key: "cta_submit_loading_label", label: "Submit button label while submitting", type: "text", placeholder: "Joining…" },
    { key: "cta_counter_suffix", label: "Text after the X / cap counter (e.g. 'joined')", type: "text", placeholder: "joined" },
    { key: "cta_perk_1", label: "Trust badge #1 (below the counter)", type: "text", placeholder: "✦ No spam, ever" },
    { key: "cta_perk_2", label: "Trust badge #2 (below the counter)", type: "text", placeholder: "✦ Free forever" },
    { key: "cta_success_new_title", label: "Success box title for first-time signups", type: "text", placeholder: "You're on the list!" },
    { key: "cta_success_new_body", label: "Success box body for first-time signups", type: "textarea", placeholder: "We've sent a thank-you note to your inbox…" },
    { key: "cta_success_existing_title", label: "Success box title for returning signups", type: "text", placeholder: "Welcome back" },
    { key: "cta_success_existing_body", label: "Success box body fallback for returning signups", type: "textarea", placeholder: "Watch your inbox — we'll email you the moment surveys go live." },
    { key: "cta_ref_badge_prefix", label: "Referral badge — text before the code", type: "text", placeholder: "✦ You were referred by a friend — code" },
    { key: "cta_ref_badge_suffix", label: "Referral badge — text after the code", type: "text", placeholder: ". They'll earn rewards once you take your first survey." },
  ]},
  { group: "Homepage — hero & stat tiles (leave any number blank to hide that tile)", keys: [
    { key: "hero_overline", label: "Hero overline (small text above title)", type: "text", placeholder: "PAKISTAN'S MARKET INTELLIGENCE PLATFORM" },
    { key: "hero_title_lead", label: "Hero title — first phrase (before highlight)", type: "text", placeholder: "The pulse of" },
    { key: "hero_title_emphasis", label: "Hero title — highlighted phrase (mango colour)", type: "text", placeholder: "230 million" },
    { key: "hero_title_trail", label: "Hero title — phrase after highlight", type: "text", placeholder: "people, charted in real time." },
    { key: "hero_subtitle", label: "Hero subtitle paragraph", type: "textarea", placeholder: "oodel. runs continuous representative surveys across Pakistan…" },
    { key: "hero_cta_primary", label: "Primary CTA button text", type: "text", placeholder: "Explore Trackers →" },
    { key: "hero_cta_secondary", label: "Secondary CTA button text", type: "text", placeholder: "Join the Panel" },
    { key: "stat_panelists_label", label: "Stat tile #1 — label", type: "text", placeholder: "ACTIVE PANELISTS" },
    { key: "stat_panelists", label: "Stat tile #1 — number (leave blank to hide tile)", type: "text", placeholder: "48,217" },
    { key: "stat_waves_label", label: "Stat tile #2 — label", type: "text", placeholder: "WAVES PUBLISHED" },
    { key: "stat_waves", label: "Stat tile #2 — number (leave blank to hide tile)", type: "text", placeholder: "312" },
    { key: "stat_datapoints_label", label: "Stat tile #3 — label", type: "text", placeholder: "DATA POINTS / WK" },
    { key: "stat_datapoints", label: "Stat tile #3 — number (leave blank to hide tile)", type: "text", placeholder: "1.2M" },
    { key: "home_insights_overline", label: "Insights section overline", type: "text", placeholder: "SECTION — LATEST INSIGHTS" },
    { key: "home_insights_heading", label: "Insights section heading", type: "text", placeholder: "What Pakistan is saying" },
    { key: "home_hero_image_url", label: "Hero background photo URL. Recommended: wide landscape, min 1600×900px. Leave blank to use the default dark data-visualization photo. If you upload a BRIGHT/light photo, also set 'Hero theme' below to Light so the headline text stays readable.", type: "text", placeholder: "https://..." },
    { key: "home_hero_theme", label: "Hero theme — Dark (white text, near-black background — best for dark/moody photos or no photo) or Light (dark text, white background + scrim — best for bright photos).", type: "radio", options: [{ value: "dark", label: "Dark (white text)" }, { value: "light", label: "Light (dark text)" }] },
  ]},
  { group: "Public Sentiment — page text", keys: [
    { key: "sentiment_hero_title", label: "/sentiment page — big heading", type: "text", placeholder: "Rate. Reflect. Repeat." },
    { key: "sentiment_hero_subtitle", label: "/sentiment page — subtitle paragraph", type: "textarea", placeholder: "Pakistan's living scorecard for the people, brands, and personalities that shape our culture. Every star is one verified voter. Sign in to rate, anyone can read." },
    { key: "sentiment_vote_prompt", label: "Vote panel heading (shown to a logged-in patron who hasn't voted yet)", type: "text", placeholder: "How would you rate them today?" },
    { key: "sentiment_first_vote_note", label: "First-vote note under the stars — keep the literal text \"{days}\" where the cooldown number should be inserted", type: "text", placeholder: "Your first rating is permanent for {days} days — then you can update it." },
    { key: "sentiment_disclaimer", label: "Fine-print disclaimer at the bottom of every /sentiment page", type: "textarea", placeholder: "oodel Sentiment reflects the current perceptions of verified oodel respondents. It does not represent objective fact, service quality or an endorsement by oodel. Sentiment naturally changes over time as public perceptions evolve." },
  ]},
  { group: "Public Sentiment — toggles", keys: [
    { key: "feature_public_sentiment_enabled", label: "Public Sentiment (public ratings)", type: "toggle" },
    { key: "public_sentiment_cooldown_days", label: "Public Sentiment cooldown (days before a user can re-rate)", type: "text", placeholder: "180" },
    { key: "sentiment_public_labels_enabled", label: "Show the sentiment label (Very Positive/Positive/Neutral/Negative/Very Negative) next to the star rating on public /sentiment cards — visible to BOTH logged-in and logged-out visitors. When OFF, only the raw star number shows (admin's own Public Sentiment console always shows the label regardless of this toggle).", type: "toggle" },
  ]},
  { group: "Survey Content & Waves — toggles", keys: [
    { key: "overseas_program_enabled", label: "Overseas patron program — when ON, the diaspora can sign up, complete a profile, and take waves marked '🌐 Pakistan + Overseas' or '✈️ Overseas only'. When OFF, only 'Overseas-only' waves are hidden from diaspora users; PK + Both waves stay visible normally.", type: "toggle" },
    { key: "wave_sector_filter_enabled", label: "Filter waves by sector interest — when ON, patrons only see waves whose sector overlaps with their chosen sectors_of_interest. When OFF, every patron sees every active wave (great for testing).", type: "toggle" },
    { key: "orbit_enabled", label: "ORBIT — the AI co-pilot rail inside the Wave Builder (duplicate radar, methodology lint, suggest order, brief→survey generation). Default ON. Turn OFF to hide the entire rail from every admin/PM. Backend endpoints will refuse calls when OFF.", type: "toggle" },
  ]},
  { group: "Urdu localization — master toggle", keys: [
    { key: "urdu_feature_enabled", label: "Urdu (اردو) localization — MASTER TOGGLE for the whole feature. When OFF: the EN/اردو pill hides from the patron header, patrons see only English regardless of their saved preference, the 'اردو' button on each wave row hides in admin, and the 'Urdu Labels' sidebar link hides. When ON: full Urdu experience is restored. Per-wave 'Show Urdu version' toggles inside the Urdu Console are independent — this master switch simply gates the whole feature on/off in one click.", type: "toggle" },
  ]},
  { group: "Chart rendering", keys: [
    { key: "tracker_min_n", label: "Minimum sample size (n) for a wave to be plotted on tracker charts. 1 = show every wave; 30 = hide noisy low-sample waves.", type: "text", placeholder: "1" },
    { key: "tracker_min_waves_to_publish", label: "Minimum closed waves before a tracker is shown on the public hub (default 2)", type: "text", placeholder: "2" },
  ]},
  { group: "Reward defaults", keys: [
    { key: "default_points_award", label: "Default points for completed survey (each wave can still override)", type: "text", placeholder: "50" },
    { key: "default_points_screened", label: "Default points for screened-out attempt (quota full)", type: "text", placeholder: "5" },
  ]},
  { group: "Navigation", keys: [
    { key: "nav_subtitle", label: "Header sub-title (under logo)", type: "text", placeholder: "Market Intelligence Pakistan" },
  ]},
  { group: "SEO — global defaults", keys: [
    { key: "seo_canonical_base", label: "Canonical site URL (no trailing slash). Used for canonical link tags + sitemap loc fields.", type: "text", placeholder: "https://www.oodel.co" },
    { key: "seo_home_title", label: "Homepage <title> (60–65 chars ideal)", type: "text", placeholder: "oodel. — Pakistan's Market Intelligence Platform" },
    { key: "seo_home_description", label: "Homepage meta description (150–160 chars ideal)", type: "textarea", placeholder: "Pakistan's first nationally representative consumer-intelligence panel. Live trackers, monthly snapshots, and continuous surveys for brands, agencies, and researchers." },
    { key: "seo_default_image_url", label: "Default social-share image URL (1200×630 recommended). Used by WhatsApp/Twitter/LinkedIn link previews.", type: "text", placeholder: "https://www.oodel.co/og-image.png" },
    { key: "seo_twitter_url", label: "Twitter/X profile URL — adds Schema.org sameAs link", type: "text", placeholder: "https://twitter.com/oodel_pk" },
    { key: "seo_linkedin_url", label: "LinkedIn company page URL — adds Schema.org sameAs link", type: "text", placeholder: "https://www.linkedin.com/company/oodel" },
    { key: "seo_keywords", label: "Default keywords meta (comma-separated, lightly weighted by modern engines but cheap to set)", type: "text", placeholder: "market research Pakistan, consumer insights Pakistan, opinion polls Pakistan, Pakistani consumer panel, brand tracker Pakistan" },
  ]},
  { group: "SEO — per-page title + description", keys: [
    { key: "seo_for_patrons_title", label: "/for-patrons — <title>", type: "text", placeholder: "Earn rewards while shaping Pakistan's insights | oodel." },
    { key: "seo_for_patrons_description", label: "/for-patrons — meta description", type: "textarea", placeholder: "Join 48,000+ verified panelists across Pakistan. Take 5-minute surveys, earn points, redeem real-world rewards via Foodpanda, Daraz, Easypaisa." },
    { key: "seo_for_businesses_title", label: "/for-businesses — <title>", type: "text", placeholder: "Pakistan's Market Intelligence Platform for Brands | oodel." },
    { key: "seo_for_businesses_description", label: "/for-businesses — meta description", type: "textarea", placeholder: "Continuous representative consumer panel for Pakistani brands. Brand health, audience sizing, concept testing — fielded weekly." },
    { key: "seo_methodology_title", label: "/methodology — <title>", type: "text", placeholder: "How oodel. Measures Pakistan — Methodology" },
    { key: "seo_methodology_description", label: "/methodology — meta description", type: "textarea", placeholder: "Stratified sampling, asset-based SEC classification, and post-stratification weighting tied to PBS quotas." },
    { key: "seo_articles_title", label: "/articles — <title>", type: "text", placeholder: "Reports & Articles — Pakistan Consumer Insights | oodel." },
    { key: "seo_articles_description", label: "/articles — meta description", type: "textarea", placeholder: "Original research, audio deep-dives and weekly briefings on Pakistani consumers." },
    { key: "seo_trackers_title", label: "/trackers — <title>", type: "text", placeholder: "Time-Series Trackers — Live Pakistan Consumer Data | oodel." },
    { key: "seo_trackers_description", label: "/trackers — meta description", type: "textarea", placeholder: "Track sentiment, brand health and consumer behaviour across Pakistan with weekly-updated time-series charts." },
    { key: "seo_sentiment_title", label: "/sentiment — <title>", type: "text", placeholder: "Public Sentiment — How Pakistanis Rate Everything | oodel." },
    { key: "seo_sentiment_description", label: "/sentiment — meta description", type: "textarea", placeholder: "Browse, rate and discuss what Pakistanis really think about brands, services and policy." },
    { key: "seo_insights_title", label: "/insights — <title>", type: "text", placeholder: "Live Insights — Pakistan Consumer Data | oodel." },
    { key: "seo_insights_description", label: "/insights — meta description", type: "textarea", placeholder: "Browse every live tracker and snapshot from oodel's representative Pakistan consumer panel." },
    { key: "seo_privacy_title", label: "/privacy — <title>", type: "text", placeholder: "Privacy notice | oodel." },
    { key: "seo_privacy_description", label: "/privacy — meta description", type: "textarea", placeholder: "How oodel. collects, stores and uses panelist data. Plain English, no dark patterns." },
    { key: "seo_terms_title", label: "/terms — <title>", type: "text", placeholder: "Terms of Service | oodel." },
    { key: "seo_terms_description", label: "/terms — meta description", type: "textarea", placeholder: "The rules of the road for panelists and customers using oodel. — transparent, balanced, and Pakistani-law-aligned." },
    { key: "seo_snapshots_title", label: "/snapshots/:id — default <title> (snapshot name auto-appended)", type: "text", placeholder: "Pakistan Snapshot | oodel." },
    { key: "seo_snapshots_description", label: "/snapshots/:id — default meta description", type: "textarea", placeholder: "One-shot national opinion snapshot from oodel.'s representative Pakistan panel." },
  ]},
  { group: "Survey quality & fraud", keys: [
    { key: "straight_line_enabled", label: "Enable straight-lining detection. Voids a wave submission if a patron picks the same answer option for a long run of consecutive questions (or for ALL questions of the same type).", type: "toggle" },
    { key: "straight_line_threshold", label: "Trigger threshold — how many consecutive identical answers count as straight-lining (5 is industry-standard).", type: "text", placeholder: "5" },
    { key: "straight_line_penalty_points", label: "Points to deduct when straight-lining is detected (set 0 to just void with no penalty).", type: "text", placeholder: "0" },
    { key: "straight_line_all_same_min_q", label: "Minimum number of questions before the 'ALL answers identical' rule kicks in (defends against a 3-question wave being false-flagged).", type: "text", placeholder: "4" },
    { key: "wave_sector_filter_enabled", label: "Show each patron only the surveys whose sector they picked in their profile. When OFF every patron sees every wave (subject to geo + demo filters). Waves without an assigned sector are always visible (treated as 'general').", type: "toggle" },
  ]},
  { group: "Google Analytics (GA4)", keys: [
    { key: "ga_enabled", label: "Master switch — turn ON to load Google Analytics tracking on every public page.", type: "toggle" },
    { key: "ga_measurement_id", label: "GA4 Measurement ID — format starts with 'G-'. Get it from analytics.google.com → Admin → Data Streams → your stream.", type: "text", placeholder: "G-XXXXXXXXXX" },
    { key: "ga_anonymize_ip", label: "Anonymise visitor IPs before they reach Google (recommended for GDPR-style hygiene).", type: "toggle" },
    { key: "ga_track_only_on_canonical_host", label: "Only fire tracking when the page host matches your canonical SEO host. Prevents preview/staging environments from polluting your production GA data.", type: "toggle" },
  ]},
  { group: "Phone OTP & verification", keys: [
    { key: "phone_otp_enabled", label: "Require phone OTP verification on signup and on phone changes. Turn OFF if your Twilio account isn't ready yet — phone numbers will still be saved (and marked verified for now) but no SMS will be sent.", type: "toggle" },
    { key: "phone_required_to_save_profile", label: "Make phone number REQUIRED to save the patron profile — when ON, the profile won't save until the user has added (and verified, if OTP is also ON) a phone number. Signup is not affected; this only kicks in on the profile page after email verification.", type: "toggle" },
    { key: "cnic_label", label: "Label shown next to the CNIC input on the patron profile. Use 'CNIC / NICOP' when overseas Pakistanis can also enter their NICOP here.", type: "text", placeholder: "CNIC / NICOP" },
    { key: "cnic_disclaimer", label: "Amber disclaimer shown under the CNIC input. Default warns the patron that the value is used to claim raffle prizes / vouchers, so it must be correct.", type: "textarea", placeholder: "Please enter your CNIC (or NICOP if you are an overseas Pakistani) correctly. You will need it to claim your prize if you win the raffle or redeem any vouchers." },
  ]},
  { group: "Patron App — Menu visibility", keys: [
    { key: "panelist_nav_trackers_enabled", label: "Show the 'Trackers' menu in the patron sidebar (default ON). Turn OFF to hide the public trackers from logged-in patrons entirely.", type: "toggle" },
    { key: "panelist_nav_snapshots_enabled", label: "Show the 'Snapshots' menu in the patron sidebar (default ON). Turn OFF to hide one-off question results from logged-in patrons.", type: "toggle" },
    { key: "panelist_nav_articles_enabled", label: "Show the 'Articles' menu in the patron sidebar (default ON). Turn OFF to hide your published editorial from patrons.", type: "toggle" },
    { key: "panelist_nav_sentiment_enabled", label: "Show the 'Sentiment' menu in the patron sidebar (default ON). Turn OFF to hide the public-sentiment voting feature entirely for patrons.", type: "toggle" },
    { key: "panelist_nav_help_enabled", label: "Show the 'Help' menu in the patron sidebar (default ON). When ON, patrons can click Help → message dialog → goes to support@oodel.co with reply-to set to the patron. Turn OFF to hide entirely.", type: "toggle" },
    { key: "panelist_help_dialog_body", label: "Help dialog — description shown above the message textarea. Leave blank to show a completely empty dialog (heading only). Supports {email} as a merge tag for the patron's email.", type: "textarea", placeholder: "Tell us what's going on. We'll reply to {email}." },
  ]},
  { group: "Patron Points History page (/panelist/points)", keys: [
    { key: "points_history_enabled", label: "Show the Points history menu and page (turn OFF to hide the menu entirely; the page will redirect home)", type: "toggle" },
    { key: "points_tab_raffle_enabled", label: "Show the 'Raffle wins' tab on the Points history page", type: "toggle" },
    { key: "points_tab_redemptions_enabled", label: "Show the 'Redemptions' tab on the Points history page", type: "toggle" },
    { key: "points_history_menu_label", label: "Sidebar menu item label", type: "text", placeholder: "Points" },
    { key: "points_history_title", label: "Page heading", type: "text", placeholder: "Points history" },
    { key: "points_history_subtitle", label: "Subtitle below the heading", type: "textarea", placeholder: "Every credit and debit on your account — explained." },
    { key: "points_history_tab_earned_label", label: "Tab label — Earned", type: "text", placeholder: "Earned" },
    { key: "points_history_tab_raffle_label", label: "Tab label — Raffle wins", type: "text", placeholder: "Raffle wins" },
    { key: "points_history_tab_redemptions_label", label: "Tab label — Redemptions", type: "text", placeholder: "Redemptions" },
    { key: "points_tile_current_label", label: "KPI tile label — Current balance", type: "text", placeholder: "CURRENT BALANCE" },
    { key: "points_tile_earned_label", label: "KPI tile label — Lifetime earned", type: "text", placeholder: "LIFETIME EARNED" },
    { key: "points_tile_spent_label", label: "KPI tile label — Lifetime spent", type: "text", placeholder: "LIFETIME SPENT" },
  ]},
  { group: "Sign-in page (left sidebar)", keys: [
    { key: "login_sidebar_overline", label: "Overline (small caps)", type: "text", placeholder: "SIGN IN" },
    { key: "login_sidebar_title", label: "Big title (HTML allowed: <br/>, <span class=\"text-mango\">…</span>)", type: "textarea", placeholder: "Welcome<br/>back to <span class=\"text-mango\">oodel.</span>" },
  ]},
  { group: "Join-the-panel page (left sidebar)", keys: [
    { key: "register_sidebar_overline", label: "Overline", type: "text", placeholder: "JOIN THE PANEL" },
    { key: "register_sidebar_title", label: "Big title (HTML allowed)", type: "textarea", placeholder: "Earn points.<br/><span class=\"text-mango\">Redeem rewards.</span>" },
    { key: "register_sidebar_body", label: "Body paragraph", type: "textarea", placeholder: "Each completed survey earns 50 points…" },
  ]},
  { group: "Panelist settings", keys: [
    { key: "allow_overseas_panelist", label: "Allow overseas Pakistanis to register as panelists (type 'true' to enable; blank/false to keep registration Pakistan-only)", type: "text", placeholder: "false" },
    { key: "referral_card_visibility", label: "Where should the patron's referral / promo code card appear?", type: "text", placeholder: "home  (other options: profile, hidden)" },
  ]},
  { group: "Home — Methodology strip", keys: [
    { key: "method_m01_title", label: "Card 01 — title", type: "text", placeholder: "Stratified quotas" },
    { key: "method_m01_body", label: "Card 01 — body", type: "textarea" },
    { key: "method_m02_title", label: "Card 02 — title", type: "text", placeholder: "Verified panel" },
    { key: "method_m02_body", label: "Card 02 — body", type: "textarea" },
    { key: "method_m03_title", label: "Card 03 — title", type: "text", placeholder: "Open methodology" },
    { key: "method_m03_body", label: "Card 03 — body", type: "textarea" },
  ]},
  { group: "Public site navigation (on/off)", keys: [
    { key: "nav_menu_order", label: "Drag to reorder the top-level public nav menu items (applies to both desktop and mobile, in this exact order)", type: "nav_order" },
    { key: "nav_area_of_interest_enabled", label: "Show 'Area of Interest' menu", type: "toggle" },
    { key: "nav_why_oodel_enabled", label: "Show 'Why oodel' menu", type: "toggle" },
    { key: "nav_editorial_enabled", label: "Show 'Editorial' menu", type: "toggle" },
    { key: "nav_public_sentiment_enabled", label: "Show top-level 'Public Sentiment' link in the public nav (also requires the feature toggle below to be ON)", type: "toggle" },
    { key: "nav_login_enabled", label: "Show 'Login' button", type: "toggle" },
    { key: "nav_join_panel_enabled", label: "Show 'Join Panel' button", type: "toggle" },
    { key: "nav_for_patrons_enabled", label: "Show 'For Patrons' submenu item (under Why oodel)", type: "toggle" },
    { key: "nav_for_businesses_enabled", label: "Show 'For Businesses' submenu item (under Why oodel)", type: "toggle" },
    { key: "nav_methodology_enabled", label: "Show 'Our Methodology' submenu item (under Why oodel)", type: "toggle" },
    { key: "nav_reports_articles_enabled", label: "Show 'Reports & Articles' submenu item (under Editorial)", type: "toggle" },
    { key: "nav_deep_dives_enabled", label: "Show 'Deep Dives (Audio)' submenu item (under Editorial)", type: "toggle" },
    { key: "nav_all_trackers_enabled", label: "Show 'All Trackers' submenu item (under Editorial)", type: "toggle" },
    { key: "nav_snapshots_enabled", label: "Show 'Snapshots' submenu item (under Editorial). Individual snapshot cards still need to be marked visibility='public' and their wave results published.", type: "toggle" },
    { key: "nav_waves_enabled", label: "Show top-level 'Waves' link in the public nav. When ON, anonymous visitors can browse every currently-open survey at /waves (name, sector, duration only — no questions). Clicking a card prompts sign-up. Default OFF.", type: "toggle" },
    { key: "nav_customer_sentiment_enabled", label: "Show top-level 'oodel SCORE' link in the public nav", type: "toggle" },
    { key: "show_snapshot_detail_link", label: "Show the 'Open detail + crosstabs →' link on each snapshot card. When OFF, only the summary chart is visible on the wave detail page — the crosstabs stay hidden. Anonymous viewers who reach a detail URL directly get a Sign-up paywall.", type: "toggle" },
    { key: "hero_cta_primary_enabled", label: "Show homepage hero 'Explore Trackers' button", type: "toggle" },
    { key: "hero_cta_secondary_enabled", label: "Show homepage hero 'Join the Panel' button", type: "toggle" },
    { key: "patrons_cta_button_enabled", label: "Show 'Join the panel' button at the bottom of /for-patrons", type: "toggle" },
    { key: "biz_cta_button_enabled", label: "Show 'Talk to Sales' button at the bottom of /for-businesses", type: "toggle" },
    { key: "home_what_pakistan_saying_enabled", label: "Show homepage 'What Pakistan is saying' section", type: "toggle" },
    { key: "home_waves_and_articles_enabled", label: "Show homepage 'Open Waves' section (3 latest open waves, right under the methodology strip). Anonymous visitors are prompted to sign up when clicking a wave.", type: "toggle" },
  ]},
  { group: "Public site navigation — rename menu labels", keys: [
    { key: "nav_label_area_of_interest", label: "Top-level: 'Area of Interest' menu label", type: "text", placeholder: "Area of Interest" },
    { key: "nav_label_why_oodel", label: "Top-level: 'Why oodel' menu label", type: "text", placeholder: "Why oodel" },
    { key: "nav_label_editorial", label: "Top-level: 'Editorial' menu label", type: "text", placeholder: "Editorial" },
    { key: "nav_label_public_sentiment", label: "Top-level: 'Public Sentiment' link label", type: "text", placeholder: "Public Sentiment" },
    { key: "nav_label_login", label: "Right-side: 'Login' button label", type: "text", placeholder: "Login" },
    { key: "nav_label_join_panel", label: "Right-side: 'Join Panel' button label", type: "text", placeholder: "Join Panel" },
    { key: "nav_label_for_patrons", label: "Why-oodel submenu: 'For Patrons' link label", type: "text", placeholder: "For Patrons" },
    { key: "nav_label_for_businesses", label: "Why-oodel submenu: 'For Businesses' link label", type: "text", placeholder: "For Businesses" },
    { key: "nav_label_methodology", label: "Why-oodel submenu: 'Our Methodology' link label", type: "text", placeholder: "Our Methodology" },
    { key: "nav_label_reports_articles", label: "Editorial submenu: 'Reports & Articles' link label", type: "text", placeholder: "Reports & Articles" },
    { key: "nav_label_deep_dives", label: "Editorial submenu: 'Deep Dives (Audio)' link label", type: "text", placeholder: "Deep Dives (Audio)" },
    { key: "nav_label_all_trackers", label: "Editorial submenu: 'All Trackers' link label", type: "text", placeholder: "All Trackers" },
    { key: "nav_label_snapshots", label: "Editorial submenu: 'Snapshots' link label", type: "text", placeholder: "Snapshots" },
    { key: "nav_label_waves", label: "Top-level: 'Waves' link label", type: "text", placeholder: "Waves" },
    { key: "nav_label_cms", label: "Logged-in admin: 'CMS' button label", type: "text", placeholder: "CMS" },
    { key: "nav_label_my_hub", label: "Logged-in subscriber/enterprise: 'My Hub' button label", type: "text", placeholder: "My Hub" },
    { key: "nav_label_my_panel", label: "Logged-in panelist: 'My Panel' button label", type: "text", placeholder: "My Panel" },
    { key: "nav_label_logout", label: "Logged-in: 'Logout' button label (desktop)", type: "text", placeholder: "Logout" },
    { key: "nav_label_sign_out", label: "Logged-in: 'Sign out' button label (mobile drawer)", type: "text", placeholder: "Sign out" },
    { key: "nav_label_open_cms", label: "Mobile drawer admin: 'Open CMS' button label", type: "text", placeholder: "Open CMS" },
    { key: "nav_label_sign_in", label: "Mobile drawer: 'Sign in' button label", type: "text", placeholder: "Sign in" },
    { key: "nav_label_join_panel_mobile", label: "Mobile drawer: 'Join the Panel →' button label", type: "text", placeholder: "Join the Panel →" },
    { key: "nav_label_public_sentiment_link", label: "Mobile drawer: 'Browse ratings' inner link under Public Sentiment group", type: "text", placeholder: "Browse ratings" },
  ]},
  { group: "Why oodel — For Patrons page (/for-patrons)", keys: [
    { key: "patrons_hero_overline", label: "Hero — overline (small caps)", type: "text", placeholder: "FOR PATRONS · CITIZENS OF PAKISTAN" },
    { key: "patrons_hero_title", label: "Hero — big title", type: "textarea", placeholder: "Your opinion is data. We pay you for it." },
    { key: "patrons_hero_sub", label: "Hero — subtitle paragraph", type: "textarea", placeholder: "Join 48,000+ verified panelists across all four provinces…" },
    { key: "patrons_steps_overline", label: "Steps section — overline", type: "text", placeholder: "HOW IT WORKS" },
    { key: "patrons_steps_title", label: "Steps section — heading", type: "text", placeholder: "Three simple steps" },
    { key: "patrons_step1_num", label: "Step 1 — number", type: "text", placeholder: "01" },
    { key: "patrons_step1_title", label: "Step 1 — title", type: "text", placeholder: "Sign up free" },
    { key: "patrons_step1_body", label: "Step 1 — body", type: "textarea", placeholder: "Verify your email and complete your demographic profile in under 2 minutes." },
    { key: "patrons_step2_num", label: "Step 2 — number", type: "text", placeholder: "02" },
    { key: "patrons_step2_title", label: "Step 2 — title", type: "text", placeholder: "Take surveys" },
    { key: "patrons_step2_body", label: "Step 2 — body", type: "textarea", placeholder: "We send you 2-3 surveys a month…" },
    { key: "patrons_step3_num", label: "Step 3 — number", type: "text", placeholder: "03" },
    { key: "patrons_step3_title", label: "Step 3 — title", type: "text", placeholder: "Earn & redeem" },
    { key: "patrons_step3_body", label: "Step 3 — body", type: "textarea", placeholder: "50 points per completion. Cash out for Foodpanda, Daraz…" },
    { key: "patrons_perks_overline", label: "Perks section — overline", type: "text", placeholder: "WHAT'S IN IT FOR YOU" },
    { key: "patrons_perks_title", label: "Perks section — heading", type: "text", placeholder: "Earn while shaping the conversation" },
    { key: "patrons_perk1_title", label: "Perk 1 — title", type: "text", placeholder: "Real cash equivalents" },
    { key: "patrons_perk1_body", label: "Perk 1 — body", type: "textarea", placeholder: "Vouchers redeemable across the major Pakistani digital marketplaces…" },
    { key: "patrons_perk2_title", label: "Perk 2 — title", type: "text", placeholder: "Privacy first" },
    { key: "patrons_perk2_body", label: "Perk 2 — body", type: "textarea", placeholder: "Your individual answers are never shared…" },
    { key: "patrons_perk3_title", label: "Perk 3 — title", type: "text", placeholder: "Real influence" },
    { key: "patrons_perk3_body", label: "Perk 3 — body", type: "textarea", placeholder: "Your views show up in trackers cited by national news…" },
    { key: "patrons_cta_title", label: "Bottom CTA strip — heading", type: "text", placeholder: "Ready to earn while informing Pakistan?" },
    { key: "patrons_cta_sub", label: "Bottom CTA strip — subtitle", type: "text", placeholder: "It takes 90 seconds." },
    { key: "patrons_cta_button", label: "Bottom CTA strip — button label", type: "text", placeholder: "Join the panel" },
  ]},
  { group: "Why oodel — For Businesses page (/for-businesses)", keys: [
    { key: "biz_hero_overline", label: "Hero — overline", type: "text", placeholder: "FOR BUSINESSES · BRANDS · POLICY" },
    { key: "biz_hero_title", label: "Hero — big title", type: "textarea", placeholder: "Stop guessing. Start measuring Pakistan." },
    { key: "biz_hero_sub", label: "Hero — subtitle paragraph", type: "textarea", placeholder: "oodel. is the country's first continuously-fielded representative panel…" },
    { key: "biz_tiers_overline", label: "Pricing-tiers section — overline", type: "text", placeholder: "WHO BUYS OODEL" },
    { key: "biz_tiers_title", label: "Pricing-tiers section — heading", type: "text", placeholder: "Two ways to work with us" },
    { key: "biz_tier1_overline", label: "Tier 1 (Subscriber) — overline", type: "text", placeholder: "SUBSCRIBER · USD 2,499 / YEAR" },
    { key: "biz_tier1_title", label: "Tier 1 — title", type: "text", placeholder: "National Intelligence Hub" },
    { key: "biz_tier1_body", label: "Tier 1 — description", type: "textarea", placeholder: "Continuous access to every public tracker…" },
    { key: "biz_tier1_feat1", label: "Tier 1 — feature 1", type: "text", placeholder: "All public trackers + crosstabs" },
    { key: "biz_tier1_feat2", label: "Tier 1 — feature 2", type: "text", placeholder: "BrandIndex 2.0 ratings" },
    { key: "biz_tier1_feat3", label: "Tier 1 — feature 3", type: "text", placeholder: "AI-drafted weekly briefings" },
    { key: "biz_tier1_feat4", label: "Tier 1 — feature 4", type: "text", placeholder: "Audio Deep Dives (commute friendly)" },
    { key: "biz_tier2_overline", label: "Tier 2 (Enterprise) — overline", type: "text", placeholder: "ENTERPRISE · USD 25K+ / YEAR" },
    { key: "biz_tier2_title", label: "Tier 2 — title", type: "text", placeholder: "Private Insights Vault" },
    { key: "biz_tier2_body", label: "Tier 2 — description", type: "textarea", placeholder: "Everything in Subscriber, plus your own custom questions…" },
    { key: "biz_tier2_feat1", label: "Tier 2 — feature 1", type: "text", placeholder: "Private waves & tracker commissioning" },
    { key: "biz_tier2_feat2", label: "Tier 2 — feature 2", type: "text", placeholder: "Crosstab cross-filter (Female × Punjab × SEC A)" },
    { key: "biz_tier2_feat3", label: "Tier 2 — feature 3", type: "text", placeholder: "Direct line to a PM" },
    { key: "biz_tier2_feat4", label: "Tier 2 — feature 4", type: "text", placeholder: "API access" },
    { key: "biz_usecases_overline", label: "Use-cases section — overline", type: "text", placeholder: "USE-CASES" },
    { key: "biz_usecases_title", label: "Use-cases section — heading", type: "text", placeholder: "Built for the questions you actually have" },
    { key: "biz_uc1_title", label: "Use-case 1 — title", type: "text", placeholder: "Brand health" },
    { key: "biz_uc1_body", label: "Use-case 1 — body", type: "textarea", placeholder: "Track unaided awareness, consideration, NPS…" },
    { key: "biz_uc2_title", label: "Use-case 2 — title", type: "text", placeholder: "Audience sizing" },
    { key: "biz_uc2_body", label: "Use-case 2 — body", type: "textarea", placeholder: "Estimate the size and SEC mix of any segment…" },
    { key: "biz_uc3_title", label: "Use-case 3 — title", type: "text", placeholder: "Concept testing" },
    { key: "biz_uc3_body", label: "Use-case 3 — body", type: "textarea", placeholder: "Field a private wave with 5 prototype concepts on Wednesday…" },
    { key: "biz_cta_title", label: "Bottom CTA strip — heading", type: "text", placeholder: "Ready to talk?" },
    { key: "biz_cta_sub", label: "Bottom CTA strip — subtitle", type: "text", placeholder: "Email sales — we'll set up a 30-minute walkthrough." },
    { key: "biz_cta_button", label: "Bottom CTA strip — button label", type: "text", placeholder: "Talk to Sales" },
    { key: "biz_cta_mailto", label: "Bottom CTA strip — button href (mailto: or https://)", type: "text", placeholder: "mailto:sales@oodel.co?subject=Pricing%20Inquiry" },
  ]},
  { group: "Why oodel — Methodology page (/methodology)", keys: [
    { key: "methodology_hero_overline", label: "Hero — overline", type: "text", placeholder: "METHODOLOGY" },
    { key: "methodology_hero_title", label: "Hero — big title", type: "textarea", placeholder: "How we make 1,000 voices speak for 230 million." },
    { key: "methodology_hero_sub", label: "Hero — subtitle paragraph", type: "textarea", placeholder: "Every oodel. number is the product of stratified sampling…" },
    { key: "methodology_s1_overline", label: "Section 1 — overline", type: "text", placeholder: "01 · PANEL DESIGN" },
    { key: "methodology_s1_title", label: "Section 1 — heading", type: "text", placeholder: "The panel" },
    { key: "methodology_s1_body", label: "Section 1 — body paragraph", type: "textarea", placeholder: "oodel. operates a continuously-renewing online panel…" },
    { key: "methodology_s1_bullet1", label: "Section 1 — bullet 1", type: "text", placeholder: "Mobile + email double opt-in" },
    { key: "methodology_s1_bullet2", label: "Section 1 — bullet 2", type: "text", placeholder: "Asset-based SEC classification (no self-reporting)" },
    { key: "methodology_s1_bullet3", label: "Section 1 — bullet 3", type: "text", placeholder: "Continuous quality scoring on response consistency…" },
    { key: "methodology_panel_label", label: "Panel-size widget — overline", type: "text", placeholder: "PANEL SIZE" },
    { key: "methodology_panel_number", label: "Panel-size widget — big number", type: "text", placeholder: "48,217" },
    { key: "methodology_panel_caption", label: "Panel-size widget — caption", type: "text", placeholder: "verified panelists across 5 regions" },
    { key: "methodology_s2_overline", label: "Section 2 — overline", type: "text", placeholder: "02 · QUOTAS & WEIGHTING" },
    { key: "methodology_s2_title", label: "Section 2 — heading", type: "text", placeholder: "Representative by design, weighted by results" },
    { key: "methodology_s2_body", label: "Section 2 — body paragraph (HTML allowed: <strong> etc.)", type: "textarea", placeholder: "Every published wave enforces hard quotas across <strong>Gender × Age × Region × SEC</strong>…" },
    { key: "methodology_quota1_label", label: "Quota 1 — label", type: "text", placeholder: "GENDER" },
    { key: "methodology_quota1_line1", label: "Quota 1 — line 1", type: "text", placeholder: "Male / Female / Other" },
    { key: "methodology_quota1_line2", label: "Quota 1 — line 2", type: "text", placeholder: "(50/50/—)" },
    { key: "methodology_quota2_label", label: "Quota 2 — label", type: "text", placeholder: "AGE BANDS" },
    { key: "methodology_quota2_line1", label: "Quota 2 — line 1", type: "text", placeholder: "18-24, 25-34, 35-44" },
    { key: "methodology_quota2_line2", label: "Quota 2 — line 2", type: "text", placeholder: "45-54, 55+" },
    { key: "methodology_quota3_label", label: "Quota 3 — label", type: "text", placeholder: "REGION" },
    { key: "methodology_quota3_line1", label: "Quota 3 — line 1", type: "text", placeholder: "Punjab 53%, Sindh 23%" },
    { key: "methodology_quota3_line2", label: "Quota 3 — line 2", type: "text", placeholder: "KPK 14%, Bal/ICT/GB/AJK 10%" },
    { key: "methodology_quota4_label", label: "Quota 4 — label", type: "text", placeholder: "SEC (ASSET-BASED)" },
    { key: "methodology_quota4_line1", label: "Quota 4 — line 1", type: "text", placeholder: "A · B · C · D · E" },
    { key: "methodology_quota4_line2", label: "Quota 4 — line 2", type: "text", placeholder: "Auto-computed" },
    { key: "methodology_s3_overline", label: "Section 3 — overline", type: "text", placeholder: "03 · ETL & CHARTS" },
    { key: "methodology_s3_title", label: "Section 3 — heading", type: "text", placeholder: "From answers to charts in minutes" },
    { key: "methodology_s3_body", label: "Section 3 — body paragraph", type: "textarea", placeholder: "When a wave closes, our ETL pipeline computes weighted percentages…" },
    { key: "methodology_s4_overline", label: "Section 4 — overline", type: "text", placeholder: "04 · MARGIN OF ERROR" },
    { key: "methodology_s4_title", label: "Section 4 — heading", type: "text", placeholder: "How confident is a typical reading?" },
    { key: "methodology_moe_header_n", label: "MOE table — header column 1", type: "text", placeholder: "SAMPLE SIZE" },
    { key: "methodology_moe_header_moe", label: "MOE table — header column 2", type: "text", placeholder: "MARGIN OF ERROR (95%)" },
    { key: "methodology_moe_row1_n", label: "MOE row 1 — sample size", type: "text", placeholder: "N = 1,000" },
    { key: "methodology_moe_row1_moe", label: "MOE row 1 — margin", type: "text", placeholder: "±3.1pp" },
    { key: "methodology_moe_row2_n", label: "MOE row 2 — sample size", type: "text", placeholder: "N = 500" },
    { key: "methodology_moe_row2_moe", label: "MOE row 2 — margin", type: "text", placeholder: "±4.4pp" },
    { key: "methodology_moe_row3_n", label: "MOE row 3 — sample size", type: "text", placeholder: "N = 200" },
    { key: "methodology_moe_row3_moe", label: "MOE row 3 — margin", type: "text", placeholder: "±6.9pp" },
    { key: "methodology_moe_row4_n", label: "MOE row 4 — sample size", type: "text", placeholder: "N = 100" },
    { key: "methodology_moe_row4_moe", label: "MOE row 4 — margin", type: "text", placeholder: "±9.8pp" },
  ]},
  { group: "Subscriber / Enterprise dashboard copy", keys: [
    { key: "subscriber_upsell_dialog_title", label: "'Upgrade to Enterprise' modal title", type: "text", placeholder: "Upgrade to Enterprise" },
    { key: "subscriber_upsell_dialog_body", label: "'Upgrade to Enterprise' modal body", type: "textarea" },
    { key: "subscriber_need_questions_heading", label: "Subscriber DIRECT LINE strip — heading", type: "text" },
    { key: "subscriber_need_questions_body", label: "Subscriber DIRECT LINE strip — body", type: "textarea" },
    { key: "sales_email", label: "Sales contact email (subscriber 'Contact Sales' button)", type: "text", placeholder: "sales@oodel.co" },
  ]},
  { group: "Raffle fairness", keys: [
    { key: "raffle_fairness_mode", label: "Fairness mode — 'cooldown' (lock recent winners out) OR 'decay' (entries = 1 / (1 + recent wins))", type: "text", placeholder: "cooldown" },
    { key: "raffle_winner_cooldown_days", label: "Winner cooldown — days (used only in 'cooldown' mode)", type: "text", placeholder: "180" },
  ]},
  { group: "Customer Sentiment — page text", keys: [
    { key: "cs_landing_hero_title", label: "oodel SCORE directory — hero title", type: "textarea", placeholder: "Real customer sentiment, verified by oodel." },
    { key: "cs_score_explainer", label: "oodel SCORE directory — explainer paragraph", type: "textarea", placeholder: "oodel SCORE\u2122 (Sentiment Captured On Real Experiences) is a trusted measure of customer sentiment based on real customer experiences. Every SCORE is calculated from structured feedback submitted after a visit, helping businesses and customers understand genuine customer perception over time." },
    { key: "cs_landing_cta_label", label: "oodel SCORE directory — CTA button label", type: "text", placeholder: "Get your business listed →" },
    { key: "cs_back_to_directory", label: "Business profile page — 'back to directory' link", type: "text", placeholder: "Back to oodel SCORE directory" },
    { key: "cs_directory_footer_title", label: "oodel SCORE directory — footer heading", type: "text", placeholder: "Already listed on oodel SCORE?" },
    { key: "cs_directory_footer_subtitle", label: "oodel SCORE directory — footer subtitle", type: "text", placeholder: "Sign in to view your live dashboard." },
    { key: "cs_directory_login_label", label: "oodel SCORE directory — 'business login' button label", type: "text", placeholder: "Business login →" },
    { key: "cs_lead_form_title", label: "Lead form modal — title", type: "text", placeholder: "Get your business listed" },
    { key: "cs_lead_form_subtitle", label: "Lead form modal — subtitle", type: "text", placeholder: "Tell us a bit about your business and we'll set you up on oodel SCORE." },
    { key: "cs_lead_form_success_title", label: "Lead form modal — success title", type: "text", placeholder: "Thanks — we'll be in touch!" },
    { key: "cs_lead_form_success_message", label: "Lead form modal — success message", type: "text", placeholder: "Our team will reach out shortly to get your business listed on oodel SCORE." },
    { key: "nav_label_customer_sentiment", label: "Top nav label", type: "text", placeholder: "oodel SCORE" },
    { key: "cs_no_ai_insight_text", label: "Business/Admin dashboard — 'oodel x insights' empty state (shown before the first AI insight has been generated for a business)", type: "text", placeholder: "No AI insight generated yet — check back soon." },
    { key: "cs_zero_response_message", label: "Oodel X Insights — text shown on a Weekly/Monthly report when that period had ZERO responses. Use the token {range} anywhere you want the exact date range (e.g. \"Aug 17 - Aug 23\") inserted.", type: "textarea", placeholder: "{range}: No responses were collected during this period." },
    { key: "cs_powered_by_logo_url", label: "Feedback form (customer-facing QR rating page) — 'Powered by' logo image URL. Leave BLANK to show the default oodel. text wordmark. Paste a direct image URL (PNG/SVG) to replace it everywhere the feedback form shows 'Powered by' — the text label itself stays unchanged.", type: "text", placeholder: "https://example.com/logo.png" },
    { key: "cs_business_dashboard_logo_url", label: "Business Dashboard (logged-in businesses) — sidebar/header logo image URL. Leave BLANK to keep the current default oodel SCORE logo. Paste a direct image URL (PNG/SVG) to replace it in the sidebar, mobile header, and mobile menu.", type: "text", placeholder: "https://example.com/logo.png" },
  ]},
  { group: "Document Signing — signer page copy", keys: [
    { key: "docsign_success_title", label: "Signer page — success screen title (after signing)", type: "text", placeholder: "Signed successfully!" },
    { key: "docsign_already_signed_title", label: "Signer page — success screen title (when reopening a link after already signing)", type: "text", placeholder: "You've already signed this document." },
    { key: "docsign_success_all_signed_message", label: "Signer page — message when this signer was the LAST one (document is now fully complete)", type: "textarea", placeholder: "All parties have now signed. A fully signed copy has been emailed to the business." },
    { key: "docsign_success_next_signer_message", label: "Signer page — message when other signers are still pending", type: "textarea", placeholder: "We'll email the next signer now. You'll get a copy once everyone has signed." },
  ]},
  { group: "Customer Sentiment — Invoice text", keys: [
    { key: "invoice_company_name", label: "Sender name (shown if logo fails to load)", type: "text", placeholder: "oodel." },
    { key: "invoice_company_tagline", label: "Sender tagline", type: "text", placeholder: "oodel SCORE — Pakistan's customer sentiment platform" },
    { key: "invoice_company_email", label: "Sender contact email", type: "text", placeholder: "info@oodel.co" },
    { key: "invoice_title_label", label: "Document title", type: "text", placeholder: "INVOICE" },
    { key: "invoice_bill_to_label", label: "'Billed to' label", type: "text", placeholder: "BILLED TO" },
    { key: "invoice_number_label", label: "Invoice number label", type: "text", placeholder: "Invoice No." },
    { key: "invoice_date_label", label: "Date label", type: "text", placeholder: "Date" },
    { key: "invoice_item_col_label", label: "Line-item table — description column header", type: "text", placeholder: "DESCRIPTION" },
    { key: "invoice_period_col_label", label: "Line-item table — period column header", type: "text", placeholder: "PERIOD" },
    { key: "invoice_amount_col_label", label: "Line-item table — amount column header", type: "text", placeholder: "AMOUNT" },
    { key: "invoice_item_description", label: "Line-item description text", type: "text", placeholder: "oodel SCORE™ subscription" },
    { key: "invoice_subtotal_label", label: "Subtotal label", type: "text", placeholder: "Subtotal" },
    { key: "invoice_tax_label", label: "Tax line label", type: "text", placeholder: "Tax (0%)" },
    { key: "invoice_total_label", label: "Total due label", type: "text", placeholder: "Total Due" },
    { key: "invoice_thank_you_text", label: "Thank-you line", type: "text", placeholder: "Thank you for partnering with oodel SCORE™." },
    { key: "invoice_payment_info_label", label: "Payment info section heading", type: "text", placeholder: "PAYMENT INFORMATION" },
    { key: "invoice_payment_instructions", label: "Payment instructions (bank details) — one item per line", type: "textarea", placeholder: "Bank: Meezan Bank\nAccount Name: Oodel Research (Pvt) Ltd\nAccount No: 0198-01-0123456-1\nPlease reference the invoice number in your bank transfer." },
    { key: "invoice_footer_note", label: "Footer note", type: "textarea", placeholder: "This invoice was generated automatically by oodel SCORE. For billing questions, contact info@oodel.co." },
  ]},
  { group: "Business Dashboard — page text (English only — this dashboard is business/admin-facing, not translated to Urdu)", keys: [
    { key: "cs_dash_title", label: "Page heading", type: "text", placeholder: "Dashboard Overview" },
    { key: "cs_dash_tab_overview", label: "Tab label — Overview", type: "text", placeholder: "Overview" },
    { key: "cs_dash_tab_demographics", label: "Tab label — Demographics", type: "text", placeholder: "Demographics" },
    { key: "cs_dash_tab_ratings", label: "Tab label — Ratings", type: "text", placeholder: "Ratings" },
    { key: "cs_dash_tab_trends", label: "Tab label — Trends", type: "text", placeholder: "Trends" },
    { key: "cs_dash_tab_insights", label: "Tab label — Insights", type: "text", placeholder: "Insights" },
    { key: "cs_dash_tab_responses", label: "Tab label — Responses", type: "text", placeholder: "Responses" },
    { key: "cs_dash_tab_alerts", label: "Tab label — Alerts", type: "text", placeholder: "Alerts" },
    { key: "cs_dash_branch_all_label", label: "Branch picker — 'all branches' option", type: "text", placeholder: "All branches" },
    { key: "cs_dash_compare_label", label: "'Compare' dropdown trigger label", type: "text", placeholder: "Compare" },
    { key: "cs_dash_compare_option_prev", label: "'Compare' dropdown — option 1", type: "text", placeholder: "vs. previous equivalent period" },
    { key: "cs_dash_compare_option_yoy", label: "'Compare' dropdown — option 2 (disabled/coming soon)", type: "text", placeholder: "vs. same period last year (coming soon)" },
    { key: "cs_dash_score_subtitle", label: "Score card — subtitle under 'oodel score'", type: "text", placeholder: "Sentiment Captured On Real Experiences" },
    { key: "cs_dash_score_vs_previous", label: "Score card — trend caption", type: "text", placeholder: "vs previous period" },
    { key: "cs_dash_score_trend_title", label: "Score Trend chart — title", type: "text", placeholder: "Score Trend" },
    { key: "cs_dash_score_distribution_title", label: "Score Distribution chart — title", type: "text", placeholder: "Score Distribution" },
    { key: "cs_dash_key_insights_title", label: "'Key Insights' row — title", type: "text", placeholder: "This Month's Key Insights" },
    { key: "cs_dash_ratings_summary_title", label: "Ratings Summary grid — title", type: "text", placeholder: "Ratings Summary (by Question)" },
    { key: "cs_dash_view_all_questions", label: "Ratings Summary — 'view all questions' link", type: "text", placeholder: "View all questions →" },
    { key: "cs_dash_ratings_delta_caption", label: "Ratings Summary — caption under the grid", type: "text", placeholder: "↑↓ Change compared to previous period" },
    { key: "cs_dash_ratings_empty", label: "Ratings Summary — empty state", type: "text", placeholder: "No ratings data yet." },
    { key: "cs_dash_ranked_empty", label: "Top Strengths/Opportunities — empty state", type: "text", placeholder: "Not enough data yet." },
    { key: "cs_dash_top_strengths_title", label: "'Top Strengths' card — title", type: "text", placeholder: "Top Strengths" },
    { key: "cs_dash_top_opportunities_title", label: "'Top Opportunities' card — title", type: "text", placeholder: "Top Opportunities" },
    { key: "cs_dash_demographics_breakdown_title", label: "Overview tab — 'Demographics Breakdown' section title", type: "text", placeholder: "Demographics Breakdown" },
    { key: "cs_dash_view_heatmap_btn", label: "'View Heatmap' button label", type: "text", placeholder: "View Heatmap" },
    { key: "cs_dash_demo_title_age", label: "Demographics table — 'By Age' title", type: "text", placeholder: "By Age" },
    { key: "cs_dash_demo_title_gender", label: "Demographics table — 'By Gender' title", type: "text", placeholder: "By Gender" },
    { key: "cs_dash_demo_title_city", label: "Demographics table — 'By City' title", type: "text", placeholder: "By City" },
    { key: "cs_dash_demo_title_location", label: "Demographics table — 'By Location' title (multi-branch businesses only)", type: "text", placeholder: "By Location" },
    { key: "cs_dash_demo_col_segment", label: "Demographics table — 'Segment' column header", type: "text", placeholder: "Segment" },
    { key: "cs_dash_demo_col_responses", label: "Demographics table — 'Responses' column header", type: "text", placeholder: "Responses" },
    { key: "cs_dash_demo_col_avg", label: "Demographics table — 'Avg' column header", type: "text", placeholder: "Avg" },
    { key: "cs_dash_demo_col_trend", label: "Demographics table — 'Trend' column header", type: "text", placeholder: "Trend" },
    { key: "cs_dash_demo_empty", label: "Demographics table — empty state", type: "text", placeholder: "No data yet." },
    { key: "cs_dash_heatmap_title", label: "Segment Analysis Heatmap — title", type: "text", placeholder: "Segment Analysis Heatmap" },
    { key: "cs_dash_heatmap_subtitle", label: "Segment Analysis Heatmap — subtitle", type: "text", placeholder: "Average score by age band × gender × city — greener is better." },
    { key: "cs_dash_heatmap_empty", label: "Segment Analysis Heatmap — empty state", type: "text", placeholder: "Not enough segmented data yet." },
    { key: "cs_dash_heatmap_col_age_band", label: "Heatmap — 'Age band' column header", type: "text", placeholder: "Age band" },
    { key: "cs_dash_heatmap_legend_low", label: "Heatmap legend — low end", type: "text", placeholder: "Low Score" },
    { key: "cs_dash_heatmap_legend_high", label: "Heatmap legend — high end", type: "text", placeholder: "High Score" },
    { key: "cs_dash_funnel_title", label: "Response Funnel card — title", type: "text", placeholder: "Response Funnel" },
    { key: "cs_dash_alerts_title", label: "Alerts card/tab — title", type: "text", placeholder: "Alerts" },
    { key: "cs_dash_alerts_empty", label: "Alerts — empty state", type: "text", placeholder: "No alerts for this period — everything's steady." },
    { key: "cs_dash_view_all_alerts", label: "Overview tab — 'view all' alerts link", type: "text", placeholder: "View all" },
    { key: "cs_dash_full_summary_title", label: "Insights tab — 'Full summary' section title", type: "text", placeholder: "Full summary" },
    { key: "cs_dash_trends_title", label: "Trends tab — title", type: "text", placeholder: "Trends by Demographics — Age Band" },
    { key: "cs_dash_trends_subtitle", label: "Trends tab — subtitle", type: "text", placeholder: "How each age group's average score has moved over the selected period." },
    { key: "cs_dash_trends_empty", label: "Trends tab — empty state", type: "text", placeholder: "Not enough age-band data yet." },
    { key: "cs_dash_recent_respondents_title", label: "Responses tab — 'Critical Responses' title", type: "text", placeholder: "Critical Responses" },
    { key: "cs_dash_recent_respondents_subtitle", label: "Responses tab — 'Critical Responses' subtitle", type: "text", placeholder: "Every respondent rating 3★ or below in the selected period — contact them for service recovery." },
    { key: "cs_dash_respondents_col_email", label: "Critical Responses / Marketing Opt-ins — 'Email' column", type: "text", placeholder: "Email" },
    { key: "cs_dash_respondents_col_reason", label: "Critical Responses — 'Action' column", type: "text", placeholder: "Action" },
    { key: "cs_dash_respondents_col_location", label: "Critical Responses / Marketing Opt-ins — 'Location' column", type: "text", placeholder: "Location" },
    { key: "cs_dash_respondents_col_rating", label: "Critical Responses / Marketing Opt-ins — 'Rating' column", type: "text", placeholder: "Rating" },
    { key: "cs_dash_respondents_col_submitted", label: "Critical Responses / Marketing Opt-ins — 'Submitted' column", type: "text", placeholder: "Submitted" },
    { key: "cs_dash_respondents_empty", label: "Critical Responses — empty state", type: "text", placeholder: "No critical responses in this period — nothing needs follow-up." },
    { key: "cs_dash_marketing_optins_title", label: "Responses tab — 'Marketing Opt-ins' title", type: "text", placeholder: "Marketing Opt-ins" },
    { key: "cs_dash_marketing_optins_subtitle", label: "Responses tab — 'Marketing Opt-ins' subtitle", type: "text", placeholder: "Every respondent — any star rating — who checked \"Keep me updated on offers & news\". Safe to email with promos." },
    { key: "cs_dash_marketing_optins_empty", label: "Marketing Opt-ins — empty state", type: "text", placeholder: "No marketing opt-ins in this period yet." },
    { key: "cs_dash_footer_copyright", label: "Dashboard footer — copyright line (use {year} for the current year)", type: "text", placeholder: "© {year} Oodel. All rights reserved." },
    { key: "cs_dash_footer_tagline", label: "Dashboard footer — 'score is a proprietary metric' tagline (appears after the word 'score')", type: "text", placeholder: "is a proprietary metric of Oodel." },
    { key: "cs_dash_footer_learn_more_enabled", label: "Show the 'Learn more about score' link in the Business Dashboard footer (toggle OFF to hide it entirely).", type: "toggle" },
    { key: "cs_dash_footer_learn_more", label: "Dashboard footer — 'learn more' link (appears before the word 'score')", type: "text", placeholder: "Learn more about" },
    { key: "cs_dash_qr_page_title", label: "QR Codes page — title", type: "text", placeholder: "QR Codes" },
    { key: "cs_dash_qr_page_subtitle", label: "QR Codes page — subtitle", type: "text", placeholder: "Your live QR codes, one per location/branch. Print one and place it where customers can scan it after their visit." },
    { key: "cs_dash_qr_download_btn", label: "QR Codes page — 'Download QR' button label", type: "text", placeholder: "Download QR" },
    { key: "cs_dash_qr_empty", label: "QR Codes page — empty state", type: "text", placeholder: "No locations yet — contact your PM to set up your first QR code." },
    { key: "cs_dash_brand_assets_title", label: "QR Codes page — 'Brand Assets' section title (previously 'Branded print kit')", type: "text", placeholder: "Brand Assets" },
    { key: "cs_dash_oodel_x_subtitle", label: "oodel x insights page — subtitle", type: "text", placeholder: "Narrative summaries generated automatically from your customer feedback — no action needed from you, new insights appear here on a regular cadence." },
    { key: "cs_dash_oodel_x_empty", label: "oodel x insights page — empty state", type: "text", placeholder: "No insights yet — check back once you've collected a bit more feedback." },
    { key: "cs_dash_invoices_title", label: "Invoices page — title", type: "text", placeholder: "Invoices" },
    { key: "cs_dash_invoices_subtitle", label: "Invoices page — subtitle", type: "text", placeholder: "Invoices are generated automatically on your agreed billing cycle. Once you've paid, your account manager marks it here as Paid." },
    { key: "cs_dash_private_insights_title", label: "Private Insights page — title", type: "text", placeholder: "Private Insights" },
    { key: "cs_dash_private_insights_subtitle", label: "Private Insights page — subtitle", type: "text", placeholder: "Commissioned studies and deep-dive reports prepared for you by the oodel. research team." },
    { key: "cs_dash_private_insights_empty", label: "Private Insights page — empty state", type: "text", placeholder: "No private studies yet. Your account manager will add them here as they're delivered." },
  ]},
  { group: "Mobile App Onboarding (3-slide welcome — Capacitor app only, web is unaffected)", keys: [    { key: "mobile_onboarding_slide1_enabled", label: "Show slide 1", type: "toggle" },
    { key: "mobile_onboarding_slide1_title", label: "Slide 1 — headline", type: "text", placeholder: "Take part in surveys on the go!" },
    { key: "mobile_onboarding_slide1_image", label: "Slide 1 — illustration URL. Recommended: square PNG/SVG, 512×512px to 768×768px (transparent background, not circular-cropped), under ~200KB. Displayed at 256×256pt inside the app, so anything much smaller looks blurry on high-DPI phones. Must be a direct, always-reachable image link (test the URL in an incognito tab first) — a broken/blocked link falls back to the built-in illustration, never a blank box. Leave blank to use the built-in default.", type: "text", placeholder: "https://your-cdn.com/slide1.png" },
    { key: "mobile_onboarding_slide2_enabled", label: "Show slide 2", type: "toggle" },
    { key: "mobile_onboarding_slide2_title", label: "Slide 2 — headline", type: "text", placeholder: "Get notified of new surveys as soon as they become available!" },
    { key: "mobile_onboarding_slide2_image", label: "Slide 2 — illustration URL. Same spec as Slide 1: square PNG/SVG, 512-768px, transparent background.", type: "text", placeholder: "https://your-cdn.com/slide2.png" },
    { key: "mobile_onboarding_slide3_enabled", label: "Show slide 3", type: "toggle" },
    { key: "mobile_onboarding_slide3_title", label: "Slide 3 — headline", type: "text", placeholder: "Redeem your points for real rewards." },
    { key: "mobile_onboarding_slide3_image", label: "Slide 3 — illustration URL. Same spec as Slide 1: square PNG/SVG, 512-768px, transparent background.", type: "text", placeholder: "https://your-cdn.com/slide3.png" },
    { key: "mobile_onboarding_cta_primary", label: "Primary button label (Sign up)", type: "text", placeholder: "Sign up" },
    { key: "mobile_onboarding_cta_secondary", label: "Secondary button label (Sign in)", type: "text", placeholder: "Sign in" },
  ]},
  { group: "Footer", keys: [
    { key: "footer_tagline", label: "Tagline", type: "text", placeholder: "Independent Actionable Intelligence | Pakistan" },
    { key: "footer_copyright", label: "Copyright line", type: "text", placeholder: "© 2026 oodel Research Pvt Ltd." },
  ]},
  { group: "Survey Intro Defaults", keys: [
    { key: "survey_intro_default", label: "Default survey-intro text (used when admin hasn't set a per-wave override). Supports {{points}} and {{topic}} placeholders.", type: "textarea", placeholder: "This survey is on the topic of {{topic}}, and the results will be used to inform our research.\n\nYour account will be credited with {{points}} points for completing the survey.\n\nWe have tested the survey and found that, on average it takes around a few minutes to complete. This time may vary depending on factors such as your Internet connection speed and the answers you give.\n\nPlease click the forward button below to continue." },
    { key: "survey_intro_default_topic", label: "Default topic label (used when admin hasn't set a per-wave topic).", type: "text", placeholder: "this research" },
  ]},
  { group: "App Store Buttons (footer)", keys: [
    { key: "app_button_ios_enabled", label: "Show 'Download on the App Store' button", type: "toggle" },
    { key: "app_button_ios_url", label: "Apple App Store URL (leave blank → button shows but isn't clickable yet)", type: "text", placeholder: "https://apps.apple.com/app/oodel/id000000000" },
    { key: "app_button_android_enabled", label: "Show 'Get it on Google Play' button", type: "toggle" },
    { key: "app_button_android_url", label: "Google Play URL (leave blank → button shows but isn't clickable yet)", type: "text", placeholder: "https://play.google.com/store/apps/details?id=co.oodel.app" },
  ]},
];

// ---------------------------------------------------------------------------
// Top-level public nav reorder — id list MUST stay in sync with
// NAV_ITEM_IDS in frontend/src/layouts/PublicNav.jsx.
// ---------------------------------------------------------------------------
const NAV_ORDER_ITEMS = [
  { id: "area_of_interest", label: "Area of Interest (dropdown)" },
  { id: "why_oodel", label: "Why oodel (dropdown)" },
  { id: "editorial", label: "Editorial (dropdown)" },
  { id: "public_sentiment", label: "Public Sentiment (link)" },
  { id: "waves", label: "Waves (link)" },
  { id: "customer_sentiment", label: "oodel SCORE (link)" },
];
const NAV_ORDER_IDS = NAV_ORDER_ITEMS.map((n) => n.id);

function getOrderedNavIds(orderStr) {
  if (!orderStr) return NAV_ORDER_IDS;
  const parsed = orderStr.split(",").map((s) => s.trim()).filter((id) => NAV_ORDER_IDS.includes(id));
  const missing = NAV_ORDER_IDS.filter((id) => !parsed.includes(id));
  return [...parsed, ...missing];
}

function SortableNavOrderItem({ item }) {
  const { attributes, listeners, setNodeRef, transform, transition, setActivatorNodeRef } = useSortable({ id: item.id });
  return (
    <div ref={setNodeRef} {...attributes} style={{ transform: CSS.Transform.toString(transform), transition }} className="bg-white border border-neutral-200 px-3 py-2 flex items-center gap-2 text-sm" data-testid={`nav-order-item-${item.id}`}>
      <span ref={setActivatorNodeRef} {...listeners} className="cursor-grab text-neutral-400 hover:text-black select-none" title="Drag to reorder">⋮⋮</span>
      <span>{item.label}</span>
    </div>
  );
}

function NavOrderEditor({ value, onChange }) {
  const items = getOrderedNavIds(value).map((id) => NAV_ORDER_ITEMS.find((n) => n.id === id)).filter(Boolean);
  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const ids = items.map((i) => i.id);
    const oldI = ids.indexOf(active.id);
    const newI = ids.indexOf(over.id);
    onChange(arrayMove(ids, oldI, newI).join(","));
  };
  return (
    <DndContext collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
        <div className="space-y-1.5" data-testid="nav-order-editor">
          {items.map((item) => <SortableNavOrderItem key={item.id} item={item} />)}
        </div>
      </SortableContext>
    </DndContext>
  );
}

export function AdminSiteContent() {
  const [values, setValues] = useState(getCachedSiteContent);
  const [savedKeys, setSavedKeys] = useState(new Set());  // keys that have actual override (not just default)
  const [savingKey, setSavingKey] = useState(null);
  const [activeTab, setActiveTab] = useState(SITE_CONTENT_TABS[0]);
  const [activeGroup, setActiveGroup] = useState(null);  // null = first group of the tab
  const [search, setSearch] = useState("");
  const [resetTarget, setResetTarget] = useState(null);  // field key pending reset confirmation

  useEffect(() => {
    api.get("/site-content").then((r) => {
      const apiValues = r.data || {};
      // Seed every editable key: if API returned a value, use it; otherwise fall back
      // to the placeholder (default) so admins see real, editable text.
      const seeded = {};
      const haveOverride = new Set();
      SITE_CONTENT_KEYS.forEach((g) => g.keys.forEach((f) => {
        if (apiValues[f.key] !== undefined && apiValues[f.key] !== "") {
          seeded[f.key] = apiValues[f.key];
          haveOverride.add(f.key);
        } else {
          seeded[f.key] = f.placeholder ?? "";
        }
      }));
      setValues(seeded);
      setSavedKeys(haveOverride);
    }).catch(() => {});
  }, []);

  const save = async (key) => {
    setSavingKey(key);
    try {
      await api.put(`/admin/site-content/${key}`, { value: values[key] ?? "" });
      toast.success(`Saved · ${key}`);
      setSavedKeys(new Set([...savedKeys, key]));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSavingKey(null);
    }
  };

  const reset = async (key) => {
    // ITER 133 — was `window.confirm()`, silently blocked inside the
    // preview iframe (same bug class as the sentiment item/category
    // deletes above). Confirmation now happens via `resetTarget` + the
    // Dialog rendered at the bottom of this component.
    try {
      await api.delete(`/admin/site-content/${key}`);
      const fieldDef = SITE_CONTENT_KEYS.flatMap((g) => g.keys).find((f) => f.key === key);
      setValues({ ...values, [key]: fieldDef?.placeholder ?? "" });
      const next = new Set(savedKeys); next.delete(key); setSavedKeys(next);
      toast.success("Reset to default");
    } catch (err) { toast.error("Reset failed"); }
    setResetTarget(null);
  };

  // Build the group list for each tab.
  const tabsWithFallback = [...SITE_CONTENT_TABS];
  const knownGroups = new Set(Object.values(SITE_CONTENT_TAB_GROUPS).flat());
  const orphanGroups = SITE_CONTENT_KEYS.map((g) => g.group).filter((g) => !knownGroups.has(g));
  if (orphanGroups.length > 0 && !tabsWithFallback.includes("Other")) {
    tabsWithFallback.push("Other");
  }
  const groupsForActiveTab = activeTab === "Other"
    ? orphanGroups
    : (SITE_CONTENT_TAB_GROUPS[activeTab] || []);
  const groupsInOrder = groupsForActiveTab
    .map((name) => SITE_CONTENT_KEYS.find((g) => g.group === name))
    .filter(Boolean);

  // Search filter — applies to ALL groups in the active tab. When search has
  // content and there are no group hits we show a "no results" notice with a
  // jump-link to other tabs that DO have hits.
  const q = search.trim().toLowerCase();
  const matchesFilter = (f) => !q || f.key.toLowerCase().includes(q) || (f.label || "").toLowerCase().includes(q);

  // Effective rendered group: when there's a search, render all matching groups
  // expanded; when no search, render ONLY the single selected group (default to
  // the first group of the tab so the page never opens as an infinite scroll).
  const currentGroup = activeGroup || groupsInOrder[0]?.group;
  const visibleGroups = q
    ? groupsInOrder.map((g) => ({ ...g, keys: g.keys.filter(matchesFilter) })).filter((g) => g.keys.length > 0)
    : groupsInOrder.filter((g) => g.group === currentGroup);
  const tabCount = (tab) => {
    if (tab === "Other") return orphanGroups.length;
    return (SITE_CONTENT_TAB_GROUPS[tab] || []).length;
  };

  // Reset the active group when switching tabs so we land on tab's first item.
  useEffect(() => { setActiveGroup(null); }, [activeTab]);

  return (
    <div data-testid="admin-site-content">
      <div className="pb-4 mb-5 flex flex-wrap justify-between items-end gap-4">
        <div>
          <div className="text-[11px] font-bold tracking-widest text-mango uppercase">Site Content</div>
          <h1 className="text-3xl font-black tracking-tight mt-0.5">Public copy editor</h1>
          <p className="text-sm text-neutral-500 mt-1.5">Edit headlines, copy, footers and toggles without a code deploy. Empty value = use the default from code.</p>
        </div>
        <div className="w-full md:w-80">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search all fields (key or label)…"
            className="w-full px-3.5 py-2.5 rounded-xl border border-neutral-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-mango/30 focus:border-mango"
            data-testid="site-content-search"
          />
          {q && (
            <div className="text-xs text-neutral-500 mt-1">
              Searching across <strong>{activeTab}</strong> · {visibleGroups.reduce((n, g) => n + g.keys.length, 0)} match{visibleGroups.reduce((n, g) => n + g.keys.length, 0) === 1 ? "" : "es"}
            </div>
          )}
        </div>
      </div>

      {/* Top tab bar */}
      <div className="flex flex-wrap gap-2 mb-5" data-testid="site-content-tabs">
        {tabsWithFallback.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            data-testid={`site-content-tab-${tab.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className={`px-4 py-2 rounded-full text-xs font-bold transition-colors flex items-center gap-2 ${
              activeTab === tab
                ? "bg-mango text-white"
                : "bg-white border border-neutral-200 text-neutral-600 hover:border-neutral-400"
            }`}
          >
            <span>{tab}</span>
            <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-full ${
              activeTab === tab ? "bg-white/20 text-white" : "bg-neutral-100 text-neutral-500"
            }`}>{tabCount(tab)}</span>
          </button>
        ))}
      </div>

      {q && visibleGroups.length === 0 && (
        <div className="bg-white rounded-2xl border border-dashed border-neutral-300 p-6 text-sm text-neutral-600" data-testid="site-content-no-match">
          No fields in <strong>{activeTab}</strong> match <code>{q}</code>.{" "}
          {tabsWithFallback.filter((t) => t !== activeTab).map((t) => {
            const groups = t === "Other" ? orphanGroups : (SITE_CONTENT_TAB_GROUPS[t] || []);
            const otherMatches = SITE_CONTENT_KEYS
              .filter((g) => groups.includes(g.group))
              .reduce((n, g) => n + g.keys.filter((f) => f.key.toLowerCase().includes(q) || (f.label || "").toLowerCase().includes(q)).length, 0);
            return otherMatches > 0 ? (
              <button key={t} onClick={() => setActiveTab(t)} className="ml-2 underline text-mango font-semibold">{t} ({otherMatches})</button>
            ) : null;
          })}
        </div>
      )}

      {/* Two-column layout: left rail (group list), right content (active group fields).
          On mobile (< md), the rail collapses into a <select> for compactness. */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
        {/* Left rail */}
        <aside className="md:col-span-3" data-testid="site-content-rail">
          {/* Mobile select */}
          <div className="md:hidden mb-3">
            <select
              value={currentGroup || ""}
              onChange={(e) => setActiveGroup(e.target.value)}
              className="w-full px-3 py-2 border-2 border-black bg-white text-sm font-bold"
            >
              {groupsInOrder.map((g) => (
                <option key={g.group} value={g.group}>{g.group} ({g.keys.length})</option>
              ))}
            </select>
          </div>
          {/* Desktop list */}
          <nav className="hidden md:block bg-white border-2 border-black sticky top-4 max-h-[calc(100vh-120px)] overflow-y-auto" aria-label="Section navigation">
            <ul>
              {groupsInOrder.map((g) => {
                const isActive = g.group === currentGroup;
                // Compute # of saved overrides per group for the badge.
                const overrides = g.keys.filter((f) => savedKeys.has(f.key)).length;
                return (
                  <li key={g.group}>
                    <button
                      onClick={() => setActiveGroup(g.group)}
                      data-testid={`site-content-group-${g.group.replace(/[^a-zA-Z0-9]+/g, "-").slice(0, 60)}`}
                      className={`w-full text-left px-3 py-2 text-xs leading-snug border-b border-neutral-100 transition-colors ${
                        isActive ? "bg-mango text-black font-bold" : "hover:bg-neutral-50 text-neutral-700"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <span className="flex-1">{g.group}</span>
                        <span className={`shrink-0 font-mono text-[9px] px-1 py-0.5 ${
                          isActive ? "bg-black text-mango" : "bg-neutral-200 text-neutral-600"
                        }`}>{g.keys.length}</span>
                      </div>
                      {overrides > 0 && !isActive && (
                        <div className="text-[9px] text-mango mt-0.5">● {overrides} customised</div>
                      )}
                    </button>
                  </li>
                );
              })}
              {groupsInOrder.length === 0 && (
                <li className="px-3 py-3 text-xs text-neutral-400 italic">No groups in this tab.</li>
              )}
            </ul>
          </nav>
        </aside>

        {/* Right column: only the active group renders. When searching, render
            all matching groups so admin sees results without manual nav clicks. */}
        <section className="md:col-span-9 space-y-6" data-testid="site-content-active">
          {visibleGroups.map((g) => (
            <div key={g.group} className="bg-white border-2 border-black">
              <div className="bg-black text-white px-4 py-2 overline flex items-center justify-between gap-2">
                <span>{g.group}</span>
                <span className="text-[10px] font-mono text-neutral-400">{g.keys.length} {g.keys.length === 1 ? "field" : "fields"}</span>
              </div>
              <div className="p-5 space-y-4">
                {g.keys.map((f) => (
                  <div key={f.key} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start" data-testid={`sc-row-${f.key}`}>
                    <div className="md:col-span-3">
                      <Label className="overline text-xs">{f.label}</Label>
                      <div className="flex items-center gap-2 mt-0.5">
                      <div className="font-mono text-[10px] text-neutral-400">{f.key}</div>
                      {savedKeys.has(f.key) && f.type !== "toggle" && f.type !== "radio" && f.type !== "nav_order" && (
                        <span className="text-[9px] bg-mango text-white px-1 font-bold" title="This field has an admin override; click Reset to restore the default.">CUSTOM</span>
                      )}
                    </div>
                  </div>
                  <div className="md:col-span-7">
                    {f.type === "toggle" ? (
                      <div className="flex items-center gap-3 py-1">
                        <Switch
                          checked={(values[f.key] || "false").toLowerCase() === "true"}
                          onCheckedChange={(v) => {
                            const next = v ? "true" : "false";
                            setValues({ ...values, [f.key]: next });
                            // Auto-save toggles (no separate Save click required).
                            api.put(`/admin/site-content/${f.key}`, { value: next })
                              .then(() => toast.success(`${f.key} = ${next}`))
                              .catch(() => toast.error("Save failed"));
                          }}
                          data-testid={`sc-toggle-${f.key}`}
                        />
                        <span className="text-sm font-mono">{(values[f.key] || "false").toLowerCase() === "true" ? "ON" : "OFF"}</span>
                      </div>
                    ) : f.type === "radio" ? (
                      <div className="flex gap-2 flex-wrap">
                        {f.options.map((opt) => (
                          <button
                            type="button"
                            key={opt.value}
                            onClick={() => {
                              setValues({ ...values, [f.key]: opt.value });
                              api.put(`/admin/site-content/${f.key}`, { value: opt.value })
                                .then(() => toast.success(`${f.key} = ${opt.value}`))
                                .catch(() => toast.error("Save failed"));
                            }}
                            className={`px-3 py-2 text-sm border-2 ${values[f.key] === opt.value ? "bg-mango text-white border-black" : "border-neutral-300 hover:border-black"}`}
                            data-testid={`sc-radio-${f.key}-${opt.value}`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    ) : f.type === "nav_order" ? (
                      <NavOrderEditor
                        value={values[f.key] || ""}
                        onChange={(next) => {
                          setValues({ ...values, [f.key]: next });
                          api.put(`/admin/site-content/${f.key}`, { value: next })
                            .then(() => toast.success("Menu order saved"))
                            .catch(() => toast.error("Save failed"));
                        }}
                      />
                    ) : f.type === "textarea" ? (
                      <Textarea
                        data-testid={`sc-${f.key}`}
                        value={values[f.key] ?? ""}
                        placeholder={f.placeholder || "(leave blank to use default)"}
                        onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                        rows={3}
                        className="rounded-none"
                      />
                    ) : (
                      <Input
                        data-testid={`sc-${f.key}`}
                        value={values[f.key] ?? ""}
                        placeholder={f.placeholder || "(leave blank to use default)"}
                        onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                        className="rounded-none"
                      />
                    )}
                  </div>
                  <div className="md:col-span-2 flex flex-col gap-2">
                    {f.type !== "toggle" && f.type !== "radio" && f.type !== "nav_order" && (
                      <>
                        <Button type="button" data-testid={`sc-save-${f.key}`} onClick={() => save(f.key)} disabled={savingKey === f.key} className="bg-black hover:bg-neutral-800 rounded-none text-xs">
                          {savingKey === f.key ? "Saving…" : "Save"}
                        </Button>
                        <Button type="button" variant="ghost" onClick={() => setResetTarget(f.key)} data-testid={`sc-reset-${f.key}`} className="rounded-none text-xs text-neutral-500 hover:text-red-600">Reset</Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
        </section>
      </div>
      <div className="mt-8 border-l-4 border-mango bg-mango/5 p-4 text-sm text-neutral-600">
        <strong>Tip:</strong> Changes appear immediately on the public homepage and navigation. Hard-refresh in another tab to verify.
      </div>
      <Dialog open={!!resetTarget} onOpenChange={(o) => { if (!o) setResetTarget(null); }}>
        <DialogContent className="rounded-none border-2 border-black max-w-md" data-testid="sc-reset-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black tracking-tighter">Reset this field?</DialogTitle>
            <DialogDescription className="text-neutral-700 pt-1">
              This deletes your override for <code className="text-xs bg-neutral-100 px-1">{resetTarget}</code> and falls back to the default text shown as its placeholder.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="pt-2">
            <Button variant="outline" onClick={() => setResetTarget(null)} data-testid="sc-reset-cancel">Cancel</Button>
            <Button onClick={() => reset(resetTarget)} className="bg-red-600 hover:bg-red-700 text-white rounded-none" data-testid="sc-reset-confirm">Reset to default</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// =================== ADMIN — WAITLIST LANDING PAGE EDITOR ===================
// Standalone CMS page for editing the Google-style waitlist landing copy +
// flipping the master toggle. Pulls from /api/site-content via the existing
// wl_* keys. Lives in /admin/waitlist-landing.
const WL_GROUPS = [
  {
    label: "Master switch",
    fields: [
      { key: "waitlist_landing_enabled", label: "Show waitlist landing page instead of the homepage", help: "When ON, every public visitor to '/' sees the waitlist landing page instead of the regular homepage. Admin / PM accounts always bypass this gate so you can keep working.", type: "toggle" },
    ],
  },
  {
    label: "Hero — headline + tagline",
    fields: [
      { key: "wl_h1_line1", label: "Hero line 1", help: "Wrap any word in single asterisks to highlight in green. e.g. Real *issues*.", placeholder: "Real *issues*." },
      { key: "wl_h1_line2", label: "Hero line 2", placeholder: "Real *people*." },
      { key: "wl_h1_line3", label: "Hero line 3", placeholder: "Real *impact*." },
      { key: "wl_sub", label: "Subtitle", help: "One short sentence under the headline.", placeholder: "Your *voice* can turn challenges into real *change*." },
      { key: "wl_topbar_tag", label: "Top-bar tag (right side)", placeholder: "Pakistan's citizen voice panel" },
    ],
  },
  {
    label: "Three value pills",
    fields: [
      { key: "wl_pill_1", label: "Pill 1", placeholder: "*Share* your opinion." },
      { key: "wl_pill_2", label: "Pill 2", placeholder: "*Create* impact." },
      { key: "wl_pill_3", label: "Pill 3", placeholder: "*Get* rewarded for it!" },
    ],
  },
  {
    label: "Form labels & CTA",
    fields: [
      { key: "wl_placeholder_name", label: "Name field placeholder", placeholder: "Your name" },
      { key: "wl_placeholder_email", label: "Email field placeholder", placeholder: "you@example.com" },
      { key: "wl_cta", label: "Submit button label", placeholder: "Join the waitlist" },
      { key: "wl_fineprint", label: "Fine print under the form", help: "Privacy / spam reassurance.", placeholder: "We'll only email you about oodel — no spam." },
    ],
  },
  {
    label: "After signup — success messages",
    fields: [
      { key: "wl_success_title", label: "Success title (new signup)", placeholder: "You're on the list!" },
      { key: "wl_success_body", label: "Success body (new signup)", type: "textarea", placeholder: "We'll email you when surveys go live..." },
      { key: "wl_already_title", label: "Title for repeat signups", placeholder: "You're already in!" },
      { key: "wl_already_body", label: "Body for repeat signups", type: "textarea", placeholder: "Looks like you joined us earlier..." },
    ],
  },
  {
    label: "Bottom strap + footer",
    fields: [
      { key: "wl_strap", label: "Strap (bold line below the hero)", type: "textarea", placeholder: "The *future* isn't decided in boardrooms *alone*. It's shaped by people like *you*." },
      { key: "wl_footer_left", label: "Footer (left)", placeholder: "© oodel. — Built in Pakistan, for Pakistan." },
      { key: "wl_footer_right", label: "Footer (right)", placeholder: "Together, let's build a better tomorrow." },
    ],
  },
  {
    label: "Visual customisation",
    fields: [
      { key: "wl_bg_color", label: "Background colour (hex)", help: "Default #F6F1E8 (cream).", placeholder: "#F6F1E8" },
      { key: "wl_hero_image_url", label: "Hero illustration URL", help: "PNG or SVG with transparent / cream background works best. Default 1:1 ratio.", placeholder: "https://...png" },
      { key: "wl_hero_image_alt", label: "Hero image alt text (accessibility)", placeholder: "Pakistanis sharing their opinions" },
    ],
  },
  {
    label: "SEO — what Google sees",
    fields: [
      { key: "wl_meta_title", label: "Page <title>", placeholder: "oodel. — Real issues. Real people. Real impact." },
      { key: "wl_meta_description", label: "Meta description", type: "textarea", placeholder: "Pakistan's first nationally-representative citizen voice panel." },
    ],
  },
];

export function AdminWaitlistLandingEditor() {
  const [values, setValues] = useState(getCachedSiteContent);
  const [loading, setLoading] = useState(() => Object.keys(getCachedSiteContent()).length === 0);
  const [saving, setSaving] = useState({});
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    api.get("/site-content").then((r) => { setValues(r.data || {}); setLoading(false); });
  }, []);

  const setVal = (k, v) => setValues((s) => ({ ...s, [k]: v }));
  const save = async (k) => {
    setSaving((s) => ({ ...s, [k]: true }));
    try {
      await api.put(`/admin/site-content/${k}`, { value: values[k] ?? "" });
      toast.success(`Saved ${k}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving((s) => ({ ...s, [k]: false }));
    }
  };
  const saveAll = async () => {
    const keys = WL_GROUPS.flatMap((g) => g.fields.map((f) => f.key));
    await Promise.all(keys.map((k) => api.put(`/admin/site-content/${k}`, { value: values[k] ?? "" })));
    toast.success("All fields saved");
  };

  const enabled = String(values.waitlist_landing_enabled || "false").toLowerCase() === "true";

  if (loading) return <div className="p-12 text-center text-neutral-400">Loading…</div>;

  return (
    <div data-testid="admin-waitlist-landing">
      <div className="border-b-2 border-black pb-4 mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="overline">SITE CONTENT</div>
          <h1 className="text-4xl font-black tracking-tighter">Waitlist landing page</h1>
          <p className="text-sm text-neutral-600 mt-2 max-w-2xl">
            Google-style minimal page that <b>replaces the public homepage</b> when the master switch below is on. Wrap any word in single-asterisks (e.g. <code className="bg-neutral-100 px-1">Real *issues*.</code>) to highlight it in brand green.
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowPreview(true)} variant="outline" className="rounded-none" data-testid="wl-preview">Preview</Button>
          <Button onClick={saveAll} className="rounded-none bg-mango hover:bg-mango/90 text-white" data-testid="wl-save-all">Save all fields</Button>
        </div>
      </div>

      {enabled && (
        <div className="bg-[#2E8B57]/10 border-l-4 border-[#2E8B57] p-3 mb-6 text-sm" data-testid="wl-status-on">
          <b>The waitlist landing is live.</b> Public visitors to <code className="font-mono">/</code> see the landing page instead of the homepage. <a href="/" target="_blank" rel="noreferrer" className="underline">Open in new tab →</a>
        </div>
      )}

      <div className="space-y-8">
        {WL_GROUPS.map((g) => (
          <section key={g.label} className="bg-white border-2 border-black" data-testid={`wl-group-${g.label.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
            <div className="bg-black text-white px-4 py-2 overline">{g.label}</div>
            <div className="p-5 space-y-4">
              {g.fields.map((f) => (
                <div key={f.key}>
                  <label className="block">
                    <div className="text-sm font-medium mb-1">{f.label}</div>
                    {f.help && <div className="text-xs text-neutral-500 mb-2">{f.help}</div>}
                    {f.type === "toggle" ? (
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => setVal(f.key, String((values[f.key] || "false").toLowerCase() !== "true"))}
                          data-testid={`wl-toggle-${f.key}`}
                          className={`relative inline-flex h-6 w-11 items-center transition-colors ${(values[f.key] || "false").toLowerCase() === "true" ? "bg-[#2E8B57]" : "bg-neutral-300"}`}
                        >
                          <span className={`inline-block h-5 w-5 transform bg-white shadow transition-transform ${(values[f.key] || "false").toLowerCase() === "true" ? "translate-x-5" : "translate-x-0.5"}`} />
                        </button>
                        <span className="font-mono text-xs text-neutral-600">{(values[f.key] || "false").toLowerCase()}</span>
                      </div>
                    ) : f.type === "textarea" ? (
                      <textarea
                        value={values[f.key] ?? ""}
                        onChange={(e) => setVal(f.key, e.target.value)}
                        placeholder={f.placeholder}
                        rows={3}
                        data-testid={`wl-field-${f.key}`}
                        className="w-full border-2 border-neutral-300 focus:border-black px-3 py-2 text-sm rounded-none font-mono"
                      />
                    ) : (
                      <Input
                        value={values[f.key] ?? ""}
                        onChange={(e) => setVal(f.key, e.target.value)}
                        placeholder={f.placeholder}
                        data-testid={`wl-field-${f.key}`}
                        className="rounded-none font-mono"
                      />
                    )}
                  </label>
                  <Button onClick={() => save(f.key)} disabled={saving[f.key]} size="sm" className="rounded-none bg-black hover:bg-neutral-800 text-white text-xs mt-2" data-testid={`wl-save-${f.key}`}>
                    {saving[f.key] ? "Saving…" : "Save field"}
                  </Button>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>

      {showPreview && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setShowPreview(false)} data-testid="wl-preview-modal">
          <div className="bg-white w-full max-w-6xl h-[90vh] border-2 border-black" onClick={(e) => e.stopPropagation()}>
            <div className="bg-black text-white px-4 py-2 flex items-center justify-between text-xs">
              <span className="overline">LIVE PREVIEW — uses your latest <i>saved</i> values</span>
              <button onClick={() => setShowPreview(false)} className="hover:text-mango" data-testid="wl-preview-close">✕ Close</button>
            </div>
            <iframe title="waitlist-preview" src="/" className="w-full h-[calc(90vh-32px)]" />
          </div>
        </div>
      )}
    </div>
  );
}

// =================== ADMIN — CHANGE OWN PASSWORD ===================
export function AdminMyAccount() {
  const { user, refresh } = useAuth();
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw1.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (pw1 !== pw2) { toast.error("Passwords don't match"); return; }
    setSaving(true);
    try {
      const { data } = await api.put("/auth/password", { new_password: pw1 });
      // ITER 81 — swap in the fresh token the backend just minted before
      // calling refresh(), otherwise refresh() uses the now-revoked old
      // token and surfaces a false "Session ended" error right after a
      // successful password change.
      if (data?.token) localStorage.setItem("pollpak_token", data.token);
      await refresh();
      toast.success("Password updated. Use the new password next time you log in.");
      setPw1(""); setPw2("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update password");
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl">
      <div className="border-b-2 border-black pb-4 mb-6">
        <div className="overline">MY ACCOUNT</div>
        <h1 className="text-4xl font-black tracking-tighter">Account & security</h1>
      </div>

      <div className="bg-white border-2 border-black p-6 mb-6">
        <div className="overline mb-2">SIGNED IN AS</div>
        <div className="text-2xl font-bold">{user?.name}</div>
        <div className="text-sm text-neutral-600 font-mono mt-1">{user?.email}</div>
        <div className="text-xs text-neutral-500 mt-2">Role: {user?.role} · ID: <span className="font-mono">{user?.id}</span></div>
      </div>

      <div className="bg-white border-2 border-black p-6">
        <div className="overline mb-2">CHANGE PASSWORD</div>
        <h2 className="text-2xl font-bold mb-1">Update your sign-in password</h2>
        <p className="text-sm text-neutral-600 mb-6">Choose a strong password (min 6 characters). You'll stay signed in here, but the new password is needed next time you sign in.</p>
        <form onSubmit={submit} className="space-y-4 max-w-md" data-testid="admin-password-form">
          <div><Label className="overline">New password</Label><PasswordInput testId="admin-pw-new" value={pw1} onChange={(e) => setPw1(e.target.value)} placeholder={`Min ${PASSWORD_MIN_LENGTH} characters`} autoComplete="new-password" /></div>
          <div><Label className="overline">Confirm new password</Label><PasswordInput testId="admin-pw-confirm" value={pw2} onChange={(e) => setPw2(e.target.value)} placeholder="Re-enter password" autoComplete="new-password" /></div>
          <Button data-testid="admin-pw-submit" disabled={saving} type="submit" className="rounded-none bg-black hover:bg-neutral-800 text-white">
            {saving ? "Saving…" : "Update password"}
          </Button>
        </form>
      </div>

      <div className="mt-8 border-l-4 border-mango bg-mango/5 p-4 text-sm text-neutral-700">
        <strong>Locked out?</strong> If you ever forget your admin password, change the <code className="font-mono bg-black text-white px-1.5 py-0.5">ADMIN_PASSWORD</code> environment variable on Render and redeploy. The backend re-syncs your admin password from that value on every startup.
      </div>
    </div>
  );
}


// =================== ADMIN — OODEL HEALTH ===================
const STATUS_COLOR = {
  ok:       { bg: "bg-emerald-50",  border: "border-emerald-500", text: "text-emerald-700", dot: "bg-emerald-500",  label: "HEALTHY"  },
  warn:     { bg: "bg-amber-50",    border: "border-amber-500",   text: "text-amber-800",   dot: "bg-amber-500",    label: "WARNING"  },
  critical: { bg: "bg-red-50",      border: "border-red-600",     text: "text-red-700",     dot: "bg-red-600",      label: "CRITICAL" },
};

function formatValue(v, fmt) {
  if (v == null) return "—";
  if (fmt === "pct") return `${v}%`;
  if (fmt === "hours") return `${v}h`;
  if (fmt === "pkr") return `Rs ${Number(v).toLocaleString()}`;
  if (fmt === "bool") return v ? "ONLINE" : "OFFLINE";
  if (fmt === "abs" || !fmt) return typeof v === "number" ? v.toLocaleString() : v;
  return String(v);
}

function MetricCard({ m, onClick }) {
  const s = STATUS_COLOR[m.status] || STATUS_COLOR.ok;
  return (
    <button
      type="button"
      onClick={() => onClick && onClick(m)}
      className={`border-l-4 ${s.border} ${s.bg} p-4 hover:shadow-md hover:-translate-y-0.5 transition-all text-left w-full focus:outline-none focus:ring-2 focus:ring-black`}
      data-testid={`hth-metric-${m.label.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className={`text-[10px] tracking-widest font-bold uppercase ${s.text} flex items-center gap-1.5`}>
            <span className={`inline-block w-1.5 h-1.5 rounded-full ${s.dot}`}></span>
            {s.label}
          </div>
          <div className="text-sm text-neutral-700 mt-1 font-medium leading-snug">{m.label}</div>
        </div>
        <div className="text-[10px] text-neutral-400 font-mono opacity-0 group-hover:opacity-100 transition-opacity">DETAILS →</div>
      </div>
      <div className="mt-3 text-3xl font-black tracking-tighter text-black">{formatValue(m.value, m.fmt)}</div>
      <div className="mt-1 text-xs text-neutral-500">Target: <span className="font-mono">{m.target || "—"}</span></div>
      {m.message && <div className="mt-3 text-xs text-neutral-600 leading-relaxed border-t border-neutral-200 pt-2 line-clamp-2">{m.message}</div>}
    </button>
  );
}

function MetricDrillDownDialog({ metric, onClose }) {
  const [drill, setDrill] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Fetch the rich drill-down payload whenever a metric is opened. The
  // backend's `/admin/health/metric/{key}` returns cohort tables, cross-tab
  // rows, and a raw Mongo query — admin can copy any table to CSV in one
  // click. Unknown metrics gracefully fall back to the meta panel.
  useEffect(() => {
    if (!metric) return;
    setDrill(null); setError(null);
    setLoading(true);
    api.get(`/admin/health/metric/${metric.key}`)
      .then((r) => setDrill(r.data))
      .catch((e) => { if (e.response?.status !== 404) setError(e.response?.data?.detail || e.message); })
      .finally(() => setLoading(false));
  }, [metric]);
  if (!metric) return null;
  const s = STATUS_COLOR[metric.status] || STATUS_COLOR.ok;
  const downloadCSV = (table) => {
    const cols = table.columns;
    const header = cols.join(",");
    const lines = table.rows.map((r) => cols.map((c) => {
      const v = r[c];
      if (v == null) return "";
      const str = String(v).replace(/"/g, '""');
      return /[,"\n]/.test(str) ? `"${str}"` : str;
    }).join(","));
    const blob = new Blob([header + "\n" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${metric.key}__${table.title.replace(/[^a-z0-9]+/gi, "_").toLowerCase()}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  };
  return (
    <Dialog open={!!metric} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-5xl rounded-none border-2 border-black p-0 max-h-[90vh] overflow-hidden flex flex-col" data-testid="hth-drill">
        {/* HEADER STRIP — status-coloured */}
        <DialogHeader className={`${s.bg} ${s.border} border-l-4 p-5 space-y-1 flex-shrink-0`}>
          <div className={`text-[10px] tracking-widest font-bold uppercase ${s.text} flex items-center gap-1.5`}>
            <span className={`inline-block w-1.5 h-1.5 rounded-full ${s.dot}`}></span>
            {s.label} · {metric.group}
          </div>
          <DialogTitle className="text-2xl font-black tracking-tighter">{drill?.title || metric.label}</DialogTitle>
          <div className="flex items-end gap-6 flex-wrap mt-2">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">Current</div>
              <div className="font-mono text-4xl font-black text-black">{formatValue(metric.value, metric.fmt)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">Healthy target</div>
              <div className="text-sm font-mono text-neutral-700">{metric.target || "—"}</div>
            </div>
            {drill?.summary && Object.entries(drill.summary).map(([k, v]) => (
              <div key={k}>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">{k.replace(/_/g, " ")}</div>
                <div className="font-mono text-lg font-bold text-black">{typeof v === "number" ? v.toLocaleString() : v}</div>
              </div>
            ))}
          </div>
        </DialogHeader>

        {/* BODY — scrollable */}
        <div className="p-5 space-y-5 overflow-y-auto flex-1">
          {/* What it means + how measured */}
          <div className="grid md:grid-cols-2 gap-4">
            <section className="bg-neutral-50 border border-neutral-200 p-3">
              <div className="overline mb-1">WHAT IT MEANS</div>
              <p className="text-sm text-neutral-700 leading-relaxed">{metric.message || "No explanation provided yet."}</p>
            </section>
            <section className="bg-neutral-50 border border-neutral-200 p-3">
              <div className="overline mb-1">HOW IT&apos;S MEASURED</div>
              <p className="text-sm text-neutral-700 leading-relaxed">{METRIC_DEFINITIONS[metric.label] || "Sampled live from production database on every snapshot (auto-refresh every 60s)."}</p>
            </section>
          </div>

          {/* Recommended action — only on warn/critical */}
          {metric.status !== "ok" && (
            <section className={`${s.bg} ${s.border} border-l-4 p-3`}>
              <div className={`overline mb-1 ${s.text}`}>RECOMMENDED ACTION</div>
              <p className="text-sm text-neutral-700 leading-relaxed">{REMEDIATION[metric.label] || "Investigate the contributing data and consult the platform handbook."}</p>
            </section>
          )}

          {/* Drill-down tables */}
          {loading && <div className="text-sm text-neutral-400 italic" data-testid="hth-drill-loading">Loading underlying data…</div>}
          {!loading && !drill && !error && (
            <div className="bg-neutral-50 border border-dashed border-neutral-300 p-4 text-center text-xs text-neutral-500">
              No deep drill-down available for this metric yet. The raw metric JSON is shown below.
            </div>
          )}
          {error && <div className="bg-red-50 border border-red-200 text-red-700 p-3 text-sm">{error}</div>}
          {drill?.tables?.map((t, i) => (
            <section key={i} className="border-2 border-black" data-testid={`hth-drill-table-${i}`}>
              <div className="bg-black text-white px-4 py-2 flex items-center justify-between">
                <div>
                  <div className="overline">{t.title}</div>
                  {t.subtitle && <div className="text-[11px] opacity-70 font-mono mt-0.5">{t.subtitle}</div>}
                </div>
                <button
                  onClick={() => downloadCSV(t)}
                  className="text-[11px] tracking-widest font-bold uppercase bg-mango text-black px-3 py-1 hover:bg-yellow-400 transition-colors"
                  data-testid={`hth-drill-csv-${i}`}
                >Download CSV</button>
              </div>
              <div className="overflow-x-auto max-h-[50vh] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-neutral-100 sticky top-0">
                    <tr>
                      {t.columns.map((c) => (
                        <th key={c} className="px-3 py-2 text-left font-bold uppercase tracking-wider text-[10px] text-neutral-600 border-b border-neutral-200 whitespace-nowrap">{c.replace(/_/g, " ")}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {t.rows.length === 0 && (
                      <tr><td className="px-3 py-6 text-center text-neutral-400 italic" colSpan={t.columns.length}>No rows — the underlying query returned empty.</td></tr>
                    )}
                    {t.rows.map((r, ri) => (
                      <tr key={ri} className="border-b border-neutral-100 hover:bg-neutral-50">
                        {t.columns.map((c) => {
                          const v = r[c];
                          let cls = "px-3 py-2 align-top";
                          let display = v == null ? "—" : String(v);
                          if (typeof v === "number") {
                            cls += " font-mono text-right tabular-nums";
                            display = v.toLocaleString();
                          }
                          // Highlight negative deltas in red, positives in emerald
                          if (c === "delta_pp" && typeof v === "number") {
                            cls += v < -5 ? " text-red-600 font-semibold" : v > 5 ? " text-emerald-600 font-semibold" : "";
                            display = `${v > 0 ? "+" : ""}${v}`;
                          }
                          return <td key={c} className={cls}>{display}</td>;
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="bg-neutral-50 px-3 py-1.5 text-[10px] text-neutral-500 border-t border-neutral-200 font-mono">{t.rows.length.toLocaleString()} rows</div>
              </div>
            </section>
          ))}

          {/* Raw query — for power users to paste into MongoDB Compass */}
          {drill?.raw_query && (
            <section className="border border-neutral-300">
              <div className="bg-neutral-100 px-3 py-2 overline flex items-center justify-between">
                <span>RAW MONGO QUERY · paste into MongoDB Compass</span>
                <button
                  onClick={() => { navigator.clipboard?.writeText(JSON.stringify(drill.raw_query, null, 2)); }}
                  className="text-[10px] tracking-widest font-bold uppercase hover:text-mango transition-colors"
                  data-testid="hth-drill-copy-query"
                >Copy</button>
              </div>
              <pre className="text-[11px] bg-neutral-50 p-3 font-mono overflow-x-auto leading-relaxed">{JSON.stringify(drill.raw_query, null, 2)}</pre>
            </section>
          )}

          {/* Raw metric — collapsed by default behaviour via small details element */}
          <details className="border border-neutral-200 bg-neutral-50">
            <summary className="px-3 py-2 cursor-pointer text-[10px] uppercase tracking-widest font-bold text-neutral-600 hover:bg-neutral-100">Raw metric metadata</summary>
            <pre className="text-[11px] p-3 font-mono overflow-x-auto">{JSON.stringify(metric, null, 2)}</pre>
          </details>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Short, human definitions for the "How it's measured" panel. Falls back to a generic line.
const METRIC_DEFINITIONS = {
  "Total verified panelists": "Patrons whose `profile.profile_complete` flag is TRUE — i.e. they completed every required DNA field. This is the same gate used by /admin/waves cohort sends and result eligibility, so the number here is the true 'usable panel'.",
  "30-day active panelists": "Panelists whose last_login_at is within the last 30 days.",
  "New signups (7d)": "Panelist accounts created in the last 7 days.",
  "Suspended panelists": "Accounts with suspended=true flag; usually fraud, double-accounts or rule violations.",
  "SEC distribution drift": "Max % deviation between our panel's SEC class share and Pakistan census targets (A=5, B=15, C=30, D=35, E=15).",
  "Regional distribution drift": "Max % deviation between our panel's region share and census targets (Punjab=53, Sindh=23, KP=14, Bal=5, ICT=3, AJK/GB=2).",
  "Female panelists": "% of profile-complete panelists where gender='Female'. Pakistan adult population ≈ 49% female.",
  "Languages represented": "Distinct languages declared by panelists in lifestyle.languages.",
  "Speed-trap void rate": "Responses voided by speed-trap (elapsed < n_questions × 3s) ÷ total responses.",
  "Average completion rate": "Mean of (completed responses ÷ opened responses) per wave, only counting waves with >5 opens.",
  "Total responses logged": "Lifetime count of documents in the responses collection (includes void).",
  "Median redemption SLA": "Median hours between reward redemption.created_at and fulfilled_at (last 50 fulfilments).",
  "Outstanding points liability": "Sum of points balance across all panelists. PKR cost depends on your redemption rate.",
  "Pending redemptions": "Redemptions in pending or in_progress status. >40% of total = ops backlog.",
  "Paying business customers": "Customers with payment_status='paid'.",
  "Estimated MRR (PKR)": "Sum of (annual_fee / 12) across paid customers. Excludes ad-hoc per-survey fees.",
  "Enterprise tier accounts": "Customers with has_private_vault_access=true.",
  "Articles published (30d)": "Articles with created_at in the last 30 days (any status).",
  "AI vs hand-edited ratio": "% of articles where ai_generated=true. 100% AI erodes editorial voice.",
  "Articles with audio briefing": "Articles with non-null audio_url.",
  "Database connection": "MongoDB Atlas ping result during snapshot generation.",
  "Fraud-flagged panelists": "Panelists with potential_fraud=true (IP/residency mismatch heuristic).",
  "Must-change-password users": "Admin-created accounts that haven't completed their first password change.",
};

const REMEDIATION = {
  "Total verified panelists": "Run a new acquisition wave — WhatsApp share contest, paid social, university partnerships. Push the referral programme (200 pts per credit).",
  "30-day active panelists": "Re-engage dormant users with a high-points hero survey. Push notification + email blast.",
  "SEC distribution drift": "Target acquisition campaigns at under-represented SEC classes. Consider partner channels (e.g., bank apps for SEC A/B; field agents for SEC D/E).",
  "Regional distribution drift": "Boost acquisition in under-covered regions (typically Balochistan + AJK/GB). Offer regional language surveys.",
  "Female panelists": "Audit acquisition channels for gender bias. Add female-friendly creative and consider women-only ambassador programmes.",
  "Speed-trap void rate": "Increase minimum time per question (current = 3s) or tighten audit rules. Investigate specific waves for fraud rings.",
  "Average completion rate": "Audit recent waves — long surveys, unclear copy or low incentive are the usual culprits.",
  "Median redemption SLA": "Add operations capacity or automate fulfilment for high-volume rewards (e.g., bulk EasyPaisa scripts).",
  "Pending redemptions": "Triage the backlog. Consider auto-fulfilment for low-risk rewards (global codes, charity).",
  "Articles published (30d)": "Schedule the editorial calendar — at least 3 articles/week. Use the AI editorial tab to draft quickly.",
  "AI vs hand-edited ratio": "Have your editor add hand-written framing/intros to the top 30% of stories.",
  "Database connection": "Check MONGO_URL, Atlas Network Access whitelist and DB user permissions.",
  "Fraud-flagged panelists": "Run a moderation review pass on flagged accounts. Suspend confirmed bad actors.",
};

function AlertsPanel({ alerts, onClose }) {
  if (!alerts?.length) {
    return (
      <div className="bg-emerald-50 border-l-4 border-emerald-500 p-5 mb-6" data-testid="hth-no-alerts">
        <div className="flex items-center gap-3"><CheckCircle size={28} className="text-emerald-600" weight="fill" />
          <div><div className="overline text-emerald-700">ALL CLEAR</div><div className="text-lg font-bold">No active alerts</div><div className="text-sm text-neutral-600">All system metrics are within healthy ranges.</div></div>
        </div>
      </div>
    );
  }
  const crit = alerts.filter((a) => a.severity === "critical");
  const warn = alerts.filter((a) => a.severity === "warn");
  return (
    <div className="mb-6 space-y-3" data-testid="hth-alerts-panel">
      {crit.length > 0 && (
        <div className="bg-red-50 border-2 border-red-600 hard-shadow">
          <div className="bg-red-600 text-white px-4 py-2 flex items-center justify-between">
            <div className="flex items-center gap-2 text-white"><Warning size={18} weight="fill" className="text-white" /><span className="overline text-white">CRITICAL · {crit.length} ITEM{crit.length > 1 ? "S" : ""}</span></div>
          </div>
          <div className="p-4 space-y-2">
            {crit.map((a, i) => (
              <div key={i} className="flex items-start gap-3 border-b border-red-200 last:border-0 pb-2 last:pb-0" data-testid={`hth-crit-${i}`}>
                <div className="w-2 h-2 mt-2 rounded-full bg-red-600 flex-shrink-0" />
                <div className="flex-1">
                  <div className="font-bold text-red-900">{a.label} <span className="font-mono ml-2 text-sm">{formatValue(a.value, a.fmt)}</span></div>
                  <div className="text-xs text-neutral-600">{a.message}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {warn.length > 0 && (
        <div className="bg-amber-50 border-2 border-amber-500">
          <div className="bg-amber-500 text-white px-4 py-2 flex items-center justify-between">
            <div className="flex items-center gap-2 text-white"><Warning size={18} weight="fill" className="text-white" /><span className="overline text-white">WARNING · {warn.length} ITEM{warn.length > 1 ? "S" : ""}</span></div>
          </div>
          <div className="p-4 space-y-2">
            {warn.map((a, i) => (
              <div key={i} className="flex items-start gap-3 border-b border-amber-200 last:border-0 pb-2 last:pb-0" data-testid={`hth-warn-${i}`}>
                <div className="w-2 h-2 mt-2 rounded-full bg-amber-500 flex-shrink-0" />
                <div className="flex-1">
                  <div className="font-bold text-amber-900">{a.label} <span className="font-mono ml-2 text-sm">{formatValue(a.value, a.fmt)}</span></div>
                  <div className="text-xs text-neutral-600">{a.message}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function DistributionBar({ title, dist, targets }) {
  const total = Object.values(dist).reduce((a, b) => a + b, 0);
  if (total === 0) return <div className="text-sm text-neutral-400">No data yet.</div>;
  const keys = Object.keys(targets || dist);
  return (
    <div className="space-y-2">
      <div className="overline text-neutral-500">{title}</div>
      {keys.map((k) => {
        const actual = Math.round(100 * (dist[k] || 0) / total);
        const tgt = targets?.[k] || 0;
        const dev = Math.abs(actual - tgt);
        const color = dev > 15 ? "bg-red-500" : dev > 10 ? "bg-amber-500" : "bg-emerald-500";
        return (
          <div key={k}>
            <div className="flex justify-between text-xs mb-1">
              <span className="font-medium">{k}</span>
              <span className="font-mono">{actual}% <span className="text-neutral-400">/ {tgt}% target</span></span>
            </div>
            <div className="h-2 bg-neutral-200 relative">
              <div className={`h-full ${color}`} style={{ width: `${Math.min(100, actual)}%` }} />
              {tgt > 0 && <div className="absolute top-0 h-full w-0.5 bg-black" style={{ left: `${Math.min(100, tgt)}%` }} title={`Target ${tgt}%`} />}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function AdminHealth() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [drillMetric, setDrillMetric] = useState(null);

  const load = async (opts = {}) => {
    setRefreshing(true);
    try {
      const r = await api.get("/admin/health", { params: opts.refresh ? { refresh: true } : {} });
      setData(r.data);
      setErr(null);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setRefreshing(false); }
  };
  useEffect(() => { load(); /* auto-refresh every 60s */ const t = setInterval(load, 60000); return () => clearInterval(t); }, []);

  if (err) return <div className="p-12 text-red-700">Failed to load health metrics: {err}</div>;
  if (!data) return <div className="p-12">Loading health snapshot…</div>;

  const byGroup = data.metrics.reduce((acc, m) => { (acc[m.group] ||= []).push(m); return acc; }, {});
  const groupOrder = ["Panel Health", "Representativeness", "Survey Quality", "Reward Economy", "Business", "Editorial", "Trust & Integrity", "System Ops"];

  return (
    <div data-testid="admin-health-page" className="max-w-7xl">
      <div className="border-b-2 border-black pb-4 mb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="overline">SYSTEM HEALTH · LIVE</div>
          <h1 className="text-3xl md:text-5xl font-black tracking-tighter">Oodel Health</h1>
          <p className="text-sm text-neutral-600 mt-2 max-w-2xl">A single pane of glass for panel quality, representativeness, survey integrity, revenue health, and system uptime. Auto-refreshes every 60 seconds.</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Badge count={data.counts.ok} label="HEALTHY" tone="emerald" />
          <Badge count={data.counts.warn} label="WARNING" tone="amber" />
          <Badge count={data.counts.critical} label="CRITICAL" tone="red" />
          <Button onClick={() => load({ refresh: true })} disabled={refreshing} variant="outline" className="rounded-none" data-testid="hth-refresh">
            {refreshing ? "Refreshing…" : "Refresh"}
          </Button>
        </div>
      </div>

      <AlertsPanel alerts={data.alerts} />

      {/* Representativeness deep-dive (drift charts) */}
      <div className="bg-white border-2 border-black mb-8" data-testid="hth-distributions">
        <div className="bg-black text-white px-4 py-2 overline flex items-center justify-between">
          <span>REPRESENTATIVENESS · LIVE PANEL VS PAKISTAN CENSUS</span>
          <span className="font-mono text-[10px] opacity-60">drift = max deviation from census target</span>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-6">
          <DistributionBar title="SEC class" dist={data.panelist_distributions.sec} targets={data.panelist_distributions.targets.sec} />
          <DistributionBar title="Region" dist={data.panelist_distributions.region} targets={data.panelist_distributions.targets.region} />
          <DistributionBar title="Gender" dist={data.panelist_distributions.gender} targets={data.panelist_distributions.targets.gender} />
        </div>
      </div>

      {/* Metric groups */}
      {groupOrder.filter((g) => byGroup[g]).map((g) => (
        <div key={g} className="mb-8">
          <h2 className="text-xs tracking-widest font-bold uppercase text-neutral-500 mb-3">{g}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid={`hth-group-${g.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
            {byGroup[g].map((m, i) => <MetricCard key={i} m={m} onClick={setDrillMetric} />)}
          </div>
        </div>
      ))}

      <MetricDrillDownDialog metric={drillMetric} onClose={() => setDrillMetric(null)} />

      <div className="mt-10 text-xs text-neutral-400 font-mono">Snapshot generated at {formatDateTime(data.generated_at)}</div>
    </div>
  );
}

function Badge({ count, label, tone }) {
  const map = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-500",
    amber: "bg-amber-50 text-amber-800 border-amber-500",
    red: "bg-red-50 text-red-700 border-red-600",
  };
  return (
    <div className={`border-l-4 ${map[tone]} px-3 py-2 flex items-baseline gap-2`} data-testid={`hth-badge-${tone}`}>
      <div className="text-2xl font-black tracking-tighter">{count}</div>
      <div className="text-[10px] tracking-widest font-bold uppercase">{label}</div>
    </div>
  );
}



// =================== ADMIN — WAVE REPORT ===================
export function AdminWaveReport() {
  const { wid } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/admin/waves/${wid}/report`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Failed to load report"));
  }, [wid]);

  if (err) return <div className="p-12 text-red-600" data-testid="wave-report-error">{err}</div>;
  if (!data) return <div className="p-12 text-neutral-500" data-testid="wave-report-loading">Loading…</div>;

  const w = data.wave || {};
  const total = (w.current_counts && w.current_counts.total) || 0;
  const filled = w.target_sample ? Math.min(100, Math.round((total / w.target_sample) * 100)) : 0;

  return (
    <div className="max-w-7xl" data-testid="admin-wave-report">
      <div className="border-b-2 border-black pb-4 mb-6">
        <div className="overline flex items-center gap-2">
          <Link to="/admin/waves" className="text-neutral-500 hover:underline">← Waves</Link>
          <span>· WAVE REPORT</span>
        </div>
        <h1 className="text-3xl md:text-5xl font-black tracking-tighter mt-2">{w.name}</h1>
        <div className="mt-3 flex flex-wrap gap-2 items-center text-xs">
          <span className={`px-2 py-0.5 ${w.status === "active" ? "bg-green-600 text-white" : w.status === "closed" ? "bg-neutral-700 text-white" : w.status === "paused" ? "bg-yellow-500 text-white" : "bg-neutral-200"}`}>
            {w.status}
          </span>
          <span className="text-neutral-500">Target n = <b className="font-mono">{w.target_sample}</b></span>
          <span className="text-neutral-500">Responses = <b className="font-mono">{total}</b> ({filled}% filled)</span>
          {w.audience === "niche" && (
            <span className="px-2 py-0.5 bg-mango text-white">NICHE AUDIENCE</span>
          )}
        </div>
      </div>

      {/* Publishing matrix */}
      <div className="bg-white border-2 border-black p-5 mb-6" data-testid="wave-report-matrix">
        <div className="overline mb-3 flex items-center gap-1">Publishing matrix <HelpHint text="Where each question's result lands once the wave is closed. A single wave can publish to many destinations at once." /></div>
        <div className="flex flex-wrap gap-3">
          {["public", "panelists", "subscribers", "enterprise", "private"].map((tier) => {
            const n = data.publishing_summary?.[tier] || 0;
            if (n === 0) return null;
            return (
              <div key={tier} className="flex items-center gap-2 bg-neutral-50 border border-neutral-200 px-3 py-2" data-testid={`wave-report-tier-${tier}`}>
                <VisibilityBadge visibility={tier} size="sm" />
                <span className="font-mono text-sm">{n} q{n === 1 ? "" : "s"}</span>
              </div>
            );
          })}
          {data.private_client_ids?.length > 0 && (
            <div className="text-xs text-neutral-500 self-center">
              Private to: <b className="font-mono">{data.private_client_ids.join(", ")}</b>
            </div>
          )}
        </div>
      </div>

      {/* Per-question result cards */}
      <div className="space-y-6">
        {data.questions.map((q, idx) => (
          <WaveReportCard key={q.id} q={q} idx={idx + 1} />
        ))}
        {data.questions.length === 0 && (
          <div className="text-neutral-500 italic">No questions in this wave.</div>
        )}
      </div>
    </div>
  );
}

function WaveReportCard({ q, idx }) {
  const result = q.result;
  const opts = q.options || [];
  const labelFor = (k) => {
    const o = opts.find((x) => x.key === k);
    return o ? o.text : k;
  };

  const renderBars = (rec) => {
    if (!rec || Object.keys(rec).length === 0) return <div className="text-xs text-neutral-400 italic">no data</div>;
    const entries = Object.entries(rec).map(([k, v]) => ({ key: k, label: labelFor(k), value: Number(v) || 0 }));
    entries.sort((a, b) => b.value - a.value);
    const max = Math.max(...entries.map((e) => e.value), 1);
    return (
      <div className="space-y-1">
        {entries.map((e) => (
          <div key={e.key} className="flex items-center gap-2 text-xs">
            <div className="w-28 truncate text-neutral-700">{e.label}</div>
            <div className="flex-1 bg-neutral-100 h-3">
              <div className="bg-mango h-3" style={{ width: `${(e.value / max) * 100}%` }} />
            </div>
            <div className="w-12 text-right font-mono">{e.value.toFixed(1)}%</div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="bg-white border-2 border-black" data-testid={`wave-report-q-${q.id}`}>
      <div className="p-5 border-b-2 border-black flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="overline">QUESTION {idx} · {q.format} · {q.display_type}</div>
          <div className="text-lg font-bold mt-1">{q.text}</div>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <VisibilityBadge visibility={q.visibility} clientId={q.client_id} size="sm" />
            {q.sector && <span className="overline text-[10px] text-neutral-500">SECTOR · {q.sector}</span>}
            {result?.n_sample != null && <span className="overline text-[10px] text-neutral-500">n = {result.n_sample}</span>}
          </div>
        </div>
        <div className="text-right text-xs text-neutral-500 max-w-[260px]">
          <div className="overline mb-1">PUBLISHED TO</div>
          <div className="space-y-0.5">
            {(q.published_to || []).map((p) => (
              <div key={p} className="font-mono">{p}</div>
            ))}
          </div>
        </div>
      </div>
      <div className="p-5 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <div className="overline mb-2">OVERALL</div>
          {renderBars(result?.overall)}
        </div>
        <div>
          <div className="overline mb-2">BY GENDER</div>
          <div className="space-y-3">
            {result?.crosstabs?.gender ? Object.entries(result.crosstabs.gender).map(([g, vals]) => (
              <div key={g}>
                <div className="text-xs font-bold text-neutral-700 mb-1">{g}</div>
                {renderBars(vals)}
              </div>
            )) : <div className="text-xs text-neutral-400 italic">no cross-tab data</div>}
          </div>
        </div>
        <div>
          <div className="overline mb-2">BY REGION</div>
          <div className="space-y-3">
            {result?.crosstabs?.region ? Object.entries(result.crosstabs.region).map(([g, vals]) => (
              <div key={g}>
                <div className="text-xs font-bold text-neutral-700 mb-1">{g}</div>
                {renderBars(vals)}
              </div>
            )) : <div className="text-xs text-neutral-400 italic">no cross-tab data</div>}
          </div>
        </div>
        <div>
          <div className="overline mb-2">BY AGE</div>
          <div className="space-y-3">
            {result?.crosstabs?.age ? Object.entries(result.crosstabs.age).map(([g, vals]) => (
              <div key={g}>
                <div className="text-xs font-bold text-neutral-700 mb-1">{g}</div>
                {renderBars(vals)}
              </div>
            )) : <div className="text-xs text-neutral-400 italic">no cross-tab data</div>}
          </div>
        </div>
        <div>
          <div className="overline mb-2">BY SEC</div>
          <div className="space-y-3">
            {result?.crosstabs?.sec ? Object.entries(result.crosstabs.sec).map(([g, vals]) => (
              <div key={g}>
                <div className="text-xs font-bold text-neutral-700 mb-1">{g}</div>
                {renderBars(vals)}
              </div>
            )) : <div className="text-xs text-neutral-400 italic">no cross-tab data</div>}
          </div>
        </div>
      </div>
    </div>
  );
}


// =================== INTELLIGENCE (PHASE 8.45) ===================
// Admin-only AI-generated narrative briefs over tracker time-series + wave
// cross-tabs. Backed by `/api/admin/intelligence/*` endpoints. Powered by
// Emergent LLM key (Claude Sonnet) — cost ~₨5-8 per report, cached after.

// ITER 56 — Per-report tier & status pickers for the reports table.
// Mirrors the editorial `TIER_META` colour language so admins learn one system.
const INTEL_TIER_META = {
  public:      { label: "Public",      cls: "bg-emerald-600 text-white" },
  panelists:   { label: "Panelists",   cls: "bg-sky-600 text-white" },
  subscribers: { label: "Subscribers", cls: "bg-violet-600 text-white" },
  enterprise:  { label: "Enterprise",  cls: "bg-black text-white" },
};
function IntelReportTierPicker({ report, onSaved }) {
  const [v, setV] = useState(report.access_tier || "enterprise");
  const [saving, setSaving] = useState(false);
  const change = async (next) => {
    if (next === v) return;
    setSaving(true);
    const prev = v; setV(next);
    try {
      const r = await api.patch(`/admin/intelligence/reports/${report.id}`, { access_tier: next });
      toast.success(`Tier → ${INTEL_TIER_META[next]?.label || next}`);
      onSaved && onSaved(r.data);
    } catch (e) {
      setV(prev);
      toast.error(e.response?.data?.detail || "Failed to change tier");
    } finally { setSaving(false); }
  };
  const meta = INTEL_TIER_META[v] || INTEL_TIER_META.enterprise;
  return (
    <div className="flex items-center gap-2" data-testid={`intel-tier-${report.id}`}>
      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 ${meta.cls}`}>{meta.label}</span>
      <select
        value={v}
        disabled={saving}
        onChange={(e) => change(e.target.value)}
        className="text-[11px] border border-neutral-300 rounded-none px-1 py-0.5 bg-white hover:border-black disabled:opacity-50"
        data-testid={`intel-tier-select-${report.id}`}
      >
        {Object.keys(INTEL_TIER_META).map((k) => (
          <option key={k} value={k}>{INTEL_TIER_META[k].label}</option>
        ))}
      </select>
    </div>
  );
}
function IntelReportStatusPicker({ report, onSaved }) {
  const [v, setV] = useState(report.status || "draft");
  const [saving, setSaving] = useState(false);
  const change = async (next) => {
    if (next === v) return;
    setSaving(true);
    const prev = v; setV(next);
    try {
      const r = await api.patch(`/admin/intelligence/reports/${report.id}`, { status: next });
      toast.success(next === "published" ? "Report published" : "Moved to draft");
      onSaved && onSaved(r.data);
    } catch (e) {
      setV(prev);
      toast.error(e.response?.data?.detail || "Failed to change status");
    } finally { setSaving(false); }
  };
  return (
    <div className="flex items-center gap-2" data-testid={`intel-status-${report.id}`}>
      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 ${v === 'published' ? 'bg-green-700 text-white' : 'bg-neutral-300 text-neutral-700'}`}>{v}</span>
      <select
        value={v}
        disabled={saving}
        onChange={(e) => change(e.target.value)}
        className="text-[11px] border border-neutral-300 rounded-none px-1 py-0.5 bg-white hover:border-black disabled:opacity-50"
        data-testid={`intel-status-select-${report.id}`}
      >
        <option value="draft">Draft</option>
        <option value="published">Published</option>
      </select>
    </div>
  );
}

export function AdminIntelligence() {
  const [tab, setTab] = useState("trackers");  // trackers | waves | reports
  const [trackers, setTrackers] = useState([]);
  const [waves, setWaves] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTracker, setSelectedTracker] = useState(null);
  const [selectedWaveIds, setSelectedWaveIds] = useState([]);
  const [selectedWave, setSelectedWave] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [currentReport, setCurrentReport] = useState(null);
  const [excludeOverseas, setExcludeOverseas] = useState(false);
  const [reportType, setReportType] = useState("intelligence"); // intelligence | public | media | executive
  // PRD Task 2 — hybrid demographic factor picker (shared by both tracker
  // and wave scopes, same as excludeOverseas above).
  const [extraDemoFactors, setExtraDemoFactors] = useState([]);
  const toggleExtraDemoFactor = (key) => setExtraDemoFactors((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);

  const REPORT_TYPES = [
    { key: "intelligence", label: "Intelligence Brief", blurb: "Pew-style 8-section analytical brief (700–1100 words)." },
    { key: "public", label: "Public Brief", blurb: "Plain-language explainer for everyday readers (400–650 words)." },
    { key: "media", label: "Media Brief", blurb: "AP-style newsroom-ready brief with pull quotes (500–750 words)." },
    { key: "executive", label: "Executive Brief", blurb: "McKinsey-style action-oriented memo (350–550 words)." },
  ];

  // Export the currently-open report as a multi-page A4 PDF. We temporarily
  // freeze the live report body to a FIXED pixel width (so responsive
  // `w-full` layouts don't get clipped on small viewports), snapshot the
  // entire thing as one tall PNG, then paginate the image across A4 pages
  // with a branded header on every page. Original styles are restored after.
  const exportReportPDF = async (rep) => {
    if (!rep) return;
    const live = document.getElementById("intel-report-body");
    if (!live) return;
    // Save styles so we can restore after
    const prev = {
      width: live.style.width,
      maxWidth: live.style.maxWidth,
      position: live.style.position,
      left: live.style.left,
      top: live.style.top,
      background: live.style.background,
    };
    try {
      toast.message("Building PDF…");
      const { default: jsPDF } = await import("jspdf");
      const { toPng } = await import("html-to-image");

      const CAPTURE_W = 1100; // A4 print-width at ~96dpi after margins
      // Temporarily force fixed pixel width on the LIVE node so recharts and
      // the rest of the layout reflow into the print width.
      live.style.width = `${CAPTURE_W}px`;
      live.style.maxWidth = `${CAPTURE_W}px`;
      live.style.background = "#ffffff";
      // Allow recharts ResponsiveContainer + browser layout to react.
      // Two RAFs + 350ms covers the recharts resize-observer cadence.
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      await new Promise((r) => setTimeout(r, 400));

      const dataUrl = await toPng(live, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: "#ffffff",
        width: CAPTURE_W,
        style: { width: `${CAPTURE_W}px`, maxWidth: `${CAPTURE_W}px` },
      });
      const img = new Image();
      img.src = dataUrl;
      await new Promise((res) => { img.onload = res; });

      const pdf = new jsPDF({ unit: "pt", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 28;
      const headerH = 56;
      const footerH = 28;
      const contentW = pageW - margin * 2;
      const contentH = pageH - headerH - footerH - margin;
      const reportTypeLabel = rep.report_type_label || "Intelligence Brief";

      const drawHeader = (pageNum) => {
        pdf.setFillColor(0, 0, 0);
        pdf.rect(0, 0, pageW, headerH, "F");
        pdf.setTextColor(255, 255, 255);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(20);
        pdf.text("oodel", margin, 34);
        pdf.setTextColor(22, 163, 74);
        pdf.text(".", margin + pdf.getTextWidth("oodel"), 34);
        pdf.setTextColor(255, 255, 255);
        const rightLabel = "oodel";
        const rightW = pdf.getTextWidth(rightLabel);
        pdf.text(rightLabel, pageW - margin - rightW - 10, 34);
        pdf.setFontSize(10);
        pdf.setTextColor(22, 163, 74);
        pdf.text("x", pageW - margin - 8, 24);
        pdf.setFontSize(8);
        pdf.setTextColor(180, 180, 180);
        pdf.text(reportTypeLabel.toUpperCase(), pageW / 2, 22, { align: "center" });
        pdf.setFontSize(10);
        pdf.setTextColor(255, 255, 255);
        pdf.setFont("helvetica", "normal");
        const titleShort = (rep.title || "").slice(0, 70);
        pdf.text(titleShort, pageW / 2, 42, { align: "center" });
        pdf.setFillColor(22, 163, 74);
        pdf.rect(0, headerH, pageW, 3, "F");
        pdf.setTextColor(150, 150, 150);
        pdf.setFontSize(8);
        pdf.text(`Page ${pageNum}`, pageW - margin, pageH - 14, { align: "right" });
        pdf.text("oodel·ˣ — Pakistan's survey intelligence engine", margin, pageH - 14);
      };

      // ── Cover page ──
      drawHeader(1);
      pdf.setTextColor(150, 150, 150);
      pdf.setFontSize(9);
      pdf.text(reportTypeLabel.toUpperCase(), margin, headerH + 50);
      pdf.setTextColor(0, 0, 0);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(24);
      const titleLines = pdf.splitTextToSize(rep.title || "Intelligence Brief", pageW - margin * 2);
      pdf.text(titleLines, margin, headerH + 90);
      pdf.setFontSize(10);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(80, 80, 80);
      const metaY = headerH + 90 + titleLines.length * 28 + 22;
      pdf.text(`Scope: ${rep.scope}`, margin, metaY);
      pdf.text(`Generated: ${formatDateTime(rep.created_at)}`, margin, metaY + 16);
      if (rep.filters?.exclude_overseas) pdf.text("Filter: Pakistan-only respondents", margin, metaY + 32);
      pdf.text(`Report ID: ${rep.id?.slice(0, 16)}`, margin, metaY + (rep.filters?.exclude_overseas ? 48 : 32));
      pdf.setDrawColor(220, 220, 220);
      pdf.line(margin, pageH - 110, pageW - margin, pageH - 110);
      pdf.setTextColor(110, 110, 110);
      pdf.setFontSize(8);
      const disclaimer = "Numbers in this report are exact aggregates from raw survey responses. The narrative is AI-generated using the oodel·ˣ analytical framework and should be reviewed by an analyst before external publication. Sample sizes and reliability caveats are noted in-line where applicable.";
      pdf.text(pdf.splitTextToSize(disclaimer, pageW - margin * 2), margin, pageH - 95);

      // ── Paginate the snapshot ──
      const scale = contentW / CAPTURE_W;
      const pageSliceH = contentH / scale;  // in source-image pixels
      let yPos = 0;
      let pageNum = 1;
      while (yPos < img.height - 2) {
        pdf.addPage();
        pageNum += 1;
        drawHeader(pageNum);
        const sliceH = Math.min(pageSliceH, img.height - yPos);
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = Math.ceil(sliceH);
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, -yPos);
        const sliceUrl = canvas.toDataURL("image/png");
        const drawH = sliceH * scale;
        pdf.addImage(sliceUrl, "PNG", margin, headerH + 12, contentW, drawH, undefined, "FAST");
        yPos += sliceH;
      }

      const fn = `${(rep.report_type || "intelligence")}-${(rep.title || "report").replace(/[^a-z0-9]+/gi, "-").toLowerCase().slice(0, 60)}.pdf`;
      pdf.save(fn);
      toast.success("PDF downloaded");
    } catch (e) {
      console.error(e);
      toast.error("PDF export failed");
    } finally {
      // Restore original styles
      live.style.width = prev.width || "";
      live.style.maxWidth = prev.maxWidth || "";
      live.style.position = prev.position || "";
      live.style.left = prev.left || "";
      live.style.top = prev.top || "";
      live.style.background = prev.background || "";
    }
  };

  const load = async () => {
    try {
      const [t, w, r] = await Promise.all([
        api.get("/admin/intelligence/trackers"),
        api.get("/admin/intelligence/waves"),
        api.get("/admin/intelligence/reports"),
      ]);
      setTrackers(t.data || []);
      setWaves(w.data || []);
      setReports(r.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleWave = (wid) => {
    setSelectedWaveIds((prev) => prev.includes(wid) ? prev.filter((x) => x !== wid) : [...prev, wid]);
  };

  const generate = async () => {
    setGenerating(true);
    try {
      let res;
      if (tab === "trackers") {
        if (!selectedTracker || selectedWaveIds.length === 0) {
          toast.error("Pick a tracker and at least one wave"); setGenerating(false); return;
        }
        res = await api.post("/admin/intelligence/generate", {
          scope: "tracker", tracker_id: selectedTracker.id, wave_ids: selectedWaveIds, exclude_overseas: excludeOverseas, report_type: reportType, demographic_factors: extraDemoFactors,
        });
      } else {
        if (!selectedWave) { toast.error("Pick a wave"); setGenerating(false); return; }
        res = await api.post("/admin/intelligence/generate", { scope: "wave", wave_id: selectedWave.id, exclude_overseas: excludeOverseas, report_type: reportType, demographic_factors: extraDemoFactors });
      }
      setCurrentReport(res.data);
      toast.success("Report generated");
      load();
    } catch (e) {
      toast.error(fmtError(e?.response?.data?.detail) || "Failed to generate");
    } finally {
      setGenerating(false);
    }
  };

  const openReport = async (rid) => {
    const r = await api.get(`/admin/intelligence/reports/${rid}`);
    setCurrentReport(r.data);
    setTab("reports");
  };

  const deleteReport = async (rid) => {
    if (!confirm("Delete this report? Cannot be undone.")) return;
    await api.delete(`/admin/intelligence/reports/${rid}`);
    toast.success("Deleted");
    if (currentReport?.id === rid) setCurrentReport(null);
    load();
  };

  return (
    <div data-testid="admin-intelligence">
      <div className="border-b-2 border-black pb-4 mb-6">
        <div className="overline">oodel·ˣ intelligence framework</div>
        <h1 className="text-3xl font-black tracking-tighter">
          oodel<sup className="text-xl ml-0.5 align-super" style={{ color: "#16A34A" }}>x</sup>
        </h1>
        <p className="text-sm text-neutral-500 mt-1">Generate admin-only narrative briefs from tracker time-series or wave cross-tabs. Powered by Claude. Cached after generation.</p>
      </div>

      <div className="flex gap-2 mb-6 border-b border-neutral-200">
        {[["trackers", "Trackers"], ["waves", "Waves"], ["reports", `Saved reports (${reports.length})`]].map(([k, lbl]) => (
          <button key={k} onClick={() => { setTab(k); setCurrentReport(null); }}
            className={`px-4 py-2 text-sm font-bold uppercase tracking-wider transition-colors ${tab === k ? "border-b-2 border-mango text-black" : "text-neutral-500 hover:text-black"}`}
            data-testid={`intel-tab-${k}`}>{lbl}</button>
        ))}
      </div>

      {tab === "trackers" && (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 border border-neutral-200 max-h-[70vh] overflow-y-auto">
            <div className="bg-neutral-50 px-3 py-2 overline border-b">Pick a tracker</div>
            {!loading && trackers.length === 0 && <div className="p-4 text-sm text-neutral-400 italic">No trackers yet.</div>}
            {trackers.map((t) => (
              <button key={t.id} onClick={() => { setSelectedTracker(t); setSelectedWaveIds(t.waves?.map((w) => w.id) || []); }}
                className={`block w-full text-left px-3 py-3 border-b border-neutral-100 hover:bg-neutral-50 transition-colors ${selectedTracker?.id === t.id ? "bg-mango/10 border-l-4 border-l-mango" : ""}`}
                data-testid={`intel-tracker-${t.id}`}>
                <div className="font-bold text-sm">{t.name}</div>
                <div className="text-xs text-neutral-500 mt-0.5">{t.category || "—"} · {(t.waves || []).length} wave{(t.waves || []).length !== 1 ? "s" : ""}</div>
              </button>
            ))}
          </div>
          <div className="lg:col-span-2 border border-neutral-200 p-4">
            {!selectedTracker ? (
              <div className="text-sm text-neutral-400 italic">Pick a tracker on the left.</div>
            ) : (
              <>
                <div className="overline">Selected tracker</div>
                <h2 className="text-xl font-black tracking-tighter">{selectedTracker.name}</h2>
                <p className="text-xs text-neutral-500 mb-4">{selectedTracker.description || "No description."}</p>
                <div className="overline mb-2">Waves to include ({selectedWaveIds.length} selected)</div>
                {(selectedTracker.waves || []).length === 0 ? (
                  <div className="text-xs text-neutral-400 italic">This tracker isn't used in any waves yet.</div>
                ) : (
                  <div className="space-y-1 max-h-[40vh] overflow-y-auto border border-neutral-100 p-2">
                    {selectedTracker.waves.map((w) => (
                      <label key={w.id} className="flex items-center gap-2 text-xs hover:bg-neutral-50 p-1 cursor-pointer">
                        <input type="checkbox" checked={selectedWaveIds.includes(w.id)} onChange={() => toggleWave(w.id)} className="w-3.5 h-3.5" />
                        <span className="flex-1">{w.name}</span>
                        <span className="text-neutral-400">{w.end_date?.slice(0, 10) || "—"}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 ${w.status === "closed" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{w.status}</span>
                      </label>
                    ))}
                  </div>
                )}
                <div className="mt-4 border border-neutral-200 bg-neutral-50 p-3">
                  <div className="overline text-[10px] text-neutral-500 mb-2">Report type</div>
                  <div className="grid sm:grid-cols-2 gap-2" data-testid="intel-report-type-tracker">
                    {REPORT_TYPES.map((rt) => (
                      <label key={rt.key} className={`flex items-start gap-2 p-2 border cursor-pointer transition-colors ${reportType === rt.key ? "border-black bg-white" : "border-neutral-200 bg-white hover:border-neutral-400"}`}>
                        <input type="radio" name="report-type-tracker" value={rt.key} checked={reportType === rt.key} onChange={() => setReportType(rt.key)} className="mt-1" data-testid={`intel-report-type-${rt.key}`} />
                        <span className="min-w-0">
                          <span className="block text-xs font-bold">{rt.label}</span>
                          <span className="block text-[10px] text-neutral-500 leading-snug">{rt.blurb}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
                <button onClick={generate} disabled={generating || !selectedTracker || selectedWaveIds.length === 0}
                  className="mt-4 bg-mango hover:bg-mango/90 text-black border-2 border-black px-6 py-2 text-sm font-bold uppercase tracking-wider disabled:opacity-50 hard-shadow"
                  data-testid="intel-generate-tracker">
                  {generating ? "Generating with Claude…" : `Generate ${REPORT_TYPES.find((r) => r.key === reportType)?.label || "report"}`}
                </button>
                <label className="flex items-center gap-2 text-xs mt-3 cursor-pointer select-none">
                  <input type="checkbox" checked={excludeOverseas} onChange={(e) => setExcludeOverseas(e.target.checked)} className="w-3.5 h-3.5" data-testid="intel-exclude-overseas-tracker" />
                  <span>Exclude overseas respondents (Pakistan-only)</span>
                </label>
                <div className="mt-3">
                  <div className="text-[10px] uppercase tracking-wide text-neutral-400 mb-1">Demographic angle — Age/Gender/SEC/Region always on. Add:</div>
                  <div className="flex gap-3">
                    {EXTRA_DEMO_FACTORS.map((f) => (
                      <label key={f.key} className="flex items-center gap-1.5 text-xs cursor-pointer select-none">
                        <input type="checkbox" checked={extraDemoFactors.includes(f.key)} onChange={() => toggleExtraDemoFactor(f.key)} className="w-3.5 h-3.5" data-testid={`intel-demo-${f.key}-tracker`} />
                        <span>{f.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <p className="text-[10px] text-neutral-400 mt-2 italic">Est. cost: ~₨5–8. Saved for free re-opens.</p>
              </>
            )}
          </div>
        </div>
      )}

      {tab === "waves" && (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 border border-neutral-200 max-h-[70vh] overflow-y-auto">
            <div className="bg-neutral-50 px-3 py-2 overline border-b">Pick a wave</div>
            {!loading && waves.length === 0 && <div className="p-4 text-sm text-neutral-400 italic">No closed/active waves yet.</div>}
            {waves.map((w) => (
              <button key={w.id} onClick={() => setSelectedWave(w)}
                className={`block w-full text-left px-3 py-3 border-b border-neutral-100 hover:bg-neutral-50 transition-colors ${selectedWave?.id === w.id ? "bg-mango/10 border-l-4 border-l-mango" : ""}`}
                data-testid={`intel-wave-${w.id}`}>
                <div className="font-bold text-sm">{w.name}</div>
                <div className="text-xs text-neutral-500 mt-0.5">{(w.question_ids || []).length} questions · {w.end_date?.slice(0, 10) || "—"} · {w.status}</div>
              </button>
            ))}
          </div>
          <div className="lg:col-span-2 border border-neutral-200 p-4">
            {!selectedWave ? (
              <div className="text-sm text-neutral-400 italic">Pick a wave on the left.</div>
            ) : (
              <>
                <div className="overline">Selected wave</div>
                <h2 className="text-xl font-black tracking-tighter">{selectedWave.name}</h2>
                <p className="text-xs text-neutral-500 mt-1 mb-4">{(selectedWave.question_ids || []).length} questions · ended {selectedWave.end_date?.slice(0, 10) || "—"}</p>
                <div className="border border-neutral-200 bg-neutral-50 p-3 mb-4">
                  <div className="overline text-[10px] text-neutral-500 mb-2">Report type</div>
                  <div className="grid sm:grid-cols-2 gap-2" data-testid="intel-report-type-wave">
                    {REPORT_TYPES.map((rt) => (
                      <label key={rt.key} className={`flex items-start gap-2 p-2 border cursor-pointer transition-colors ${reportType === rt.key ? "border-black bg-white" : "border-neutral-200 bg-white hover:border-neutral-400"}`}>
                        <input type="radio" name="report-type-wave" value={rt.key} checked={reportType === rt.key} onChange={() => setReportType(rt.key)} className="mt-1" data-testid={`intel-report-type-wave-${rt.key}`} />
                        <span className="min-w-0">
                          <span className="block text-xs font-bold">{rt.label}</span>
                          <span className="block text-[10px] text-neutral-500 leading-snug">{rt.blurb}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
                <button onClick={generate} disabled={generating}
                  className="bg-mango hover:bg-mango/90 text-black border-2 border-black px-6 py-2 text-sm font-bold uppercase tracking-wider disabled:opacity-50 hard-shadow"
                  data-testid="intel-generate-wave">
                  {generating ? "Generating with Claude…" : `Generate ${REPORT_TYPES.find((r) => r.key === reportType)?.label || "report"}`}
                </button>
                <label className="flex items-center gap-2 text-xs mt-3 cursor-pointer select-none">
                  <input type="checkbox" checked={excludeOverseas} onChange={(e) => setExcludeOverseas(e.target.checked)} className="w-3.5 h-3.5" data-testid="intel-exclude-overseas-wave" />
                  <span>Exclude overseas respondents (Pakistan-only)</span>
                </label>
                <div className="mt-3">
                  <div className="text-[10px] uppercase tracking-wide text-neutral-400 mb-1">Demographic angle — Age/Gender/SEC/Region always on. Add:</div>
                  <div className="flex gap-3">
                    {EXTRA_DEMO_FACTORS.map((f) => (
                      <label key={f.key} className="flex items-center gap-1.5 text-xs cursor-pointer select-none">
                        <input type="checkbox" checked={extraDemoFactors.includes(f.key)} onChange={() => toggleExtraDemoFactor(f.key)} className="w-3.5 h-3.5" data-testid={`intel-demo-${f.key}-wave`} />
                        <span>{f.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <p className="text-[10px] text-neutral-400 mt-2 italic">Est. cost: ~₨5–8. Saved for free re-opens.</p>
              </>
            )}
          </div>
        </div>
      )}

      {tab === "reports" && !currentReport && (
        <div className="border border-neutral-200">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b">
              <tr>
                <th className="text-left py-2 px-3 overline">Title</th>
                <th className="text-left py-2 px-3 overline">Scope</th>
                <th className="text-left py-2 px-3 overline">Tier</th>
                <th className="text-left py-2 px-3 overline">Status</th>
                <th className="text-left py-2 px-3 overline">Generated</th>
                <th className="text-right py-2 px-3 overline">Actions</th>
              </tr>
            </thead>
            <tbody>
              {!loading && reports.length === 0 && (
                <tr><td colSpan={6} className="text-center py-8 text-neutral-400 italic">No reports yet. Generate one from the Trackers or Waves tab.</td></tr>
              )}
              {reports.map((r) => (
                <tr key={r.id} className="border-b border-neutral-100 hover:bg-neutral-50">
                  <td className="py-3 px-3 font-medium">
                    {r.title}
                    {r.report_type_label && <div className="text-[10px] text-mango font-bold uppercase tracking-wider mt-0.5">{r.report_type_label}</div>}
                  </td>
                  <td className="py-3 px-3 text-xs"><span className="bg-neutral-100 px-2 py-0.5">{r.scope}</span></td>
                  <td className="py-3 px-3 text-xs">
                    <IntelReportTierPicker
                      report={r}
                      onSaved={(patched) => setReports(reports.map((x) => x.id === patched.id ? patched : x))}
                    />
                  </td>
                  <td className="py-3 px-3 text-xs">
                    <IntelReportStatusPicker
                      report={r}
                      onSaved={(patched) => setReports(reports.map((x) => x.id === patched.id ? patched : x))}
                    />
                  </td>
                  <td className="py-3 px-3 text-xs text-neutral-500">{r.created_at?.slice(0, 16).replace("T", " ") || "—"}</td>
                  <td className="py-3 px-3 text-right">
                    <button onClick={() => openReport(r.id)} className="text-xs underline mr-3" data-testid={`intel-open-${r.id}`}>Open</button>
                    <button onClick={() => deleteReport(r.id)} className="text-xs text-red-600 underline" data-testid={`intel-delete-${r.id}`}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {currentReport && (
        <IntelReportView
          report={currentReport}
          onClose={() => setCurrentReport(null)}
          onExport={() => exportReportPDF(currentReport)}
          onSaved={(patched) => {
            setCurrentReport(patched);
            setReports(reports.map((x) => x.id === patched.id ? patched : x));
          }}
        />
      )}
    </div>
  );
}


// ITER 58 — Editable intelligence report view.
// Every text surface (title, section label, narrative markdown) is click-to-edit
// and PATCHed via /api/admin/intelligence/reports/{rid}. `AI-drafted` mentions
// removed — the report is presented as the analyst's finalized work product.
function IntelReportView({ report, onClose, onExport, onSaved }) {
  const [editingNarrative, setEditingNarrative] = useState(false);
  const [narrativeDraft, setNarrativeDraft] = useState(report.narrative || "");
  const [savingNarrative, setSavingNarrative] = useState(false);
  useEffect(() => { setNarrativeDraft(report.narrative || ""); }, [report.id, report.narrative]);

  const patch = async (fields) => {
    try {
      const r = await api.patch(`/admin/intelligence/reports/${report.id}`, fields);
      onSaved && onSaved(r.data);
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
      return false;
    }
  };
  const saveNarrative = async () => {
    setSavingNarrative(true);
    const ok = await patch({ narrative: narrativeDraft });
    setSavingNarrative(false);
    if (ok) {
      setEditingNarrative(false);
      toast.success("Narrative saved");
    }
  };

  return (
    <div className="border-2 border-black bg-white shadow-2xl" data-testid="intel-report-view">
      {/* ─── Branded report header (oodel left · oodel·ˣ right) ─── */}
      <div className="bg-black text-white px-6 py-5 flex items-center justify-between gap-4 border-b-4 border-mango">
        <div className="flex items-center gap-3 min-w-0">
          <span className="font-black tracking-tighter text-2xl leading-none whitespace-nowrap">
            <span className="text-white">oodel</span><span style={{ color: "#16A34A" }}>.</span>
          </span>
        </div>
        <div className="flex items-center gap-4 whitespace-nowrap">
          <span className="font-black tracking-tighter text-2xl leading-none">
            oodel<sup className="text-sm ml-0.5 align-super" style={{ color: "#16A34A" }}>x</sup>
          </span>
          <button onClick={onExport} className="text-[10px] uppercase tracking-wider px-3 py-1.5 border border-white/50 hover:bg-white/10 inline-flex items-center gap-1 font-bold" data-testid="intel-export-pdf">
            Export PDF
          </button>
          <button onClick={onClose} className="text-xs underline" data-testid="intel-report-close">Close</button>
        </div>
      </div>

      {/* ─── Cover strip — EDITABLE title + label ─── */}
      <div className="px-6 py-5 border-b border-neutral-200 bg-gradient-to-b from-neutral-50 to-white">
        <EditableInline
          value={report.report_type_label || "Intelligence Brief"}
          onSave={(v) => patch({ report_type_label: v })}
          className="overline text-mango font-bold"
          data-testid="intel-edit-label"
        />
        <EditableInline
          value={report.title}
          onSave={(v) => patch({ title: v })}
          className="text-2xl sm:text-3xl font-black tracking-tighter mt-1 mb-3 block"
          multiline
          data-testid="intel-edit-title"
        />
        <div className="flex items-center gap-2 flex-wrap text-[10px]">
          <span className="px-2 py-1 border border-neutral-300 uppercase tracking-wider bg-white">{report.scope}</span>
          {report.filters?.exclude_overseas && (
            <span className="px-2 py-1 border border-mango text-mango uppercase tracking-wider bg-mango/5 font-bold">Pakistan-only</span>
          )}
          <span className="px-2 py-1 border border-neutral-300 uppercase tracking-wider bg-white">{formatDate(report.created_at)}</span>
        </div>
      </div>

      <div id="intel-report-body">
        <IntelligenceInfographic scope={report.scope} charts={report.data_payload?.charts} reportTypeLabel={report.report_type_label} />
        <IntelligenceCharts scope={report.scope} charts={report.data_payload?.charts} />

        {/* ─── Editable narrative body ─── */}
        <div className="relative group" data-testid="intel-report-narrative">
          {!editingNarrative && (
            <>
              <button
                onClick={() => setEditingNarrative(true)}
                className="absolute top-3 right-3 text-[10px] uppercase tracking-wider px-2 py-1 bg-black text-white opacity-0 group-hover:opacity-100 transition-opacity z-10"
                data-testid="intel-edit-narrative-btn"
              >
                Edit report
              </button>
              <div className="p-6 md:p-10 prose prose-sm sm:prose-base max-w-none font-serif leading-relaxed prose-headings:font-black prose-headings:tracking-tighter prose-strong:font-bold prose-strong:text-black prose-p:mb-3 prose-li:my-1 prose-ul:my-3 prose-ul:list-disc prose-ul:pl-6">
                {/* Heading color is set via an explicit ReactMarkdown component override
                    (not a `prose-h2:` modifier) — the typography plugin's own default
                    h2 color rule is scoped with :where() (zero specificity), which was
                    silently beating the `prose-h2:text-mango` modifier (also zero
                    specificity, source-order loser) and rendering headings near-black-on-black. */}
                <ReactMarkdown
                  components={{
                    h2: ({ node, ...props }) => (
                      <h2 className="text-base mt-8 mb-4 px-3 py-2 font-sans font-black tracking-wider uppercase bg-black text-mango border-l-4 border-mango first:mt-0" {...props} />
                    ),
                  }}
                >{report.narrative || "_(No narrative yet — click Edit report to add one.)_"}</ReactMarkdown>
              </div>
            </>
          )}
          {editingNarrative && (
            <div className="p-6 bg-neutral-50 border-t border-b border-neutral-300">
              <div className="flex items-center justify-between mb-3">
                <div className="overline">EDITING NARRATIVE · MARKDOWN</div>
                <div className="flex gap-2">
                  <button
                    onClick={() => { setEditingNarrative(false); setNarrativeDraft(report.narrative || ""); }}
                    className="text-xs px-3 py-1 border border-neutral-400 hover:bg-white"
                    data-testid="intel-narrative-cancel"
                  >Cancel</button>
                  <button
                    onClick={saveNarrative}
                    disabled={savingNarrative}
                    className="text-xs px-3 py-1 bg-black text-white hover:bg-neutral-800 disabled:opacity-50 font-bold"
                    data-testid="intel-narrative-save"
                  >{savingNarrative ? "Saving…" : "Save"}</button>
                </div>
              </div>
              {/* ITER 82 — rich text (Markdown WYSIWYG) editor. Storage format
                  is unchanged (still a plain Markdown string in `narrative`,
                  rendered via <ReactMarkdown> everywhere it's read), so no
                  backend/migration changes are needed. Reuses the app's
                  existing `MarkdownEditor` component (toolbar + live split
                  preview via a plain textarea — no external rich-text
                  dependency, no React 19 ref issues), already used by the
                  Legal Pages CMS. */}
              <MarkdownEditor
                value={narrativeDraft}
                onChange={setNarrativeDraft}
                minHeight="h-[480px]"
                testid="intel-narrative-mdeditor"
              />
              <div className="mt-2 text-[10px] text-neutral-500">Markdown supported — # H1, ## H2, **bold**, - list, `code`. Percentages format like `42%` (no decimals beyond one place).</div>
            </div>
          )}
        </div>

        {/* ─── Footer with oodel·ˣ FRAMEWORK logo ─── */}
        <div className="px-6 py-8 border-t-2 border-black bg-white flex flex-col items-center gap-3">
          <img
            src="https://customer-assets.emergentagent.com/job_pk-poll-platform/artifacts/zvsjt9d2_oodelxframework-black.png"
            alt="oodel·ˣ FRAMEWORK"
            className="h-16 md:h-20 object-contain"
            data-testid="intel-report-footer-logo"
          />
          <div className="text-[10px] text-neutral-400 font-mono uppercase tracking-wider">
            REPORT · {report.id?.slice(0, 12)}
          </div>
        </div>
      </div>
    </div>
  );
}


// Lightweight click-to-edit text primitive used inside IntelReportView.
// Renders as a display span; on click, swaps to an input/textarea, saves on
// blur or Enter (Shift+Enter for newline in multiline mode). Cancels on Esc.
function EditableInline({ value, onSave, className = "", multiline = false, "data-testid": tid }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setDraft(value || ""); }, [value]);
  const commit = async () => {
    if (draft === value) { setEditing(false); return; }
    setSaving(true);
    const ok = await onSave(draft);
    setSaving(false);
    if (ok !== false) setEditing(false);
  };
  const cancel = () => { setDraft(value || ""); setEditing(false); };
  if (!editing) {
    return (
      <span
        onClick={() => setEditing(true)}
        className={`${className} cursor-text hover:bg-mango/10 transition-colors rounded-sm px-0.5 -mx-0.5 border-b border-dashed border-transparent hover:border-mango`}
        title="Click to edit"
        data-testid={tid}
      >{value}</span>
    );
  }
  if (multiline) {
    return (
      <textarea
        value={draft}
        autoFocus
        rows={2}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Escape") cancel(); if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); commit(); } }}
        disabled={saving}
        className={`${className} bg-mango/5 border-b-2 border-mango outline-none w-full resize-none`}
        data-testid={`${tid}-input`}
      />
    );
  }
  return (
    <input
      value={draft}
      autoFocus
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Escape") cancel(); if (e.key === "Enter") commit(); }}
      disabled={saving}
      className={`${className} bg-mango/5 border-b-2 border-mango outline-none w-full block`}
      data-testid={`${tid}-input`}
    />
  );
}



// Phase 8.50 — Admin Referrals page. Surfaces every referral signup in the
// system (each row = inviter ↔ invitee pair). Reads from
// /api/admin/referrals + /api/admin/referrals/stats which join on
// users.invited_by. Empty list is a perfectly valid state — just means no
// patron has signed up via a referral code yet.
export function AdminReferrals() {
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");      // "" | "credited" | "pending"
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const credited = filter === "credited" ? "true" : filter === "pending" ? "false" : undefined;
      const [s, r] = await Promise.all([
        api.get("/admin/referrals/stats"),
        api.get("/admin/referrals", { params: credited ? { credited } : {} }),
      ]);
      setStats(s.data);
      setRows(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filter]);

  const q = search.trim().toLowerCase();
  const filtered = q
    ? rows.filter((row) => {
        const hay = [
          row.inviter?.email, row.inviter?.name, row.inviter?.referral_code,
          row.invitee?.email, row.invitee?.name,
        ].filter(Boolean).join(" ").toLowerCase();
        return hay.includes(q);
      })
    : rows;

  return (
    <div className="space-y-6" data-testid="admin-referrals-page">
      <div className="border-b-2 border-black pb-4 mb-6">
        <div className="overline">REFERRALS</div>
        <h1 className="text-3xl font-black tracking-tighter">Referrals</h1>
        <p className="text-sm text-neutral-500 mt-1">Every patron signup that came in via someone else's referral code. Empty until referrals start flowing.</p>
      </div>

      {/* Stat tiles */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="referral-stats">
          {[
            { label: "Total referrals", value: stats.total_referrals },
            { label: "Credited", value: stats.credited, cls: "text-emerald-700" },
            { label: "Pending", value: stats.pending, cls: "text-amber-700" },
            { label: "Unique inviters", value: stats.unique_inviters },
            { label: "Credit rate", value: `${stats.credit_rate_pct}%` },
          ].map((t) => (
            <div key={t.label} className="bg-white border-2 border-black p-3">
              <div className="overline text-[10px] text-neutral-500">{t.label}</div>
              <div className={`font-black text-2xl mt-1 tabular-nums ${t.cls || ""}`}>{t.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search inviter or invitee…"
          className="border-2 border-neutral-300 px-3 py-1.5 text-sm w-72"
          data-testid="referrals-search"
        />
        <div className="flex items-center gap-1">
          <span className="overline">filter</span>
          {[{ v: "", l: "All" }, { v: "credited", l: "Credited" }, { v: "pending", l: "Pending" }].map((r) => (
            <button
              key={r.v}
              onClick={() => setFilter(r.v)}
              data-testid={`referrals-filter-${r.v || "all"}`}
              className={`px-3 py-1 text-xs border ${filter === r.v ? "bg-black text-white border-black" : "border-neutral-300 hover:border-black"}`}
            >
              {r.l}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border-2 border-black overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b-2 border-black bg-neutral-50">
              <th className="text-left py-3 px-3 overline">Inviter</th>
              <th className="text-left py-3 px-3 overline">Code</th>
              <th className="text-left py-3 px-3 overline">Invitee</th>
              <th className="text-left py-3 px-3 overline">Joined</th>
              <th className="text-left py-3 px-3 overline">Status</th>
              <th className="text-right py-3 px-3 overline">Credit</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="py-10 text-center text-neutral-400 italic">Loading…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={6} className="py-12 text-center" data-testid="referrals-empty-row">
                <div className="text-neutral-500 italic mb-2">No referrals to show yet.</div>
                <div className="text-[11px] text-neutral-400">When a patron signs up using someone else's referral code, the relationship will appear here.</div>
              </td></tr>
            )}
            {!loading && filtered.map((row, i) => (
              <tr key={`${row.invitee?.id}-${i}`} className="border-b border-neutral-200 hover:bg-neutral-50" data-testid={`referral-row-${row.invitee?.id}`}>
                <td className="py-3 px-3">
                  <div className="font-medium">{row.inviter?.name || <span className="italic text-neutral-400">unknown</span>}</div>
                  <div className="font-mono text-[11px] text-neutral-500">{row.inviter?.email || "—"}</div>
                </td>
                <td className="py-3 px-3 font-mono text-xs">{row.inviter?.referral_code || "—"}</td>
                <td className="py-3 px-3">
                  <div className="font-medium">{row.invitee?.name || <span className="italic text-neutral-400">no name</span>}</div>
                  <div className="font-mono text-[11px] text-neutral-500">{row.invitee?.email}</div>
                </td>
                <td className="py-3 px-3 text-xs text-neutral-600">{row.invitee?.created_at ? String(row.invitee.created_at).slice(0, 10) : "—"}</td>
                <td className="py-3 px-3">
                  {row.credited
                    ? <span className="text-[11px] bg-emerald-100 text-emerald-700 px-2 py-0.5 border border-emerald-300">Credited</span>
                    : <span className="text-[11px] bg-amber-100 text-amber-700 px-2 py-0.5 border border-amber-300">Pending</span>}
                </td>
                <td className="py-3 px-3 text-right font-mono text-xs tabular-nums">
                  {row.credited ? `+${row.credit_amount} pt` : <span className="text-neutral-400">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
